import { entrarConSesionCompartida, borrarSesion } from '../ui/sesion.js';
import Entrando from '../ui/Entrando.jsx';
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import AutoLeadsAuditPage from './AutoLeadsAuditPage';
// Sistema de diseño de la suite: los mismos tokens y las mismas piezas que ya
// usan Email Leads y Auto Leads, para que las tres apps se vean hermanas.
import { C, GRAD } from '../ui/theme.js';
import { EmptyState } from '../ui/ui.jsx';
import { StatCard, Ic } from '../ui/premium.jsx';

// ==================== SISTEMA DE LOGGING SEGURO ====================
// FASE 4: Sanitización de errores - NO exponer detalles técnicos
const secureLog = {
  // Solo en desarrollo - en producción estos no hacen nada
  dev: (...args) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[DEV]', ...args);
    }
  },
  // Errores internos - se guardan pero NO se muestran al usuario
  error: (context, error) => {
    if (process.env.NODE_ENV === 'development') {
      console.error(`[ERROR: ${context}]`, error);
    }
    // En producción, aquí podrías enviar a un servicio de monitoreo (Sentry, etc.)
  },
  // Warning interno
  warn: (context, message) => {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[WARN: ${context}]`, message);
    }
  }
};

// ==================== SUPABASE CONFIG ====================
const SUPABASE_URL = 'https://zkznupgyaorojxpydlly.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inprem51cGd5YW9yb2p4cHlkbGx5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjYwMjA5NTMsImV4cCI6MjA4MTU5Njk1M30.gKCpGxBeOCaaCRm10qQ_YpxXwTZQGbtTREsWrUG2RFY';

// ==================== EMAILJS CONFIG ====================
// Configurado para Grupo Dimoauto (misma configuración que Autoleads)
const EMAILJS_CONFIG = {
  enabled: true,
  serviceId: 'service_xxn17h1',
  templateId: 'template_rpzw4fr',
  publicKey: 'sWKqE0R5hzfSrgnme'
};

// Direcciones de centros para el email
const CENTROS_INFO = {
  'DIMOAUTO Hospitalet': { direccion: 'Rambla Marina 442, 08907 Hospitalet de Llobregat', horarios: '9:30-13:30 | 16:00-20:00 | Sáb 10:00-14:00' },
  'DIMOAUTO Molins de rei': { direccion: 'C/ Major 50, 08750 Molins de Rei', horarios: '9:30-13:30 | 16:00-20:00 | Sáb 10:00-14:00' },
  'JP CARRION Hospitalet': { direccion: 'Rambla Marina 442, 08907 Hospitalet de Llobregat', horarios: '9:30-13:30 | 16:00-20:00 | Sáb 10:00-14:00' },
  'JP CARRION Molins de rei': { direccion: 'C/ Major 50, 08750 Molins de Rei', horarios: '9:30-13:30 | 16:00-20:00 | Sáb 10:00-14:00' },
  'MAAM Cornellà de Llobregat': { direccion: 'Av. Barcelona 245, 08940 Cornellà de Llobregat', horarios: '9:30-13:30 | 16:00-20:00 | Sáb 10:00-14:00' },
  'MAAM L\'Hospitalet de Llobregat': { direccion: 'C/ Roger de Flor 245-247, 08905 L\'Hospitalet de Llobregat', horarios: '9:30-13:30 | 16:00-20:00 | Sáb 10:00-14:00' },
  'Dimoauto Flotas': { direccion: 'Rambla Marina 442, 08907 Hospitalet de Llobregat', horarios: '9:30-13:30 | 16:00-20:00 | L-V' }
};

// Cliente Supabase simple (sin dependencias externas)
const supabase = {
  from: (table) => ({
    select: async (columns = '*') => {
      const url = `${SUPABASE_URL}/rest/v1/${table}?select=${columns}`;
      try {
        const response = await fetch(url, {
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json'
          }
        });
        if (!response.ok) throw new Error(`Error ${response.status}`);
        const data = await response.json();
        return { data, error: null };
      } catch (error) {
        return { data: null, error };
      }
    },
    insert: async (records) => {
      const url = `${SUPABASE_URL}/rest/v1/${table}`;
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
            'Prefer': 'return=representation'
          },
          body: JSON.stringify(Array.isArray(records) ? records : [records])
        });
        if (!response.ok) throw new Error(`Error ${response.status}`);
        const data = await response.json();
        return { data, error: null };
      } catch (error) {
        return { data: null, error };
      }
    },
    update: (record) => ({
      eq: async (column, value) => {
        const url = `${SUPABASE_URL}/rest/v1/${table}?${column}=eq.${value}`;
        try {
          const response = await fetch(url, {
            method: 'PATCH',
            headers: {
              'apikey': SUPABASE_ANON_KEY,
              'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
              'Content-Type': 'application/json',
              'Prefer': 'return=representation'
            },
            body: JSON.stringify(record)
          });
          if (!response.ok) throw new Error(`Error ${response.status}`);
          const data = await response.json();
          return { data, error: null };
        } catch (error) {
          return { data: null, error };
        }
      }
    })
    // DELETE eliminado por seguridad - usar funciones RPC en backend
  })
};

// ==================== SUPABASE AUTH (Email/Password seguro) ====================
const supabaseAuth = {
  // Login con email/password
  signIn: async (email, password) => {
    try {
      const response = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
        method: 'POST',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });
      
      if (!response.ok) {
        const error = await response.json();
        return { data: null, error: error.error_description || 'Credenciales incorrectas' };
      }
      
      const data = await response.json();
      localStorage.setItem('sb-access-token', data.access_token);
      localStorage.setItem('sb-refresh-token', data.refresh_token);
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error.message };
    }
  },

  // Registrar nuevo usuario
  signUp: async (email, password) => {
    try {
      const response = await fetch(`${SUPABASE_URL}/auth/v1/signup`, {
        method: 'POST',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          email, 
          password,
          email_confirm: true // Auto-confirmar email
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        return { data: null, error: error.msg || error.error_description || 'Error al crear usuario' };
      }
      
      const data = await response.json();
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error.message };
    }
  },

  // Logout
  signOut: async () => {
    const token = localStorage.getItem('sb-access-token');
    if (token) {
      try {
        await fetch(`${SUPABASE_URL}/auth/v1/logout`, {
          method: 'POST',
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${token}`
          }
        });
      } catch (e) {
        // Ignorar errores de logout
      }
    }
    localStorage.removeItem('sb-access-token');
    localStorage.removeItem('sb-refresh-token');
  },

  // Obtener usuario actual
  getCurrentUser: async () => {
    const token = localStorage.getItem('sb-access-token');
    if (!token) return null;
    
    try {
      const response = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    }
  },

  // Refrescar token
  refreshSession: async () => {
    const refreshToken = localStorage.getItem('sb-refresh-token');
    if (!refreshToken) return null;
    
    try {
      const response = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`, {
        method: 'POST',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refresh_token: refreshToken })
      });
      
      if (!response.ok) return null;
      
      const data = await response.json();
      localStorage.setItem('sb-access-token', data.access_token);
      localStorage.setItem('sb-refresh-token', data.refresh_token);
      return data;
    } catch {
      return null;
    }
  }
};

// Buscar admin por auth_user_id
const findAdminByAuthId = async (authUserId) => {
  try {
    const url = `${SUPABASE_URL}/rest/v1/administradores?auth_user_id=eq.${authUserId}&activo=eq.true&select=*`;
    const response = await fetch(url, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`
      }
    });
    const data = await response.json();
    return data && data.length > 0 ? data[0] : null;
  } catch (error) {
    secureLog.error('findAdminByAuthId', error);
    return null;
  }
};

// Vincular auth_user_id a un administrador
const vincularAuthAdmin = async (adminId, authUserId) => {
  try {
    await fetch(`${SUPABASE_URL}/rest/v1/administradores?id=eq.${adminId}`, {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ auth_user_id: authUserId })
    });
  } catch (error) {
    secureLog.error('vincularAuthAdmin', error);
  }
};

// Migrar usuario existente a Supabase Auth
const migrarUsuarioAAuth = async (email, password, adminId) => {
  // 1. Crear usuario en Supabase Auth
  const { data: signUpData, error: signUpError } = await supabaseAuth.signUp(email, password);
  
  if (signUpError) {
    // Si el usuario ya existe en Auth, intentar login
    if (signUpError.includes('already registered')) {
      return { alreadyExists: true };
    }
    return { error: signUpError };
  }
  
  // 2. Vincular el auth_user_id al admin
  if (signUpData?.user?.id) {
    await vincularAuthAdmin(adminId, signUpData.user.id);
  }
  
  return { data: signUpData, error: null };
};

// Función para cargar leads desde Supabase
// El shim 'supabase' de este fichero NO es chainable (no .order/.limit),
// así que cargamos todo y ordenamos en JS: DESC por fecha_creacion (más nuevo
// en pos 001, más antiguo al final) con tiebreaker por id DESC.
// Resultado: los leads más recientes aparecen en la primera página del Listado
// detalle. La columna "Num" del listado es solo la posición visual.
const fetchLeads = async () => {
  try {
    // PostgREST limita a 1000 filas/request por defecto. El shim 'supabase'
    // no soporta .order/.range/.limit; paginamos con fetch directo (offset
    // + limit) hasta agotar el dataset.
    const PAGE_SIZE = 500;
    const MAX_ROWS = 100000;
    const rows = [];
    let offset = 0;
    while (offset < MAX_ROWS) {
      const response = await fetch(`${SUPABASE_URL}/rest/v1/leads?select=*&offset=${offset}&limit=${PAGE_SIZE}`, {
        headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error(`Error ${response.status}`);
      const data = await response.json();
      if (!Array.isArray(data) || data.length === 0) break;
      rows.push(...data);
      offset += data.length;
    }
    rows.sort((a, b) => {
      const ta = a && a.fecha_creacion ? new Date(a.fecha_creacion).getTime() : 0;
      const tb = b && b.fecha_creacion ? new Date(b.fecha_creacion).getTime() : 0;
      if (ta !== tb) return tb - ta;
      const ia = a && typeof a.id === 'number' ? a.id : Number(a && a.id) || 0;
      const ib = b && typeof b.id === 'number' ? b.id : Number(b && b.id) || 0;
      return ib - ia;
    });
    return rows;
  } catch (error) {
    secureLog.error('fetchLeads', error);
    return null;
  }
};

// Función para cargar administradores desde Supabase
const fetchAdministradores = async () => {
  try {
    const { data, error } = await supabase.from('administradores').select('*');
    if (error) throw error;
    return (data || []).filter(admin => admin.activo);
  } catch (error) {
    secureLog.error('fetchAdministradores', error);
    return null;
  }
};

// Función para cargar configuración desde Supabase
const fetchConfiguracion = async () => {
  try {
    const { data, error } = await supabase.from('configuracion').select('*');
    if (error) throw error;
    return (data || []).sort((a, b) => (a.orden ?? 999) - (b.orden ?? 999));
  } catch (error) {
    secureLog.error('fetchConfiguracion', error);
    return [];
  }
};

// Funciones de normalización
const normalizePhone = (phone) => {
  if (!phone) return '';
  let normalized = phone.replace(/[\s\-\(\)\.]/g, '');
  normalized = normalized.replace(/^(\+34|0034|34)/, '');
  normalized = normalized.replace(/^00/, '');
  return normalized.trim();
};

const normalizeEmail = (email) => {
  if (!email) return '';
  return email.toLowerCase().trim();
};

// Función para verificar si existe un lead duplicado (mismo móvil + marca)
const checkDuplicateLead = async (movil, marca) => {
  if (!movil || !marca) return { exists: false, lead: null };
  
  try {
    const url = `${SUPABASE_URL}/rest/v1/leads?movil=eq.${encodeURIComponent(movil)}&marca=eq.${encodeURIComponent(marca)}&select=id,nombre,apellidos,vendedor_nombre,estado_cita,asistencia_cita,fecha_creacion`;
    const response = await fetch(url, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`
      }
    });
    const data = await response.json();
    
    if (data && data.length > 0) {
      return { exists: true, lead: data[0] };
    }
    return { exists: false, lead: null };
  } catch (error) {
    secureLog.error('checkDuplicateLead', error);
    return { exists: false, lead: null };
  }
};

// Función para verificar si existe un lead duplicado (mismo email + marca)
const checkDuplicateByEmail = async (email, marca) => {
  if (!email || !marca) return { exists: false, lead: null };
  
  try {
    const url = `${SUPABASE_URL}/rest/v1/leads?email=eq.${encodeURIComponent(email)}&marca=eq.${encodeURIComponent(marca)}&select=id,nombre,apellidos,vendedor_nombre,estado_cita,asistencia_cita,fecha_creacion,email`;
    const response = await fetch(url, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`
      }
    });
    const data = await response.json();
    
    if (data && data.length > 0) {
      return { exists: true, lead: data[0] };
    }
    return { exists: false, lead: null };
  } catch (error) {
    console.error('Error checkDuplicateByEmail:', error);
    return { exists: false, lead: null };
  }
};

// Función para buscar duplicados existentes en la BD
const findExistingDuplicates = async () => {
  try {
    console.log('Buscando duplicados existentes...');

    // Paginamos con fetch directo (PostgREST limita a 1000/request por defecto)
    // para traer TODA la tabla de leads y poder detectar todos los duplicados.
    const PAGE_SIZE = 500;
    const MAX_ROWS = 100000;
    const allLeads = [];
    let offset = 0;
    const cols = 'id,nombre,apellidos,movil,email,marca,vendedor_nombre,estado_cita,asistencia_cita,fecha_creacion';
    while (offset < MAX_ROWS) {
      const response = await fetch(`${SUPABASE_URL}/rest/v1/leads?select=${cols}&offset=${offset}&limit=${PAGE_SIZE}`, {
        headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error(`Error ${response.status}`);
      const data = await response.json();
      if (!Array.isArray(data) || data.length === 0) break;
      allLeads.push(...data);
      offset += data.length;
    }
    if (allLeads.length === 0) return [];
    
    const duplicatesByPhone = {};
    const duplicatesByEmail = {};
    
    allLeads.forEach(lead => {
      if (lead.movil && lead.marca) {
        const normalizedMovil = normalizePhone(lead.movil);
        const key = normalizedMovil + '_' + lead.marca;
        if (!duplicatesByPhone[key]) duplicatesByPhone[key] = [];
        duplicatesByPhone[key].push(lead);
      }
      
      if (lead.email && lead.marca) {
        const normalizedEmail = normalizeEmail(lead.email);
        const key = normalizedEmail + '_' + lead.marca;
        if (!duplicatesByEmail[key]) duplicatesByEmail[key] = [];
        duplicatesByEmail[key].push(lead);
      }
    });
    
    const phoneDuplicates = Object.entries(duplicatesByPhone)
      .filter(([_, leads]) => leads.length > 1)
      .map(([key, leads]) => ({
        type: 'Movil + Marca',
        key: key.split('_')[0],
        marca: key.split('_')[1],
        count: leads.length,
        leads: leads.sort((a, b) => new Date(b.fecha_creacion) - new Date(a.fecha_creacion))
      }));
    
    const emailDuplicates = Object.entries(duplicatesByEmail)
      .filter(([_, leads]) => leads.length > 1)
      .map(([key, leads]) => ({
        type: 'Email + Marca',
        key: key.split('_')[0],
        marca: key.split('_')[1],
        count: leads.length,
        leads: leads.sort((a, b) => new Date(b.fecha_creacion) - new Date(a.fecha_creacion))
      }));
    
    return [...phoneDuplicates, ...emailDuplicates];
  } catch (error) {
    console.error('Error buscando duplicados:', error);
    return [];
  }
};

// ==================== FUNCIÓN DE AUDITORÍA ====================
// FASE 3: Registrar acciones críticas en auditoria_logs
const registrarAuditoria = async (action, tableName = null, recordId = null, details = null) => {
  // 🔍 Logging visible para debug
  console.log('🔍 registrarAuditoria llamada:', action);
  
  try {
    const token = localStorage.getItem('sb-access-token');
    if (!token) {
      console.warn('⚠️ registrarAuditoria: No hay token');
      return;
    }
    
    console.log('✅ Token encontrado');
    
    // Obtener usuario DIRECTAMENTE con fetch (más confiable que supabaseAuth.getCurrentUser)
    const userResponse = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!userResponse.ok) {
      console.error('❌ Error obteniendo usuario:', userResponse.status);
      return;
    }
    
    const user = await userResponse.json();
    console.log('✅ Usuario obtenido:', user.email);
    
    // Preparar datos de auditoría
    const auditData = {
      user_id: user.id,
      user_email: user.email,
      action,
      table_name: tableName,
      record_id: recordId ? String(recordId) : null,
      details: details || null,
      user_agent: navigator.userAgent || null  // User Agent del navegador
    };
    
    console.log('📝 Datos a insertar:', auditData);
    
    // Insertar auditoría
    const insertResponse = await fetch(`${SUPABASE_URL}/rest/v1/auditoria_logs`, {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=minimal'
      },
      body: JSON.stringify(auditData)
    });
    
    console.log('📡 Response status:', insertResponse.status);
    
    if (!insertResponse.ok) {
      const errorText = await insertResponse.text();
      console.error('❌ Error en auditoría:', insertResponse.status, errorText);
    } else {
      console.log('✅ Auditoría registrada exitosamente:', action);
    }
    
  } catch (error) {
    console.error('❌ Exception en registrarAuditoria:', error);
  }
};

// Función para insertar un nuevo lead
const insertLead = async (lead) => {
  try {
    const { data, error } = await supabase.from('leads').insert(lead);
    if (error) throw error;
    
    // FASE 3: Registrar creación en auditoría
    if (data && data[0]) {
      await registrarAuditoria('crear_lead', 'leads', data[0].id, {
        nombre: lead.nombre,
        marca: lead.marca,
        modelo: lead.modelo
      });
    }
    
    return { data, error: null };
  } catch (error) {
    secureLog.error('insertLead', error);
    return { data: null, error };
  }
};

// Función para actualizar un lead
const updateLead = async (id, updates) => {
  try {
    const { data, error } = await supabase.from('leads').update(updates).eq('id', id);
    if (error) throw error;
    
    // FASE 3: Registrar modificación en auditoría
    await registrarAuditoria('modificar_lead', 'leads', id, {
      campos_modificados: Object.keys(updates)
    });
    
    return { data, error: null };
  } catch (error) {
    secureLog.error('updateLead', error);
    return { data: null, error };
  }
};

// Función para cargar comerciales desde Supabase
const fetchComerciales = async () => {
  try {
    const { data, error } = await supabase.from('comerciales').select('*');
    if (error) throw error;
    return data || [];
  } catch (error) {
    secureLog.error('fetchComerciales', error);
    return [];
  }
};

// Función para cargar TODOS los administradores (para configuración)
const fetchAllAdministradores = async () => {
  try {
    const { data, error } = await supabase.from('administradores').select('*');
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching administradores:', error);
    return [];
  }
};

// Función para insertar un nuevo comercial
const insertComercial = async (comercial) => {
  try {
    const { data, error } = await supabase.from('comerciales').insert({
      nombre: comercial.nombre,
      email: comercial.email,
      centro: comercial.centro,
      auth_user_id: comercial.auth_user_id,
      activo: true
    });
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    secureLog.error('insertComercial', error);
    return { data: null, error };
  }
};

// Función para insertar un nuevo administrador
const insertAdministrador = async (admin) => {
  try {
    const { data, error } = await supabase.from('administradores').insert({
      nombre: admin.nombre,
      email: admin.email,
      auth_user_id: admin.auth_user_id,
      activo: true
    });
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    secureLog.error('insertAdministrador', error);
    return { data: null, error };
  }
};

// Función para eliminar un comercial (via RPC seguro)
const deleteComercial = async (id) => {
  try {
    const url = `${SUPABASE_URL}/rest/v1/rpc/delete_comercial`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ comercial_id: id })
    });
    if (!response.ok) throw new Error(`Error ${response.status}`);
    return { error: null };
  } catch (error) {
    secureLog.error('deleteComercial', error);
    return { error };
  }
};

// Función para eliminar un administrador (via RPC seguro)
const deleteAdministrador = async (id) => {
  try {
    const url = `${SUPABASE_URL}/rest/v1/rpc/delete_administrador`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ admin_id: id })
    });
    if (!response.ok) throw new Error(`Error ${response.status}`);
    return { error: null };
  } catch (error) {
    secureLog.error('deleteAdministrador', error);
    return { error };
  }
};

// Función para insertar configuración
const insertConfiguracion = async (tipo, valor, color = null) => {
  try {
    const record = {
      tipo,
      valor,
      activo: true,
      orden: 99
    };
    if (color) record.color = color;
    
    const { data, error } = await supabase.from('configuracion').insert(record);
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    secureLog.error('insertConfiguracion', error);
    return { data: null, error };
  }
};

// Función para eliminar configuración (via RPC seguro)
const deleteConfiguracion = async (tipo, valor) => {
  try {
    const url = `${SUPABASE_URL}/rest/v1/rpc/delete_configuracion`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ config_tipo: tipo, config_valor: valor })
    });
    if (!response.ok) throw new Error(`Error ${response.status}`);
    return { error: null };
  } catch (error) {
    secureLog.error('deleteConfiguracion', error);
    return { error };
  }
};

// Transformar datos de Supabase al formato del CRM
const transformSupabaseData = (rows) => {
  if (!rows || rows.length === 0) return [];
  
  return rows.map((row, index) => {
    const origen = row.origen || 'Desconocido';
    const origenData = ORIGENES.find(o => o.nombre === origen) || { color: '#808080' };
    
    const fechaCreacion = row.fecha_creacion ? new Date(row.fecha_creacion) : null;
    const primeraLlamada = row.primera_llamada ? new Date(row.primera_llamada) : null;
    
    return {
      id: row.id,
      num: String(index + 1).padStart(3, '0'),
      leadId: row.lead_id || `lead-${row.id}`,
      tipo: row.lead_tipo || 'Lead',
      origen: origen,
      origenColor: origenData.color,
      detalleOrigen: row.detalle_origen || 'Orgánico',
      tieneFicha: false,
      tieneObservaciones: !!row.observaciones,
      campana: row.campana || '',
      estado: row.estado_cita || 'Pendiente',
      motivoNoCompra: row.motivo_no_compra || null,
      usuarioAlta: row.usuario_alta || '',
      fechaCreacion: fechaCreacion,
      tiempoHastaPrimeraLlamada: primeraLlamada && fechaCreacion ? primeraLlamada.getTime() - fechaCreacion.getTime() : null,
      primeraLlamada: primeraLlamada,
      ultimaLlamada: row.ultima_llamada ? new Date(row.ultima_llamada) : null,
      duracionLlamada: 0,
      citaAgendada: row.fecha_agendada ? new Date(row.fecha_agendada) : null,
      // tipo_agenda distingue una CITA de verdad de un seguimiento o de una
      // llamada programada (los tres usan el mismo campo fecha_agendada)
      tipoAgenda: row.tipo_agenda || null,
      asistenciaCita: row.asistencia_cita || null,
      tiempoDesdeCita: null,
      fechaModificacion: row.fecha_modificacion ? new Date(row.fecha_modificacion) : null,
      // Fecha en la que el comercial marcó la venta (para "Ventas anteriores")
      fechaVenta: row.fecha_venta ? new Date(row.fecha_venta) : null,
      // Datos del cliente
      nombre: row.nombre || '',
      apellidos: row.apellidos || '',
      razonSocial: row.razon_social || null,
      tipoCliente: row.tipo_cliente || 'Particular',
      telefono: row.telefono || '',
      movil: row.movil || '',
      email: row.email || '',
      cp: row.cp || '',
      provincia: row.provincia || '',
      // Datos del vehículo
      marca: row.marca || 'Desconocido',
      modelo: row.modelo || '',
      motor: row.motor || '',
      combustible: row.combustible || '',
      kilometros: parseInt(row.kilometros) || 0,
      matricula: row.matricula || null,
      tipoVenta: row.tipo_venta || 'VN',
      // Asignación
      vendedor: row.vendedor_nombre || '',
      centro: row.centro || 'No categorizado',
      // UTM
      utmSource: row.utm_source || null,
      utmMedium: row.utm_medium || null,
      utmCampaign: row.utm_campaign || null,
      // Extras
      observaciones: row.observaciones || '',
      enlaceOrigen: row.enlace_origen || '',
      historial: row.historial || [],
      comentarios: row.comentarios || []
    };
  });
};


// FIX 30-08: primera respuesta REAL de un lead = su primera acción registrada
// (primer apunte del historial o la primera llamada), NUNCA fecha_modificacion,
// que es la ÚLTIMA modificación y penalizaba a quien más trabaja sus leads.
const primeraRespuestaMs = (l) => {
  if (!l?.fechaCreacion) return null;
  const fc = new Date(l.fechaCreacion).getTime();
  if (!Number.isFinite(fc)) return null;
  const candidatas = [];
  if (Array.isArray(l.historial)) {
    for (const h of l.historial) {
      const t = h && h.fecha ? new Date(h.fecha).getTime() : NaN;
      if (Number.isFinite(t)) candidatas.push(t);
    }
  }
  if (l.primeraLlamada) {
    const t = new Date(l.primeraLlamada).getTime();
    if (Number.isFinite(t)) candidatas.push(t);
  }
  if (!candidatas.length) return null;
  const ms = Math.min(...candidatas) - fc;
  if (!Number.isFinite(ms) || ms < 0 || ms > 365 * 24 * 60 * 60 * 1000) return null;
  return ms;
};

// ==================== DATA (valores por defecto, se sobreescriben con Supabase) ====================
// Estos valores se usan como fallback si Supabase no está disponible
let ESTADOS_LEAD = {
  'Pendiente': { color: '#C0C0C0' },
  'Contactado': { color: '#7DD3FC' },
  'Atendido': { color: '#FFD700' },
  'Citado': { color: '#FFB366' },
  'Ofertado': { color: '#FFA500' },
  'Cancelado': { color: '#808080' },
  'Vendido': { color: '#32CD32' }
};

const LEAD_TIPOS = { 'Lead Válido': '#FFFF00', 'Visita particular': '#32CD32', 'Visita empresa': '#228B22', 'Llamada': '#87CEEB' };
// Misma lista que Recepción (panel-recepcion MOTIVOS_CANCELACION) y que el CRM de
// comerciales — petición Adriana 13/09: una única lista de motivos de descarte.
const MOTIVOS_NO_COMPRA = ['Pospone compra', 'Compra en competencia', 'Imposible contactar', 'Tras contactar, ya no contesta', 'Por precio', 'Por ubicación', 'No tenemos lo que busca', 'No ha pedido información', 'Financiera denegada', 'Vender su coche', 'Compra otra marca dentro del grupo'];
const motivosConActual = (actual) => (actual && !MOTIVOS_NO_COMPRA.includes(actual) ? [actual, ...MOTIVOS_NO_COMPRA] : MOTIVOS_NO_COMPRA);

// Valores por defecto (fallback) - Se cargan desde Supabase
let CENTROS_DEFAULT = ['DIMOAUTO Hospitalet', 'DIMOAUTO Molins de rei', 'JP CARRION Hospitalet', 'JP CARRION Molins de rei', 'MAAM Cornellà de Llobregat', 'MAAM L\'Hospitalet de Llobregat', 'Dimoauto Flotas', 'No categorizado'];
let MARCAS_DEFAULT = ['Peugeot', 'Citroën', 'Opel', 'Fiat', 'Jeep', 'Abarth', 'Leapmotor', 'Desconocido'];
let ORIGENES_DEFAULT = [
  { nombre: 'Carnovo', color: '#1E3A5F' }, { nombre: 'Coches.net', color: '#FF6B35' },
  { nombre: 'Autocasión', color: '#808080' }, { nombre: 'StockSpark', color: '#A0A0A0' },
  { nombre: 'Wallapop', color: '#13C1AC' }, { nombre: 'CUSTOMER PEUGEOT', color: '#0066B3' },
  { nombre: 'CUSTOMER CITROEN', color: '#AC0F25' }, { nombre: 'CUSTOMER OPEL', color: '#9aa4af' },
  { nombre: 'CUSTOMER JEEP', color: '#2F4F4F' }, { nombre: 'CUSTOMER FIAT', color: '#005187' },
  { nombre: 'Facebook', color: '#1877F2' }, { nombre: 'Instagram', color: '#E4405F' },
  { nombre: 'Web Leapmotor', color: '#00A0E3' }, { nombre: 'Web Dimoauto', color: '#6B0000' }
];
let CAMPANAS_DEFAULT = ['Carnovo Dimoauto', 'Coches.net Dimoauto', 'Coches.net J.P.Carrión', 'LEADS REDES SOCIALES', 'Traspaso Leads', 'Wallapop Dimoauto', 'Web Dimoauto'];

// Variables que se actualizarán con datos de Supabase
let CENTROS = [...CENTROS_DEFAULT];
let MARCAS = [...MARCAS_DEFAULT];
let ORIGENES = [...ORIGENES_DEFAULT];
let CAMPANAS = [...CAMPANAS_DEFAULT];

const USUARIOS = ['Debora Menacho', 'Esther Fernandez', 'Brian Suárez', 'Jessica Rojo', 'Sistema AVI Automoción'];
const VENDEDORES_DEFAULT = [];
let VENDEDORES = [...VENDEDORES_DEFAULT];

const COMBUSTIBLES = ['Gasolina', 'Diésel', 'Híbrido', 'Eléctrico', 'GLP'];
const TIPOS_VENTA = ['VN', 'VO', 'B2B'];
const TIPOS_CLIENTE = ['Particular', 'Empresa'];

// Función para procesar configuración de Supabase
const processConfiguracion = (configRows) => {
  if (!configRows || configRows.length === 0) return;
  
  const estados = {};
  const centros = [];
  const marcas = [];
  const origenes = [];
  const campanas = [];
  
  configRows.forEach(row => {
    // Verificar activo como boolean o string
    const isActivo = row.activo === true || row.activo === 'true';
    if (!isActivo) return;
    
    switch (row.tipo) {
      case 'estado':
        estados[row.valor] = { color: row.color || '#808080' };
        break;
      case 'centro':
        centros.push(row.valor);
        break;
      case 'marca':
        marcas.push(row.valor);
        break;
      case 'origen':
        origenes.push({ nombre: row.valor, color: row.color || '#808080' });
        break;
      case 'campana':
        campanas.push(row.valor);
        break;
    }
  });
  
  // Actualizar variables globales
  if (Object.keys(estados).length > 0) ESTADOS_LEAD = estados;
  if (centros.length > 0) CENTROS = centros;
  if (marcas.length > 0) MARCAS = marcas;
  if (origenes.length > 0) ORIGENES = origenes;
  if (campanas.length > 0) CAMPANAS = campanas;
  
  console.log('✅ Configuración cargada desde Supabase:', {
    estados: Object.keys(ESTADOS_LEAD).length,
    centros: CENTROS.length,
    marcas: MARCAS.length,
    origenes: ORIGENES.length,
    campanas: CAMPANAS.length
  });
};

// Función para cargar vendedores desde comerciales
const loadVendedoresFromComerciales = async () => {
  const comerciales = await fetchComerciales();
  if (comerciales && comerciales.length > 0) {
    VENDEDORES = comerciales.filter(c => c.activo).map(c => c.nombre);
    console.log('✅ Vendedores cargados desde Supabase:', VENDEDORES.length);
  }
};

// Helpers
const formatDate = (date) => date ? new Date(date).toLocaleDateString('es-ES') : '-';
const formatDateTime = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  return `${d.toLocaleDateString('es-ES')} ${d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`;
};
const formatDuration = (ms) => {
  if (!ms || ms < 0) return '-';
  const days = Math.floor(ms / (24 * 60 * 60 * 1000));
  const hours = Math.floor((ms % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
  const minutes = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
  let result = '';
  if (days > 0) result += `${days}d `;
  result += `${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m`;
  return result;
};
const formatDurationShort = (minutes) => minutes ? `${minutes}m` : '00m';
const getTimeClass = (ms) => {
  if (!ms) return '';
  const hours = ms / (60 * 60 * 1000);
  if (hours < 1) return '#228B22';
  if (hours < 24) return '#FFA500';
  return '#DC143C';
};

// Función para sanitizar texto y evitar problemas con caracteres especiales en JSON
const sanitizeText = (text) => {
  if (!text) return '';
  return text
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, '') // Caracteres de control
    .replace(/[""'']/g, '"') // Comillas tipográficas a rectas
    .replace(/[—–]/g, '-') // Guiones especiales
    .replace(/…/g, '...') // Puntos suspensivos
    .replace(/[\\]/g, '') // Backslashes
    .replace(/[\t\r]/g, ' ') // Tabuladores y retornos
    .replace(/\n/g, ' ') // Saltos de línea
    .trim();
};

// Función para validar input en tiempo real (solo permite caracteres seguros)
const handleSafeInput = (e, setter, fieldName, currentData) => {
  const value = e.target.value;
  // Permitir solo caracteres alfanuméricos, espacios y puntuación básica
  const sanitized = value.replace(/[^\w\sáéíóúüñÁÉÍÓÚÜÑ.,;:!?¿¡@#€$%&()+-]/g, '');
  setter({ ...currentData, [fieldName]: sanitized });
};

const aggregateByField = (leads, field) => {
  const aggregated = {};
  leads.forEach(lead => {
    const key = lead[field] || 'No categorizado';
    if (!aggregated[key]) {
      aggregated[key] = { nombre: key, total: 0, estados: {} };
      Object.keys(ESTADOS_LEAD).forEach(estado => { aggregated[key].estados[estado] = 0; });
    }
    aggregated[key].total++;
    aggregated[key].estados[lead.estado]++;
  });
  return Object.values(aggregated).map(item => ({
    ...item, ...item.estados,
    porcentajeCierre: item.total > 0 ? ((item.estados['Vendido'] || 0) / item.total * 100).toFixed(2) : '0.00'
  }));
};

// Helper para normalizar/unificar modelos (agrupa variantes como "B10", "B10 Blanco" -> "B10")
const normalizeModelo = (modelo) => {
  if (!modelo) return '';
  // Extraer la primera palabra y convertir a mayúsculas
  const firstWord = modelo.trim().split(/[\s-]/)[0].toUpperCase();
  return firstWord;
};

// Helper para obtener modelos unificados con sus variantes originales
const getUnifiedModelos = (leads, filterMarca = '') => {
  const modelosMap = {};
  leads
    .filter(l => !filterMarca || l.marca === filterMarca)
    .forEach(lead => {
      if (!lead.modelo) return;
      const normalized = normalizeModelo(lead.modelo);
      if (!modelosMap[normalized]) {
        modelosMap[normalized] = {
          normalized,
          variants: new Set()
        };
      }
      modelosMap[normalized].variants.add(lead.modelo);
    });
  
  return Object.values(modelosMap)
    .map(m => ({
      normalized: m.normalized,
      variants: Array.from(m.variants)
    }))
    .sort((a, b) => a.normalized.localeCompare(b.normalized));
};

// Helper para verificar si un modelo original pertenece a un grupo normalizado
const modeloMatchesNormalized = (modeloOriginal, modeloNormalizado) => {
  if (!modeloOriginal || !modeloNormalizado) return false;
  return normalizeModelo(modeloOriginal) === modeloNormalizado;
};

// ── Formas del sistema de diseño de la suite ───────────────────────────────
// Solo presentación: mismos datos y misma disposición, pero con la forma de
// siempre del resto de apps del grupo. Los radios van por familia: 10 en
// botones y campos, 12 en tarjetas, 999 en píldoras.
const pildora = (color) => ({
  display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap',
  padding: '3px 10px', borderRadius: 999, fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.3px',
  color, background: `${color}18`, border: `1px solid ${color}44`,
});
const botonPrimario = {
  background: C.brand, color: '#fff', border: 'none', borderRadius: 10,
  padding: '9px 16px', fontSize: '13px', fontWeight: 800, cursor: 'pointer',
  fontFamily: 'inherit', boxShadow: '0 3px 10px rgba(0,81,135,.28)',
};
const botonSecundario = {
  background: '#fff', color: C.page, border: `1px solid ${C.line}`, borderRadius: 10,
  padding: '9px 16px', fontSize: '13px', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit',
};
const botonPeligro = {
  background: C.rojo, color: '#fff', border: 'none', borderRadius: 10,
  padding: '9px 16px', fontSize: '13px', fontWeight: 800, cursor: 'pointer',
  fontFamily: 'inherit', boxShadow: '0 3px 10px rgba(210,59,52,.26)',
};
const tarjeta = { background: C.card, border: `1px solid ${C.line}`, borderRadius: 12 };
const campo = {
  border: `1.5px solid ${C.line}`, borderRadius: 10, background: '#fff',
  fontSize: '13.5px', padding: '8px 12px', color: C.ink, boxSizing: 'border-box', fontFamily: 'inherit',
};
// Tabla: cabecera gris clara en versalitas y filas separadas por una línea fina.
const celdaTitulo = {
  padding: '9px 12px', textAlign: 'left', background: '#f7f8fa', color: C.muted,
  fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.3px', textTransform: 'uppercase',
  borderBottom: `1px solid ${C.line}`,
};
const SEPARADOR_FILA = '1px solid #eaecef';
// Modales: el mismo velo y la misma sombra en toda la suite.
const VELO_MODAL = 'rgba(15,28,43,0.65)';
const SOMBRA_MODAL = '0 20px 60px rgba(15,42,70,0.35)';
// Color de cada estado del lead en la paleta de la suite: un solo verde, un
// solo ámbar y un solo rojo. Es una capa de presentación — los estados y sus
// colores de origen (ESTADOS_LEAD, que viene de Supabase) no se tocan.
// Lo mismo para el tipo de lead: los hex originales (amarillo puro, verde lima)
// no se leen sobre un fondo claro, así que en pantalla se pintan con la paleta.
const COLOR_ESTADO = {
  'Pendiente': C.naranja,
  'Contactado': C.brand,
  'Atendido': C.brand,
  'Citado': C.morado,
  'Ofertado': C.naranja,
  'Cancelado': C.rojo,
  'Vendido': C.verde,
};
const colorEstado = (estado) => COLOR_ESTADO[estado] || ESTADOS_LEAD[estado]?.color || C.gris;

const COLOR_TIPO_LEAD = {
  'Lead Válido': C.brand,
  'Visita particular': C.verde,
  'Visita empresa': C.morado,
  'Llamada': C.naranja,
};
const colorTipoLead = (tipo) => COLOR_TIPO_LEAD[tipo] || C.gris;

// Components - Header con usuario y logout
// (El logo de respaldo ya no hace falta: la cabecera no pinta marca, de eso se
//  encarga el menú lateral de la suite.)

// ==================== FUNCIÓN PARA ENVIAR EMAILS ====================
// ==================== EMAIL SERVICE (EmailJS) ====================
const sendEmailNotification = async (to, subject, body, type = 'lead_update', lead = {}, comercial = {}) => {
  console.log('📧 sendEmailNotification llamada:', { to, subject, type });
  
  if (!EMAILJS_CONFIG.enabled) {
    console.log('📧 EmailJS desactivado - configurar en EMAILJS_CONFIG');
    return { success: false, reason: 'disabled' };
  }
  
  if (!to) {
    console.log('📧 Email de destino no proporcionado');
    return { success: false, reason: 'no_email' };
  }
  
  // Cargar EmailJS si no está cargado
  if (!window.emailjs) {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/@emailjs/browser@3/dist/email.min.js';
    document.head.appendChild(script);
    await new Promise(resolve => script.onload = resolve);
    window.emailjs.init(EMAILJS_CONFIG.publicKey);
  }

  const centroInfo = CENTROS_INFO[comercial.centro || lead.centro] || { 
    direccion: comercial.centro || lead.centro || '', 
    horarios: '' 
  };
  const now = new Date();
  
  // Preparar parámetros del template según el tipo de notificación
  const templateParams = {
    // Destinatario
    to_email: to,
    to_name: comercial.nombre || 'Comercial',
    
    // Subject
    subject: subject,
    
    // CITA - usar fecha actual si no se proporciona
    fecha_cita: lead.citaAgendada 
      ? new Date(lead.citaAgendada).toLocaleDateString('es-ES')
      : now.toLocaleDateString('es-ES'),
    hora_cita: lead.citaAgendada 
      ? new Date(lead.citaAgendada).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
      : now.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
    tipo_cita: type === 'new_lead' ? 'Lead Válido' : 'Actualización de Lead',
    
    // COMERCIAL ASIGNADO
    comercial_nombre: comercial.nombre || '',
    comercial_apellidos: comercial.apellidos || comercial.cargo || '',
    comercial_telefono: comercial.telefono || '',
    comercial_email: to,
    
    // OBSERVACIONES
    origen: lead.origen || 'CRM Admin',
    
    // CLIENTE
    razon_social: lead.razonSocial || '',
    cliente_nombre: lead.nombre || '',
    cliente_apellidos: lead.apellidos || '',
    
    // CONTACTO
    telefono: lead.telefono || '',
    movil: lead.movil || lead.telefono || '',
    cliente_email: lead.email || '',
    
    // VEHÍCULO
    marca: lead.marca || '',
    modelo: lead.modelo || '',
    motor: lead.motor || '',
    matricula: lead.matricula || '',
    kilometros: lead.kilometros ? String(lead.kilometros) : '0',
    
    // UBICACIÓN
    centro_nombre: comercial.centro || lead.centro || '',
    centro_direccion: centroInfo.direccion,
    centro_horarios: centroInfo.horarios
  };

  try {
    const response = await window.emailjs.send(
      EMAILJS_CONFIG.serviceId, 
      EMAILJS_CONFIG.templateId, 
      templateParams
    );
    console.log('✅ Email enviado exitosamente a', to);
    return { success: true, response };
  } catch (error) {
    console.error('❌ Error enviando email:', error);
    return { success: false, error: 'Error al enviar el email' };
  }
};


// Función para notificar al comercial sobre cambios en un lead
const notifyComercialLeadUpdate = async (comerciales, vendedorNombre, lead, changeType, adminName) => {
  console.log('🔔 notifyComercialLeadUpdate llamada:', { vendedorNombre, changeType, adminName });
  
  const comercial = comerciales.find(c => c.nombre === vendedorNombre);
  
  if (!comercial) {
    console.warn('⚠️ No se encontró comercial con nombre:', vendedorNombre);
    console.log('📋 Comerciales disponibles:', comerciales.map(c => c.nombre));
    secureLog.warn('notifyComercialLeadUpdate', 'No se encontró comercial');
    return;
  }
  
  if (!comercial.email) {
    console.warn('⚠️ Comercial sin email:', vendedorNombre);
    secureLog.warn('notifyComercialLeadUpdate', 'No email del comercial');
    return;
  }
  
  console.log('✅ Comercial encontrado:', { nombre: comercial.nombre, email: comercial.email });
  
  const subject = changeType === 'new' 
    ? `🆕 Nuevo Lead Asignado: ${lead.nombre || 'Sin nombre'}`
    : `📝 Lead Actualizado: ${lead.nombre || 'Sin nombre'}`;
  
  const body = `
Hola ${vendedorNombre},

${changeType === 'new' ? 'Se te ha asignado un nuevo lead' : 'Se ha actualizado un lead asignado a ti'} desde el Panel de Administración.

📋 DETALLES DEL LEAD:
━━━━━━━━━━━━━━━━━━━━━
• Cliente: ${lead.nombre || ''} ${lead.apellidos || ''}
• Teléfono: ${lead.movil || lead.telefono || 'No disponible'}
• Email: ${lead.email || 'No disponible'}
• Marca: ${lead.marca || 'Sin especificar'}
• Modelo: ${lead.modelo || 'Sin especificar'}
• Estado: ${lead.estado || 'Pendiente'}
• Centro: ${lead.centro || 'Sin especificar'}
${lead.citaAgendada ? `• Cita: ${new Date(lead.citaAgendada).toLocaleString('es-ES')}` : ''}

${changeType === 'update' ? `
📝 MODIFICACIÓN:
━━━━━━━━━━━━━━━━
Modificado por: ${adminName}
Fecha: ${new Date().toLocaleString('es-ES')}
` : ''}

Accede al CRM Comerciales para más detalles.

Saludos,
Sistema Lead Center - Grupo Dimoauto
  `.trim();
  
  console.log('📧 Enviando email a:', comercial.email);
  
  // Pasar comercial y lead completos para que EmailJS tenga todos los datos
  const result = await sendEmailNotification(
    comercial.email, 
    subject, 
    body, 
    changeType === 'new' ? 'new_lead' : 'lead_update',
    lead,
    comercial
  );
  
  console.log('📬 Resultado envío email:', result);
};

// ==================== MODAL CAMBIO DE CONTRASEÑA ====================
const ChangePasswordModal = ({ onClose, admin }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    
    if (newPassword.length < 6) {
      setError('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Primero verificar la contraseña actual haciendo login
      const { data: verifyData, error: verifyError } = await supabaseAuth.signIn(admin.email, currentPassword);
      
      if (verifyError) {
        setError('La contraseña actual es incorrecta');
        setIsLoading(false);
        return;
      }
      
      // Actualizar contraseña usando la API de Supabase Auth
      const token = localStorage.getItem('sb-access-token');
      const response = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
        method: 'PUT',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password: newPassword })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al cambiar la contraseña');
      }
      
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
      
    } catch (err) {
      setError(err.message || 'Error al cambiar la contraseña');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: VELO_MODAL,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 12,
        width: '100%',
        maxWidth: '400px',
        boxShadow: SOMBRA_MODAL,
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          background: GRAD,
          padding: '16px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h3 style={{margin: 0, color: 'white', fontSize: '18px', fontWeight: 600}}>
            🔐 Cambiar Contraseña
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              color: 'white',
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            ✕
          </button>
        </div>
        
        {/* Content */}
        <form onSubmit={handleChangePassword} style={{padding: '24px'}}>
          {success ? (
            <div style={{
              background: '#e6f4ec',
              border: '1px solid #2e9e5b',
              borderRadius: 10,
              padding: '20px',
              textAlign: 'center'
            }}>
              <span style={{fontSize: '48px', display: 'block', marginBottom: '12px'}}>✅</span>
              <p style={{color: '#2e9e5b', fontWeight: 600, margin: 0}}>
                Contraseña cambiada correctamente
              </p>
            </div>
          ) : (
            <>
              <div style={{marginBottom: '16px'}}>
                <label style={{display: 'block', fontSize: '13px', fontWeight: 500, color: '#2b333d', marginBottom: '6px'}}>
                  Usuario
                </label>
                <input
                  type="text"
                  value={admin?.email || ''}
                  disabled
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: '1px solid #e7e9ec',
                    fontSize: '14px',
                    background: '#f4f5f7',
                    color: '#8a929c',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              
              <div style={{marginBottom: '16px'}}>
                <label style={{display: 'block', fontSize: '13px', fontWeight: 500, color: '#2b333d', marginBottom: '6px'}}>
                  Contraseña actual *
                </label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: '1px solid #e7e9ec',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              
              <div style={{marginBottom: '16px'}}>
                <label style={{display: 'block', fontSize: '13px', fontWeight: 500, color: '#2b333d', marginBottom: '6px'}}>
                  Nueva contraseña *
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: '1px solid #e7e9ec',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
                <span style={{fontSize: '13px', color: '#8a929c'}}>Mínimo 6 caracteres</span>
              </div>
              
              <div style={{marginBottom: '20px'}}>
                <label style={{display: 'block', fontSize: '13px', fontWeight: 500, color: '#2b333d', marginBottom: '6px'}}>
                  Confirmar nueva contraseña *
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: '1px solid #e7e9ec',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              
              {error && (
                <div style={{
                  background: '#f9e4e3',
                  border: '1px solid #f2ccca',
                  borderRadius: 10,
                  padding: '10px 14px',
                  marginBottom: '16px',
                  color: '#d23b34',
                  fontSize: '13px'
                }}>
                  ⚠️ {error}
                </div>
              )}
              
              <div style={{display: 'flex', gap: '12px'}}>
                <button
                  type="button"
                  onClick={onClose}
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: 10,
                    border: '1px solid #e7e9ec',
                    background: '#fff',
                    color: '#2b333d',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: 10,
                    border: 'none',
                    background: '#005187',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    opacity: isLoading ? 0.7 : 1
                  }}
                >
                  {isLoading ? '⏳ Cambiando...' : '🔐 Cambiar Contraseña'}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

// Cabecera con la forma de la suite: sin logotipo (de la marca ya se encarga el
// menú lateral, y verla dos veces era el defecto más visible del conjunto),
// título de 19 px y una línea que explica qué se está mirando.
/* Cabecera aligerada (petición 13-ago): se recupera altura arriba.

   Se van tres cosas que se leían una vez y luego solo ocupaban:
   · las píldoras «Supabase» y «Real-time activo» — son estado técnico, y si la
     conexión falla ya se entera uno porque no hay datos;
   · el subtítulo «Panel de administración del CRM…», que describía lo que se
     tiene delante;
   · y, en la vista general, el rótulo «Estado Lead Center», que repetía el
     título de la pantalla dos líneas más abajo.

   El buscador sube a esta misma fila, a la derecha del título: era el único
   control de la barra de filtros que no es un filtro, y aquí aprovecha el
   hueco que dejaba el subtítulo en vez de pedir su propio renglón. */
const Header = ({ admin, onLogout, onOpenChangePassword, filters, setFilters }) => {
  return (
  <header style={{background: C.card, padding: '9px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '14px', flexWrap: 'wrap', borderBottom: `1px solid ${C.line}`}}>
    <div style={{display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap', minWidth: 0}}>
      <h1 style={{fontSize: '19px', fontWeight: 800, color: C.ink, margin: 0, letterSpacing: '-0.2px', whiteSpace: 'nowrap'}}>Lead Center</h1>

      {setFilters && (
        <div style={{...campo, display: 'flex', alignItems: 'center', padding: '5px 10px', gap: '6px'}}>
          <span style={{color: C.muted, fontSize: '13px'}}>🔍</span>
          <input
            className="fb-buscador"
            style={{background: 'transparent', border: 'none', color: C.ink, fontSize: '13.5px', width: '190px', outline: 'none', fontFamily: 'inherit'}}
            type="text"
            placeholder="Buscar cliente, teléfono, matrícula…"
            value={filters?.search || ''}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
          {/* El placeholder se pinta solo aquí: sin la clase, la regla teñía de
              blanco el de todos los campos de la aplicación. */}
          <style>{`.fb-buscador::placeholder { color: ${C.faint}; opacity: 1; }`}</style>
        </div>
      )}
    </div>
    <div style={{display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap'}}>
      {admin && (
        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
          <div
            onClick={onOpenChangePassword}
            className="fg-nav"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              background: '#fff',
              borderRadius: 999,
              fontSize: '12px',
              fontWeight: 700,
              color: C.page,
              border: `1px solid ${C.line}`,
              cursor: 'pointer'
            }}
            title="Clic para cambiar contraseña"
          >
            <span>👤</span>
            <span>{admin.nombre}</span>
            <span style={{fontSize: '10px', opacity: 0.6}}>🔐</span>
          </div>
          <button
            onClick={onLogout}
            style={{...botonSecundario, padding: '7px 12px', color: C.rojo, display: 'flex', alignItems: 'center', gap: '4px'}}
            title="Cerrar sesión"
          >
            🚪 Salir
          </button>
        </div>
      )}
    </div>
  </header>
  );
};

const FilterBar = ({ filters, setFilters, onRefresh, leads = [] }) => {
  const resetFilters = () => {
    const today = new Date();
    const hasta = today.toISOString().split('T')[0];
    const desde = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    setFilters({ search: '', desde, hasta, tipoLead: '', marca: '', modelo: '', comercial: '' });
  };

  // Extraer opciones únicas de los leads
  const marcasUnicas = useMemo(() => [...new Set(leads.map(l => l.marca).filter(Boolean))].sort(), [leads]);
  // Modelos unificados (agrupa variantes como B10, B10 Blanco -> B10)
  const modelosUnificados = useMemo(() => getUnifiedModelos(leads, filters.marca), [leads, filters.marca]);
  const comercialesUnicos = useMemo(() => [...new Set(leads.map(l => l.vendedor).filter(Boolean))].sort(), [leads]);
  
  return (
    // Barra de filtros en blanco, con los campos de la suite (radio 10, borde
    // fino). Antes era una franja azul con controles sin borde que no se
    // distinguían del fondo.
    <div style={{background: C.card, padding: '9px 16px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', fontSize: '13px', borderBottom: `1px solid ${C.line}`}}>
      {/* El buscador se ha ido a la cabecera, junto al título: no era un filtro
          y aquí obligaba a un spacer para centrar los que sí lo son. */}

      {/* Filtros centrados */}
      <div style={{display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap'}}>
        <div style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
          <label style={{color: C.muted, fontSize: '12.5px', fontWeight: 700}}>Desde</label>
          <input style={{...campo, padding: '6px 9px'}} type="date" value={filters.desde} onChange={(e) => setFilters({ ...filters, desde: e.target.value })} />
        </div>
        <div style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
          <label style={{color: C.muted, fontSize: '12.5px', fontWeight: 700}}>Hasta</label>
          <input style={{...campo, padding: '6px 9px'}} type="date" value={filters.hasta} onChange={(e) => setFilters({ ...filters, hasta: e.target.value })} />
        </div>
        <div style={{position: 'relative'}}>
          <select style={{...campo, padding: '6px 9px', cursor: 'pointer'}} value={filters.tipoLead} onChange={(e) => setFilters({ ...filters, tipoLead: e.target.value })}>
            <option value="">Todos ▼</option>
            <option value="all">Todos</option>
            {Object.keys(LEAD_TIPOS).map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        {/* Nuevo filtro: Marca */}
        <select style={{...campo, padding: '6px 9px', cursor: 'pointer', minWidth: '90px'}} value={filters.marca} onChange={(e) => setFilters({ ...filters, marca: e.target.value, modelo: '' })}>
          <option value="">Marca ▼</option>
          {marcasUnicas.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        {/* Nuevo filtro: Modelo (unificado) */}
        <select style={{...campo, padding: '6px 9px', cursor: 'pointer', minWidth: '90px'}} value={filters.modelo} onChange={(e) => setFilters({ ...filters, modelo: e.target.value })}>
          <option value="">Modelo ▼</option>
          {modelosUnificados.map(m => (
            <option key={m.normalized} value={m.normalized}>
              {m.normalized} {m.variants.length > 1 ? `(${m.variants.length})` : ''}
            </option>
          ))}
        </select>
        {/* Nuevo filtro: Comercial */}
        <select style={{...campo, padding: '6px 9px', cursor: 'pointer', minWidth: '105px'}} value={filters.comercial} onChange={(e) => setFilters({ ...filters, comercial: e.target.value })}>
          <option value="">Comercial ▼</option>
          {comercialesUnicos.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <button className="fg-nav" style={{...botonSecundario, padding: '7px 13px'}} onClick={resetFilters}>Limpiar filtros</button>
        <button style={{...botonPrimario, padding: '7px 13px'}} onClick={onRefresh}>Actualizar Informe</button>
      </div>
      
      {/* Spacer para centrar */}
      <div style={{flex: 1}}></div>
    </div>
  );
};

// ==================== FUNCIÓN ENVÍO EMAIL AL COMERCIAL ====================
const sendEmailToComercial = async (comercialEmail, comercialNombre, leadData, accion, detalles = '') => {
  console.log('📧 sendEmailToComercial llamada:', { comercialEmail, comercialNombre, accion });
  
  if (!comercialEmail) {
    console.error('❌ Sin email del comercial');
    return { success: false, error: 'Sin email' };
  }

  if (!EMAILJS_CONFIG.enabled) {
    console.log('📧 EmailJS desactivado - configurar en EMAILJS_CONFIG');
    return { success: false, error: 'EmailJS desactivado' };
  }

  // Cargar EmailJS si no está cargado
  if (!window.emailjs) {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/@emailjs/browser@3/dist/email.min.js';
    document.head.appendChild(script);
    await new Promise(resolve => script.onload = resolve);
    window.emailjs.init(EMAILJS_CONFIG.publicKey);
  }

  try {
    const templateParams = {
      to_email: comercialEmail,
      to_name: comercialNombre,
      accion: accion,
      cliente_nombre: (leadData.nombre || '') + ' ' + (leadData.apellidos || ''),
      telefono: leadData.movil || leadData.telefono || 'No disponible',
      vehiculo: (leadData.marca || '') + ' ' + (leadData.modelo || ''),
      estado: leadData.estado || 'Pendiente',
      cita: leadData.citaAgendada ? new Date(leadData.citaAgendada).toLocaleString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '',
      notas: detalles || ''
    };

    console.log('📧 Enviando email con EmailJS...');
    
    const response = await window.emailjs.send(
      EMAILJS_CONFIG.serviceId,
      EMAILJS_CONFIG.templateId,
      templateParams
    );

    console.log('✅ Email enviado exitosamente:', response);
    return { success: true };
  } catch (error) {
    console.error('❌ Error al enviar email:', error);
    return { success: false, error: 'Error al enviar el email' };
  }
};

// ==================== MODAL EDITAR LEAD DESDE AGENDA ====================
const EditarLeadAgendaModal = ({ lead, onClose, onUpdate, comerciales, adminNombre }) => {
  const [activeTab, setActiveTab] = useState('info');
  const [formData, setFormData] = useState({
    estado: lead.estado || 'Pendiente',
    motivoNoCompra: lead.motivoNoCompra || '',
    citaAgendada: lead.citaAgendada ? new Date(lead.citaAgendada).toISOString().slice(0, 16) : '',
    observaciones: lead.observaciones || '',
    vendedor: lead.vendedor || ''
  });
  const [nuevoComentario, setNuevoComentario] = useState('');
  const [saving, setSaving] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);

  // Obtener email del comercial asignado
  const getComercialEmail = (vendedorNombre) => {
    const comercial = comerciales.find(c => c.nombre === vendedorNombre);
    return comercial?.email || null;
  };

  const handleSave = async () => {
    if (formData.estado === 'Citado' && !formData.citaAgendada) {
      alert('Para el estado "Citado" debes indicar la fecha y hora de la cita');
      return;
    }

    setSaving(true);
    setEmailStatus(null);

    const now = new Date().toISOString();
    
    // Crear entrada de historial
    const historialEntry = {
      fecha: now,
      estado: formData.estado,
      usuario: adminNombre || 'Administrador',
      accion: 'Modificación desde Agenda Admin'
    };

    // Preparar actualizaciones
    const updates = {
      estado_cita: formData.estado,
      motivo_no_compra: formData.motivoNoCompra || null,
      fecha_agendada: formData.citaAgendada ? new Date(formData.citaAgendada).toISOString() : null,
      observaciones: formData.observaciones,
      vendedor_nombre: formData.vendedor,
      fecha_modificacion: now
    };

    try {
      // Actualizar en Supabase
      const { error } = await updateLead(lead.id, updates);
      
      if (error) throw error;

      // Enviar email al comercial si hay cambios relevantes
      const comercialEmail = getComercialEmail(formData.vendedor);
      if (comercialEmail) {
        const accion = formData.citaAgendada !== (lead.citaAgendada ? new Date(lead.citaAgendada).toISOString().slice(0, 16) : '')
          ? '📅 Se ha modificado la cita de un lead asignado'
          : formData.estado !== lead.estado
            ? `🔄 Estado cambiado a: ${formData.estado}`
            : '📝 Se ha actualizado un lead asignado';

        const emailResult = await sendEmailToComercial(
          comercialEmail,
          formData.vendedor,
          { ...lead, ...formData, citaAgendada: formData.citaAgendada ? new Date(formData.citaAgendada) : null },
          accion,
          formData.observaciones
        );
        
        setEmailStatus(emailResult.success ? 'sent' : 'failed');
      }

      // Notificar actualización al componente padre
      if (onUpdate) {
        onUpdate(lead.id, {
          ...lead,
          estado: formData.estado,
          motivoNoCompra: formData.motivoNoCompra,
          citaAgendada: formData.citaAgendada ? new Date(formData.citaAgendada) : null,
          observaciones: formData.observaciones,
          vendedor: formData.vendedor,
          fechaModificacion: new Date(now),
          historial: [...(lead.historial || []), historialEntry]
        });
      }

      setTimeout(() => onClose(), 1500);
    } catch (error) {
      console.error('Error guardando lead:', error);
      alert('Error al guardar los cambios');
    } finally {
      setSaving(false);
    }
  };

  const handleAddComment = async () => {
    if (!nuevoComentario.trim()) return;

    const now = new Date().toISOString();
    const newComment = {
      fecha: now,
      usuario: adminNombre || 'Administrador',
      texto: nuevoComentario.trim()
    };

    const updatedComments = [...(lead.comentarios || []), newComment];
    
    try {
      await updateLead(lead.id, { 
        comentarios: JSON.stringify(updatedComments),
        fecha_modificacion: now
      });

      // Enviar email al comercial sobre el nuevo comentario
      const comercialEmail = getComercialEmail(formData.vendedor);
      if (comercialEmail) {
        await sendEmailToComercial(
          comercialEmail,
          formData.vendedor,
          lead,
          '💬 Nuevo comentario añadido a tu lead',
          nuevoComentario.trim()
        );
      }

      if (onUpdate) {
        onUpdate(lead.id, { ...lead, comentarios: updatedComments });
      }

      setNuevoComentario('');
    } catch (error) {
      console.error('Error añadiendo comentario:', error);
    }
  };

  const handleTraspasar = async (nuevoVendedor) => {
    if (!nuevoVendedor || nuevoVendedor === lead.vendedor) return;

    setSaving(true);
    const now = new Date().toISOString();

    try {
      await updateLead(lead.id, {
        vendedor_nombre: nuevoVendedor,
        fecha_modificacion: now
      });

      // Enviar email al NUEVO comercial
      const nuevoComercialEmail = getComercialEmail(nuevoVendedor);
      if (nuevoComercialEmail) {
        await sendEmailToComercial(
          nuevoComercialEmail,
          nuevoVendedor,
          { ...lead, vendedor: nuevoVendedor },
          '🆕 Se te ha asignado un nuevo lead',
          `Lead traspasado desde ${lead.vendedor || 'Sin asignar'}`
        );
      }

      // Notificar al comercial anterior
      const anteriorEmail = getComercialEmail(lead.vendedor);
      if (anteriorEmail && lead.vendedor) {
        await sendEmailToComercial(
          anteriorEmail,
          lead.vendedor,
          lead,
          '↗️ Un lead ha sido reasignado',
          `El lead ha sido traspasado a: ${nuevoVendedor}`
        );
      }

      setFormData({ ...formData, vendedor: nuevoVendedor });
      
      if (onUpdate) {
        onUpdate(lead.id, { ...lead, vendedor: nuevoVendedor, fechaModificacion: new Date(now) });
      }

    } catch (error) {
      console.error('Error traspasando lead:', error);
      alert('Error al traspasar el lead');
    } finally {
      setSaving(false);
    }
  };

  const clienteNombre = lead.razonSocial || `${lead.nombre || ''} ${lead.apellidos || ''}`.trim() || 'Sin nombre';
  const mostrarMotivoNoCompra = ['Cancelado', 'Cancelada Call', 'Cancelada Comercial'].includes(formData.estado);

  const formatDateTime = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const tabs = [
    { id: 'info', label: 'Información', icon: '📋' },
    { id: 'gestion', label: 'Gestión', icon: '⚡' },
    { id: 'comentarios', label: 'Comentarios', icon: '💬' },
    { id: 'historial', label: 'Historial', icon: '📜' }
  ];

  const InfoRow = ({ label, value, icon }) => (
    <div style={{display: 'flex', alignItems: 'center', padding: '8px 12px', background: '#f4f5f7', borderRadius: 10, marginBottom: '6px'}}>
      {icon && <span style={{marginRight: '8px'}}>{icon}</span>}
      <span style={{color: '#8a929c', fontSize: '14px', minWidth: '120px'}}>{label}:</span>
      <span style={{color: '#1f2a37', fontSize: '13px', fontWeight: 500}}>{value || '-'}</span>
    </div>
  );

  // Obtener estilo del estado
  const getEstadoStyle = (estado) => {
    const estadoData = ESTADOS_LEAD[estado];
    if (!estadoData) return { bg: '#808080', text: '#fff' };
    const lightStates = ['Pendiente', 'Contactado', 'Atendido', 'Citado', 'Ofertado'];
    return {
      bg: estadoData.color,
      text: lightStates.includes(estado) ? '#2b333d' : '#fff'
    };
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: VELO_MODAL,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px',
        backdropFilter: 'blur(4px)'
      }}
      onClick={onClose}
    >
      <div 
        style={{
          width: '100%',
          maxWidth: '1100px',
          maxHeight: '90vh',
          background: '#ffffff',
          borderRadius: 12,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: GRAD
        }}>
          <div style={{display: 'flex', alignItems: 'center', gap: '16px'}}>
            <div style={{color: 'white', fontSize: '18px', fontWeight: 600}}>
              📋 {clienteNombre}
            </div>
            <span style={{
              padding: '4px 12px',
              borderRadius: 12,
              fontSize: '14px',
              fontWeight: 600,
              background: getEstadoStyle(lead.estado).bg,
              color: getEstadoStyle(lead.estado).text
            }}>
              {lead.estado}
            </span>
          </div>
          <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
            {lead.movil && (
              <a href={`tel:${lead.movil}`} style={{
                padding: '8px 16px',
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                borderRadius: 10,
                fontSize: '13px',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                📞 {lead.movil}
              </a>
            )}
            {lead.email && (
              <a href={`mailto:${lead.email}`} style={{
                padding: '8px 16px',
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                borderRadius: 10,
                fontSize: '13px',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                ✉️ Email
              </a>
            )}
            <button
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                color: 'white',
                width: '32px',
                height: '32px',
                borderRadius: 10,
                cursor: 'pointer',
                fontSize: '18px'
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Contenido */}
        <div style={{display: 'flex', flex: 1, overflow: 'hidden'}}>
          {/* Sidebar izquierdo con info del cliente */}
          <div style={{width: '280px', borderRight: '1px solid #e7e9ec', padding: '20px', background: '#f4f5f7', overflowY: 'auto'}}>
            <div style={{textAlign: 'center', marginBottom: '20px'}}>
              <div style={{
                width: '70px',
                height: '70px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #005187, #1a9cd4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 12px',
                color: 'white',
                fontSize: '24px',
                fontWeight: 700
              }}>
                {clienteNombre.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
              </div>
              <div style={{color: '#1f2a37', fontSize: '16px', fontWeight: 600}}>{clienteNombre}</div>
              <div style={{color: '#8a929c', fontSize: '14px', marginTop: '4px'}}>{lead.tipoCliente || 'Particular'}</div>
            </div>

            <div style={{borderTop: '1px solid #e7e9ec', paddingTop: '16px'}}>
              <h4 style={{margin: '0 0 12px 0', color: '#2b333d', fontSize: '14px', fontWeight: 600, textTransform: 'uppercase'}}>Contacto</h4>
              {lead.movil && <InfoRow label="Móvil" value={lead.movil} icon="📱" />}
              {lead.telefono && <InfoRow label="Teléfono" value={lead.telefono} icon="📞" />}
              {lead.email && <InfoRow label="Email" value={lead.email} icon="✉️" />}
            </div>

            <div style={{borderTop: '1px solid #e7e9ec', paddingTop: '16px', marginTop: '16px'}}>
              <h4 style={{margin: '0 0 12px 0', color: '#2b333d', fontSize: '14px', fontWeight: 600, textTransform: 'uppercase'}}>Vehículo</h4>
              <InfoRow label="Marca" value={lead.marca} icon="🚗" />
              <InfoRow label="Modelo" value={lead.modelo} />
              {lead.motor && <InfoRow label="Motor" value={lead.motor} />}
              {lead.tipoVenta && <InfoRow label="Tipo" value={lead.tipoVenta === 'VN' ? 'Nuevo' : lead.tipoVenta === 'VO' ? 'Ocasión' : lead.tipoVenta === 'B2B' ? 'Empresa (B2B)' : lead.tipoVenta} />}
            </div>

            <div style={{borderTop: '1px solid #e7e9ec', paddingTop: '16px', marginTop: '16px'}}>
              <h4 style={{margin: '0 0 12px 0', color: '#2b333d', fontSize: '14px', fontWeight: 600, textTransform: 'uppercase'}}>Asignación</h4>
              <InfoRow label="Vendedor" value={formData.vendedor || 'Sin asignar'} icon="👤" />
              <InfoRow label="Centro" value={lead.centro} icon="🏢" />
            </div>
          </div>

          {/* Panel principal con tabs */}
          <div style={{flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
            {/* Tabs */}
            <div style={{display: 'flex', gap: '4px', padding: '16px 24px', borderBottom: '1px solid #e7e9ec', background: '#fff'}}>
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    padding: '10px 20px',
                    borderRadius: 10,
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 500,
                    background: activeTab === tab.id ? '#005187' : '#f4f5f7',
                    color: activeTab === tab.id ? 'white' : '#8a929c',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s'
                  }}
                >
                  <span>{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Contenido del tab */}
            <div style={{flex: 1, overflowY: 'auto', padding: '24px'}}>
              {/* TAB INFO */}
              {activeTab === 'info' && (
                <div>
                  <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px'}}>
                    <div>
                      <h4 style={{margin: '0 0 16px 0', color: '#1f2a37', fontSize: '15px', fontWeight: 600}}>📋 Datos del Lead</h4>
                      <InfoRow label="Lead ID" value={lead.leadId} />
                      <InfoRow label="Tipo" value={lead.tipo} />
                      <InfoRow label="Origen" value={lead.origen} />
                      <InfoRow label="Campaña" value={lead.campana} />
                      <InfoRow label="Usuario Alta" value={lead.usuarioAlta} />
                      <InfoRow label="Fecha Creación" value={formatDateTime(lead.fechaCreacion)} />
                    </div>
                    <div>
                      <h4 style={{margin: '0 0 16px 0', color: '#1f2a37', fontSize: '15px', fontWeight: 600}}>📅 Fechas</h4>
                      <InfoRow label="Creación" value={formatDateTime(lead.fechaCreacion)} />
                      <InfoRow label="Primera Llamada" value={formatDateTime(lead.primeraLlamada)} />
                      <InfoRow label="Última Llamada" value={formatDateTime(lead.ultimaLlamada)} />
                      <InfoRow label="Cita Agendada" value={formatDateTime(lead.citaAgendada)} />
                      <InfoRow label="Modificación" value={formatDateTime(lead.fechaModificacion)} />
                    </div>
                  </div>

                  {/* Observaciones actuales */}
                  {lead.observaciones && (
                    <div style={{marginTop: '24px'}}>
                      <h4 style={{margin: '0 0 12px 0', color: '#1f2a37', fontSize: '15px', fontWeight: 600}}>📝 Observaciones</h4>
                      <div style={{
                        padding: '16px',
                        background: '#fbf1de',
                        borderRadius: 10,
                        border: '1px solid #efc274',
                        fontSize: '13px',
                        color: '#a8660f',
                        whiteSpace: 'pre-wrap'
                      }}>
                        {lead.observaciones}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* TAB GESTIÓN */}
              {activeTab === 'gestion' && (
                <div>
                  <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px'}}>
                    <div>
                      <label style={{display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: '#2b333d'}}>Estado del Lead</label>
                      <select
                        value={formData.estado}
                        onChange={(e) => setFormData({...formData, estado: e.target.value})}
                        style={{
                          width: '100%',
                          padding: '12px',
                          borderRadius: 10,
                          border: '1px solid #e7e9ec',
                          fontSize: '14px',
                          background: '#fff',
                          cursor: 'pointer'
                        }}
                      >
                        {Object.keys(ESTADOS_LEAD).map(estado => (
                          <option key={estado} value={estado}>{estado}</option>
                        ))}
                      </select>
                    </div>

                    <div style={{opacity: mostrarMotivoNoCompra ? 1 : 0.4, pointerEvents: mostrarMotivoNoCompra ? 'auto' : 'none'}}>
                      <label style={{display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: '#2b333d'}}>Motivo No Compra</label>
                      <select
                        value={formData.motivoNoCompra}
                        onChange={(e) => setFormData({...formData, motivoNoCompra: e.target.value})}
                        style={{
                          width: '100%',
                          padding: '12px',
                          borderRadius: 10,
                          border: '1px solid #e7e9ec',
                          fontSize: '14px',
                          background: '#fff'
                        }}
                      >
                        <option value="">Seleccionar motivo...</option>
                        {motivosConActual(formData.motivoNoCompra).map(motivo => (
                          <option key={motivo} value={motivo}>{motivo}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div style={{marginBottom: '24px'}}>
                    <label style={{display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: '#2b333d'}}>📅 Fecha y Hora de Cita</label>
                    <input
                      type="datetime-local"
                      value={formData.citaAgendada}
                      onChange={(e) => setFormData({...formData, citaAgendada: e.target.value})}
                      style={{
                        padding: '12px',
                        borderRadius: 10,
                        border: '1px solid #e7e9ec',
                        fontSize: '14px',
                        maxWidth: '300px'
                      }}
                    />
                    {formData.citaAgendada && (
                      <div style={{
                        marginTop: '12px',
                        padding: '12px 16px',
                        background: '#e8f2f8',
                        borderRadius: 10,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        border: '1px solid #0284c7'
                      }}>
                        <span style={{fontSize: '18px'}}>📅</span>
                        <span style={{color: '#005187', fontSize: '14px', fontWeight: 500}}>
                          {new Date(formData.citaAgendada).toLocaleString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    )}
                  </div>

                  <div style={{marginBottom: '24px'}}>
                    <label style={{display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: '#2b333d'}}>👤 Comercial Asignado</label>
                    <select
                      value={formData.vendedor}
                      onChange={(e) => handleTraspasar(e.target.value)}
                      style={{
                        width: '100%',
                        maxWidth: '400px',
                        padding: '12px',
                        borderRadius: 10,
                        border: '1px solid #e7e9ec',
                        fontSize: '14px',
                        background: '#fff'
                      }}
                    >
                      <option value="">Sin asignar</option>
                      {comerciales.filter(c => c.activo !== false).map(c => (
                        <option key={c.id} value={c.nombre}>{c.nombre} {c.centro ? `(${c.centro})` : ''}</option>
                      ))}
                    </select>
                  </div>

                  <div style={{marginBottom: '24px'}}>
                    <label style={{display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: '#2b333d'}}>📝 Observaciones</label>
                    <textarea
                      value={formData.observaciones}
                      onChange={(e) => setFormData({...formData, observaciones: e.target.value})}
                      placeholder="Añade notas sobre el lead..."
                      style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: 10,
                        border: '1px solid #e7e9ec',
                        fontSize: '14px',
                        minHeight: '120px',
                        resize: 'vertical',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '20px', borderTop: '1px solid #e7e9ec'}}>
                    <div>
                      {emailStatus === 'sent' && (
                        <span style={{color: '#2e9e5b', fontSize: '13px'}}>✅ Email enviado al comercial</span>
                      )}
                      {emailStatus === 'failed' && (
                        <span style={{color: '#d23b34', fontSize: '13px'}}>⚠️ No se pudo enviar el email</span>
                      )}
                    </div>
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      style={{
                        padding: '14px 28px',
                        borderRadius: 10,
                        border: 'none',
                        cursor: saving ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: 600,
                        background: saving ? '#9aa4af' : '#2e9e5b',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      {saving ? '⏳ Guardando...' : '💾 Guardar Cambios'}
                    </button>
                  </div>
                </div>
              )}

              {/* TAB COMENTARIOS */}
              {activeTab === 'comentarios' && (
                <div>
                  <div style={{marginBottom: '24px', padding: '20px', background: '#f4f5f7', borderRadius: 12, border: '1px solid #e7e9ec'}}>
                    <h4 style={{margin: '0 0 12px 0', color: '#1f2a37', fontSize: '14px', fontWeight: 600}}>💬 Añadir Comentario</h4>
                    <textarea
                      value={nuevoComentario}
                      onChange={(e) => setNuevoComentario(e.target.value)}
                      placeholder="Escribe un comentario para el comercial..."
                      style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: 10,
                        border: '1px solid #e7e9ec',
                        fontSize: '14px',
                        minHeight: '80px',
                        resize: 'vertical',
                        boxSizing: 'border-box',
                        marginBottom: '12px'
                      }}
                    />
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                      <span style={{fontSize: '14px', color: '#8a929c'}}>
                        Se enviará email al comercial al añadir el comentario
                      </span>
                      <button
                        onClick={handleAddComment}
                        disabled={!nuevoComentario.trim()}
                        style={{
                          padding: '10px 20px',
                          borderRadius: 10,
                          border: 'none',
                          cursor: nuevoComentario.trim() ? 'pointer' : 'not-allowed',
                          fontSize: '13px',
                          fontWeight: 600,
                          background: nuevoComentario.trim() ? '#005187' : '#e7e9ec',
                          color: nuevoComentario.trim() ? 'white' : '#9aa4af'
                        }}
                      >
                        Añadir Comentario
                      </button>
                    </div>
                  </div>

                  <h4 style={{margin: '0 0 16px 0', color: '#1f2a37', fontSize: '15px', fontWeight: 600}}>💬 Comentarios ({(lead.comentarios || []).length})</h4>
                  {lead.comentarios && lead.comentarios.length > 0 ? (
                    <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                      {[...lead.comentarios].reverse().map((comment, idx) => (
                        <div key={idx} style={{
                          padding: '16px',
                          background: '#fff',
                          borderRadius: 10,
                          border: '1px solid #e7e9ec',
                          borderLeft: '4px solid #005187'
                        }}>
                          <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '8px'}}>
                            <span style={{fontWeight: 600, color: '#1f2a37', fontSize: '13px'}}>{comment.usuario}</span>
                            <span style={{color: '#8a929c', fontSize: '14px'}}>{formatDateTime(comment.fecha)}</span>
                          </div>
                          <p style={{margin: 0, color: '#2b333d', fontSize: '14px', lineHeight: '1.5'}}>{comment.texto}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{textAlign: 'center', padding: '40px', color: '#8a929c', background: '#f4f5f7', borderRadius: 12}}>
                      <div style={{fontSize: '40px', marginBottom: '12px'}}>💬</div>
                      <div>Sin comentarios aún</div>
                    </div>
                  )}
                </div>
              )}

              {/* TAB HISTORIAL */}
              {activeTab === 'historial' && (
                <div>
                  <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '24px'}}>
                    <div style={{padding: '16px', background: '#f4f5f7', borderRadius: 10, border: '1px solid #e7e9ec'}}>
                      <div style={{color: '#8a929c', fontSize: '13px', marginBottom: '4px', textTransform: 'uppercase'}}>Primera Llamada</div>
                      <div style={{color: '#1f2a37', fontSize: '14px', fontWeight: 500}}>{formatDateTime(lead.primeraLlamada)}</div>
                    </div>
                    <div style={{padding: '16px', background: '#f4f5f7', borderRadius: 10, border: '1px solid #e7e9ec'}}>
                      <div style={{color: '#8a929c', fontSize: '13px', marginBottom: '4px', textTransform: 'uppercase'}}>Última Llamada</div>
                      <div style={{color: '#1f2a37', fontSize: '14px', fontWeight: 500}}>{formatDateTime(lead.ultimaLlamada)}</div>
                    </div>
                    <div style={{padding: '16px', background: '#f4f5f7', borderRadius: 10, border: '1px solid #e7e9ec'}}>
                      <div style={{color: '#8a929c', fontSize: '13px', marginBottom: '4px', textTransform: 'uppercase'}}>Última Modificación</div>
                      <div style={{color: '#1f2a37', fontSize: '14px', fontWeight: 500}}>{formatDateTime(lead.fechaModificacion)}</div>
                    </div>
                  </div>

                  <h4 style={{margin: '0 0 16px 0', color: '#1f2a37', fontSize: '15px', fontWeight: 600}}>📜 Historial de Acciones</h4>
                  {lead.historial && lead.historial.length > 0 ? (
                    <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                      {[...lead.historial].reverse().map((item, idx) => {
                        const estadoStyle = getEstadoStyle(item.estado);
                        return (
                          <div key={idx} style={{
                            display: 'flex',
                            gap: '16px',
                            padding: '16px',
                            background: '#fff',
                            borderRadius: 10,
                            border: '1px solid #e7e9ec',
                            alignItems: 'center'
                          }}>
                            <div style={{flex: 1}}>
                              <div style={{color: '#1f2a37', fontSize: '14px', fontWeight: 500}}>{item.accion}</div>
                              <div style={{color: '#8a929c', fontSize: '14px', marginTop: '4px'}}>
                                {item.usuario} • {formatDateTime(item.fecha)}
                              </div>
                            </div>
                            <span style={{
                              padding: '4px 12px',
                              borderRadius: 12,
                              fontSize: '13px',
                              fontWeight: 600,
                              background: estadoStyle.bg,
                              color: estadoStyle.text
                            }}>
                              {item.estado}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{textAlign: 'center', padding: '40px', color: '#8a929c', background: '#f4f5f7', borderRadius: 12}}>
                      <div style={{fontSize: '40px', marginBottom: '12px'}}>📜</div>
                      <div>Sin historial de acciones</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== MODAL DETALLE LEAD AGENDA ====================
const LeadDetailAgendaModal = ({ lead, onClose, onSave, comerciales, adminNombre }) => {
  const [activeTab, setActiveTab] = useState('info');
  const [formData, setFormData] = useState({
    nombre: lead.nombre || '',
    apellidos: lead.apellidos || '',
    telefono: lead.telefono || '',
    movil: lead.movil || '',
    email: lead.email || '',
    marca: lead.marca || '',
    modelo: lead.modelo || '',
    estado: lead.estado || 'Pendiente',
    vendedor: lead.vendedor || '',
    centro: lead.centro || '',
    citaAgendada: lead.citaAgendada ? new Date(lead.citaAgendada).toISOString().slice(0, 16) : '',
    observaciones: lead.observaciones || '',
    motivoNoCompra: lead.motivoNoCompra || ''
  });
  const [newNote, setNewNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Historial del lead (simulado basado en fechas disponibles)
  const historial = useMemo(() => {
    const events = [];
    
    if (lead.fechaCreacion) {
      events.push({
        fecha: new Date(lead.fechaCreacion),
        tipo: 'creacion',
        descripcion: `Lead creado por ${lead.usuarioAlta || 'Sistema'}`,
        icon: '🆕'
      });
    }
    
    if (lead.primeraLlamada) {
      events.push({
        fecha: new Date(lead.primeraLlamada),
        tipo: 'llamada',
        descripcion: 'Primera llamada realizada',
        icon: '📞'
      });
    }
    
    if (lead.citaAgendada) {
      events.push({
        fecha: new Date(lead.citaAgendada),
        tipo: 'cita',
        descripcion: `Cita agendada`,
        icon: '📅'
      });
    }
    
    if (lead.fechaModificacion) {
      events.push({
        fecha: new Date(lead.fechaModificacion),
        tipo: 'modificacion',
        descripcion: 'Última modificación',
        icon: '✏️'
      });
    }
    
    // Añadir comentarios al historial
    if (lead.comentarios && Array.isArray(lead.comentarios)) {
      lead.comentarios.forEach(c => {
        events.push({
          fecha: new Date(c.fecha),
          tipo: 'nota',
          descripcion: c.texto,
          autor: c.autor,
          icon: '📝'
        });
      });
    }
    
    return events.sort((a, b) => b.fecha - a.fecha);
  }, [lead]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Preparar datos para guardar
      const updates = {
        nombre: formData.nombre,
        apellidos: formData.apellidos,
        telefono: formData.telefono,
        movil: formData.movil,
        email: formData.email,
        marca: formData.marca,
        modelo: formData.modelo,
        estado_cita: formData.estado,
        vendedor_nombre: formData.vendedor,
        centro: formData.centro,
        fecha_agendada: formData.citaAgendada ? new Date(formData.citaAgendada).toISOString() : null,
        observaciones: formData.observaciones,
        motivo_no_compra: formData.motivoNoCompra || null,
        fecha_modificacion: new Date().toISOString()
      };
      
      await onSave(lead.id, updates, formData.vendedor);
      setHasChanges(false);
    } catch (error) {
      alert('Error al guardar: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    setIsSaving(true);
    try {
      const currentComments = lead.comentarios || [];
      const newComment = {
        fecha: new Date().toISOString(),
        texto: newNote.trim(),
        autor: adminNombre || 'Admin'
      };
      
      const updates = {
        comentarios: [...currentComments, newComment],
        fecha_modificacion: new Date().toISOString()
      };
      
      await onSave(lead.id, updates, formData.vendedor, 'nota');
      setNewNote('');
    } catch (error) {
      alert('Error al añadir nota: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const getEstadoStyle = (estado) => {
    const estadoData = ESTADOS_LEAD[estado];
    if (!estadoData) return { bg: '#808080', text: '#fff' };
    const lightStates = ['Pendiente', 'Contactado', 'Atendido', 'Citado', 'Ofertado'];
    return {
      bg: estadoData.color,
      text: lightStates.includes(estado) ? '#2b333d' : '#fff'
    };
  };

  const inputStyle = {...campo, width: '100%', padding: '10px 12px'};

  const labelStyle = {display: 'block', fontSize: '12.5px', fontWeight: 700, color: C.muted, marginBottom: '5px'};

  // Pestañas en píldora: la activa con el gradiente de marca, como en el resto
  // de la suite.
  const tabStyle = (isActive) => ({
    padding: '8px 14px',
    background: isActive ? GRAD : 'transparent',
    color: isActive ? '#fff' : C.page,
    border: 'none',
    borderRadius: 10,
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 700,
    boxShadow: isActive ? '0 6px 16px rgba(0,81,135,.28)' : 'none'
  });

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: VELO_MODAL,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 12,
        width: '100%',
        maxWidth: '900px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 25px 80px rgba(0,0,0,0.4)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #005187 0%, #003d66 100%)',
          padding: '20px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{margin: 0, color: 'white', fontSize: '20px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '10px'}}>
              {lead.razonSocial || `${lead.nombre || ''} ${lead.apellidos || ''}`.trim() || 'Lead sin nombre'}
              <span style={{
                ...getEstadoStyle(lead.estado),
                background: getEstadoStyle(lead.estado).bg,
                color: getEstadoStyle(lead.estado).text,
                padding: '4px 12px',
                borderRadius: 10,
                fontSize: '14px',
                fontWeight: 600
              }}>
                {lead.estado}
              </span>
            </h2>
            <p style={{margin: '4px 0 0 0', color: 'rgba(255,255,255,0.8)', fontSize: '13px'}}>
              {lead.marca} {lead.modelo} • {lead.centro}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.15)',
              border: 'none',
              color: 'white',
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              cursor: 'pointer',
              fontSize: '18px',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
          >
            ✕
          </button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '8px',
          padding: '16px 24px',
          background: '#f4f5f7',
          borderBottom: '1px solid #e7e9ec'
        }}>
          <button style={tabStyle(activeTab === 'info')} onClick={() => setActiveTab('info')}>
            📋 Información
          </button>
          <button style={tabStyle(activeTab === 'editar')} onClick={() => setActiveTab('editar')}>
            ✏️ Editar Lead
          </button>
          <button style={tabStyle(activeTab === 'notas')} onClick={() => setActiveTab('notas')}>
            📝 Notas ({(lead.comentarios || []).length})
          </button>
          <button style={tabStyle(activeTab === 'historial')} onClick={() => setActiveTab('historial')}>
            📜 Historial
          </button>
        </div>

        {/* Content */}
        <div style={{flex: 1, overflowY: 'auto', padding: '24px'}}>
          
          {/* Tab Info */}
          {activeTab === 'info' && (
            <div>
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px'}}>
                {/* Datos del Cliente */}
                <div style={{background: '#f4f5f7', borderRadius: 12, padding: '20px', border: '1px solid #e7e9ec'}}>
                  <h4 style={{margin: '0 0 16px 0', color: '#2b333d', fontSize: '15px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px'}}>
                    👤 Datos del Cliente
                  </h4>
                  <div style={{display: 'grid', gap: '12px'}}>
                    <div>
                      <span style={{color: '#8a929c', fontSize: '14px'}}>Nombre completo</span>
                      <p style={{margin: '4px 0 0 0', fontWeight: 500}}>{lead.nombre || ''} {lead.apellidos || ''}</p>
                    </div>
                    {lead.razonSocial && (
                      <div>
                        <span style={{color: '#8a929c', fontSize: '14px'}}>Razón Social</span>
                        <p style={{margin: '4px 0 0 0', fontWeight: 500}}>{lead.razonSocial}</p>
                      </div>
                    )}
                    <div>
                      <span style={{color: '#8a929c', fontSize: '14px'}}>Teléfono</span>
                      <p style={{margin: '4px 0 0 0', fontWeight: 500, color: '#005187'}}>
                        📞 {lead.movil || lead.telefono || 'No disponible'}
                      </p>
                    </div>
                    <div>
                      <span style={{color: '#8a929c', fontSize: '14px'}}>Email</span>
                      <p style={{margin: '4px 0 0 0', fontWeight: 500}}>
                        ✉️ {lead.email || 'No disponible'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Datos del Vehículo */}
                <div style={{background: '#f4f5f7', borderRadius: 12, padding: '20px', border: '1px solid #e7e9ec'}}>
                  <h4 style={{margin: '0 0 16px 0', color: '#2b333d', fontSize: '15px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px'}}>
                    🚗 Vehículo de Interés
                  </h4>
                  <div style={{display: 'grid', gap: '12px'}}>
                    <div>
                      <span style={{color: '#8a929c', fontSize: '14px'}}>Marca</span>
                      <p style={{margin: '4px 0 0 0', fontWeight: 600, fontSize: '16px'}}>{lead.marca || 'Sin especificar'}</p>
                    </div>
                    <div>
                      <span style={{color: '#8a929c', fontSize: '14px'}}>Modelo</span>
                      <p style={{margin: '4px 0 0 0', fontWeight: 500}}>{lead.modelo || 'Sin especificar'}</p>
                    </div>
                    <div>
                      <span style={{color: '#8a929c', fontSize: '14px'}}>Tipo</span>
                      <p style={{margin: '4px 0 0 0', fontWeight: 500}}>{lead.tipo || 'Lead'}</p>
                    </div>
                  </div>
                </div>

                {/* Asignación */}
                <div style={{background: '#f4f5f7', borderRadius: 12, padding: '20px', border: '1px solid #e7e9ec'}}>
                  <h4 style={{margin: '0 0 16px 0', color: '#2b333d', fontSize: '15px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px'}}>
                    🏢 Asignación
                  </h4>
                  <div style={{display: 'grid', gap: '12px'}}>
                    <div>
                      <span style={{color: '#8a929c', fontSize: '14px'}}>Vendedor</span>
                      <p style={{margin: '4px 0 0 0', fontWeight: 600, color: '#005187'}}>{lead.vendedor || 'Sin asignar'}</p>
                    </div>
                    <div>
                      <span style={{color: '#8a929c', fontSize: '14px'}}>Centro</span>
                      <p style={{margin: '4px 0 0 0', fontWeight: 500}}>{lead.centro || 'Sin especificar'}</p>
                    </div>
                    <div>
                      <span style={{color: '#8a929c', fontSize: '14px'}}>Origen</span>
                      <p style={{margin: '4px 0 0 0', fontWeight: 500}}>{lead.origen || 'Desconocido'}</p>
                    </div>
                  </div>
                </div>

                {/* Cita */}
                <div style={{background: lead.citaAgendada ? '#f3faf6' : '#f4f5f7', borderRadius: 12, padding: '20px', border: `1px solid ${lead.citaAgendada ? '#2e9e5b' : '#e7e9ec'}`}}>
                  <h4 style={{margin: '0 0 16px 0', color: '#2b333d', fontSize: '15px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px'}}>
                    📅 Cita Agendada
                  </h4>
                  {lead.citaAgendada ? (
                    <div>
                      <p style={{margin: 0, fontSize: '18px', fontWeight: 600, color: '#2e9e5b'}}>
                        {new Date(lead.citaAgendada).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
                      </p>
                      <p style={{margin: '4px 0 0 0', fontSize: '24px', fontWeight: 700, color: '#2e9e5b'}}>
                        {new Date(lead.citaAgendada).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  ) : (
                    <p style={{margin: 0, color: '#9aa4af', fontSize: '14px'}}>Sin cita programada</p>
                  )}
                </div>
              </div>

              {/* Observaciones */}
              {lead.observaciones && (
                <div style={{marginTop: '20px', background: '#fdf8ef', borderRadius: 12, padding: '20px', border: '1px solid #efc274'}}>
                  <h4 style={{margin: '0 0 12px 0', color: '#a8660f', fontSize: '14px', fontWeight: 600}}>
                    📌 Observaciones
                  </h4>
                  <p style={{margin: 0, color: '#a8660f', whiteSpace: 'pre-wrap', lineHeight: '1.5'}}>{lead.observaciones}</p>
                </div>
              )}
            </div>
          )}

          {/* Tab Editar */}
          {activeTab === 'editar' && (
            <div>
              <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px'}}>
                <div>
                  <label style={labelStyle}>Nombre</label>
                  <input
                    type="text"
                    value={formData.nombre}
                    onChange={(e) => handleInputChange('nombre', e.target.value)}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Apellidos</label>
                  <input
                    type="text"
                    value={formData.apellidos}
                    onChange={(e) => handleInputChange('apellidos', e.target.value)}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Teléfono</label>
                  <input
                    type="tel"
                    value={formData.telefono}
                    onChange={(e) => handleInputChange('telefono', e.target.value)}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Móvil</label>
                  <input
                    type="tel"
                    value={formData.movil}
                    onChange={(e) => handleInputChange('movil', e.target.value)}
                    style={inputStyle}
                  />
                </div>
                <div style={{gridColumn: 'span 2'}}>
                  <label style={labelStyle}>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Marca</label>
                  <select
                    value={formData.marca}
                    onChange={(e) => handleInputChange('marca', e.target.value)}
                    style={inputStyle}
                  >
                    <option value="">Seleccionar marca</option>
                    {MARCAS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Modelo</label>
                  <input
                    type="text"
                    value={formData.modelo}
                    onChange={(e) => handleInputChange('modelo', e.target.value)}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Estado</label>
                  <select
                    value={formData.estado}
                    onChange={(e) => handleInputChange('estado', e.target.value)}
                    style={{...inputStyle, background: getEstadoStyle(formData.estado).bg, color: getEstadoStyle(formData.estado).text}}
                  >
                    {Object.keys(ESTADOS_LEAD).map(e => <option key={e} value={e}>{e}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Vendedor</label>
                  <select
                    value={formData.vendedor}
                    onChange={(e) => handleInputChange('vendedor', e.target.value)}
                    style={inputStyle}
                  >
                    <option value="">Seleccionar vendedor</option>
                    {comerciales.filter(c => c.activo).map(c => <option key={c.id} value={c.nombre}>{c.nombre}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Centro</label>
                  <select
                    value={formData.centro}
                    onChange={(e) => handleInputChange('centro', e.target.value)}
                    style={inputStyle}
                  >
                    <option value="">Seleccionar centro</option>
                    {CENTROS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Fecha y Hora de Cita</label>
                  <input
                    type="datetime-local"
                    value={formData.citaAgendada}
                    onChange={(e) => handleInputChange('citaAgendada', e.target.value)}
                    style={inputStyle}
                  />
                </div>
                {(formData.estado === 'Cancelado' || formData.estado === 'Cancelada Call' || formData.estado === 'Cancelada Comercial') && (
                  <div style={{gridColumn: 'span 2'}}>
                    <label style={labelStyle}>Motivo de No Compra</label>
                    <select
                      value={formData.motivoNoCompra}
                      onChange={(e) => handleInputChange('motivoNoCompra', e.target.value)}
                      style={inputStyle}
                    >
                      <option value="">Seleccionar motivo</option>
                      {motivosConActual(formData.motivoNoCompra).map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                )}
                <div style={{gridColumn: 'span 2'}}>
                  <label style={labelStyle}>Observaciones</label>
                  <textarea
                    value={formData.observaciones}
                    onChange={(e) => handleInputChange('observaciones', e.target.value)}
                    style={{...inputStyle, minHeight: '100px', resize: 'vertical'}}
                  />
                </div>
              </div>

              {hasChanges && (
                <div style={{
                  marginTop: '20px',
                  padding: '16px',
                  background: '#fbf1de',
                  borderRadius: 10,
                  border: '1px solid #efc274',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{color: '#a8660f', fontSize: '14px', fontWeight: 500}}>
                    ⚠️ Tienes cambios sin guardar
                  </span>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    style={{
                      padding: '10px 20px',
                      background: C.brand,
                      color: 'white',
                      border: 'none',
                      borderRadius: 10,
                      boxShadow: '0 3px 10px rgba(0,81,135,.28)',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: isSaving ? 'not-allowed' : 'pointer',
                      opacity: isSaving ? 0.7 : 1
                    }}
                  >
                    {isSaving ? '⏳ Guardando...' : '💾 Guardar Cambios'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Tab Notas */}
          {activeTab === 'notas' && (
            <div>
              {/* Añadir nueva nota */}
              <div style={{
                background: '#f4f5f7',
                borderRadius: 12,
                padding: '20px',
                border: '1px solid #e7e9ec',
                marginBottom: '20px'
              }}>
                <h4 style={{margin: '0 0 12px 0', color: '#2b333d', fontSize: '14px', fontWeight: 600}}>
                  ✍️ Añadir Nueva Nota
                </h4>
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Escribe una nota para este lead..."
                  style={{
                    ...inputStyle,
                    minHeight: '80px',
                    resize: 'vertical',
                    marginBottom: '12px'
                  }}
                />
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <span style={{fontSize: '14px', color: '#8a929c'}}>
                    Se notificará al comercial asignado ({formData.vendedor || 'Sin asignar'})
                  </span>
                  <button
                    onClick={handleAddNote}
                    disabled={!newNote.trim() || isSaving}
                    style={{
                      padding: '8px 20px',
                      background: newNote.trim() ? '#005187' : '#e7e9ec',
                      color: 'white',
                      border: 'none',
                      borderRadius: 10,
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: newNote.trim() && !isSaving ? 'pointer' : 'not-allowed'
                    }}
                  >
                    {isSaving ? '⏳ Añadiendo...' : '📝 Añadir Nota'}
                  </button>
                </div>
              </div>

              {/* Lista de notas */}
              <div>
                <h4 style={{margin: '0 0 16px 0', color: '#2b333d', fontSize: '14px', fontWeight: 600}}>
                  📋 Notas del Lead ({(lead.comentarios || []).length})
                </h4>
                {(lead.comentarios || []).length === 0 ? (
                  <EmptyState icon="📝" title="No hay notas para este lead" />
                ) : (
                  <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                    {[...(lead.comentarios || [])].reverse().map((nota, idx) => (
                      <div key={idx} style={{
                        background: '#fff',
                        borderRadius: 10,
                        padding: '16px',
                        border: '1px solid #e7e9ec',
                        borderLeft: '3px solid #005187'
                      }}>
                        <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '8px'}}>
                          <span style={{fontWeight: 600, color: '#2b333d', fontSize: '13px'}}>
                            👤 {nota.autor || 'Sistema'}
                          </span>
                          <span style={{color: '#9aa4af', fontSize: '14px'}}>
                            {new Date(nota.fecha).toLocaleString('es-ES')}
                          </span>
                        </div>
                        <p style={{margin: 0, color: '#8a929c', lineHeight: '1.5', whiteSpace: 'pre-wrap'}}>
                          {nota.texto}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tab Historial */}
          {activeTab === 'historial' && (
            <div>
              <h4 style={{margin: '0 0 16px 0', color: '#2b333d', fontSize: '14px', fontWeight: 600}}>
                📜 Historial del Lead
              </h4>
              {historial.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '40px',
                  color: '#9aa4af',
                  background: '#f4f5f7',
                  borderRadius: 12,
                  border: '1px dashed #e7e9ec'
                }}>
                  <span style={{fontSize: '48px', display: 'block', marginBottom: '12px'}}>📜</span>
                  No hay historial disponible
                </div>
              ) : (
                <div style={{position: 'relative', paddingLeft: '24px'}}>
                  {/* Línea vertical */}
                  <div style={{
                    position: 'absolute',
                    left: '8px',
                    top: '10px',
                    bottom: '10px',
                    width: '2px',
                    background: '#e7e9ec'
                  }} />
                  
                  {historial.map((event, idx) => (
                    <div key={idx} style={{
                      position: 'relative',
                      marginBottom: '20px',
                      paddingLeft: '24px'
                    }}>
                      {/* Punto */}
                      <div style={{
                        position: 'absolute',
                        left: '-20px',
                        top: '6px',
                        width: '16px',
                        height: '16px',
                        background: '#005187',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px'
                      }}>
                        {event.icon}
                      </div>
                      
                      <div style={{
                        background: '#fff',
                        borderRadius: 10,
                        padding: '14px',
                        border: '1px solid #e7e9ec'
                      }}>
                        <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}>
                          <span style={{fontWeight: 600, color: '#2b333d', fontSize: '13px', textTransform: 'capitalize'}}>
                            {event.tipo}
                          </span>
                          <span style={{color: '#9aa4af', fontSize: '14px'}}>
                            {event.fecha.toLocaleString('es-ES')}
                          </span>
                        </div>
                        <p style={{margin: 0, color: '#8a929c', fontSize: '13px'}}>
                          {event.descripcion}
                        </p>
                        {event.autor && (
                          <span style={{fontSize: '13px', color: '#9aa4af', marginTop: '4px', display: 'block'}}>
                            Por: {event.autor}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #e7e9ec',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          background: '#f4f5f7'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 24px',
              background: '#fff',
              color: '#2b333d',
              border: '1px solid #e7e9ec',
              borderRadius: 10,
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            Cerrar
          </button>
          {activeTab === 'editar' && hasChanges && (
            <button
              onClick={handleSave}
              disabled={isSaving}
              style={{
                padding: '10px 20px',
                background: C.brand,
                color: 'white',
                border: 'none',
                borderRadius: 10,
                boxShadow: '0 3px 10px rgba(0,81,135,.28)',
                fontSize: '13px',
                fontWeight: 600,
                cursor: isSaving ? 'not-allowed' : 'pointer',
                opacity: isSaving ? 0.7 : 1
              }}
            >
              {isSaving ? '⏳ Guardando...' : '💾 Guardar Cambios'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== AGENDA ADMIN PAGE ====================
const AgendaAdminPage = ({ leads, comerciales, onRefresh, adminNombre }) => {
  const [viewMode, setViewMode] = useState('weekly');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedLead, setSelectedLead] = useState(null);
  
  // Filtros
  const [filtroVendedor, setFiltroVendedor] = useState('');
  const [filtroMarca, setFiltroMarca] = useState('');
  const [filtroCentro, setFiltroCentro] = useState('');
  
  // Obtener vendedores, marcas y centros únicos
  const vendedoresUnicos = useMemo(() => {
    const vendedores = [...new Set(leads.map(l => l.vendedor).filter(Boolean))];
    return vendedores.sort();
  }, [leads]);

  const marcasUnicas = useMemo(() => {
    const marcas = [...new Set(leads.map(l => l.marca).filter(Boolean))];
    return marcas.sort();
  }, [leads]);

  const centrosUnicos = useMemo(() => {
    const centros = [...new Set(leads.map(l => l.centro).filter(Boolean))];
    return centros.sort();
  }, [leads]);

  // Filtrar leads con citas
  const citas = useMemo(() => {
    return leads.filter(lead => {
      if (!lead.citaAgendada) return false;
      if (filtroVendedor && lead.vendedor !== filtroVendedor) return false;
      if (filtroMarca && lead.marca !== filtroMarca) return false;
      if (filtroCentro && lead.centro !== filtroCentro) return false;
      return true;
    }).sort((a, b) => new Date(a.citaAgendada) - new Date(b.citaAgendada));
  }, [leads, filtroVendedor, filtroMarca, filtroCentro]);

  // Filtrar leads nuevos (sin cita, por fecha de creación)
  const leadsNuevos = useMemo(() => {
    return leads.filter(lead => {
      if (!lead.fechaCreacion) return false;
      if (lead.citaAgendada) return false; // Excluir los que ya tienen cita
      if (filtroVendedor && lead.vendedor !== filtroVendedor) return false;
      if (filtroMarca && lead.marca !== filtroMarca) return false;
      if (filtroCentro && lead.centro !== filtroCentro) return false;
      return true;
    }).sort((a, b) => new Date(a.fechaCreacion) - new Date(b.fechaCreacion));
  }, [leads, filtroVendedor, filtroMarca, filtroCentro]);

  // Helpers de fecha
  const getWeekDays = (date) => {
    const start = new Date(date);
    start.setDate(start.getDate() - start.getDay() + 1);
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const getMonthDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPad = (firstDay.getDay() + 6) % 7;
    
    const days = [];
    for (let i = startPad - 1; i >= 0; i--) {
      const d = new Date(year, month, -i);
      days.push({ date: d, isCurrentMonth: false });
    }
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    return days;
  };

  const getCitasForDate = (date) => {
    return citas.filter(cita => {
      const citaDate = new Date(cita.citaAgendada);
      return citaDate.toDateString() === date.toDateString();
    });
  };

  const getLeadsNuevosForDate = (date) => {
    return leadsNuevos.filter(lead => {
      const creacionDate = new Date(lead.fechaCreacion);
      return creacionDate.toDateString() === date.toDateString();
    });
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => setCurrentDate(new Date());

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  const weekDays = getWeekDays(currentDate);
  const monthDays = getMonthDays(currentDate);
  const today = new Date().toDateString();

  const formatWeekRange = () => {
    const start = weekDays[0];
    const end = weekDays[6];
    if (start.getMonth() === end.getMonth()) {
      return `${start.getDate()} - ${end.getDate()} ${start.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}`;
    }
    return `${start.getDate()} ${start.toLocaleDateString('es-ES', { month: 'short' })} - ${end.getDate()} ${end.toLocaleDateString('es-ES', { month: 'short', year: 'numeric' })}`;
  };

  // Obtener color del estado
  const getEstadoStyle = (estado) => {
    const estadoData = ESTADOS_LEAD[estado];
    if (!estadoData) return { bg: '#808080', text: '#fff' };
    
    const lightStates = ['Pendiente', 'Contactado', 'Atendido', 'Citado', 'Ofertado'];
    return {
      bg: estadoData.color,
      text: lightStates.includes(estado) ? '#2b333d' : '#fff'
    };
  };

  // Componente de tarjeta de cita - Estilo basado en la imagen
  const CitaCard = ({ cita, compact = false }) => {
    const estadoStyle = getEstadoStyle(cita.estado);
    
    if (compact) {
      return (
        <div
          onClick={() => setSelectedLead(cita)}
          style={{
            background: estadoStyle.bg,
            color: estadoStyle.text,
            fontSize: '14px',
            padding: '4px 8px',
            borderRadius: 10,
            marginBottom: '4px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontWeight: 500
          }}
        >
          {formatTime(cita.citaAgendada)} {cita.nombre || cita.razonSocial}
        </div>
      );
    }

    return (
      <div
        onClick={() => setSelectedLead(cita)}
        style={{
          background: estadoStyle.bg,
          borderRadius: 10,
          padding: '8px 10px',
          marginBottom: '6px',
          cursor: 'pointer',
          color: estadoStyle.text,
          fontSize: '13px',
          boxShadow: 'none'
        }}
        className="fg-card"
      >
        {/* Hora y Estado */}
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px'}}>
          <span style={{fontWeight: 700}}>{formatTime(cita.citaAgendada)}</span>
          <span style={{
            background: 'rgba(255,255,255,0.25)',
            padding: '1px 8px',
            borderRadius: 999,
            fontSize: '10.5px',
            fontWeight: 800
          }}>
            {cita.tipo || 'Lead'}
          </span>
        </div>
        
        {/* Nombre */}
        <div style={{fontWeight: 600, marginBottom: '3px', lineHeight: '1.2'}}>
          {cita.razonSocial || `${cita.nombre || ''} ${cita.apellidos || ''}`.trim() || 'Sin nombre'}
        </div>
        
        {/* Teléfono */}
        {(cita.telefono || cita.movil) && (
          <div style={{display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '3px', opacity: 0.9}}>
            <span>📞</span>
            <span>{cita.movil || cita.telefono}</span>
          </div>
        )}
        
        {/* Marca */}
        <div style={{fontWeight: 500, marginBottom: '2px'}}>
          {cita.marca || 'Sin marca'}
        </div>
        
        {/* Centro */}
        <div style={{fontSize: '14px', opacity: 0.9, marginBottom: '2px'}}>
          {cita.centro || 'Sin centro'}
        </div>
        
        {/* Vendedor y Origen */}
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px', paddingTop: '4px', borderTop: '1px solid rgba(255,255,255,0.2)'}}>
          <span style={{fontSize: '14px', opacity: 0.9}}>{cita.vendedor || 'Sin asignar'}</span>
          <span style={{fontSize: '13px', opacity: 0.8}}>{cita.origen || ''}</span>
        </div>
      </div>
    );
  };

  // Barra de estados
  const EstadosBar = () => (
    <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px',
      padding: '12px 16px',
      background: '#ffffff',
      borderRadius: 10,
      border: '1px solid #e7e9ec',
      marginTop: '20px'
    }}>
      <span style={{fontWeight: 600, color: '#2b333d', marginRight: '8px', fontSize: '13px'}}>Estados:</span>
      {Object.entries(ESTADOS_LEAD).map(([estado, data]) => {
        const lightStates = ['Pendiente', 'Contactado', 'Atendido', 'Citado', 'Ofertado'];
        const textColor = lightStates.includes(estado) ? '#2b333d' : '#fff';
        const count = citas.filter(c => c.estado === estado).length;
        return (
          <div
            key={estado}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '4px 10px',
              background: data.color,
              color: textColor,
              borderRadius: 10,
              fontSize: '13px',
              fontWeight: 500,
              border: '1px solid rgba(0,0,0,0.1)'
            }}
          >
            {estado}
            {count > 0 && <span style={{opacity: 0.8}}>({count})</span>}
          </div>
        );
      })}
    </div>
  );

  const buttonStyle = {
    padding: '8px 16px',
    borderRadius: 10,
    border: 'none',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 700,
    fontFamily: 'inherit'
  };

  return (
    <div style={{padding: '24px', height: 'calc(100vh - 140px)', overflowY: 'auto', background: '#f4f5f7'}}>
      {/* Header */}
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
        <div>
          <h2 style={{margin: 0, color: C.ink, fontSize: '19px', fontWeight: 800, letterSpacing: '-0.2px', display: 'flex', alignItems: 'center', gap: '10px'}}>
            📅 Agenda General
          </h2>
          <p style={{margin: '3px 0 0 0', color: C.muted, fontSize: '12.5px'}}>
            {citas.length} citas programadas de todos los comerciales
          </p>
        </div>
        
        <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
          {/* Toggle Vista */}
          <div style={{display: 'flex', background: '#ffffff', borderRadius: 10, padding: '4px', border: '1px solid #e7e9ec', boxShadow: 'none'}}>
            <button
              onClick={() => setViewMode('weekly')}
              style={{
                ...buttonStyle,
                background: viewMode === 'weekly' ? GRAD : 'transparent',
                color: viewMode === 'weekly' ? '#fff' : C.page,
                boxShadow: viewMode === 'weekly' ? '0 6px 16px rgba(0,81,135,.28)' : 'none'
              }}
              className="fg-nav"
            >
              📆 Semanal
            </button>
            <button
              onClick={() => setViewMode('monthly')}
              style={{
                ...buttonStyle,
                background: viewMode === 'monthly' ? GRAD : 'transparent',
                color: viewMode === 'monthly' ? '#fff' : C.page,
                boxShadow: viewMode === 'monthly' ? '0 6px 16px rgba(0,81,135,.28)' : 'none'
              }}
              className="fg-nav"
            >
              🗓️ Mensual
            </button>
          </div>

          <button onClick={goToToday} className="fg-nav" style={{...buttonStyle, ...botonSecundario, padding: '8px 16px'}}>
            Hoy
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '20px',
        padding: '16px',
        background: '#ffffff',
        borderRadius: 12,
        border: '1px solid #e7e9ec',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        <span style={{fontWeight: 600, color: '#2b333d', fontSize: '13px'}}>🔍 Filtros:</span>
        
        <select
          value={filtroVendedor}
          onChange={(e) => setFiltroVendedor(e.target.value)}
          style={{...campo, padding: '8px 12px', minWidth: '180px'}}
        >
          <option value="">Todos los vendedores</option>
          {vendedoresUnicos.map(v => <option key={v} value={v}>{v}</option>)}
        </select>

        <select
          value={filtroMarca}
          onChange={(e) => setFiltroMarca(e.target.value)}
          style={{...campo, padding: '8px 12px', minWidth: '150px'}}
        >
          <option value="">Todas las marcas</option>
          {marcasUnicas.map(m => <option key={m} value={m}>{m}</option>)}
        </select>

        <select
          value={filtroCentro}
          onChange={(e) => setFiltroCentro(e.target.value)}
          style={{
            padding: '8px 12px',
            borderRadius: 10,
            border: '1px solid #e7e9ec',
            fontSize: '13px',
            minWidth: '200px',
            background: '#fff'
          }}
        >
          <option value="">Todos los centros</option>
          {centrosUnicos.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        {(filtroVendedor || filtroMarca || filtroCentro) && (
          <button
            onClick={() => { setFiltroVendedor(''); setFiltroMarca(''); setFiltroCentro(''); }}
            style={{
              ...buttonStyle,
              background: '#f9e4e3',
              color: '#d23b34',
              border: '1px solid #f2ccca'
            }}
          >
            ✕ Limpiar filtros
          </button>
        )}
      </div>

      {/* Navigation */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        padding: '16px',
        background: '#ffffff',
        borderRadius: 12,
        border: '1px solid #e7e9ec',
        boxShadow: 'none'
      }}>
        <button 
          onClick={() => viewMode === 'weekly' ? navigateWeek(-1) : navigateMonth(-1)}
          style={{...buttonStyle, background: '#e7e9ec', color: '#2b333d'}}
        >
          ← Anterior
        </button>
        <div style={{color: '#1f2a37', fontSize: '18px', fontWeight: 600}}>
          {viewMode === 'weekly' ? formatWeekRange() : currentDate.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
        </div>
        <button 
          onClick={() => viewMode === 'weekly' ? navigateWeek(1) : navigateMonth(1)}
          style={{...buttonStyle, background: '#e7e9ec', color: '#2b333d'}}
        >
          Siguiente →
        </button>
      </div>

      {/* Vista Semanal */}
      {viewMode === 'weekly' && (
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '12px'}}>
          {weekDays.map((day, idx) => {
            const citasDelDia = getCitasForDate(day);
            const leadsNuevosDelDia = getLeadsNuevosForDate(day);
            const totalEventos = citasDelDia.length + leadsNuevosDelDia.length;
            const isToday = day.toDateString() === today;
            const dayName = day.toLocaleDateString('es-ES', { weekday: 'short' }).toUpperCase();
            
            return (
              <div 
                key={idx}
                style={{
                  background: isToday ? '#e8f2f8' : '#ffffff',
                  borderRadius: 12,
                  border: `2px solid ${isToday ? '#005187' : '#e7e9ec'}`,
                  minHeight: '450px',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: 'none'
                }}
              >
                <div style={{
                  padding: '14px',
                  borderBottom: '1px solid #e7e9ec',
                  textAlign: 'center',
                  background: isToday ? '#005187' : 'transparent',
                  borderRadius: '10px 10px 0 0'
                }}>
                  <div style={{color: isToday ? '#fff' : '#8a929c', fontSize: '14px', fontWeight: 600}}>{dayName}</div>
                  <div style={{color: isToday ? '#fff' : '#1f2a37', fontSize: '26px', fontWeight: 700, marginTop: '4px'}}>{day.getDate()}</div>
                  {totalEventos > 0 && (
                    <div style={{
                      marginTop: '6px',
                      background: isToday ? 'rgba(255,255,255,0.2)' : '#e7e9ec',
                      color: isToday ? '#fff' : '#2b333d',
                      fontSize: '14px',
                      padding: '2px 8px',
                      borderRadius: 10,
                      display: 'inline-block'
                    }}>
                      {citasDelDia.length > 0 && `${citasDelDia.length} cita${citasDelDia.length > 1 ? 's' : ''}`}
                      {citasDelDia.length > 0 && leadsNuevosDelDia.length > 0 && ' · '}
                      {leadsNuevosDelDia.length > 0 && `${leadsNuevosDelDia.length} nuevo${leadsNuevosDelDia.length > 1 ? 's' : ''}`}
                    </div>
                  )}
                </div>
                
                <div style={{padding: '10px', flex: 1, overflowY: 'auto'}}>
                  {totalEventos > 0 ? (
                    <div style={{display: 'flex', flexDirection: 'column', gap: '6px'}}>
                      {/* Leads nuevos */}
                      {leadsNuevosDelDia.map(lead => (
                        <div
                          key={lead.id}
                          onClick={() => setSelectedLead(lead)}
                          style={{
                            background: '#fbf1de',
                            borderRadius: 10,
                            padding: '8px 10px',
                            cursor: 'pointer',
                            borderLeft: '3px solid #e08c1e',
                          }}
                          className="fg-card"
                        >
                          <div style={{color: '#a8660f', fontSize: '14px', fontWeight: 600, marginBottom: '2px'}}>🆕 NUEVO LEAD</div>
                          <div style={{color: '#1f2a37', fontSize: '13px', fontWeight: 500}}>{lead.nombre || lead.razonSocial || 'Sin nombre'} {lead.apellidos || ''}</div>
                          <div style={{color: '#8a929c', fontSize: '14px', marginTop: '2px'}}>{lead.marca} {lead.modelo}</div>
                          <div style={{color: '#8a929c', fontSize: '13px', marginTop: '2px'}}>{lead.origen} · {lead.estado || 'Pendiente'}</div>
                        </div>
                      ))}
                      {/* Citas */}
                      {citasDelDia.map(cita => (
                        <CitaCard key={cita.id} cita={cita} />
                      ))}
                    </div>
                  ) : (
                    <div style={{textAlign: 'center', padding: '30px 10px', color: '#9aa4af', fontSize: '14px'}}>
                      Sin eventos
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Vista Mensual */}
      {viewMode === 'monthly' && (
        <div>
          {/* Cabecera días */}
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', marginBottom: '8px'}}>
            {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map(day => (
              <div key={day} style={{textAlign: 'center', padding: '10px', color: '#8a929c', fontSize: '14px', fontWeight: 600, textTransform: 'uppercase'}}>
                {day}
              </div>
            ))}
          </div>
          
          {/* Grid días */}
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px'}}>
            {monthDays.map((dayObj, idx) => {
              const citasDelDia = getCitasForDate(dayObj.date);
              const leadsNuevosDelDia = getLeadsNuevosForDate(dayObj.date);
              const totalEventos = citasDelDia.length + leadsNuevosDelDia.length;
              const isToday = dayObj.date.toDateString() === today;
              
              return (
                <div
                  key={idx}
                  style={{
                    background: isToday ? '#e8f2f8' : dayObj.isCurrentMonth ? '#ffffff' : '#f4f5f7',
                    borderRadius: 10,
                    minHeight: '110px',
                    padding: '8px',
                    border: `1px solid ${isToday ? '#005187' : '#e7e9ec'}`,
                    opacity: dayObj.isCurrentMonth ? 1 : 0.5
                  }}
                >
                  <div style={{
                    color: isToday ? '#005187' : '#1f2a37',
                    fontSize: '14px',
                    fontWeight: isToday ? 700 : 500,
                    marginBottom: '6px'
                  }}>
                    {dayObj.date.getDate()}
                  </div>
                  
                  {/* Leads nuevos */}
                  {leadsNuevosDelDia.slice(0, 2).map(lead => (
                    <div
                      key={lead.id}
                      onClick={() => setSelectedLead(lead)}
                      style={{
                        background: '#e08c1e',
                        color: '#000',
                        fontSize: '13px',
                        padding: '2px 6px',
                        borderRadius: 999,
                        marginBottom: '2px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      🆕 {lead.nombre || lead.razonSocial || 'Nuevo'}
                    </div>
                  ))}
                  
                  {/* Citas */}
                  {citasDelDia.slice(0, leadsNuevosDelDia.length > 0 ? 1 : 3).map(cita => (
                    <CitaCard key={cita.id} cita={cita} compact={true} />
                  ))}
                  
                  {totalEventos > 3 && (
                    <div style={{color: '#8a929c', fontSize: '14px', textAlign: 'center', marginTop: '4px'}}>
                      +{totalEventos - 3} más
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Barra de Estados */}
      <EstadosBar />

      {/* Próximas Citas */}
      <div style={{marginTop: '24px'}}>
        <h3 style={{color: '#1f2a37', fontSize: '18px', fontWeight: 600, marginBottom: '16px'}}>📋 Próximas Citas</h3>
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '12px'}}>
          {citas.filter(c => new Date(c.citaAgendada) >= new Date()).slice(0, 8).map(cita => {
            const estadoStyle = getEstadoStyle(cita.estado);
            return (
              <div
                key={cita.id}
                onClick={() => setSelectedLead(cita)}
                style={{
                  background: '#ffffff',
                  borderRadius: 12,
                  padding: '16px',
                  border: '1px solid #e7e9ec',
                  cursor: 'pointer',
                  borderLeft: `4px solid ${estadoStyle.bg}`,
                  boxShadow: 'none'
                }}
                className="fg-card"
              >
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px'}}>
                  <div>
                    <div style={{color: '#1f2a37', fontSize: '14px', fontWeight: 600}}>
                      {cita.razonSocial || `${cita.nombre || ''} ${cita.apellidos || ''}`.trim() || 'Sin nombre'}
                    </div>
                    <div style={{color: '#8a929c', fontSize: '14px', marginTop: '4px'}}>
                      {cita.marca} {cita.modelo}
                    </div>
                  </div>
                  <span style={{
                    padding: '4px 10px',
                    borderRadius: 10,
                    fontSize: '13px',
                    fontWeight: 600,
                    background: estadoStyle.bg,
                    color: estadoStyle.text
                  }}>
                    {cita.estado}
                  </span>
                </div>
                <div style={{display: 'flex', alignItems: 'center', gap: '8px', color: '#005187', fontSize: '13px', marginBottom: '8px'}}>
                  <span>📅</span>
                  {new Date(cita.citaAgendada).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })} - {formatTime(cita.citaAgendada)}
                </div>
                <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#8a929c', paddingTop: '8px', borderTop: '1px solid #e7e9ec'}}>
                  <span>👤 {cita.vendedor || 'Sin asignar'}</span>
                  <span>🏢 {cita.centro || 'Sin centro'}</span>
                </div>
              </div>
            );
          })}
        </div>
        {citas.filter(c => new Date(c.citaAgendada) >= new Date()).length === 0 && (
          <div className="fg-card" style={{...tarjeta}}>
            <EmptyState icon="📅" title="No hay citas programadas próximamente" />
          </div>
        )}
      </div>

      {/* Modal de Edición de Lead */}
      {selectedLead && (
        <LeadDetailAgendaModal
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onSave={async (id, updates, vendedorNombre, changeType = 'update') => {
            try {
              // Guardar cambios en Supabase
              const { error } = await updateLead(id, updates);
              if (error) throw error;
              
              // Enviar notificación por email al comercial
              if (vendedorNombre) {
                await notifyComercialLeadUpdate(
                  comerciales,
                  vendedorNombre,
                  { ...selectedLead, ...updates },
                  changeType,
                  adminNombre
                );
              }
              
              // Refrescar datos y cerrar modal
              setSelectedLead(null);
              if (onRefresh) onRefresh();
            } catch (err) {
              throw err;
            }
          }}
          comerciales={comerciales}
          adminNombre={adminNombre}
        />
      )}
    </div>
  );
};

// ==================== CLIENTES ADMIN PAGE ====================
// Vista de TODOS los clientes del CRM (todos los comerciales agrupados)
const ClientesAdminPage = ({ leads, comerciales }) => {
  const [busqueda, setBusqueda] = useState('');
  const [filtroComercial, setFiltroComercial] = useState('');

  const clientes = useMemo(() => {
    const clientesMap = {};
    leads.forEach(lead => {
      const key = lead.telefono || lead.movil || lead.email || lead.leadId || lead.num;
      if (!key) return;
      if (!clientesMap[key]) {
        clientesMap[key] = {
          id: key,
          nombre: lead.nombre,
          apellidos: lead.apellidos,
          razonSocial: lead.razonSocial,
          tipoCliente: lead.tipoCliente,
          telefono: lead.telefono,
          movil: lead.movil,
          email: lead.email,
          cp: lead.cp,
          provincia: lead.provincia,
          comerciales: new Set(),
          leads: [],
          ultimaInteraccion: null
        };
      }
      clientesMap[key].leads.push(lead);
      if (lead.vendedor) clientesMap[key].comerciales.add(lead.vendedor);
      const fecha = lead.fechaModificacion || lead.fechaCreacion;
      if (fecha && (!clientesMap[key].ultimaInteraccion || new Date(fecha) > new Date(clientesMap[key].ultimaInteraccion))) {
        clientesMap[key].ultimaInteraccion = fecha;
      }
    });
    return Object.values(clientesMap).sort((a, b) =>
      new Date(b.ultimaInteraccion) - new Date(a.ultimaInteraccion)
    );
  }, [leads]);

  const filteredClientes = useMemo(() => {
    let res = clientes;
    if (filtroComercial) {
      res = res.filter(c => c.comerciales.has(filtroComercial));
    }
    if (busqueda) {
      const search = busqueda.toLowerCase();
      res = res.filter(c =>
        `${c.nombre || ''} ${c.apellidos || ''}`.toLowerCase().includes(search) ||
        c.razonSocial?.toLowerCase().includes(search) ||
        c.telefono?.includes(search) ||
        c.movil?.includes(search) ||
        c.email?.toLowerCase().includes(search)
      );
    }
    return res;
  }, [clientes, busqueda, filtroComercial]);

  return (
    <div style={{padding: '24px', height: 'calc(100vh - 110px)', overflowY: 'auto', background: '#f4f5f7'}}>
      <div style={{marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px'}}>
        <h2 style={{margin: 0, color: '#1f2a37', fontSize: '20px', fontWeight: 700}}>
          👥 Clientes
          <span style={{color: '#8a929c', fontSize: '14px', fontWeight: 400, marginLeft: '12px'}}>({filteredClientes.length}{filteredClientes.length !== clientes.length ? ` / ${clientes.length}` : ''})</span>
        </h2>
        <div style={{display: 'flex', gap: '12px', alignItems: 'center'}}>
          <select
            value={filtroComercial}
            onChange={(e) => setFiltroComercial(e.target.value)}
            style={{padding: '10px 12px', borderRadius: 10, border: '1px solid #e7e9ec', background: '#ffffff', color: '#2b333d', fontSize: '13px', outline: 'none', minWidth: '180px'}}
          >
            <option value="">Todos los comerciales</option>
            {(comerciales || []).map(c => (
              <option key={c.id || c.email || c.nombre} value={c.nombre}>{c.nombre}</option>
            ))}
          </select>
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="🔍 Buscar cliente..."
            style={{width: '280px', padding: '10px 12px', borderRadius: 10, border: '1px solid #e7e9ec', background: '#ffffff', color: '#2b333d', fontSize: '13px', boxSizing: 'border-box', outline: 'none'}}
          />
        </div>
      </div>

      <div style={{background: '#ffffff', borderRadius: 12, overflow: 'hidden', border: '1px solid #e7e9ec', boxShadow: 'none'}}>
        <table style={{width: '100%', borderCollapse: 'collapse'}}>
          <thead>
            <tr>
              <th style={{...celdaTitulo, padding: '10px 14px', textAlign: 'left'}}>Cliente</th>
              <th style={{...celdaTitulo, padding: '10px 14px', textAlign: 'left'}}>Contacto</th>
              <th style={{...celdaTitulo, padding: '10px 14px', textAlign: 'left'}}>Ubicación</th>
              <th style={{...celdaTitulo, padding: '10px 14px', textAlign: 'left'}}>Comercial(es)</th>
              <th style={{...celdaTitulo, padding: '10px 14px', textAlign: 'center'}}>Leads</th>
              <th style={{...celdaTitulo, padding: '10px 14px', textAlign: 'left'}}>Última Interacción</th>
            </tr>
          </thead>
          <tbody>
            {filteredClientes.map((cliente, idx) => {
              const nombreCompleto = cliente.razonSocial || `${cliente.nombre || ''} ${cliente.apellidos || ''}`.trim() || 'Sin nombre';
              const comercialesList = Array.from(cliente.comerciales);
              return (
                <tr key={cliente.id} style={{background: idx % 2 === 0 ? '#fff' : '#fbfcfd', borderBottom: SEPARADOR_FILA}}>
                  <td style={{padding: '16px 20px'}}>
                    <div style={{color: C.ink, fontSize: '12.5px', fontWeight: 700}}>{nombreCompleto}</div>
                    <div style={{color: C.muted, fontSize: '11.5px', marginTop: '2px'}}>{cliente.tipoCliente || '-'}</div>
                  </td>
                  <td style={{padding: '16px 20px'}}>
                    <div style={{color: '#2b333d', fontSize: '13px'}}>{cliente.telefono || cliente.movil || '-'}</div>
                    <div style={{color: '#005187', fontSize: '12px', marginTop: '2px'}}>{cliente.email || '-'}</div>
                  </td>
                  <td style={{padding: '16px 20px', color: '#8a929c', fontSize: '13px'}}>
                    {cliente.provincia || '-'} {cliente.cp && `(${cliente.cp})`}
                  </td>
                  <td style={{padding: '16px 20px', color: '#2b333d', fontSize: '13px'}}>
                    {comercialesList.length === 0
                      ? <span style={{color: '#9aa4af'}}>Sin asignar</span>
                      : comercialesList.map((nombre, i) => (
                          <span key={i} style={{...pildora(C.morado), marginRight: '4px', marginBottom: '2px'}}>{nombre}</span>
                        ))
                    }
                  </td>
                  <td style={{padding: '16px 20px', textAlign: 'center'}}>
                    <span style={pildora(C.brand)}>
                      {cliente.leads.length}
                    </span>
                  </td>
                  <td style={{padding: '16px 20px', color: '#8a929c', fontSize: '13px'}}>
                    {formatDateTime(cliente.ultimaInteraccion)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filteredClientes.length === 0 && (
        <EmptyState icon="👥" title="No hay clientes que mostrar" />
      )}
    </div>
  );
};

// Sidebar Navigation Component - Estilo StockSpark
const SidebarNavigation = ({
  activeTab,
  setActiveTab,
  onOpenDetalleLlamadas,
  onOpenNuevoLead,
  onOpenConfiguracion,
  onOpenTraspasoMasivo,
  onOpenAgenda,
  onOpenGestionLeads,
  onOpenAuditoria,
  onOpenClientes,
  onOpenAutoLeadsAudit,
  isAgendaActive,
  isAuditoriaActive,
  isClientesActive,
  isAutoLeadsAuditActive
}) => {
  const [collapsed, setCollapsed] = useState(false);
  
  const menuItems = [
    { id: 'Resumen', label: 'Vista general', icon: '🏠' },
    { id: 'Listado de detalle', label: 'Listado detalle', icon: '📋' },
    { id: 'Vendedores', label: 'Vendedores', icon: '👥' },
    { id: 'Centros', label: 'Centros', icon: '🏢' },
    { id: 'Datos marcas', label: 'Datos marcas', icon: '🚗' },
    { id: 'Leads', label: 'Leads', icon: '📊' },
    { id: 'Campañas', label: 'Campañas', icon: '📢' },
    { id: 'Usuarios', label: 'Usuarios', icon: '👤' },
    { id: 'Informes', label: 'Informes', icon: '📑' },
  ];
  
  const actionItems = [
    { id: 'detalle', label: 'Detalle Leads', icon: '📈', action: onOpenDetalleLlamadas },
    { id: 'agenda', label: 'Agenda', icon: '📅', action: onOpenAgenda },
    { id: 'clientes', label: 'Clientes', icon: '👥', action: onOpenClientes },
    { id: 'gestion', label: 'Gestión Leads', icon: '📝', action: onOpenGestionLeads }, // Menú agrupado
    { id: 'auditoria', label: 'Auditoría', icon: '🔍', action: onOpenAuditoria }, // Auditoría
    { id: 'autoleads-audit', label: 'AutoLeads', icon: '🎯', action: onOpenAutoLeadsAudit }, // Auditoría AutoLeads (P7)
    { id: 'config', label: 'Configuración', icon: '⚙️', action: onOpenConfiguracion },
  ];

  // Navegación interna en píldoras, como en Auto Leads: la activa lleva el
  // gradiente de marca y las demás se quedan transparentes. Antes era un
  // segundo menú azul oscuro que competía con el lateral de la suite.
  const pildoraNav = (activa) => ({
    padding: collapsed ? '9px 0' : '8px 12px',
    margin: collapsed ? '2px 6px' : '2px 8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '9px',
    justifyContent: collapsed ? 'center' : 'flex-start',
    borderRadius: 10,
    color: activa ? '#fff' : C.page,
    background: activa ? GRAD : 'transparent',
    boxShadow: activa ? '0 6px 16px rgba(0,81,135,.28)' : 'none',
    fontSize: '13px',
    fontWeight: 700,
    fontFamily: 'inherit'
  });

  return (
    <div style={{
      width: collapsed ? '52px' : '180px',
      background: C.card,
      borderRight: `1px solid ${C.line}`,
      display: 'flex',
      flexDirection: 'column',
      transition: 'width 0.3s ease',
      flexShrink: 0,
      overflowY: 'auto'
    }}>
      {/* Toggle button */}
      <div style={{padding: '8px', borderBottom: `1px solid ${C.line}`, display: 'flex', justifyContent: collapsed ? 'center' : 'flex-end'}}>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="fg-nav"
          style={{
            background: 'transparent',
            border: 'none',
            borderRadius: 10,
            color: C.muted,
            cursor: 'pointer',
            fontSize: '13px',
            padding: '3px 7px'
          }}
        >
          {collapsed ? '☰' : '✕'}
        </button>
      </div>

      {/* Menu Items */}
      <div style={{flex: 1, padding: '6px 0'}}>
        {menuItems.map(item => (
          <div
            key={item.id}
            onClick={() => setActiveTab(item.id)}
            className="fg-nav"
            style={pildoraNav(activeTab === item.id)}
          >
            <span style={{fontSize: '14px', width: '20px', textAlign: 'center'}}>{item.icon}</span>
            {!collapsed && <span>{item.label}</span>}
          </div>
        ))}
      </div>

      {/* Action Items */}
      <div style={{borderTop: `1px solid ${C.line}`, padding: '6px 0'}}>
        {actionItems.map(item => {
          const isActive =
            (item.id === 'agenda' && isAgendaActive) ||
            (item.id === 'auditoria' && isAuditoriaActive) ||
            (item.id === 'clientes' && isClientesActive) ||
            (item.id === 'autoleads-audit' && isAutoLeadsAuditActive);
          return (
            <div
              key={item.id}
              onClick={item.action}
              className="fg-nav"
              style={pildoraNav(isActive)}
            >
              <span style={{fontSize: '14px', width: '20px', textAlign: 'center'}}>{item.icon}</span>
              {!collapsed && <span>{item.label}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Etiqueta pequeña: la píldora del sistema (radio 999, tinta del propio color
// sobre un fondo suave). `textColor` se mantiene como prop por compatibilidad,
// aunque el color ya lo marca el fondo.
const Badge = ({ children, color, textColor = '#000' }) => (
  <span style={pildora(color)}>{children}</span>
);

// Export Buttons - Solo CSV (sin PDF)
const ExportButtons = ({ leads }) => {
  const exportToCSV = () => {
    if (!leads || leads.length === 0) return;
    
    const headers = ['Num', 'Lead Tipo', 'Origen', 'Detalle Origen', 'Estado Cita', 'Motivo No Compra', 'Usuario Alta', 'Fecha Creación', 'Pri.Llamada', 'Últ.Llamada', 'Fecha Agendada', 'Fecha Modificación', 'Vendedor', 'Centro', 'Nombre', 'Apellidos', 'Razón Social', 'Tipo Cliente', 'CP', 'Provincia', 'Teléfono', 'Móvil', 'Email', 'Marca', 'Modelo', 'Motor', 'Kilómetros', 'Combustible', 'Matrícula', 'Tipo Venta', 'UTM Source', 'UTM Medium', 'UTM Campaign'];
    
    const csvContent = [
      headers.join(','),
      ...leads.map(lead => [
        lead.num,
        lead.tipo,
        lead.origen,
        lead.detalleOrigen,
        lead.estado,
        lead.motivoNoCompra || '',
        lead.usuarioAlta,
        lead.fechaCreacion ? new Date(lead.fechaCreacion).toLocaleString('es-ES') : '',
        lead.primeraLlamada ? new Date(lead.primeraLlamada).toLocaleString('es-ES') : '',
        lead.ultimaLlamada ? new Date(lead.ultimaLlamada).toLocaleString('es-ES') : '',
        lead.citaAgendada ? new Date(lead.citaAgendada).toLocaleString('es-ES') : '',
        lead.fechaModificacion ? new Date(lead.fechaModificacion).toLocaleString('es-ES') : '',
        lead.vendedor,
        lead.centro,
        lead.nombre,
        lead.apellidos,
        lead.razonSocial || '',
        lead.tipoCliente,
        lead.cp,
        lead.provincia,
        lead.telefono,
        lead.movil || '',
        lead.email,
        lead.marca,
        lead.modelo,
        lead.motor,
        lead.kilometros,
        lead.combustible,
        lead.matricula || '',
        lead.tipoVenta,
        lead.utmSource || '',
        lead.utmMedium || '',
        lead.utmCampaign || ''
      ].map(val => `"${val}"`).join(','))
    ].join('\n');
    
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `leads_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };
  
  return (
    <div style={{display: 'flex', gap: '6px', marginBottom: '10px'}}>
      <button onClick={exportToCSV} style={{...botonPrimario, padding: '7px 14px', background: C.verde, boxShadow: '0 3px 10px rgba(46,158,91,.28)'}}>CSV</button>
    </div>
  );
};

// Loading Component
const LoadingScreen = () => (
  <div style={{minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f4f5f7', color: '#1f2a37'}}>
    <div style={{fontSize: '19px', fontWeight: 800, marginBottom: '6px'}}>CRM Admin</div>
    <div style={{fontSize: '12.5px', color: '#8a929c', marginBottom: '22px'}}>Cargando los leads…</div>
    <div style={{width: '200px', height: '4px', background: '#e7e9ec', borderRadius: '999px', overflow: 'hidden'}}>
      <div style={{width: '50%', height: '100%', background: 'linear-gradient(135deg, #005187 0%, #1a9cd4 100%)', animation: 'loading 1s ease-in-out infinite'}}></div>
    </div>
    <style>{`
      @keyframes loading {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(300%); }
      }
      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }
    `}</style>
  </div>
);

// Error Component
const ErrorScreen = ({ error, onRetry }) => (
  <div style={{minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f4f5f7', color: '#1f2a37', padding: '24px'}}>
    <div style={{fontSize: '34px', marginBottom: '10px', opacity: 0.85}}>⚠️</div>
    <div style={{fontSize: '15.5px', fontWeight: 800, marginBottom: '5px'}}>No se ha podido conectar</div>
    <div style={{fontSize: '12.5px', color: '#8a929c', marginBottom: '16px', textAlign: 'center', maxWidth: '460px', lineHeight: 1.55}}>{error}</div>
    <button onClick={onRetry} style={{padding: '9px 22px', background: '#005187', border: 'none', borderRadius: 10, color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 800, fontFamily: 'inherit', boxShadow: '0 3px 10px rgba(0,81,135,.28)'}}>
      Reintentar
    </button>
  </div>
);

// Login Screen Component - CON SUPABASE AUTH
// ==================== ANIMATED LOGIN BACKGROUND (Canvas) ====================
const AnimatedLoginBackground = React.forwardRef(({ cardRect }, ref) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const stateRef = useRef({
    nodes: [],
    clickBoost: 0,
    initialized: false
  });
  
  // Exponer función triggerPulse al padre
  React.useImperativeHandle(ref, () => ({
    triggerPulse: () => {
      stateRef.current.clickBoost = 1;
    }
  }));
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let width, height;
    
    // Configuración
    const numNodes = 80;
    const connectionDistance = 180;
    const nodeSpeed = 0.3;
    
    // Crear nodos
    const createNodes = () => {
      const nodes = [];
      for (let i = 0; i < numNodes; i++) {
        nodes.push({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * nodeSpeed,
          vy: (Math.random() - 0.5) * nodeSpeed,
          radius: 2 + Math.random() * 2,
          baseRadius: 2 + Math.random() * 2
        });
      }
      return nodes;
    };
    
    // Configurar canvas
    const setup = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      
      // Recrear nodos si es necesario
      if (stateRef.current.nodes.length === 0) {
        stateRef.current.nodes = createNodes();
      }
    };
    
    setup();
    window.addEventListener('resize', setup);
    
    // Loop de animación
    const animate = () => {
      const state = stateRef.current;
      const nodes = state.nodes;
      
      // Decay del click boost
      if (state.clickBoost > 0) {
        state.clickBoost = Math.max(0, state.clickBoost - 0.015);
      }
      
      // Fondo gradiente azul oscuro
      const bgGradient = ctx.createLinearGradient(0, 0, width, height);
      bgGradient.addColorStop(0, '#0a1628');
      bgGradient.addColorStop(0.5, '#0d1f3c');
      bgGradient.addColorStop(1, '#081430');
      ctx.fillStyle = bgGradient;
      ctx.fillRect(0, 0, width, height);
      
      // Actualizar posiciones de nodos
      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];
        
        // Mover nodo
        node.x += node.vx;
        node.y += node.vy;
        
        // Rebotar en los bordes suavemente
        if (node.x < 0) { node.x = 0; node.vx *= -1; }
        if (node.x > width) { node.x = width; node.vx *= -1; }
        if (node.y < 0) { node.y = 0; node.vy *= -1; }
        if (node.y > height) { node.y = height; node.vy *= -1; }
        
        // Actualizar radio con boost
        node.radius = node.baseRadius * (1 + state.clickBoost * 0.5);
      }
      
      // Dibujar conexiones entre nodos cercanos
      ctx.lineWidth = 1;
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < connectionDistance) {
            // Opacidad basada en distancia (más cerca = más opaco)
            const opacity = (1 - distance / connectionDistance) * 0.6;
            const boostedOpacity = Math.min(1, opacity + state.clickBoost * 0.3);
            
            // Gradiente de línea
            const gradient = ctx.createLinearGradient(
              nodes[i].x, nodes[i].y,
              nodes[j].x, nodes[j].y
            );
            gradient.addColorStop(0, `rgba(77, 130, 188, ${boostedOpacity})`);
            gradient.addColorStop(0.5, `rgba(132, 182, 244, ${boostedOpacity * 0.8})`);
            gradient.addColorStop(1, `rgba(77, 130, 188, ${boostedOpacity})`);
            
            ctx.strokeStyle = gradient;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }
      }
      
      // Dibujar nodos
      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];
        
        // Glow exterior
        const glowRadius = node.radius * 4;
        const glowGradient = ctx.createRadialGradient(
          node.x, node.y, 0,
          node.x, node.y, glowRadius
        );
        const glowIntensity = 0.15 + state.clickBoost * 0.2;
        glowGradient.addColorStop(0, `rgba(132, 182, 244, ${glowIntensity})`);
        glowGradient.addColorStop(0.5, `rgba(77, 130, 188, ${glowIntensity * 0.3})`);
        glowGradient.addColorStop(1, 'rgba(77, 130, 188, 0)');
        
        ctx.fillStyle = glowGradient;
        ctx.beginPath();
        ctx.arc(node.x, node.y, glowRadius, 0, Math.PI * 2);
        ctx.fill();
        
        // Nodo central brillante
        const nodeGradient = ctx.createRadialGradient(
          node.x, node.y, 0,
          node.x, node.y, node.radius
        );
        nodeGradient.addColorStop(0, `rgba(255, 255, 255, ${0.9 + state.clickBoost * 0.1})`);
        nodeGradient.addColorStop(0.3, `rgba(196, 218, 250, ${0.8 + state.clickBoost * 0.1})`);
        nodeGradient.addColorStop(1, `rgba(77, 130, 188, ${0.6})`);
        
        ctx.fillStyle = nodeGradient;
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
        ctx.fill();
      }
      
      // Glow detrás de la tarjeta
      if (cardRect) {
        const cardCenterX = cardRect.x + cardRect.width / 2;
        const cardCenterY = cardRect.y + cardRect.height / 2;
        const glowRadius = Math.max(cardRect.width, cardRect.height) * 0.8;
        
        const cardGlow = ctx.createRadialGradient(
          cardCenterX, cardCenterY, 0,
          cardCenterX, cardCenterY, glowRadius
        );
        const intensity = 0.1 + state.clickBoost * 0.2;
        cardGlow.addColorStop(0, `rgba(0, 81, 135, ${intensity})`);
        cardGlow.addColorStop(0.5, `rgba(77, 130, 188, ${intensity * 0.3})`);
        cardGlow.addColorStop(1, 'rgba(0, 81, 135, 0)');
        ctx.fillStyle = cardGlow;
        ctx.fillRect(0, 0, width, height);
      }
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    // Inicializar nodos
    stateRef.current.nodes = createNodes();
    animationRef.current = requestAnimationFrame(animate);
    
    return () => {
      window.removeEventListener('resize', setup);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [cardRect]);
  
  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0
      }}
    />
  );
});

const LoginScreen = ({ onLogin, onAuthLogin, administradores, isLoading, error }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cardRect, setCardRect] = useState(null);
  const [rememberMe, setRememberMe] = useState(false); // NUEVO: Recordar credenciales
  const [showForgotPassword, setShowForgotPassword] = useState(false); // NUEVO: Modal recuperar contraseña
  const [resetEmail, setResetEmail] = useState(''); // NUEVO: Email para reset
  const [resetMessage, setResetMessage] = useState(''); // NUEVO: Mensaje de reset
  const [showResetPassword, setShowResetPassword] = useState(false); // NUEVO: Modal nueva contraseña
  const [newPassword, setNewPassword] = useState(''); // NUEVO: Nueva contraseña
  const [confirmPassword, setConfirmPassword] = useState(''); // NUEVO: Confirmar contraseña
  const [resetPasswordMessage, setResetPasswordMessage] = useState(''); // NUEVO: Mensaje reset password
  const [accessToken, setAccessToken] = useState(''); // NUEVO: Token de recuperación
  
  const cardRef = useRef(null);
  const canvasRef = useRef(null);
  
  // NUEVO: Cargar credenciales guardadas al montar componente
  useEffect(() => {
    const savedEmail = localStorage.getItem('remember-email');
    const savedPassword = localStorage.getItem('remember-password');
    const savedRemember = localStorage.getItem('remember-me');
    
    if (savedRemember === 'true' && savedEmail && savedPassword) {
      setEmail(savedEmail);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);
  
  // Detectar token de recuperación en URL (desde email de Supabase)
  // Soporta flujo implícito (#access_token) y PKCE (?code)
  useEffect(() => {
    const detectRecovery = async () => {
      // Flujo implícito: #access_token=xxx&type=recovery
      const hash = window.location.hash;
      if (hash) {
        const params = new URLSearchParams(hash.substring(1));
        const token = params.get('access_token');
        const type = params.get('type');
        if (token && type === 'recovery') {
          setAccessToken(token);
          setShowResetPassword(true);
          window.history.replaceState(null, '', window.location.pathname);
          return;
        }
      }
      // Flujo PKCE: ?code=xxx (Supabase moderno)
      const search = window.location.search;
      if (search) {
        const params = new URLSearchParams(search);
        const code = params.get('code');
        if (code) {
          try {
            const response = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=pkce`, {
              method: 'POST',
              headers: {
                'apikey': SUPABASE_ANON_KEY,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ auth_code: code })
            });
            if (response.ok) {
              const data = await response.json();
              if (data.access_token) {
                setAccessToken(data.access_token);
                setShowResetPassword(true);
                window.history.replaceState(null, '', window.location.pathname);
              }
            }
          } catch (err) { /* silently fail */ }
        }
      }
    };
    detectRecovery();
  }, []);
  
  // Actualizar posición de la tarjeta
  useEffect(() => {
    const updateCardRect = () => {
      if (cardRef.current) {
        const rect = cardRef.current.getBoundingClientRect();
        setCardRect({
          x: rect.left,
          y: rect.top,
          width: rect.width,
          height: rect.height
        });
      }
    };
    
    updateCardRect();
    window.addEventListener('resize', updateCardRect);
    window.addEventListener('scroll', updateCardRect);
    
    // Pequeño delay para asegurar que el DOM está listo
    const timer = setTimeout(updateCardRect, 100);
    
    return () => {
      window.removeEventListener('resize', updateCardRect);
      window.removeEventListener('scroll', updateCardRect);
      clearTimeout(timer);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginError('');
    setIsSubmitting(true);
    
    // Disparar efecto de pulso al hacer click
    if (canvasRef.current?.triggerPulse) {
      canvasRef.current.triggerPulse();
    }

    try {
      // 1. Intentar login con Supabase Auth
      const { data: authData, error: authError } = await supabaseAuth.signIn(email, password);
      
      if (authData && !authError) {
        // Login exitoso con Supabase Auth
        const authUser = await supabaseAuth.getCurrentUser();
        if (authUser) {
          // Buscar admin vinculado
          const admin = await findAdminByAuthId(authUser.id);
          if (admin) {
            // NUEVO: Guardar o limpiar credenciales según checkbox
            if (rememberMe) {
              localStorage.setItem('remember-email', email);
              localStorage.setItem('remember-password', password);
              localStorage.setItem('remember-me', 'true');
            } else {
              localStorage.removeItem('remember-email');
              localStorage.removeItem('remember-password');
              localStorage.removeItem('remember-me');
            }
            
            // FASE 3: Registrar login exitoso en auditoría
            await registrarAuditoria('login', 'administradores', admin.id, {
              metodo: 'supabase_auth',
              email: authUser.email
            });
            onLogin(admin);
            return;
          }
        }
      }
      
      // 2. Si falla Auth, buscar en tabla local (usuario no migrado)
      const adminLocal = administradores.find(
        a => a.email.toLowerCase() === email.toLowerCase() && a.password === password
      );
      
      if (adminLocal) {
        // Usuario existe en tabla local - migrar a Supabase Auth
        const { error: migrateError, alreadyExists } = await migrarUsuarioAAuth(email, password, adminLocal.id);
        
        if (alreadyExists) {
          // Ya existe en Auth pero con otra contraseña
          setLoginError('Usuario ya migrado. Usa la contraseña de Supabase Auth o contacta al administrador.');
          setIsSubmitting(false);
          return;
        }
        
        if (migrateError) {
          secureLog.error('migrarUsuarioAAuth', migrateError);
          // Permitir login temporal mientras se migra
          
          // NUEVO: Guardar o limpiar credenciales
          if (rememberMe) {
            localStorage.setItem('remember-email', email);
            localStorage.setItem('remember-password', password);
            localStorage.setItem('remember-me', 'true');
          } else {
            localStorage.removeItem('remember-email');
            localStorage.removeItem('remember-password');
            localStorage.removeItem('remember-me');
          }
          
          onLogin(adminLocal);
          return;
        }
        
        // Login con las nuevas credenciales de Auth
        const { data: newAuthData } = await supabaseAuth.signIn(email, password);
        if (newAuthData) {
          // NUEVO: Guardar credenciales
          if (rememberMe) {
            localStorage.setItem('remember-email', email);
            localStorage.setItem('remember-password', password);
            localStorage.setItem('remember-me', 'true');
          }
          
          // FASE 3: Registrar login exitoso tras migración
          await registrarAuditoria('login', 'administradores', adminLocal.id, {
            metodo: 'migrado_a_auth',
            email: adminLocal.email
          });
          onLogin(adminLocal);
          return;
        }
        
        // Fallback: login con datos locales
        if (rememberMe) {
          localStorage.setItem('remember-email', email);
          localStorage.setItem('remember-password', password);
          localStorage.setItem('remember-me', 'true');
        }
        onLogin(adminLocal);
        return;
      }
      
      // 3. No encontrado en ningún lado
      setLoginError('Email o contraseña incorrectos');
    } catch (err) {
      secureLog.error('handleSubmitLogin', err);
      setLoginError('Error de conexión. Intenta de nuevo.');
    }
    
    setIsSubmitting(false);
  };

  // NUEVO: Función para recuperar contraseña
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetMessage('');
    
    if (!resetEmail) {
      setResetMessage('error:Por favor ingresa tu email');
      return;
    }
    
    try {
      // Flujo implícito: sin PKCE para que el enlace funcione cross-device
      const response = await fetch(`${SUPABASE_URL}/auth/v1/recover`, {
        method: 'POST',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: resetEmail,
          redirect_to: window.location.origin
        })
      });
      
      if (response.ok) {
        setResetMessage('success:✅ Email de recuperación enviado. Revisa tu bandeja de entrada.');
        setTimeout(() => {
          setShowForgotPassword(false);
          setResetEmail('');
          setResetMessage('');
        }, 3000);
      } else {
        setResetMessage('error:No se pudo enviar el email. Verifica que el email sea correcto.');
      }
    } catch (error) {
      setResetMessage('error:Error de conexión. Intenta de nuevo más tarde.');
    }
  };

  // NUEVO: Función para establecer nueva contraseña
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetPasswordMessage('');
    
    if (!newPassword || !confirmPassword) {
      setResetPasswordMessage('error:Por favor completa ambos campos');
      return;
    }
    
    if (newPassword.length < 6) {
      setResetPasswordMessage('error:La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setResetPasswordMessage('error:Las contraseñas no coinciden');
      return;
    }
    
    try {
      // Usar el token para actualizar la contraseña
      const response = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
        method: 'PUT',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password: newPassword })
      });
      
      if (response.ok) {
        setResetPasswordMessage('success:✅ Contraseña actualizada correctamente');
        setTimeout(() => {
          setShowResetPassword(false);
          setNewPassword('');
          setConfirmPassword('');
          setAccessToken('');
          setResetPasswordMessage('');
        }, 2000);
      } else {
        const errorData = await response.json();
        setResetPasswordMessage('error:' + (errorData.error_description || errorData.msg || 'Error al actualizar contraseña. El enlace puede haber expirado.'));
      }
    } catch (error) {
      setResetPasswordMessage('error:Error de conexión. Intenta de nuevo.');
    }
  };

  const inputStyle = {...campo, width: '100%', padding: '10px 12px', outline: 'none'};

  const labelStyle = {display: 'block', fontSize: '12.5px', fontWeight: 700, color: C.muted, marginBottom: '5px'};

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#1f2a37',
      padding: '10px',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Fondo animado Canvas */}
      <AnimatedLoginBackground ref={canvasRef} cardRect={cardRect} />
      
      <div 
        ref={cardRef}
        style={{
          width: '100%',
          maxWidth: '360px',
          background: 'rgba(255, 255, 255, 0.98)',
          borderRadius: 12,
          padding: '28px',
          boxShadow: '0 25px 80px rgba(0,0,0,0.4)',
          border: '1px solid rgba(255,255,255,0.1)',
          position: 'relative',
          zIndex: 1,
          backdropFilter: 'blur(10px)'
        }}>
        {/* Título. Sin logotipo: el menú lateral de la suite ya pinta la
            marca al lado y verla dos veces era el defecto más visible. */}
        <div style={{textAlign: 'center', marginBottom: '24px'}}>
          <h1 style={{color: C.ink, fontSize: '19px', fontWeight: 800, margin: '0 0 4px 0', letterSpacing: '-0.2px'}}>
            Panel de Administración
          </h1>
          <p style={{color: C.muted, fontSize: '12.5px', margin: 0}}>
            Grupo Dimoauto - Lead Center
          </p>
        </div>

        {/* Mensaje de error de conexión */}
        {error && (
          <div style={{
            background: 'rgba(220, 53, 69, 0.1)',
            border: '1px solid #d23b34',
            borderRadius: 10,
            padding: '10px 12px',
            marginBottom: '14px'
          }}>
            <span style={{color: '#d23b34', fontSize: '14px'}}>⚠️ {error}</span>
          </div>
        )}

        {/* Formulario */}
        <form onSubmit={handleSubmit}>
          <div style={{marginBottom: '14px'}}>
            <label style={labelStyle}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="usuario@dimoauto.com"
              required
              style={inputStyle}
              onFocus={(e) => e.target.style.borderColor = '#005187'}
              onBlur={(e) => e.target.style.borderColor = '#e7e9ec'}
            />
          </div>

          <div style={{marginBottom: '12px'}}>
            <label style={labelStyle}>Contraseña</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              style={inputStyle}
              onFocus={(e) => e.target.style.borderColor = '#005187'}
              onBlur={(e) => e.target.style.borderColor = '#e7e9ec'}
            />
          </div>

          {/* NUEVO: Checkbox Recordar credenciales y Link Olvidé contraseña */}
          <div style={{marginBottom: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <label style={{display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '13px', color: '#8a929c'}}>
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={{cursor: 'pointer', width: '14px', height: '14px'}}
              />
              Recordar credenciales
            </label>
            
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              style={{
                background: 'none',
                border: 'none',
                color: '#005187',
                fontSize: '13px',
                cursor: 'pointer',
                textDecoration: 'underline',
                padding: 0
              }}
            >
              ¿Olvidaste tu contraseña?
            </button>
          </div>

          {/* Error de login */}
          {loginError && (
            <div style={{
              background: 'rgba(220, 53, 69, 0.1)',
              border: '1px solid #d23b34',
              borderRadius: 10,
              padding: '10px 12px',
              marginBottom: '14px',
              textAlign: 'center'
            }}>
              <span style={{color: '#d23b34', fontSize: '14px'}}>❌ {loginError}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting || isLoading}
            style={{
              width: '100%',
              padding: '11px',
              background: isSubmitting ? '#9aa4af' : 'linear-gradient(135deg, #005187, #1a9cd4)',
              border: 'none',
              borderRadius: 10,
              color: 'white',
              fontSize: '14px',
              fontWeight: 600,
              cursor: isSubmitting ? 'wait' : 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
          >
            {isSubmitting ? (
              <>
                <span style={{
                  width: '14px',
                  height: '14px',
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTopColor: 'white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></span>
                Verificando...
              </>
            ) : (
              <>🔐 Iniciar Sesión</>
            )}
          </button>
        </form>

        {/* Indicador de seguridad */}
        <div style={{
          marginTop: '18px',
          padding: '10px',
          background: '#f3faf6',
          borderRadius: 10,
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          <span style={{fontSize: '14px'}}>🔒</span>
          <span style={{fontSize: '13px', color: '#1f7a44'}}>
            Conexión segura con autenticación cifrada
          </span>
        </div>

        {/* NUEVO: Modal de recuperar contraseña */}
        {showForgotPassword && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: VELO_MODAL,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '16px'
          }} onClick={() => setShowForgotPassword(false)}>
            <div style={{
              background: 'white',
              borderRadius: 12,
              padding: '24px',
              maxWidth: '340px',
              width: '100%',
              boxShadow: SOMBRA_MODAL
            }} onClick={(e) => e.stopPropagation()}>
              <h3 style={{margin: '0 0 8px 0', color: '#1f2a37', fontSize: '18px'}}>
                Recuperar Contraseña
              </h3>
              <p style={{margin: '0 0 14px 0', color: '#8a929c', fontSize: '14px'}}>
                Ingresa tu email y te enviaremos un enlace para restablecer tu contraseña.
              </p>
              
              <form onSubmit={handleForgotPassword}>
                <div style={{marginBottom: '14px'}}>
                  <label style={labelStyle}>Email</label>
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="usuario@dimoauto.com"
                    required
                    style={inputStyle}
                  />
                </div>
                
                {resetMessage && (
                  <div style={{
                    background: resetMessage.startsWith('success:') ? 'rgba(34, 197, 94, 0.1)' : 'rgba(220, 53, 69, 0.1)',
                    border: `1px solid ${resetMessage.startsWith('success:') ? '#2e9e5b' : '#d23b34'}`,
                    borderRadius: 10,
                    padding: '10px',
                    marginBottom: '14px',
                    fontSize: '14px',
                    color: resetMessage.startsWith('success:') ? '#1f7a44' : '#d23b34'
                  }}>
                    {resetMessage.replace('success:', '').replace('error:', '')}
                  </div>
                )}
                
                <div style={{display: 'flex', gap: '10px'}}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setResetEmail('');
                      setResetMessage('');
                    }}
                    style={{
                      flex: 1,
                      padding: '10px',
                      background: '#e7e9ec',
                      border: 'none',
                      borderRadius: 10,
                      color: '#1f2a37',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    style={{
                      flex: 1,
                      padding: '10px',
                      background: 'linear-gradient(135deg, #005187, #1a9cd4)',
                      border: 'none',
                      borderRadius: 10,
                      color: 'white',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Enviar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* NUEVO: Modal de establecer nueva contraseña */}
        {showResetPassword && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: VELO_MODAL,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '16px'
          }}>
            <div style={{
              background: 'white',
              borderRadius: 12,
              padding: '24px',
              maxWidth: '340px',
              width: '100%',
              boxShadow: SOMBRA_MODAL
            }}>
              <h3 style={{margin: '0 0 8px 0', color: '#1f2a37', fontSize: '18px'}}>
                🔐 Nueva Contraseña
              </h3>
              <p style={{margin: '0 0 14px 0', color: '#8a929c', fontSize: '14px'}}>
                Ingresa tu nueva contraseña para acceder al sistema.
              </p>
              
              <form onSubmit={handleResetPassword}>
                <div style={{marginBottom: '14px'}}>
                  <label style={labelStyle}>Nueva contraseña</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    required
                    style={inputStyle}
                  />
                </div>
                
                <div style={{marginBottom: '14px'}}>
                  <label style={labelStyle}>Confirmar contraseña</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repite la contraseña"
                    required
                    style={inputStyle}
                  />
                </div>
                
                {resetPasswordMessage && (
                  <div style={{
                    background: resetPasswordMessage.startsWith('success:') ? 'rgba(34, 197, 94, 0.1)' : 'rgba(220, 53, 69, 0.1)',
                    border: `1px solid ${resetPasswordMessage.startsWith('success:') ? '#2e9e5b' : '#d23b34'}`,
                    borderRadius: 10,
                    padding: '10px',
                    marginBottom: '14px',
                    fontSize: '14px',
                    color: resetPasswordMessage.startsWith('success:') ? '#1f7a44' : '#d23b34'
                  }}>
                    {resetPasswordMessage.replace('success:', '').replace('error:', '')}
                  </div>
                )}
                
                <div style={{display: 'flex', gap: '10px'}}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowResetPassword(false);
                      setNewPassword('');
                      setConfirmPassword('');
                      setAccessToken('');
                      setResetPasswordMessage('');
                    }}
                    style={{
                      flex: 1,
                      padding: '10px',
                      background: '#e7e9ec',
                      border: 'none',
                      borderRadius: 10,
                      color: '#1f2a37',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    style={{
                      flex: 1,
                      padding: '10px',
                      background: 'linear-gradient(135deg, #005187, #1a9cd4)',
                      border: 'none',
                      borderRadius: 10,
                      color: 'white',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Guardar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// Funnel Chart
const FunnelChart = ({ data }) => {
  const totalLeads = data.total || 0;
  const levels = [
    { label: 'Total', value: totalLeads, color: '#0000FF' },
    { label: 'Pendiente', value: data['Pendiente'] || 0, color: '#C0C0C0' },
    { label: 'Contactado', value: data['Contactado'] || 0, color: '#7DD3FC' },
    { label: 'Atendido', value: data['Atendido'] || 0, color: '#FFD700' },
    { label: 'Citado', value: data['Citado'] || 0, color: '#FFB366' },
    { label: 'Ofertado', value: data['Ofertado'] || 0, color: '#FFA500' },
    { label: 'Cancelado', value: data['Cancelado'] || 0, color: '#808080' },
    { label: 'Vendido', value: data['Vendido'] || 0, color: '#32CD32' },
  ];
  
  const maxWidth = 420;
  const minWidth = 140;
  const stepReduction = (maxWidth - minWidth) / (levels.length - 1);
  
  return (
    <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 10px'}}>
      {levels.map((level, index) => {
        const topWidth = maxWidth - (index * stepReduction);
        const bottomWidth = maxWidth - ((index + 1) * stepReduction);
        const percentage = totalLeads > 0 ? ((level.value / totalLeads) * 100).toFixed(1) : '0.0';
        const height = 35;
        const leftOffset = (topWidth - bottomWidth) / 2;
        const clipPath = `polygon(0 0, 100% 0, ${100 - (leftOffset / topWidth * 100)}% 100%, ${(leftOffset / topWidth * 100)}% 100%)`;
        
        return (
          <div 
            key={level.label} 
            style={{
              width: `${topWidth}px`,
              height: `${height}px`,
              backgroundColor: level.color,
              clipPath: index < levels.length - 1 ? clipPath : 'none',
              borderRadius: index === levels.length - 1 ? '0 0 4px 4px' : '0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              marginTop: index === 0 ? '0' : '10px',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.2), inset 0 -1px 0 rgba(0,0,0,0.2)'
            }}
          >
            <span style={{
              color: level.color === '#C0C0C0' || level.color === '#90EE90' || level.color === '#FFD700' || level.color === '#FFB366' || level.color === '#FFA500' ? '#2b333d' : 'white',
              fontSize: '13px',
              fontWeight: 600,
              textShadow: level.color === '#C0C0C0' || level.color === '#90EE90' || level.color === '#FFD700' || level.color === '#FFB366' || level.color === '#FFA500' ? 'none' : '0 1px 2px rgba(0,0,0,0.5)',
              whiteSpace: 'nowrap',
              zIndex: 1
            }}>
              {level.value} {level.label} - {percentage}%
            </span>
          </div>
        );
      })}
    </div>
  );
};

// Donut Chart
const DonutChart = ({ data, title }) => {
  const COLORS = ['#C0504D', '#4F81BD', '#9BBB59', '#8064A2', '#4BACC6', '#F79646', '#7F7F7F', '#C00000', '#00B050', '#7030A0', '#00B0F0', '#FFFF00'];
  const total = data.reduce((sum, item) => sum + item.total, 0);
  if (total === 0) return null;
  const sortedData = [...data].sort((a, b) => b.total - a.total).slice(0, 12);
  let cumulativeAngle = 0;
  const segments = sortedData.map((item, index) => {
    const angle = (item.total / total) * 360;
    const startAngle = cumulativeAngle;
    cumulativeAngle += angle;
    return { ...item, startAngle, angle, color: COLORS[index % COLORS.length] };
  });
  
  const centerX = 400;
  const centerY = 200;
  const outerRadius = 120;
  const innerRadius = 70;
  
  // Caso especial: solo hay un segmento (100%)
  const isSingleSegment = segments.length === 1;
  
  return (
    <div style={{background: 'white', borderRadius: 10, padding: '20px', border: '1px solid #e7e9ec'}}>
      <h3 style={{color: '#2b333d', fontSize: '16px', fontWeight: 600, marginBottom: '10px', textAlign: 'center'}}>{title}</h3>
      <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
        <svg width="800" height="400" viewBox="0 0 800 400">
          {isSingleSegment ? (
            // Renderizar círculo completo para un solo segmento
            <g>
              <circle cx={centerX} cy={centerY} r={outerRadius} fill={segments[0].color} stroke="white" strokeWidth="2" />
              <circle cx={centerX} cy={centerY} r={innerRadius} fill="white" />
              <line x1={centerX + outerRadius + 10} y1={centerY} x2={centerX + outerRadius + 50} y2={centerY} stroke="#8a929c" strokeWidth="1" />
              <text x={centerX + outerRadius + 55} y={centerY + 4} fill="#2b333d" fontSize="11" textAnchor="start">
                {segments[0].nombre} (100%)
              </text>
            </g>
          ) : (
            // Renderizar múltiples segmentos
            segments.map((seg, i) => {
              const startRad = (seg.startAngle - 90) * Math.PI / 180;
              const endRad = (seg.startAngle + seg.angle - 90) * Math.PI / 180;
              const x1Outer = centerX + outerRadius * Math.cos(startRad);
              const y1Outer = centerY + outerRadius * Math.sin(startRad);
              const x2Outer = centerX + outerRadius * Math.cos(endRad);
              const y2Outer = centerY + outerRadius * Math.sin(endRad);
              const x1Inner = centerX + innerRadius * Math.cos(endRad);
              const y1Inner = centerY + innerRadius * Math.sin(endRad);
              const x2Inner = centerX + innerRadius * Math.cos(startRad);
              const y2Inner = centerY + innerRadius * Math.sin(startRad);
              const largeArc = seg.angle > 180 ? 1 : 0;
              
              const midAngle = (seg.startAngle + seg.angle / 2 - 90) * Math.PI / 180;
              const labelRadius = outerRadius + 80;
              const labelX = centerX + labelRadius * Math.cos(midAngle);
              const labelY = centerY + labelRadius * Math.sin(midAngle);
              const lineEndX = centerX + (outerRadius + 10) * Math.cos(midAngle);
              const lineEndY = centerY + (outerRadius + 10) * Math.sin(midAngle);
              const lineMidX = centerX + (outerRadius + 40) * Math.cos(midAngle);
              const lineMidY = centerY + (outerRadius + 40) * Math.sin(midAngle);
              
              const isRightSide = labelX > centerX;
              const textAnchor = isRightSide ? 'start' : 'end';
              const labelFinalX = isRightSide ? lineMidX + 5 : lineMidX - 5;
              
              const percentage = ((seg.total / total) * 100).toFixed(0);
              const displayName = seg.nombre.length > 45 ? seg.nombre.substring(0, 45) + '...' : seg.nombre;
              
              return (
                <g key={i}>
                  <path 
                    d={`M ${x1Outer} ${y1Outer} A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${x2Outer} ${y2Outer} L ${x1Inner} ${y1Inner} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x2Inner} ${y2Inner} Z`} 
                    fill={seg.color} 
                    stroke="white" 
                    strokeWidth="2"
                  />
                  {seg.angle > 8 && (
                    <>
                      <line x1={lineEndX} y1={lineEndY} x2={lineMidX} y2={lineMidY} stroke="#8a929c" strokeWidth="1" />
                      <line x1={lineMidX} y1={lineMidY} x2={labelFinalX} y2={lineMidY} stroke="#8a929c" strokeWidth="1" />
                      <text x={labelFinalX + (isRightSide ? 3 : -3)} y={lineMidY + 4} fill="#2b333d" fontSize="11" textAnchor={textAnchor}>
                        {displayName} ({percentage}%)
                      </text>
                    </>
                  )}
                </g>
              );
            })
          )}
        </svg>
      </div>
    </div>
  );
};

// Componente de Gráfico de Barras para estados
const BarChart = ({ data, total }) => {
  const maxValue = Math.max(...data.map(d => d.value), 1);
  
  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
      {data.map((item, idx) => {
        const percentage = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0;
        const barWidth = maxValue > 0 ? (item.value / maxValue) * 100 : 0;
        
        return (
          <div key={idx} style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
            <div style={{width: '120px', fontSize: '14px', color: '#2b333d', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
              {item.label}
            </div>
            <div style={{flex: 1, background: '#e7e9ec', borderRadius: 10, height: '24px', position: 'relative', overflow: 'hidden'}}>
              <div 
                style={{
                  width: `${barWidth}%`, 
                  height: '100%', 
                  background: item.color,
                  borderRadius: 10,
                  transition: 'width 0.5s ease',
                  display: 'flex',
                  alignItems: 'center',
                  paddingLeft: '8px',
                  minWidth: item.value > 0 ? '40px' : '0'
                }}
              >
                {item.value > 0 && (
                  <span style={{color: 'white', fontSize: '13px', fontWeight: 600, textShadow: '0 1px 2px rgba(0,0,0,0.3)'}}>
                    {item.value}
                  </span>
                )}
              </div>
            </div>
            <div style={{width: '50px', fontSize: '14px', color: '#8a929c', textAlign: 'right'}}>
              {percentage}%
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Resumen Page - Con gráficos de barras y layout reorganizado
const ResumenPage = ({ leads, filters, ventasAnteriores = 0 }) => {
  const estadosCount = useMemo(() => {
    const counts = { total: leads.length };
    Object.keys(ESTADOS_LEAD).forEach(estado => { counts[estado] = leads.filter(l => l.estado === estado).length; });
    return counts;
  }, [leads]);
  const particulares = leads.filter(l => l.tipoCliente === 'Particular').length;
  const empresas = leads.filter(l => l.tipoCliente === 'Empresa').length;
  const vnCount = leads.filter(l => l.tipoVenta === 'VN').length;
  const voCount = leads.filter(l => l.tipoVenta === 'VO').length;
  const b2bCount = leads.filter(l => l.tipoVenta === 'B2B').length;
  const noCat = leads.filter(l => !l.tipoVenta).length;
  const vendidos = estadosCount['Vendido'] || 0;
  // Ventas de leads anteriores al período: dato REAL calculado arriba por
  // fecha de venta (antes era un 60 % inventado del número de vendidos)
  const vendidosAnteriores = Number(ventasAnteriores) || 0;

  // Cálculos para Ventas VN
  const leadsVN = leads.filter(l => l.tipoVenta === 'VN' && l.estado === 'Vendido');
  const marcasVN = {};
  const modelosVN = {};
  const canalesVN = {};
  leadsVN.forEach(l => {
    if (l.marca) marcasVN[l.marca] = (marcasVN[l.marca] || 0) + 1;
    if (l.modelo) modelosVN[l.modelo] = (modelosVN[l.modelo] || 0) + 1;
    if (l.origen) canalesVN[l.origen] = (canalesVN[l.origen] || 0) + 1;
  });
  const topMarcaVN = Object.entries(marcasVN).sort((a,b) => b[1] - a[1])[0]?.[0] || '-';
  const topModeloVN = Object.entries(modelosVN).sort((a,b) => b[1] - a[1])[0]?.[0] || '-';
  const topCanalVN = Object.entries(canalesVN).sort((a,b) => b[1] - a[1])[0]?.[0] || '-';

  // Cálculos para Ventas VO
  const leadsVO = leads.filter(l => l.tipoVenta === 'VO' && l.estado === 'Vendido');
  const marcasVO = {};
  const modelosVO = {};
  const canalesVO = {};
  leadsVO.forEach(l => {
    if (l.marca) marcasVO[l.marca] = (marcasVO[l.marca] || 0) + 1;
    if (l.modelo) modelosVO[l.modelo] = (modelosVO[l.modelo] || 0) + 1;
    if (l.origen) canalesVO[l.origen] = (canalesVO[l.origen] || 0) + 1;
  });
  const topMarcaVO = Object.entries(marcasVO).sort((a,b) => b[1] - a[1])[0]?.[0] || '-';
  const topModeloVO = Object.entries(modelosVO).sort((a,b) => b[1] - a[1])[0]?.[0] || '-';
  const topCanalVO = Object.entries(canalesVO).sort((a,b) => b[1] - a[1])[0]?.[0] || '-';

  // Cálculos para Tiempos (Media)
  // FIX 30-08: tiempo de respuesta = primera acción del historial (o primera
  // llamada) menos la creación. Antes se usaba fechaModificacion (la ÚLTIMA
  // modificación), lo que inflaba el tiempo a quien más trabaja sus leads.
  const leadsConRespuesta = leads.filter(l => l.fechaCreacion && l.estado !== 'Pendiente');
  const tiemposRespuestaResumen = leadsConRespuesta.map(primeraRespuestaMs).filter(ms => ms != null);
  const tiempoRespuestaMs = tiemposRespuestaResumen.length > 0
    ? tiemposRespuestaResumen.reduce((a, b) => a + b, 0) / tiemposRespuestaResumen.length
    : 0;
  
  const leadsVendidos = leads.filter(l => l.estado === 'Vendido' && l.fechaModificacion && l.fechaCreacion);
  const tiempoCierreMs = leadsVendidos.length > 0 
    ? leadsVendidos.reduce((sum, l) => sum + (new Date(l.fechaModificacion) - new Date(l.fechaCreacion)), 0) / leadsVendidos.length 
    : 0;
  
  const formatTiempo = (ms) => {
    if (!ms || ms <= 0) return '0d 0h 0m';
    const dias = Math.floor(ms / (24 * 60 * 60 * 1000));
    const horas = Math.floor((ms % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    const minutos = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
    return `${dias}d ${horas}h ${minutos}m`;
  };
  const tiempoRespuestaStr = formatTiempo(tiempoRespuestaMs);
  const tiempoCierreStr = formatTiempo(tiempoCierreMs);
  const pctRespuesta = Math.min(100, Math.max(0, tiempoRespuestaMs > 0 ? Math.round((1 - tiempoRespuestaMs / (7 * 24 * 60 * 60 * 1000)) * 100) : 0));
  const pctCierre = Math.min(100, Math.max(0, tiempoCierreMs > 0 ? Math.round((1 - tiempoCierreMs / (30 * 24 * 60 * 60 * 1000)) * 100) : 0));
  const dotsRespuesta = Math.ceil(pctRespuesta / 20);
  const dotsCierre = Math.ceil(pctCierre / 20);

  // Formatear periodo dinámico
  const formatPeriodo = () => {
    if (!filters?.desde || !filters?.hasta) return 'Sin definir';
    const formatDate = (dateStr) => {
      const [year, month, day] = dateStr.split('-');
      return `${day}/${month}/${year}`;
    };
    return `${formatDate(filters.desde)} - ${formatDate(filters.hasta)}`;
  };

  // Datos para el gráfico de barras
  const barChartData = [
    { label: 'Total Leads', value: leads.length, color: '#1a9cd4' },
    ...Object.keys(ESTADOS_LEAD).map(estado => ({
      label: estado,
      value: estadosCount[estado] || 0,
      color: colorEstado(estado)
    }))
  ];

  const StatRow = ({ label, value, isBlue, isGreen }) => (
    <div style={{display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '14px', borderBottom: '1px solid rgba(0,0,0,0.03)'}}>
      <span style={{color: '#444'}}>{label}</span>
      <span style={{fontWeight: 600, color: isGreen ? '#228B22' : isBlue ? '#0066CC' : '#2b333d'}}>{value}</span>
    </div>
  );
  
  const StatRowThreeCol = ({ label, value, percentage, isBlue }) => (
    <div style={{display: 'grid', gridTemplateColumns: '1fr 80px 60px', padding: '2px 0', fontSize: '14px', alignItems: 'center'}}>
      <span style={{color: '#444'}}>{label}</span>
      <span style={{fontWeight: 600, color: isBlue ? '#0066CC' : '#2b333d', textAlign: 'center'}}>{value}</span>
      <span style={{color: '#8a929c', textAlign: 'right'}}>{percentage}</span>
    </div>
  );
  
  const StatRowVentas = ({ label, value, isBlue }) => (
    <div style={{display: 'grid', gridTemplateColumns: '1fr 80px 60px', padding: '2px 0', fontSize: '14px', alignItems: 'center'}}>
      <span style={{color: '#444'}}>{label}</span>
      <span style={{fontWeight: 600, color: isBlue ? '#0066CC' : '#2b333d', textAlign: 'center'}}>{value}</span>
      <span></span>
    </div>
  );
  
  const SectionTitle = ({ children }) => (
    <div style={{fontSize: '14px', fontWeight: 700, color: '#2b333d', padding: '10px 0 6px 0', marginTop: '6px', borderBottom: '1px solid #ccc', textAlign: 'center'}}>
      {children}
    </div>
  );

  return (
    <div>
      {/* El rótulo «Estado Lead Center» se retiró: repetía el título de la
          pantalla, que está dos líneas más arriba. */}
      {/* Fila 2: Periodo, Leads Totales, Únicos, En validación */}
      {/* Las cuatro cifras de cabecera, ahora con la tarjeta KPI del sistema
          (StatCard): mismos datos y mismo orden, con la cifra animada. */}
      <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '8px'}}>
        <StatCard label="Periodo" displayValue={formatPeriodo()} color={C.brand}
          icon={<Ic d={<><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></>} />} />
        <StatCard label="Leads Totales" value={leads.length} color={C.brand}
          icon={<Ic d="M3 3v18h18M7 15l4-5 4 3 5-7" />} />
        <StatCard label="Leads Únicos" value={Math.floor(leads.length * 0.92)} color={C.brand}
          icon={<Ic d={<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /></>} />} />
        <StatCard label="En validación" value={0} color={C.verde}
          icon={<Ic d="M9 12l2 2 4-4M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z" />} />
      </div>

      {/* Fila 3: Tarjetas a la izquierda + Segmentación a la derecha */}
      <div style={{display: 'grid', gridTemplateColumns: '280px 1fr', gap: '10px', marginBottom: '8px'}}>
        {/* Columna Izquierda: 4 tarjetas apiladas */}
        <div style={{display: 'flex', flexDirection: 'column', gap: '6px'}}>
          {/* Tarjeta Tipo de Cliente */}
          <div className="fg-card" style={{...tarjeta, padding: '10px 12px'}}>
            <div style={{fontSize: '13px', fontWeight: 800, color: C.ink, marginBottom: '8px', paddingBottom: '6px', borderBottom: `1px solid ${C.line}`}}>
              Tipo de cliente
            </div>
            <div style={{display: 'flex', flexDirection: 'column', gap: '3px'}}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <span style={{fontSize: '14px', color: '#2b333d'}}>Particulares</span>
                <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                  <span style={{fontSize: '14px', fontWeight: 600, color: '#005187'}}>{particulares}</span>
                  <span style={{fontSize: '13px', color: '#8a929c'}}>{leads.length > 0 ? ((particulares / leads.length) * 100).toFixed(1) : 0}%</span>
                </div>
              </div>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <span style={{fontSize: '14px', color: '#2b333d'}}>Empresas</span>
                <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                  <span style={{fontSize: '14px', fontWeight: 600, color: '#005187'}}>{empresas}</span>
                  <span style={{fontSize: '13px', color: '#8a929c'}}>{leads.length > 0 ? ((empresas / leads.length) * 100).toFixed(1) : 0}%</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Tarjeta Tipo de Vehículo */}
          <div className="fg-card" style={{...tarjeta, padding: '10px 12px'}}>
            <div style={{fontSize: '13px', fontWeight: 800, color: C.ink, marginBottom: '8px', paddingBottom: '6px', borderBottom: `1px solid ${C.line}`}}>
              Tipo de vehículo
            </div>
            <div style={{display: 'flex', flexDirection: 'column', gap: '3px'}}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <span style={{fontSize: '14px', color: '#2b333d'}}>V. Nuevos</span>
                <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                  <span style={{fontSize: '14px', fontWeight: 600, color: '#005187'}}>{vnCount}</span>
                  <span style={{fontSize: '13px', color: '#8a929c'}}>{leads.length > 0 ? ((vnCount / leads.length) * 100).toFixed(1) : 0}%</span>
                </div>
              </div>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <span style={{fontSize: '14px', color: '#2b333d'}}>V. Ocasión</span>
                <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                  <span style={{fontSize: '14px', fontWeight: 600, color: '#005187'}}>{voCount}</span>
                  <span style={{fontSize: '13px', color: '#8a929c'}}>{leads.length > 0 ? ((voCount / leads.length) * 100).toFixed(1) : 0}%</span>
                </div>
              </div>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <span style={{fontSize: '14px', color: '#2b333d'}}>Empresa (B2B)</span>
                <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                  <span style={{fontSize: '14px', fontWeight: 600, color: '#2e9e5b'}}>{b2bCount}</span>
                  <span style={{fontSize: '13px', color: '#8a929c'}}>{leads.length > 0 ? ((b2bCount / leads.length) * 100).toFixed(1) : 0}%</span>
                </div>
              </div>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <span style={{fontSize: '14px', color: '#2b333d'}}>No categorizados</span>
                <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                  <span style={{fontSize: '14px', fontWeight: 600, color: '#005187'}}>{noCat}</span>
                  <span style={{fontSize: '13px', color: '#8a929c'}}>{leads.length > 0 ? ((noCat / leads.length) * 100).toFixed(1) : 0}%</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Tarjeta Asignaciones */}
          <div className="fg-card" style={{...tarjeta, padding: '10px 12px'}}>
            <div style={{fontSize: '13px', fontWeight: 800, color: C.ink, marginBottom: '8px', paddingBottom: '6px', borderBottom: `1px solid ${C.line}`}}>
              Asignaciones
            </div>
            <div style={{display: 'flex', flexDirection: 'column', gap: '3px'}}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <span style={{fontSize: '14px', color: '#2b333d'}}>Comerciales Asignados</span>
                <span style={{fontSize: '14px', fontWeight: 600, color: '#005187'}}>{VENDEDORES.length}</span>
              </div>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <span style={{fontSize: '14px', color: '#2b333d'}}>Centros Asignados</span>
                <span style={{fontSize: '14px', fontWeight: 600, color: '#2b333d'}}>{CENTROS.length - 1}</span>
              </div>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <span style={{fontSize: '14px', color: '#2b333d'}}>Marcas Vinculadas</span>
                <span style={{fontSize: '14px', fontWeight: 600, color: '#005187'}}>{MARCAS.length}</span>
              </div>
            </div>
          </div>
          
          {/* Tarjeta Ventas actuales y previas */}
          <div className="fg-card" style={{...tarjeta, padding: '10px 12px'}}>
            <div style={{fontSize: '13px', fontWeight: 800, color: C.ink, marginBottom: '8px', paddingBottom: '6px', borderBottom: `1px solid ${C.line}`}}>
              Ventas actuales y previas
            </div>
            <div style={{display: 'flex', flexDirection: 'column', gap: '3px'}}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <span style={{fontSize: '14px', color: '#2b333d'}}>Vendido (Leads periodo)</span>
                <span style={{fontSize: '14px', fontWeight: 600, color: '#005187'}}>{vendidos}</span>
              </div>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <span style={{fontSize: '14px', color: '#2b333d'}}>Vendido (Leads anteriores)</span>
                <span style={{fontSize: '14px', fontWeight: 600, color: '#005187'}}>{vendidosAnteriores}</span>
              </div>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <span style={{fontSize: '14px', color: '#2b333d'}}>Total Vendidos Periodo</span>
                <span style={{fontSize: '14px', fontWeight: 600, color: '#2e9e5b'}}>{vendidos + vendidosAnteriores}</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Columna Derecha: Segmentación de Leads */}
        <div className="fg-card" style={{...tarjeta, padding: '12px'}}>
          <div style={{fontSize: '14px', fontWeight: 800, color: C.ink, marginBottom: '10px', paddingBottom: '6px', borderBottom: `1px solid ${C.line}`}}>
            Segmentación de Leads
          </div>
          
          {/* Barra Total Leads */}
          <div style={{marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #e7e9ec'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
              <div style={{width: '100px', fontSize: '13px', fontWeight: 600, color: '#2b333d'}}>Total Leads</div>
              <div style={{flex: 1, background: '#e7e9ec', borderRadius: 10, height: '36px', position: 'relative', overflow: 'hidden'}}>
                <div style={{width: '100%', height: '100%', background: '#005187', borderRadius: 10, display: 'flex', alignItems: 'center', paddingLeft: '10px'}}>
                  <span style={{color: 'white', fontSize: '14px', fontWeight: 600}}>{leads.length}</span>
                </div>
              </div>
              <div style={{width: '45px', fontSize: '14px', color: '#8a929c', textAlign: 'right'}}>100%</div>
            </div>
          </div>
          
          {/* Barras por Estado */}
          <div style={{fontSize: '14px', fontWeight: 600, color: '#8a929c', marginBottom: '6px'}}>Estado del lead</div>
          <div style={{display: 'flex', flexDirection: 'column', gap: '5px'}}>
            {Object.keys(ESTADOS_LEAD).map((estado) => {
              const count = estadosCount[estado] || 0;
              const percentage = leads.length > 0 ? ((count / leads.length) * 100).toFixed(1) : 0;
              const maxValue = Math.max(...Object.values(estadosCount).filter(v => typeof v === 'number' && v !== leads.length), 1);
              const barWidth = maxValue > 0 ? (count / maxValue) * 100 : 0;
              
              return (
                <div key={estado} style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                  <div style={{width: '100px', fontSize: '14px', color: '#2b333d', display: 'flex', alignItems: 'center', gap: '4px'}}>
                    <div style={{width: '8px', height: '8px', borderRadius: '50%', background: colorEstado(estado), flexShrink: 0}}></div>
                    <span style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{estado}</span>
                  </div>
                  <div style={{flex: 1, background: '#e7e9ec', borderRadius: 10, height: '32px', position: 'relative', overflow: 'hidden'}}>
                    <div 
                      style={{
                        width: `${barWidth}%`, 
                        height: '100%', 
                        background: colorEstado(estado),
                        borderRadius: 10,
                        transition: 'width 0.5s ease',
                        display: 'flex',
                        alignItems: 'center',
                        paddingLeft: count > 0 ? '8px' : '0',
                        minWidth: count > 0 ? '28px' : '0'
                      }}
                    >
                      {count > 0 && (
                        <span style={{color: 'white', fontSize: '13px', fontWeight: 600, textShadow: '0 1px 2px rgba(0,0,0,0.3)'}}>
                          {count}
                        </span>
                      )}
                    </div>
                    {count === 0 && (
                      <span style={{position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '14px', color: '#9aa4af'}}>0</span>
                    )}
                  </div>
                  <div style={{width: '45px', fontSize: '14px', color: '#8a929c', textAlign: 'right'}}>{percentage}%</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Fila 4: Tarjetas de Ventas VN, VO y Tiempos */}
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px'}}>
        {/* Tarjeta Ventas */}
        <div className="fg-card" style={{...tarjeta, overflow: 'hidden'}}>
          <div style={{background: '#f7f8fa', padding: '8px', textAlign: 'center', borderBottom: `1px solid ${C.line}`}}>
            <span style={{color: C.muted, fontWeight: 800, fontSize: '10.5px', letterSpacing: '0.4px', textTransform: 'uppercase'}}>Ventas</span>
          </div>
          <div style={{padding: '10px', display: 'flex', gap: '10px', alignItems: 'center'}}>
            <div style={{flex: 1}}>
              <div style={{display: 'flex', alignItems: 'flex-end', gap: '8px', marginBottom: '4px'}}>
                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                  <span style={{color: '#e08c1e', fontSize: '14px', fontWeight: 700}}>{vendidos}</span>
                  <div style={{width: '24px', height: `${Math.max(18, vendidos * 2)}px`, background: 'linear-gradient(to top, #e08c1e, #e8ac52)', borderRadius: '3px 3px 0 0'}}></div>
                  <span style={{color: '#8a929c', fontSize: '7px', marginTop: '3px', textAlign: 'center'}}>Ventas<br/>periodo</span>
                </div>
                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                  <span style={{color: '#9aa4af', fontSize: '14px', fontWeight: 600}}>{vendidosAnteriores}</span>
                  <div style={{width: '24px', height: `${Math.max(14, vendidosAnteriores * 1.5)}px`, background: '#e7e9ec', borderRadius: '3px 3px 0 0'}}></div>
                  <span style={{color: '#8a929c', fontSize: '7px', marginTop: '3px', textAlign: 'center'}}>Ventas<br/>anteriores</span>
                </div>
              </div>
            </div>
            <div style={{textAlign: 'center', borderLeft: '1px solid #e7e9ec', paddingLeft: '10px'}}>
              <div style={{fontSize: '13px', color: '#9aa4af', marginBottom: '2px'}}>⏱️</div>
              <div style={{fontSize: '20px', fontWeight: 700, color: '#2b333d'}}>{leads.length}</div>
              <div style={{fontSize: '8px', color: '#8a929c'}}>Desde el inicio</div>
            </div>
          </div>
        </div>

        {/* Tarjeta Ventas VN */}
        <div className="fg-card" style={{...tarjeta, overflow: 'hidden'}}>
          <div style={{background: '#f7f8fa', padding: '8px', textAlign: 'center', borderBottom: `1px solid ${C.line}`}}>
            <span style={{color: C.muted, fontWeight: 800, fontSize: '10.5px', letterSpacing: '0.4px', textTransform: 'uppercase'}}>Ventas VN</span>
          </div>
          <div style={{padding: '10px'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
              <div style={{width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                {topMarcaVN && topMarcaVN !== '-' ? (
                  <img
                    src={`/marcas/${topMarcaVN.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')}.png`}
                    alt={topMarcaVN}
                    style={{maxWidth: '32px', maxHeight: '32px', objectFit: 'contain'}}
                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
                  />
                ) : null}
                <span style={{fontSize: '24px', display: topMarcaVN && topMarcaVN !== '-' ? 'none' : 'block'}}>🚗</span>
              </div>
              <div style={{flex: 1}}>
                <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}>
                  <span style={{color: '#8a929c', fontSize: '13px'}}>🏆 Marca más vendida</span>
                  <span style={{color: '#2b333d', fontSize: '13px', fontWeight: 600}}>{topMarcaVN}</span>
                </div>
                <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}>
                  <span style={{color: '#8a929c', fontSize: '13px'}}>🚙 Modelo más vendido</span>
                  <span style={{color: '#2b333d', fontSize: '13px', fontWeight: 600}}>{topModeloVN}</span>
                </div>
                <div style={{display: 'flex', justifyContent: 'space-between'}}>
                  <span style={{color: '#8a929c', fontSize: '13px'}}>📢 Canal con más ventas</span>
                  <span style={{color: '#2b333d', fontSize: '13px', fontWeight: 600}}>{topCanalVN}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tarjeta Ventas VO */}
        <div className="fg-card" style={{...tarjeta, overflow: 'hidden'}}>
          <div style={{background: '#f7f8fa', padding: '8px', textAlign: 'center', borderBottom: `1px solid ${C.line}`}}>
            <span style={{color: C.muted, fontWeight: 800, fontSize: '10.5px', letterSpacing: '0.4px', textTransform: 'uppercase'}}>Ventas VO</span>
          </div>
          <div style={{padding: '10px'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
              <div style={{width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                {topMarcaVO && topMarcaVO !== '-' ? (
                  <img
                    src={`/marcas/${topMarcaVO.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')}.png`}
                    alt={topMarcaVO}
                    style={{maxWidth: '32px', maxHeight: '32px', objectFit: 'contain'}}
                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
                  />
                ) : null}
                <span style={{fontSize: '24px', display: topMarcaVO && topMarcaVO !== '-' ? 'none' : 'block'}}>🚗</span>
              </div>
              <div style={{flex: 1}}>
                <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}>
                  <span style={{color: '#8a929c', fontSize: '13px'}}>🏆 Marca más vendida</span>
                  <span style={{color: '#2b333d', fontSize: '13px', fontWeight: 600}}>{topMarcaVO}</span>
                </div>
                <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '4px'}}>
                  <span style={{color: '#8a929c', fontSize: '13px'}}>🚙 Modelo más vendido</span>
                  <span style={{color: '#2b333d', fontSize: '13px', fontWeight: 600}}>{topModeloVO}</span>
                </div>
                <div style={{display: 'flex', justifyContent: 'space-between'}}>
                  <span style={{color: '#8a929c', fontSize: '13px'}}>📢 Canal con más ventas</span>
                  <span style={{color: '#2b333d', fontSize: '13px', fontWeight: 600}}>{topCanalVO}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tarjeta Tiempos (Media) */}
        <div className="fg-card" style={{...tarjeta, overflow: 'hidden'}}>
          <div style={{background: '#f7f8fa', padding: '8px', textAlign: 'center', borderBottom: `1px solid ${C.line}`}}>
            <span style={{color: C.muted, fontWeight: 800, fontSize: '10.5px', letterSpacing: '0.4px', textTransform: 'uppercase'}}>Tiempos (Media)</span>
          </div>
          <div style={{padding: '10px'}}>
            <div style={{marginBottom: '10px'}}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px'}}>
                <span style={{color: '#8a929c', fontSize: '13px'}}>Tiempo de respuesta</span>
                <span style={pildora(C.verde)}>{tiempoRespuestaStr}</span>
              </div>
              <div style={{display: 'flex', alignItems: 'center', gap: '2px'}}>
                {[1,2,3,4,5].map(i => <div key={i} style={{width: '6px', height: '6px', borderRadius: '50%', background: i <= dotsRespuesta ? '#2e9e5b' : '#e7e9ec'}}></div>)}
                <div style={{flex: 1, height: '2px', background: '#e7e9ec', margin: '0 4px', borderRadius: '1px'}}>
                  <div style={{width: `${pctRespuesta}%`, height: '100%', background: '#2e9e5b', borderRadius: '1px'}}></div>
                </div>
              </div>
            </div>
            <div>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px'}}>
                <span style={{color: '#8a929c', fontSize: '13px'}}>Tiempo de Cierre</span>
                <span style={pildora(C.naranja)}>{tiempoCierreStr}</span>
              </div>
              <div style={{display: 'flex', alignItems: 'center', gap: '2px'}}>
                {[1,2,3,4,5].map(i => <div key={i} style={{width: '6px', height: '6px', borderRadius: '50%', background: i <= dotsCierre ? '#e08c1e' : '#e7e9ec'}}></div>)}
                <div style={{flex: 1, height: '2px', background: '#e7e9ec', margin: '0 4px', borderRadius: '1px'}}>
                  <div style={{width: `${pctCierre}%`, height: '100%', background: '#e08c1e', borderRadius: '1px'}}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div style={{fontSize: '8px', color: '#9aa4af', marginTop: '8px', textAlign: 'center'}}>
        * Datos sincronizados desde Supabase
      </div>
    </div>
  );
};

// Listado de Detalle Page - TODAS LAS COLUMNAS del CSV (sin Lead ID) con scroll horizontal y más altura
const ListadoDetallePage = ({ leads, comerciales = [], onTraspasar, onEditar }) => {
  const [page, setPage] = useState(1);
  // Reset a página 1 cuando cambia el conjunto de leads (filtros aplicados),
  // si no, slice((page-1)*perPage, page*perPage) puede caer fuera del array
  // reducido y mostrar cero filas aunque el contador diga "X registros".
  useEffect(() => { setPage(1); }, [leads]);
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showHistorial, setShowHistorial] = useState(false);
  const [showComentarios, setShowComentarios] = useState(false);
  const [showDetalle, setShowDetalle] = useState(false);
  const [showTraspasar, setShowTraspasar] = useState(false);
  const [showEditar, setShowEditar] = useState(false);
  const perPage = 100;
  const totalPages = Math.ceil(leads.length / perPage);
  const currentLeads = leads.slice((page - 1) * perPage, page * perPage);
  
  // Tabla con la forma de la suite: cabecera gris clara en versalitas, celdas
  // de 12,5 px y una línea fina de separación entre filas.
  const thStyle = {...celdaTitulo, padding: '8px 10px', whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 10};
  const tdStyle = {padding: '7px 10px', borderBottom: SEPARADOR_FILA, color: C.page, fontSize: '12.5px', whiteSpace: 'nowrap'};
  const tdNumStyle = {background: '#f7f8fa', color: C.brand, textAlign: 'center', fontWeight: 800, padding: '7px 10px', fontSize: '12.5px', borderBottom: SEPARADOR_FILA, position: 'sticky', left: 0, zIndex: 5};

  const handleRowClick = (e, lead) => {
    e.preventDefault();
    setSelectedLead(lead);
    setContextMenu({ x: e.clientX, y: e.clientY });
  };

  return (
    <div>
      <ExportButtons leads={leads} />
      <div className="fg-card" style={{...tarjeta, overflow: 'hidden', padding: 0}}>
        <div style={{padding: '9px 16px', background: '#f7f8fa', color: C.page, fontSize: '12.5px', fontWeight: 700, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px', borderBottom: `1px solid ${C.line}`}}>
          {leads.length} registros - 
          {(() => {
            // Mostrar una ventana de hasta 10 páginas alrededor de la actual,
            // con saltos al primero y último para datasets grandes.
            const WINDOW = 10;
            const half = Math.floor(WINDOW / 2);
            let start = Math.max(1, page - half);
            let end = Math.min(totalPages, start + WINDOW - 1);
            if (end - start + 1 < WINDOW) start = Math.max(1, end - WINDOW + 1);
            const items = [];
            if (start > 1) {
              items.push(<span key="first" className="fg-nav" style={{padding: '3px 9px', cursor: 'pointer', borderRadius: 10}} onClick={() => setPage(1)}>1</span>);
              if (start > 2) items.push(<span key="dots1" style={{padding: '2px 4px'}}>…</span>);
            }
            for (let p = start; p <= end; p++) {
              items.push(
                <span key={p} className="fg-nav" style={{padding: '3px 9px', cursor: 'pointer', borderRadius: 10, fontWeight: page === p ? 800 : 600, color: page === p ? '#fff' : C.page, background: page === p ? GRAD : 'transparent', boxShadow: page === p ? '0 6px 16px rgba(0,81,135,.28)' : 'none'}} onClick={() => setPage(p)}>{p}</span>
              );
            }
            if (end < totalPages) {
              if (end < totalPages - 1) items.push(<span key="dots2" style={{padding: '2px 4px'}}>…</span>);
              items.push(<span key="last" className="fg-nav" style={{padding: '3px 9px', cursor: 'pointer', borderRadius: 10}} onClick={() => setPage(totalPages)}>{totalPages}</span>);
            }
            return items;
          })()}
        </div>
        <div style={{overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 280px)', position: 'relative'}}>
          <table style={{width: 'max-content', minWidth: '2200px', borderCollapse: 'collapse'}}>
            <thead>
              <tr>
                <th style={{...thStyle, position: 'sticky', left: 0, zIndex: 15}}>Num</th>
                <th style={thStyle}>Lead Tipo</th>
                <th style={thStyle}>Origen</th>
                <th style={thStyle}>Detalle Origen</th>
                <th style={thStyle}>Estado Cita</th>
                <th style={thStyle}>Motivo No Compra</th>
                <th style={thStyle}>Usuario Alta</th>
                <th style={thStyle}>Fecha Creación</th>
                <th style={thStyle}>Pri.Llamada</th>
                <th style={thStyle}>Últ.Llamada</th>
                <th style={thStyle}>Fecha Agendada</th>
                <th style={thStyle}>Fecha Modificación</th>
                <th style={thStyle}>Vendedor</th>
                <th style={thStyle}>Centro</th>
                <th style={thStyle}>Nombre</th>
                <th style={thStyle}>Apellidos</th>
                <th style={thStyle}>Razón Social</th>
                <th style={thStyle}>Tipo Cliente</th>
                <th style={thStyle}>CP</th>
                <th style={thStyle}>Provincia</th>
                <th style={thStyle}>Teléfono</th>
                <th style={thStyle}>Móvil</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Marca</th>
                <th style={thStyle}>Modelo</th>
                <th style={thStyle}>Motor</th>
                <th style={thStyle}>Kilómetros</th>
                <th style={thStyle}>Combustible</th>
                <th style={thStyle}>Matrícula</th>
                <th style={thStyle}>Tipo Venta</th>
                <th style={thStyle}>UTM Source</th>
                <th style={thStyle}>UTM Medium</th>
                <th style={thStyle}>UTM Campaign</th>
              </tr>
            </thead>
            <tbody>
              {currentLeads.map((lead, idx) => (
                <tr key={lead.id} style={{background: idx % 2 === 0 ? '#fff' : '#fbfcfd', cursor: 'pointer'}} onClick={(e) => handleRowClick(e, lead)}>
                  <td style={tdNumStyle}>{lead.num}</td>
                  <td style={tdStyle}><Badge color={colorTipoLead(lead.tipo)}>{lead.tipo}</Badge></td>
                  <td style={tdStyle}><span style={{color: C.brand, fontWeight: 700}}>{lead.origen}</span></td>
                  <td style={tdStyle}>{lead.detalleOrigen}</td>
                  <td style={tdStyle}><span style={pildora(colorEstado(lead.estado))}>{lead.estado}</span></td>
                  <td style={tdStyle}>{lead.motivoNoCompra && <span style={pildora(C.rojo)}>{lead.motivoNoCompra}</span>}</td>
                  <td style={tdStyle}>{lead.usuarioAlta}</td>
                  <td style={tdStyle}>{formatDateTime(lead.fechaCreacion)}</td>
                  <td style={tdStyle}>{formatDateTime(lead.primeraLlamada)}</td>
                  <td style={tdStyle}>{formatDateTime(lead.ultimaLlamada)}</td>
                  <td style={tdStyle}>{formatDateTime(lead.citaAgendada)}</td>
                  <td style={tdStyle}>{formatDateTime(lead.fechaModificacion)}</td>
                  <td style={tdStyle}>{lead.vendedor}</td>
                  <td style={tdStyle}>{lead.centro}</td>
                  <td style={tdStyle}>{lead.nombre}</td>
                  <td style={tdStyle}>{lead.apellidos}</td>
                  <td style={tdStyle}>{lead.razonSocial || '-'}</td>
                  <td style={tdStyle}>{lead.tipoCliente}</td>
                  <td style={tdStyle}>{lead.cp}</td>
                  <td style={tdStyle}>{lead.provincia}</td>
                  <td style={tdStyle}>{lead.telefono}</td>
                  <td style={tdStyle}>{lead.movil || '-'}</td>
                  <td style={tdStyle}>{lead.email}</td>
                  <td style={tdStyle}>{lead.marca}</td>
                  <td style={tdStyle}>{lead.modelo}</td>
                  <td style={tdStyle}>{lead.motor}</td>
                  <td style={tdStyle}>{lead.kilometros}</td>
                  <td style={tdStyle}>{lead.combustible}</td>
                  <td style={tdStyle}>{lead.matricula || '-'}</td>
                  <td style={tdStyle}>{lead.tipoVenta}</td>
                  <td style={tdStyle}>{lead.utmSource || '-'}</td>
                  <td style={tdStyle}>{lead.utmMedium || '-'}</td>
                  <td style={tdStyle}>{lead.utmCampaign || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{padding: '10px 16px', background: '#f7f8fa', borderTop: `1px solid ${C.line}`, fontSize: '12.5px', color: C.muted}}>
          * Haz clic en cualquier fila para ver opciones del lead
        </div>
      </div>
      
      {/* Menú Contextual */}
      {contextMenu && (
        <LeadContextMenu 
          x={contextMenu.x} 
          y={contextMenu.y} 
          onClose={() => setContextMenu(null)}
          onHistorial={() => { setShowHistorial(true); setContextMenu(null); }}
          onComentarios={() => { setShowComentarios(true); setContextMenu(null); }}
          onDetalle={() => { setShowDetalle(true); setContextMenu(null); }}
          onEditar={() => { setShowEditar(true); setContextMenu(null); }}
          onTraspasar={() => { setShowTraspasar(true); setContextMenu(null); }}
        />
      )}
      
      {/* Modales de Lead */}
      {showHistorial && <HistorialLeadModal lead={selectedLead} onClose={() => setShowHistorial(false)} />}
      {showComentarios && <ComentariosLeadModal lead={selectedLead} onClose={() => setShowComentarios(false)} />}
      {showDetalle && (
        <FichaLeadAdmin
          lead={selectedLead}
          onClose={() => setShowDetalle(false)}
          onEditar={() => { setShowDetalle(false); setShowEditar(true); }}
          onTraspasar={() => { setShowDetalle(false); setShowTraspasar(true); }}
        />
      )}
      {showEditar && <EditarLeadModal lead={selectedLead} comerciales={comerciales} onClose={() => setShowEditar(false)} onEditar={onEditar} />}
      {showTraspasar && <TraspasoLeadModal lead={selectedLead} comerciales={comerciales} onClose={() => setShowTraspasar(false)} onTraspasar={onTraspasar} />}
    </div>
  );
};

// Modal Base
const Modal = ({ onClose, title, children, width = '800px' }) => (
  <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: VELO_MODAL, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px'}} onClick={onClose}>
    <div style={{background: 'white', borderRadius: 14, width: '100%', maxWidth: width, maxHeight: '90vh', overflow: 'hidden', boxShadow: SOMBRA_MODAL}} onClick={e => e.stopPropagation()}>
      <div style={{background: GRAD, padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
        <span style={{color: 'white', fontWeight: 800, fontSize: '14px'}}>{title}</span>
        <button onClick={onClose} style={{background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 999, width: 28, height: 28, color: 'white', cursor: 'pointer', fontSize: '15px'}}>✕</button>
      </div>
      <div style={{padding: '20px', overflowY: 'auto', maxHeight: 'calc(90vh - 60px)', background: 'white'}}>{children}</div>
    </div>
  </div>
);

// Lead Context Menu
const LeadContextMenu = ({ x, y, onClose, onHistorial, onComentarios, onDetalle, onTraspasar, onEditar }) => {
  const menuRef = React.useRef(null);
  React.useEffect(() => {
    const handleClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);
  // El realce al pasar el ratón lo pone `fg-nav`; antes iba escrito a mano
  // con onMouseEnter/Leave en cada opción.
  const itemStyle = {padding: '10px 14px', cursor: 'pointer', fontSize: '12.5px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '10px', color: C.page, borderBottom: SEPARADOR_FILA};
  return (
    <div ref={menuRef} style={{position: 'fixed', left: Math.min(x, window.innerWidth - 220), top: Math.min(y, window.innerHeight - 240), background: 'white', borderRadius: 12, boxShadow: '0 14px 30px rgba(15,42,70,.16)', zIndex: 1001, minWidth: '200px', overflow: 'hidden', border: `1px solid ${C.line}`}}>
      <div onClick={onHistorial} className="fg-nav" style={itemStyle}>📋 Historial del Lead</div>
      <div onClick={onComentarios} className="fg-nav" style={itemStyle}>💬 Comentarios del Lead</div>
      <div onClick={onDetalle} className="fg-nav" style={itemStyle}>📄 Detalle del Lead</div>
      <div onClick={onEditar} className="fg-nav" style={{...itemStyle, color: C.verde}}>✏️ Editar Lead</div>
      <div onClick={onTraspasar} className="fg-nav" style={{...itemStyle, borderBottom: 'none', color: C.brand}}>🔄 Traspasar Lead</div>
    </div>
  );
};

// Historial Lead Modal
const HistorialLeadModal = ({ lead, onClose }) => {
  if (!lead) return null;
  const clienteNombre = lead.razonSocial || `${lead.nombre} ${lead.apellidos}`.trim() || 'Sin nombre';
  return (
    <Modal onClose={onClose} title="Historial del Lead" width="750px">
      <h3 style={{margin: '0 0 15px 0', fontSize: '15px', fontWeight: 800, color: C.ink, borderBottom: `1px solid ${C.line}`, paddingBottom: '8px'}}>HISTORIAL DEL LEAD</h3>
      <div style={{background: '#fbfcfd', padding: '15px', borderRadius: 10, marginBottom: '20px', border: '1px solid #e7e9ec'}}>
        <p style={{margin: '0 0 8px 0', fontSize: '13px', color: '#2b333d'}}><strong>Cliente:</strong> {clienteNombre}</p>
        <p style={{margin: '0 0 8px 0', fontSize: '13px', color: '#2b333d'}}>Lead creado por <strong>{lead.usuarioAlta || 'Sistema'}</strong> el {formatDateTime(lead.fechaCreacion)}.</p>
        <p style={{margin: '0 0 8px 0', fontSize: '13px', color: '#2b333d'}}>Vendedor asignad@: <strong>{lead.vendedor || 'Sin asignar'}</strong></p>
        {lead.citaAgendada && <p style={{margin: '0', fontSize: '13px', color: '#2b333d'}}>Cita creada por <strong>{lead.usuarioAlta || 'Sistema'}</strong> el {formatDateTime(lead.citaAgendada)}.</p>}
      </div>
      <div style={{borderTop: '2px solid #005187', paddingTop: '15px'}}>
        {lead.fechaCreacion && (
          <div style={{padding: '12px 15px', marginBottom: '10px', background: '#f0f5ff', borderLeft: '4px solid #4169E1', borderRadius: '0 6px 6px 0'}}>
            <p style={{margin: '0', fontSize: '14px', color: '#2b333d'}}>📅 Lead creado - {formatDateTime(lead.fechaCreacion)}</p>
          </div>
        )}
        {lead.primeraLlamada && (
          <div style={{padding: '12px 15px', marginBottom: '10px', background: '#f0fff0', borderLeft: '4px solid #228B22', borderRadius: '0 6px 6px 0'}}>
            <p style={{margin: '0', fontSize: '14px', color: '#2b333d'}}>📞 Primera llamada - {formatDateTime(lead.primeraLlamada)}</p>
          </div>
        )}
        {lead.citaAgendada && (
          <div style={{padding: '12px 15px', marginBottom: '10px', background: '#fff8e0', borderLeft: '4px solid #FFA500', borderRadius: '0 6px 6px 0'}}>
            <p style={{margin: '0', fontSize: '14px', color: '#2b333d'}}>📆 Cita agendada - {formatDateTime(lead.citaAgendada)}</p>
          </div>
        )}
        {lead.estado && lead.estado !== 'Pendiente' && (
          <div style={{padding: '12px 15px', marginBottom: '10px', background: '#fff0f0', borderLeft: '4px solid #DC143C', borderRadius: '0 6px 6px 0'}}>
            <p style={{margin: '0 0 8px 0', fontSize: '14px', color: '#2b333d'}}>🔄 Cambio Estado Cita el {formatDateTime(lead.fechaModificacion)} por {lead.usuarioAlta || 'Sistema'}</p>
            <span style={pildora(colorEstado(lead.estado))}>{lead.estado}</span>
          </div>
        )}
        {lead.motivoNoCompra && (
          <div style={{padding: '12px 15px', background: '#fcf1f0', borderLeft: '4px solid #f44336', borderRadius: '0 6px 6px 0'}}>
            <p style={{margin: '0', fontSize: '14px', color: '#2b333d'}}>❌ Motivo no compra: <strong>{lead.motivoNoCompra}</strong></p>
          </div>
        )}
      </div>
    </Modal>
  );
};

// Comentarios Lead Modal
const ComentariosLeadModal = ({ lead, onClose }) => {
  if (!lead) return null;
  return (
    <Modal onClose={onClose} title="Comentarios del Lead" width="750px">
      <h3 style={{margin: '0 0 20px 0', fontSize: '15px', fontWeight: 800, color: C.ink, borderBottom: `1px solid ${C.line}`, paddingBottom: '8px'}}>COMENTARIOS DEL CLIENTE</h3>
      
      <div style={{padding: '15px', marginBottom: '15px', background: '#fbfcfd', borderRadius: 10, borderLeft: '4px solid #8a929c', border: '1px solid #e7e9ec'}}>
        <p style={{margin: '0 0 8px 0', fontSize: '14px', fontWeight: 600, color: '#2b333d'}}>{formatDateTime(lead.fechaCreacion)} por {lead.usuarioAlta || 'Sistema'}</p>
        <p style={{margin: '0', fontSize: '13px', color: '#444'}}>- Campaña: {lead.campana || 'Sin campaña'}. - Lead: {lead.leadId}.</p>
        {lead.detalleOrigen && <p style={{margin: '8px 0 0 0', fontSize: '13px', color: '#444'}}>{lead.detalleOrigen}</p>}
      </div>

      {lead.marca && lead.modelo && (
        <div style={{padding: '15px', marginBottom: '15px', background: '#e3f2fd', borderRadius: 10, borderLeft: '4px solid #1976d2', border: '1px solid #bbdefb'}}>
          <p style={{margin: '0 0 8px 0', fontSize: '14px', fontWeight: 600, color: '#2b333d'}}>{formatDateTime(lead.fechaModificacion || lead.fechaCreacion)} por {lead.usuarioAlta || 'Sistema'}</p>
          <p style={{margin: '0', fontSize: '13px', color: '#444'}}>- Cita: {lead.leadId}. - Vehículo: {lead.marca} {lead.modelo}.</p>
        </div>
      )}

      {lead.origen && (
        <div style={{padding: '15px', marginBottom: '15px', background: '#fff3e0', borderRadius: 10, borderLeft: '4px solid #ff9800', border: '1px solid #ffe0b2'}}>
          <p style={{margin: '0', fontSize: '13px', color: '#444'}}><strong>Origen:</strong> {lead.origen}</p>
          {lead.tipoCliente && <p style={{margin: '8px 0 0 0', fontSize: '13px', color: '#444'}}><strong>Tipo cliente:</strong> {lead.tipoCliente}</p>}
        </div>
      )}

      {lead.vendedor && (
        <div style={{padding: '15px', background: '#e8f5e9', borderRadius: 10, borderLeft: '4px solid #4caf50', border: '1px solid #c8e6c9'}}>
          <p style={{margin: '0', fontSize: '13px', color: '#444'}}><strong>Vendedor asignado:</strong> {lead.vendedor}</p>
          {lead.centro && <p style={{margin: '8px 0 0 0', fontSize: '13px', color: '#444'}}><strong>Centro:</strong> {lead.centro}</p>}
        </div>
      )}
    </Modal>
  );
};

// Detalle Lead Modal
// ==================== FICHA DE LEAD (misma estructura que Recepción/Adriana) ====================
// Sustituye a DetalleLeadModal en el Listado Detalle. Solo lectura + adjuntos;
// Editar y Traspasar reutilizan los modales existentes del listado.
const FICHA_ADMIN_CHIP = {
  Pendiente: { color: '#a8660f', bg: '#fbf1de' },
  Contactado: { color: '#005187', bg: '#e8f2f8' },
  Atendido:  { color: '#003258', bg: '#dceaf4' },
  Citado:    { color: '#6a4cc0', bg: '#efeafa' },
  Ofertado:  { color: '#0f766e', bg: '#ccfbf1' },
  Cancelado: { color: '#a32b25', bg: '#f9e4e3' },
  Vendido:   { color: '#1f7a44', bg: '#e6f4ec' },
};
const fichaAdminFmt = (f) => f ? new Date(f).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';
const fichaAdminEsImg = (a) => (a.mime || '').startsWith('image/');

// ── Dimo Phone: llamadas GSM del lead registradas por la app del comercial ──
const DIMO_TIPO_ADMIN = {
  entrante:  { icon: '↙', label: 'Entrante',  color: '#1f7a44', bg: '#e6f4ec' },
  saliente:  { icon: '↗', label: 'Saliente',  color: '#003258', bg: '#dceaf4' },
  perdida:   { icon: '✕', label: 'Perdida',   color: '#a32b25', bg: '#f9e4e3' },
  rechazada: { icon: '⊘', label: 'Rechazada', color: '#a8660f', bg: '#fbf1de' },
  bloqueada: { icon: '⛔', label: 'Bloqueada', color: '#8a929c', bg: '#f4f5f7' },
  buzon:     { icon: '📩', label: 'Buzón',     color: '#6a4cc0', bg: '#efeafa' },
};
const dimoAdminDur = (s) => {
  const seg = Number(s) || 0;
  if (seg <= 0) return '—';
  const m = Math.floor(seg / 60), r = seg % 60;
  return m > 0 ? `${m} min ${String(r).padStart(2, '0')} s` : `${r} s`;
};

const DimoLlamadasCardAdmin = ({ leadId }) => {
  const [llamadas, setLlamadas] = useState([]);
  const [nombres, setNombres] = useState({});

  useEffect(() => {
    if (!leadId) { setLlamadas([]); return undefined; }
    let vivo = true;
    const headers = { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` };
    fetch(`${SUPABASE_URL}/rest/v1/dimo_llamadas?lead_id=eq.${leadId}&order=fecha.desc&select=*`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(d => {
        if (!vivo) return;
        const lista = Array.isArray(d) ? d : [];
        setLlamadas(lista);
        const emails = [...new Set(lista.map(l => (l.user_email || '').toLowerCase()).filter(Boolean))];
        if (!emails.length) return;
        fetch(`${SUPABASE_URL}/rest/v1/comerciales?email=in.(${emails.map(encodeURIComponent).join(',')})&select=email,nombre,apellidos`, { headers })
          .then(r => r.ok ? r.json() : [])
          .then(cs => {
            if (vivo && Array.isArray(cs)) {
              setNombres(Object.fromEntries(cs.map(c => [String(c.email || '').toLowerCase(), `${c.nombre || ''} ${c.apellidos || ''}`.trim()])));
            }
          }).catch(() => {});
      }).catch(() => {});
    return () => { vivo = false; };
  }, [leadId]);

  if (!llamadas.length) return null;
  const tarjeta = { background: '#fff', border: '1px solid #e7e9ec', borderRadius: 12, padding: '14px 16px' };
  const tituloTarjeta = { fontSize: 11, fontWeight: 700, color: '#005187', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 };
  return (
    <div style={tarjeta}>
      <div style={tituloTarjeta}>📱 Llamadas Dimo Phone ({llamadas.length})</div>
      <div style={{ display: 'grid', gap: 6, maxHeight: 260, overflowY: 'auto' }}>
        {llamadas.map(ll => {
          const t = DIMO_TIPO_ADMIN[ll.tipo] || { icon: '📞', label: ll.tipo || 'Llamada', color: '#8a929c', bg: '#f4f5f7' };
          const quien = nombres[(ll.user_email || '').toLowerCase()] || ll.user_email || '—';
          return (
            <div key={ll.id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#f4f5f7', borderRadius: 10, padding: '8px 12px', borderLeft: `4px solid ${t.color}` }}>
              <span style={{ width: 26, height: 26, borderRadius: '50%', background: t.bg, color: t.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{t.icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: '#1f2a37' }}>
                  <b>{t.label}</b>
                  <span style={{ color: '#8a929c' }}> · {dimoAdminDur(ll.duracion_seg)} · {ll.numero || ''}</span>
                </div>
                <div style={{ fontSize: 11, color: '#8a929c', marginTop: 1 }}>{fichaAdminFmt(ll.fecha)} — {quien}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const FichaLeadAdmin = ({ lead, onClose, onEditar, onTraspasar }) => {
  const [adjuntos, setAdjuntos] = useState([]);
  const [visor, setVisor] = useState(null);
  const [subiendo, setSubiendo] = useState(false);
  const fileRef = useRef(null);
  // Cita: cambiar fecha/hora y marcar si el cliente ha venido
  const [asisCita, setAsisCita] = useState(lead?.asistenciaCita || null);
  const [citaLocal, setCitaLocal] = useState(lead?.citaAgendada || null);
  const [editCitaOpen, setEditCitaOpen] = useState(false);
  const [nuevaCita, setNuevaCita] = useState('');
  const [guardandoCita, setGuardandoCita] = useState(false);

  const patchCita = async (campos) => {
    const r = await fetch(`${SUPABASE_URL}/rest/v1/leads?id=eq.${lead.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`, Prefer: 'return=minimal' },
      body: JSON.stringify(campos),
    });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
  };

  const abrirEditorCita = () => {
    const d = citaLocal ? new Date(citaLocal) : new Date();
    const p = (n) => String(n).padStart(2, '0');
    setNuevaCita(`${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`);
    setEditCitaOpen(true);
  };

  const guardarNuevaCita = async () => {
    if (!nuevaCita) return;
    setGuardandoCita(true);
    try {
      const iso = new Date(nuevaCita).toISOString();
      await patchCita({ fecha_agendada: iso });
      setCitaLocal(new Date(iso));
      setEditCitaOpen(false);
    } catch (e) { alert('No se pudo modificar la cita: ' + e.message); }
    finally { setGuardandoCita(false); }
  };

  const marcarAsistencia = async (valor) => {
    const v = asisCita === valor ? null : valor;
    try { await patchCita({ asistencia_cita: v }); setAsisCita(v); }
    catch (e) { alert('No se pudo guardar la asistencia: ' + e.message); }
  };

  useEffect(() => {
    if (!lead?.id) return;
    fetch(`${SUPABASE_URL}/rest/v1/lead_archivos?lead_id=eq.${lead.id}&order=created_at.desc&select=*`, {
      headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
    }).then(r => r.ok ? r.json() : []).then(d => setAdjuntos(Array.isArray(d) ? d : [])).catch(() => {});
  }, [lead?.id]);

  const subirAdjunto = async (files) => {
    if (!files?.length) return;
    setSubiendo(true);
    try {
      for (const f of files) {
        const limpio = f.name.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-zA-Z0-9._-]/g, '_');
        const path = `lead-${lead.id}/${Date.now()}-${limpio}`;
        const up = await fetch(`${SUPABASE_URL}/storage/v1/object/lead-archivos/${path}`, {
          method: 'POST',
          headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': f.type || 'application/octet-stream', 'x-upsert': 'true' },
          body: f,
        });
        if (!up.ok) throw new Error(`subida ${up.status}`);
        const url = `${SUPABASE_URL}/storage/v1/object/public/lead-archivos/${path}`;
        const ins = await fetch(`${SUPABASE_URL}/rest/v1/lead_archivos`, {
          method: 'POST',
          headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json', Prefer: 'return=representation' },
          body: JSON.stringify({ lead_id: lead.id, nombre: f.name, path, url, mime: f.type || null, tamano: f.size || null, subido_por: 'Admin' }),
        });
        const filas = ins.ok ? await ins.json() : [];
        if (filas[0]) setAdjuntos(prev => [filas[0], ...prev]);
      }
    } catch { alert('No se pudo subir el archivo.'); }
    finally { setSubiendo(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const timeline = useMemo(() => {
    const items = [];
    (Array.isArray(lead?.historial) ? lead.historial : []).forEach(h => {
      const acc = String(h.accion || '');
      const tipo = acc.includes('📞') ? 'llamada' : acc.includes('Cita') || acc.includes('cita') ? 'cita' : acc.includes('Traspas') || acc.includes('Asignado') ? 'traspaso' : 'historial';
      items.push({ fecha: h.fecha, tipo, texto: acc, quien: h.usuario });
    });
    (Array.isArray(lead?.comentarios) ? lead.comentarios : []).forEach(c => items.push({ fecha: c.fecha, tipo: 'comentario', texto: c.texto, quien: c.usuario }));
    adjuntos.forEach(a => items.push({ fecha: a.created_at, tipo: 'adjunto', texto: `📎 ${a.nombre}`, quien: a.subido_por, adjunto: a }));
    return items.filter(i => i.fecha).sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
  }, [lead, adjuntos]);

  if (!lead) return null;
  const ICONO = { llamada: '📞', cita: '📅', traspaso: '⇄', historial: '📜', comentario: '🗒️', adjunto: '📎' };
  const COLOR = { llamada: '#7cb518', cita: '#d63384', traspaso: '#005187', historial: '#8a929c', comentario: '#1a9cd4', adjunto: '#6a4cc0' };
  const telefonoLead = lead.movil || lead.telefono || '';
  const nombreCompleto = lead.razonSocial || `${lead.nombre || ''} ${lead.apellidos || ''}`.trim() || 'Sin nombre';
  const est = lead.estado || 'Pendiente';
  const chip = FICHA_ADMIN_CHIP[est] || { color: '#8a929c', bg: '#f4f5f7' };
  const tarjeta = { background: '#fff', border: '1px solid #e7e9ec', borderRadius: 12, padding: '14px 16px' };
  const tituloTarjeta = { fontSize: 11, fontWeight: 700, color: '#005187', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 };
  const btnSec = { padding: '7px 14px', borderRadius: 10, border: '1px solid #e7e9ec', background: '#fff', fontSize: 12.5, fontWeight: 600, color: '#1f2a37', cursor: 'pointer' };

  const handleWhatsAppAdmin = () => {
    if (!telefonoLead) return;
    const phone = telefonoLead.replace(/\D/g, '');
    window.open(`https://wa.me/${phone.startsWith('34') ? phone : `34${phone}`}`, '_blank');
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#f4f5f7', zIndex: 1400, overflowY: 'auto' }}>
      <div style={{ position: 'sticky', top: 0, zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '10px 18px', background: '#fff', borderBottom: '1px solid #e7e9ec' }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#1f2a37', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {nombreCompleto}
          <span style={{ fontWeight: 500, color: '#1a9cd4' }}> — {[lead.marca, lead.modelo].filter(Boolean).join(' ')}</span>
          <span style={{ marginLeft: 10, padding: '2px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600, color: chip.color, background: chip.bg }}>{est}</span>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          {onEditar && <button onClick={onEditar} style={btnSec}>✏️ Editar</button>}
          {onTraspasar && <button onClick={onTraspasar} style={btnSec}>⇄ Traspasar</button>}
          {telefonoLead && <button onClick={handleWhatsAppAdmin} style={{ ...btnSec, color: '#2e9e5b', border: '1px solid #2e9e5b' }}>💬 WhatsApp</button>}
          <button onClick={onClose} style={btnSec}>✕ Cerrar</button>
        </div>
      </div>

      <div style={{ padding: '14px 18px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(340px, 5fr) minmax(380px, 6fr)', gap: 14, alignItems: 'start' }}>
          {/* IZQUIERDA */}
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={tarjeta}>
              <div style={tituloTarjeta}>🚗 Vehículo de interés</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#1f2a37', marginBottom: 4 }}>
                {[lead.marca, lead.modelo].filter(Boolean).join(' ') || 'Sin vehículo'}
              </div>
              <div style={{ fontSize: 13, color: '#2b333d' }}>
                {[
                  lead.tipoVenta ? (lead.tipoVenta === 'VN' ? 'VN (nuevo)' : lead.tipoVenta === 'VO' ? 'VO (ocasión)' : lead.tipoVenta) : null,
                  lead.combustible, lead.motor,
                  lead.kilometros ? `${Number(lead.kilometros).toLocaleString('es-ES')} km` : null,
                  lead.matricula,
                ].filter(Boolean).join(' | ') || '—'}
              </div>
              {lead.enlaceOrigen && (
                <a href={lead.enlaceOrigen} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: 8, fontSize: 12.5, color: '#005187', fontWeight: 600 }}>Ver anuncio original ↗</a>
              )}
            </div>

            <div style={tarjeta}>
              <div style={tituloTarjeta}>▾ Origen (source)</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                <div><div style={{ fontSize: 10.5, color: '#8a929c', fontWeight: 600 }}>ORIGEN</div><div style={{ fontSize: 13, fontWeight: 600, color: '#1f2a37' }}>{lead.origen || '—'}</div></div>
                <div><div style={{ fontSize: 10.5, color: '#8a929c', fontWeight: 600 }}>DETALLE</div><div style={{ fontSize: 13, fontWeight: 600, color: '#1f2a37' }}>{lead.detalleOrigen || '—'}</div></div>
                <div><div style={{ fontSize: 10.5, color: '#8a929c', fontWeight: 600 }}>CENTRO</div><div style={{ fontSize: 13, fontWeight: 600, color: '#1f2a37' }}>{lead.centro || '—'}</div></div>
                <div><div style={{ fontSize: 10.5, color: '#8a929c', fontWeight: 600 }}>VENDEDOR</div><div style={{ fontSize: 13, fontWeight: 600, color: '#005187' }}>{lead.vendedor || 'Sin asignar'}</div></div>
                <div><div style={{ fontSize: 10.5, color: '#8a929c', fontWeight: 600 }}>ENTRADA</div><div style={{ fontSize: 13, color: '#1f2a37' }}>{fichaAdminFmt(lead.fechaCreacion)}</div></div>
                <div><div style={{ fontSize: 10.5, color: '#8a929c', fontWeight: 600 }}>1ª LLAMADA</div><div style={{ fontSize: 13, color: '#1f2a37' }}>{lead.primeraLlamada ? fichaAdminFmt(lead.primeraLlamada) : 'Pendiente'}</div></div>
                <div>
                  <div style={{ fontSize: 10.5, color: '#8a929c', fontWeight: 600 }}>CITA</div>
                  <div style={{ fontSize: 13, color: '#1f2a37', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span>{citaLocal ? fichaAdminFmt(citaLocal) : '—'}</span>
                    <button onClick={() => (editCitaOpen ? setEditCitaOpen(false) : abrirEditorCita())}
                      title="Modificar la cita o marcar si el cliente ha venido"
                      style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, padding: 0, opacity: 0.75 }}>✏️</button>
                    {asisCita && <span style={{ background: asisCita === 'Ha venido' ? '#e6f4ec' : '#f9e4e3', color: asisCita === 'Ha venido' ? '#1f7a44' : '#a32b25', borderRadius: 999, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}>{asisCita === 'Ha venido' ? '✅ Ha venido' : '❌ No ha venido'}</span>}
                  </div>
                  {editCitaOpen && (
                    <div style={{ marginTop: 6, background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10, padding: 8 }}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                        <input type="datetime-local" value={nuevaCita} onChange={e => setNuevaCita(e.target.value)}
                          style={{ padding: '5px 8px', borderRadius: 10, border: '1px solid #bae6fd', fontSize: 12 }} />
                        <button onClick={guardarNuevaCita} disabled={!nuevaCita || guardandoCita}
                          style={{ padding: '5px 10px', borderRadius: 10, border: 'none', background: '#005187', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: (!nuevaCita || guardandoCita) ? 0.5 : 1 }}>{guardandoCita ? 'Guardando…' : 'Guardar fecha'}</button>
                      </div>
                      <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                        <button onClick={() => marcarAsistencia('Ha venido')}
                          style={{ padding: '5px 10px', borderRadius: 10, border: '1px solid #a6d7b8', background: asisCita === 'Ha venido' ? '#e6f4ec' : '#fff', color: '#1f7a44', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>✅ Ha venido</button>
                        <button onClick={() => marcarAsistencia('No ha venido')}
                          style={{ padding: '5px 10px', borderRadius: 10, border: '1px solid #e69d99', background: asisCita === 'No ha venido' ? '#f9e4e3' : '#fff', color: '#a32b25', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>❌ No ha venido</button>
                      </div>
                    </div>
                  )}
                </div>
                <div><div style={{ fontSize: 10.5, color: '#8a929c', fontWeight: 600 }}>MODIFICADO</div><div style={{ fontSize: 13, color: '#1f2a37' }}>{fichaAdminFmt(lead.fechaModificacion)}</div></div>
                {lead.motivoNoCompra && <div><div style={{ fontSize: 10.5, color: '#8a929c', fontWeight: 600 }}>MOTIVO NO COMPRA</div><div style={{ fontSize: 13, color: '#a32b25', fontWeight: 600 }}>{lead.motivoNoCompra}</div></div>}
              </div>
            </div>

            <div style={tarjeta}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ ...tituloTarjeta, marginBottom: 0 }}>📎 Adjuntos ({adjuntos.length})</div>
                <button onClick={() => fileRef.current?.click()} disabled={subiendo}
                  style={{ padding: '5px 12px', borderRadius: 10, border: '1px dashed #1a9cd4', background: '#fff', color: '#005187', fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: subiendo ? 0.6 : 1 }}>
                  {subiendo ? 'Subiendo…' : '+ Subir'}
                </button>
                <input ref={fileRef} type="file" multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt" onChange={e => subirAdjunto([...e.target.files])} style={{ display: 'none' }} />
              </div>
              {adjuntos.length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                  {adjuntos.map(a => (
                    <div key={a.id} style={{ width: 70 }}>
                      <div onClick={() => fichaAdminEsImg(a) ? setVisor(a) : window.open(a.url, '_blank')} title={a.nombre}
                        style={{ width: 70, height: 56, borderRadius: 10, border: '1px solid #e7e9ec', background: '#f4f5f7', cursor: 'pointer', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {fichaAdminEsImg(a)
                          ? <img src={a.url} alt={a.nombre} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          : <span style={{ fontSize: 20 }}>{(a.mime || '').includes('pdf') ? '📄' : '📁'}</span>}
                      </div>
                      <div style={{ fontSize: 9.5, color: '#8a929c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>{a.nombre}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {lead.observaciones && (
              <div style={tarjeta}>
                <div style={tituloTarjeta}>Observaciones</div>
                <div style={{ fontSize: 13, color: '#2b333d', whiteSpace: 'pre-wrap' }}>{lead.observaciones}</div>
              </div>
            )}
          </div>

          {/* DERECHA */}
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={tarjeta}>
              <div style={tituloTarjeta}>👤 Cliente</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                <div>
                  <div style={{ fontSize: 10.5, color: '#8a929c', fontWeight: 600 }}>NOMBRE</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#1f2a37' }}>{nombreCompleto}</div>
                  <div style={{ fontSize: 11.5, color: '#8a929c', marginTop: 2 }}>{lead.tipoCliente || 'Particular'}{lead.cp || lead.provincia ? ` · ${[lead.cp, lead.provincia].filter(Boolean).join(' ')}` : ''}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10.5, color: '#8a929c', fontWeight: 600 }}>TELÉFONOS</div>
                  {telefonoLead ? <a href={`tel:${telefonoLead}`} style={{ fontSize: 14, fontWeight: 600, color: '#005187' }}>{telefonoLead}</a> : <div style={{ fontSize: 13, color: '#8a929c' }}>—</div>}
                  {lead.telefono && lead.movil && lead.telefono !== lead.movil && <div><a href={`tel:${lead.telefono}`} style={{ fontSize: 12.5, color: '#005187' }}>{lead.telefono}</a></div>}
                </div>
                <div>
                  <div style={{ fontSize: 10.5, color: '#8a929c', fontWeight: 600 }}>EMAIL</div>
                  {lead.email ? <a href={`mailto:${lead.email}`} style={{ fontSize: 13, fontWeight: 600, color: '#005187', wordBreak: 'break-all' }}>{lead.email}</a> : <div style={{ fontSize: 13, color: '#8a929c' }}>—</div>}
                </div>
              </div>
            </div>

            <DimoLlamadasCardAdmin leadId={lead.id} />

            <div style={tarjeta}>
              <div style={tituloTarjeta}>🕓 Actividad con el cliente ({timeline.length})</div>
              {timeline.length === 0 && <div style={{ fontSize: 13, color: '#8a929c' }}>Sin actividad todavía.</div>}
              <div style={{ display: 'grid', gap: 8, maxHeight: 520, overflowY: 'auto' }}>
                {timeline.map((it, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, background: '#f4f5f7', borderRadius: 10, padding: '9px 12px', borderLeft: `4px solid ${COLOR[it.tipo] || '#8a929c'}`, alignItems: 'flex-start' }}>
                    <span style={{ fontSize: 15 }}>{ICONO[it.tipo] || '•'}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: '#1f2a37', wordBreak: 'break-word' }}>
                        {it.adjunto
                          ? <span onClick={() => fichaAdminEsImg(it.adjunto) ? setVisor(it.adjunto) : window.open(it.adjunto.url, '_blank')} style={{ cursor: 'pointer', color: '#005187', fontWeight: 600 }}>{it.texto}</span>
                          : it.texto}
                      </div>
                      <div style={{ fontSize: 11, color: '#8a929c', marginTop: 2 }}>{fichaAdminFmt(it.fecha)}{it.quien ? ` — ${it.quien}` : ''}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {visor && (
        <div onClick={() => setVisor(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 30, cursor: 'zoom-out' }}>
          <div style={{ textAlign: 'center' }}>
            <img src={visor.url} alt={visor.nombre} style={{ maxWidth: '90vw', maxHeight: '84vh', borderRadius: 10 }} />
            <div style={{ color: '#fff', fontSize: 13, marginTop: 8 }}>{visor.nombre} · <a href={visor.url} target="_blank" rel="noreferrer" style={{ color: '#7cc4e8' }}>Abrir original ↗</a></div>
          </div>
        </div>
      )}
    </div>
  );
};

const DetalleLeadModal = ({ lead, onClose }) => {
  if (!lead) return null;
  const Field = ({ label, value }) => (
    <div style={{display: 'flex', padding: '6px 0', borderBottom: SEPARADOR_FILA}}>
      <span style={{width: '140px', fontSize: '14px', color: '#8a929c', fontWeight: 500}}>{label}:</span>
      <span style={{flex: 1, fontSize: '14px', color: '#2b333d'}}>{value || '-'}</span>
    </div>
  );
  const Section = ({ title, children }) => (
    <div style={{marginBottom: '20px'}}>
      <h4 style={{margin: '0 0 10px 0', fontSize: '13px', color: '#005187', borderBottom: `1px solid ${C.line}`, paddingBottom: '5px'}}>{title}</h4>
      {children}
    </div>
  );
  return (
    <Modal onClose={onClose} title="Detalle del Lead" width="900px">
      <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px'}}>
        <div>
          <Section title="Información del Lead">
            <Field label="Lead ID" value={lead.leadId} />
            <Field label="Tipo" value={lead.tipo} />
            <Field label="Origen" value={lead.origen} />
            <Field label="Detalle Origen" value={lead.detalleOrigen} />
            <Field label="Campaña" value={lead.campana} />
            {lead.enlaceOrigen && (
              <div style={{display: 'flex', padding: '6px 0', borderBottom: SEPARADOR_FILA}}>
                <span style={{width: '140px', fontSize: '14px', color: '#8a929c', fontWeight: 500}}>Enlace origen:</span>
                <span style={{flex: 1, fontSize: '14px', wordBreak: 'break-all'}}>
                  <a href={lead.enlaceOrigen} target="_blank" rel="noopener noreferrer" style={{color: '#005187', textDecoration: 'underline'}}>🔗 Abrir lead en plataforma</a>
                </span>
              </div>
            )}
            <Field label="Estado" value={lead.estado} />
            <Field label="Motivo No Compra" value={lead.motivoNoCompra} />
            <Field label="Usuario Alta" value={lead.usuarioAlta} />
          </Section>
          <Section title="Datos del Cliente">
            <Field label="Nombre" value={lead.nombre} />
            <Field label="Apellidos" value={lead.apellidos} />
            <Field label="Razón Social" value={lead.razonSocial} />
            <Field label="Tipo Cliente" value={lead.tipoCliente} />
            <Field label="Teléfono" value={lead.telefono} />
            <Field label="Móvil" value={lead.movil} />
            <Field label="Email" value={lead.email} />
            <Field label="CP" value={lead.cp} />
            <Field label="Provincia" value={lead.provincia} />
          </Section>
        </div>
        <div>
          <Section title="Fechas">
            <Field label="Fecha Creación" value={formatDateTime(lead.fechaCreacion)} />
            <Field label="Pri. Llamada" value={formatDateTime(lead.primeraLlamada)} />
            <Field label="Últ. Llamada" value={formatDateTime(lead.ultimaLlamada)} />
            <Field label="Cita Agendada" value={formatDateTime(lead.citaAgendada)} />
            <Field label="Fecha Modificación" value={formatDateTime(lead.fechaModificacion)} />
          </Section>
          <Section title="Vehículo">
            <Field label="Marca" value={lead.marca} />
            <Field label="Modelo" value={lead.modelo} />
            <Field label="Motor" value={lead.motor} />
            <Field label="Combustible" value={lead.combustible} />
            <Field label="Kilómetros" value={lead.kilometros} />
            <Field label="Matrícula" value={lead.matricula} />
            <Field label="Tipo Venta" value={lead.tipoVenta} />
          </Section>
          <Section title="Asignación">
            <Field label="Vendedor" value={lead.vendedor} />
            <Field label="Centro" value={lead.centro} />
          </Section>
          <Section title="UTM">
            <Field label="Source" value={lead.utmSource} />
            <Field label="Medium" value={lead.utmMedium} />
            <Field label="Campaign" value={lead.utmCampaign} />
          </Section>
        </div>
      </div>
    </Modal>
  );
};

// Modal Traspaso Lead Individual
const TraspasoLeadModal = ({ lead, comerciales, onClose, onTraspasar }) => {
  const [nuevoComercial, setNuevoComercial] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  if (!lead) return null;
  
  const comercialesDisponibles = comerciales.filter(c => c.nombre !== lead.vendedor && c.activo !== false);
  
  const handleTraspasar = async () => {
    if (!nuevoComercial) return;
    setIsLoading(true);
    try {
      await onTraspasar(lead.id, nuevoComercial);
      onClose();
    } catch (error) {
      console.error('Error al traspasar:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Modal onClose={onClose} title="🔄 Traspasar Lead" width="500px">
      <div style={{marginBottom: '20px', padding: '15px', background: '#f0f5ff', borderRadius: 10, border: '1px solid #c4dafa'}}>
        <h4 style={{margin: '0 0 10px 0', fontSize: '14px', color: '#005187'}}>Lead a traspasar</h4>
        <p style={{margin: '0 0 5px 0', fontSize: '13px', color: '#2b333d'}}><strong>Cliente:</strong> {lead.nombre} {lead.apellidos}</p>
        <p style={{margin: '0 0 5px 0', fontSize: '13px', color: '#2b333d'}}><strong>Origen:</strong> {lead.origen}</p>
        <p style={{margin: '0 0 5px 0', fontSize: '13px', color: '#2b333d'}}><strong>Vehículo:</strong> {lead.marca} {lead.modelo}</p>
        <p style={{margin: '0', fontSize: '13px', color: '#2b333d'}}><strong>Comercial actual:</strong> <span style={{color: '#005187', fontWeight: 600}}>{lead.vendedor || 'Sin asignar'}</span></p>
      </div>
      
      <div style={{marginBottom: '20px'}}>
        <label style={{display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: '#2b333d'}}>Seleccionar nuevo comercial:</label>
        <select
          value={nuevoComercial}
          onChange={(e) => setNuevoComercial(e.target.value)}
          style={{width: '100%', padding: '10px 12px', borderRadius: 10, border: `1px solid ${C.line}`, fontSize: '14px', background: 'white'}}
        >
          <option value="">-- Seleccionar comercial --</option>
          {comercialesDisponibles.map(c => (
            <option key={c.id} value={c.nombre}>{c.nombre} {c.centro ? `(${c.centro})` : ''}</option>
          ))}
        </select>
      </div>
      
      <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
        <button onClick={onClose} style={{padding: '10px 20px', borderRadius: 10, border: `1px solid ${C.line}`, background: 'white', cursor: 'pointer', fontSize: '14px'}}>Cancelar</button>
        <button 
          onClick={handleTraspasar} 
          disabled={!nuevoComercial || isLoading}
          style={{padding: '10px 20px', borderRadius: 10, border: 'none', background: nuevoComercial ? '#005187' : '#ccc', color: 'white', cursor: nuevoComercial ? 'pointer' : 'not-allowed', fontSize: '14px', fontWeight: 600}}
        >
          {isLoading ? 'Traspasando...' : '🔄 Traspasar Lead'}
        </button>
      </div>
    </Modal>
  );
};

// ==================== EDITAR LEAD MODAL (v1) ====================
// Permite editar la información de un lead. Registra cada cambio en auditoria_logs
// vía el handler onEditar (handleEditarLead) del padre, que llama a updateLead
// con sólo los campos modificados (en snake_case) y a registrarAuditoria con el
// diff (antes/después) para que quede traza completa en la sección Auditoría.
const EditarLeadModal = ({ lead, comerciales = [], onClose, onEditar }) => {
  const initial = React.useMemo(() => ({
    tipo: lead?.tipo || 'Lead Válido',
    origen: lead?.origen || '',
    detalleOrigen: lead?.detalleOrigen || '',
    campana: lead?.campana || '',
    estado: lead?.estado || 'Pendiente',
    motivoNoCompra: lead?.motivoNoCompra || '',
    nombre: lead?.nombre || '',
    apellidos: lead?.apellidos || '',
    razonSocial: lead?.razonSocial || '',
    tipoCliente: lead?.tipoCliente || 'Particular',
    telefono: lead?.telefono || '',
    movil: lead?.movil || '',
    email: lead?.email || '',
    cp: lead?.cp || '',
    provincia: lead?.provincia || '',
    marca: lead?.marca || '',
    modelo: lead?.modelo || '',
    motor: lead?.motor || '',
    combustible: lead?.combustible || '',
    kilometros: lead?.kilometros || 0,
    matricula: lead?.matricula || '',
    tipoVenta: lead?.tipoVenta || 'VN',
    vendedor: lead?.vendedor || '',
    centro: lead?.centro || '',
    observaciones: lead?.observaciones || ''
  }), [lead]);

  const [formData, setFormData] = useState(initial);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);

  React.useEffect(() => { setFormData(initial); }, [initial]);

  if (!lead) return null;

  const inputStyle = {...campo, width: '100%', padding: '9px 11px'};
  const labelStyle = {display: 'block', fontSize: '12.5px', fontWeight: 700, color: C.muted, marginBottom: '5px'};
  const sectionStyle = {background: '#fbfcfd', borderRadius: 10, padding: '16px', marginBottom: '16px', border: '1px solid #e7e9ec'};

  const set = (k, v) => setFormData(prev => ({ ...prev, [k]: v }));

  const buildDiff = () => {
    const diff = {};
    Object.keys(formData).forEach(k => {
      const before = initial[k] ?? '';
      const after = formData[k] ?? '';
      const a = typeof before === 'number' ? String(before) : (before || '');
      const b = typeof after === 'number' ? String(after) : (after || '');
      if (a !== b) diff[k] = { antes: before, despues: after };
    });
    return diff;
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (isSubmitting) return;
    const diff = buildDiff();
    if (Object.keys(diff).length === 0) {
      window.alert('ℹ️ No has modificado ningún campo.');
      return;
    }
    setIsSubmitting(true);
    try {
      const ok = await onEditar(lead.id, formData, diff, lead);
      if (ok !== false) {
        setSaved(true);
        setTimeout(() => { setSaved(false); onClose(); }, 1200);
      }
    } catch (err) {
      console.error('Error editando lead:', err);
      window.alert('❌ Error al guardar los cambios: ' + (err.message || err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const comercialesDisponibles = comerciales.filter(c => c.activo !== false);

  return (
    <Modal onClose={onClose} title={`✏️ Editar Lead — ${lead.leadId || ('#' + lead.id)}`} width="950px">
      {saved && <div style={{background: '#4CAF50', color: 'white', padding: '12px', borderRadius: 10, marginBottom: '15px', textAlign: 'center', fontWeight: 500}}>✓ Cambios guardados correctamente y registrados en auditoría</div>}
      <form onSubmit={handleSubmit}>
        <div style={sectionStyle}>
          <h4 style={{margin: '0 0 12px 0', fontSize: '13px', color: '#005187', borderBottom: `1px solid ${C.line}`, paddingBottom: '5px'}}>Información del Lead</h4>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px'}}>
            <div><label style={labelStyle}>Tipo de Lead</label><select style={inputStyle} value={formData.tipo} onChange={e => set('tipo', e.target.value)}>{Object.keys(LEAD_TIPOS).map(t => <option key={t}>{t}</option>)}</select></div>
            <div><label style={labelStyle}>Origen</label><select style={inputStyle} value={formData.origen} onChange={e => set('origen', e.target.value)}><option value="">Seleccionar...</option>{ORIGENES.map(o => <option key={o.nombre}>{o.nombre}</option>)}</select></div>
            <div><label style={labelStyle}>Detalle Origen</label><input style={inputStyle} value={formData.detalleOrigen} onChange={e => set('detalleOrigen', e.target.value)} /></div>
            <div><label style={labelStyle}>Campaña</label><input style={inputStyle} value={formData.campana} onChange={e => set('campana', e.target.value)} /></div>
            <div><label style={labelStyle}>Estado Cita</label><select style={inputStyle} value={formData.estado} onChange={e => set('estado', e.target.value)}>{Object.keys(ESTADOS_LEAD).map(s => <option key={s}>{s}</option>)}</select></div>
            <div><label style={labelStyle}>Motivo No Compra</label><select style={inputStyle} value={formData.motivoNoCompra} onChange={e => set('motivoNoCompra', e.target.value)}><option value="">-- Ninguno --</option>{motivosConActual(formData.motivoNoCompra).map(m => <option key={m}>{m}</option>)}</select></div>
            <div><label style={labelStyle}>Tipo Venta</label><select style={inputStyle} value={formData.tipoVenta} onChange={e => set('tipoVenta', e.target.value)}>{TIPOS_VENTA.map(t => <option key={t}>{t}</option>)}</select></div>
            <div><label style={labelStyle}>Tipo Cliente</label><select style={inputStyle} value={formData.tipoCliente} onChange={e => set('tipoCliente', e.target.value)}>{TIPOS_CLIENTE.map(t => <option key={t}>{t}</option>)}</select></div>
          </div>
        </div>

        <div style={sectionStyle}>
          <h4 style={{margin: '0 0 12px 0', fontSize: '13px', color: '#005187', borderBottom: `1px solid ${C.line}`, paddingBottom: '5px'}}>Datos del Cliente</h4>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px'}}>
            <div><label style={labelStyle}>Nombre</label><input style={inputStyle} value={formData.nombre} onChange={e => set('nombre', e.target.value)} /></div>
            <div><label style={labelStyle}>Apellidos</label><input style={inputStyle} value={formData.apellidos} onChange={e => set('apellidos', e.target.value)} /></div>
            <div style={{gridColumn: 'span 2'}}><label style={labelStyle}>Razón Social</label><input style={inputStyle} value={formData.razonSocial} onChange={e => set('razonSocial', e.target.value)} /></div>
            <div><label style={labelStyle}>Teléfono Fijo</label><input style={inputStyle} value={formData.telefono} onChange={e => set('telefono', e.target.value)} /></div>
            <div><label style={labelStyle}>Móvil</label><input style={inputStyle} value={formData.movil} onChange={e => set('movil', e.target.value)} /></div>
            <div style={{gridColumn: 'span 2'}}><label style={labelStyle}>Email</label><input style={inputStyle} type="email" value={formData.email} onChange={e => set('email', e.target.value)} /></div>
            <div><label style={labelStyle}>CP</label><input style={inputStyle} value={formData.cp} onChange={e => set('cp', e.target.value)} /></div>
            <div><label style={labelStyle}>Provincia</label><input style={inputStyle} value={formData.provincia} onChange={e => set('provincia', e.target.value)} /></div>
          </div>
        </div>

        <div style={sectionStyle}>
          <h4 style={{margin: '0 0 12px 0', fontSize: '13px', color: '#005187', borderBottom: `1px solid ${C.line}`, paddingBottom: '5px'}}>Vehículo</h4>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px'}}>
            <div><label style={labelStyle}>Marca</label><select style={inputStyle} value={formData.marca} onChange={e => set('marca', e.target.value)}><option value="">Seleccionar...</option>{MARCAS.map(m => <option key={m}>{m}</option>)}</select></div>
            <div><label style={labelStyle}>Modelo</label><input style={inputStyle} value={formData.modelo} onChange={e => set('modelo', e.target.value)} /></div>
            <div><label style={labelStyle}>Motor</label><input style={inputStyle} value={formData.motor} onChange={e => set('motor', e.target.value)} /></div>
            <div><label style={labelStyle}>Combustible</label><input style={inputStyle} value={formData.combustible} onChange={e => set('combustible', e.target.value)} /></div>
            <div><label style={labelStyle}>Kilómetros</label><input style={inputStyle} type="number" value={formData.kilometros} onChange={e => set('kilometros', parseInt(e.target.value) || 0)} /></div>
            <div><label style={labelStyle}>Matrícula</label><input style={inputStyle} value={formData.matricula} onChange={e => set('matricula', e.target.value.toUpperCase())} placeholder="Ej: 1234ABC" /></div>
          </div>
        </div>

        <div style={sectionStyle}>
          <h4 style={{margin: '0 0 12px 0', fontSize: '13px', color: '#005187', borderBottom: `1px solid ${C.line}`, paddingBottom: '5px'}}>Asignación</h4>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px'}}>
            <div><label style={labelStyle}>Vendedor</label><select style={inputStyle} value={formData.vendedor} onChange={e => set('vendedor', e.target.value)}><option value="">-- Sin asignar --</option>{comercialesDisponibles.map(c => <option key={c.id || c.nombre} value={c.nombre}>{c.nombre} {c.centro ? `(${c.centro})` : ''}</option>)}</select></div>
            <div><label style={labelStyle}>Centro</label><select style={inputStyle} value={formData.centro} onChange={e => set('centro', e.target.value)}><option value="">Seleccionar...</option>{CENTROS.map(c => <option key={c}>{c}</option>)}</select></div>
          </div>
        </div>

        <div style={sectionStyle}>
          <h4 style={{margin: '0 0 12px 0', fontSize: '13px', color: '#005187', borderBottom: `1px solid ${C.line}`, paddingBottom: '5px'}}>Observaciones</h4>
          <textarea style={{...inputStyle, minHeight: '70px', resize: 'vertical', fontFamily: 'inherit'}} value={formData.observaciones} onChange={e => set('observaciones', e.target.value)} placeholder="Observaciones internas del lead..." />
        </div>

        <div style={{padding: '10px 14px', background: '#fff8e1', border: '1px solid #ffe0b2', borderRadius: 10, marginBottom: '14px', fontSize: '12px', color: '#7c5e00'}}>
          🔒 <strong>Auditoría:</strong> cada cambio queda registrado en la sección <em>Auditoría del Sistema</em> (acción <code>editar_lead</code>) con tu usuario, la fecha y el detalle antes/después de cada campo modificado.
        </div>

        <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
          <button type="button" onClick={onClose} style={{padding: '10px 24px', background: '#8a929c', border: 'none', borderRadius: 10, color: 'white', cursor: 'pointer', fontSize: '13px'}}>Cancelar</button>
          <button type="submit" disabled={isSubmitting} style={{padding: '10px 24px', background: isSubmitting ? '#9aa4af' : '#228B22', border: 'none', borderRadius: 10, color: 'white', cursor: isSubmitting ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600}}>{isSubmitting ? '⏳ Guardando...' : '💾 Guardar cambios'}</button>
        </div>
      </form>
    </Modal>
  );
};


// Modal Traspaso Masivo de Leads
const TraspasoMasivoModal = ({ leads, comerciales, onClose, onTraspasar, onRefresh }) => {
  const [comercialOrigen, setComercialOrigen] = useState('');
  const [comercialDestino, setComercialDestino] = useState('');
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [resultado, setResultado] = useState(null);
  
  const comercialesUnicos = useMemo(() => {
    const nombres = [...new Set(leads.map(l => l.vendedor).filter(Boolean))];
    return nombres.sort();
  }, [leads]);
  
  const leadsDelComercial = useMemo(() => {
    if (!comercialOrigen) return [];
    return leads.filter(l => l.vendedor === comercialOrigen);
  }, [leads, comercialOrigen]);
  
  const comercialesDestino = useMemo(() => {
    return comerciales.filter(c => c.nombre !== comercialOrigen && c.activo !== false);
  }, [comerciales, comercialOrigen]);
  
  const handleSelectAll = () => {
    if (selectedLeads.length === leadsDelComercial.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(leadsDelComercial.map(l => l.id));
    }
  };
  
  const handleToggleLead = (leadId) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };
  
  const handleTraspasoMasivo = async () => {
    if (!comercialDestino || selectedLeads.length === 0) return;
    setIsLoading(true);
    setResultado(null);
    try {
      let exitosos = 0;
      let fallidos = 0;
      for (const leadId of selectedLeads) {
        try {
          await onTraspasar(leadId, comercialDestino);
          exitosos++;
        } catch (error) {
          fallidos++;
        }
      }
      setResultado({ exitosos, fallidos });
      setSelectedLeads([]);
      setComercialOrigen('');
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error en traspaso masivo:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <Modal onClose={onClose} title="🔄 Traspaso Masivo de Leads" width="900px">
      {resultado && (
        <div style={{marginBottom: '20px', padding: '15px', background: resultado.fallidos > 0 ? '#fff3e0' : '#e8f5e9', borderRadius: 10, border: `1px solid ${resultado.fallidos > 0 ? '#ffcc80' : '#a5d6a7'}`}}>
          <p style={{margin: '0', fontSize: '14px', color: '#2b333d'}}>
            ✅ <strong>{resultado.exitosos}</strong> leads traspasados correctamente
            {resultado.fallidos > 0 && <span style={{color: '#f44336'}}> | ❌ <strong>{resultado.fallidos}</strong> fallidos</span>}
          </p>
        </div>
      )}
      
      <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px'}}>
        <div>
          <label style={{display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: '#2b333d'}}>Comercial origen:</label>
          <select
            value={comercialOrigen}
            onChange={(e) => { setComercialOrigen(e.target.value); setSelectedLeads([]); }}
            style={{width: '100%', padding: '10px 12px', borderRadius: 10, border: `1px solid ${C.line}`, fontSize: '14px', background: 'white'}}
          >
            <option value="">-- Seleccionar comercial --</option>
            {comercialesUnicos.map(nombre => (
              <option key={nombre} value={nombre}>{nombre} ({leads.filter(l => l.vendedor === nombre).length} leads)</option>
            ))}
          </select>
        </div>
        <div>
          <label style={{display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: '#2b333d'}}>Comercial destino:</label>
          <select
            value={comercialDestino}
            onChange={(e) => setComercialDestino(e.target.value)}
            disabled={!comercialOrigen}
            style={{width: '100%', padding: '10px 12px', borderRadius: 10, border: `1px solid ${C.line}`, fontSize: '14px', background: comercialOrigen ? 'white' : '#f5f5f5'}}
          >
            <option value="">-- Seleccionar destino --</option>
            {comercialesDestino.map(c => (
              <option key={c.id} value={c.nombre}>{c.nombre} {c.centro ? `(${c.centro})` : ''}</option>
            ))}
          </select>
        </div>
      </div>
      
      {comercialOrigen && (
        <>
          <div style={{marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <span style={{fontSize: '13px', color: '#8a929c'}}>
              {leadsDelComercial.length} leads de <strong>{comercialOrigen}</strong> | {selectedLeads.length} seleccionados
            </span>
            <button 
              onClick={handleSelectAll}
              style={{padding: '6px 12px', borderRadius: 10, border: '1px solid #005187', background: 'white', color: '#005187', cursor: 'pointer', fontSize: '14px'}}
            >
              {selectedLeads.length === leadsDelComercial.length ? 'Deseleccionar todos' : 'Seleccionar todos'}
            </button>
          </div>
          
          <div style={{maxHeight: '350px', overflowY: 'auto', border: '1px solid #e7e9ec', borderRadius: 10, background: '#f4f5f7'}}>
            <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '14px'}}>
              <thead style={{position: 'sticky', top: 0}}>
                <tr>
                  <th style={{...celdaTitulo, padding: '10px', textAlign: 'center', width: '40px'}}>
                    <input type="checkbox" checked={selectedLeads.length === leadsDelComercial.length && leadsDelComercial.length > 0} onChange={handleSelectAll} />
                  </th>
                  <th style={{...celdaTitulo, padding: '10px', textAlign: 'left'}}>Cliente</th>
                  <th style={{...celdaTitulo, padding: '10px', textAlign: 'left'}}>Origen</th>
                  <th style={{...celdaTitulo, padding: '10px', textAlign: 'left'}}>Vehículo</th>
                  <th style={{...celdaTitulo, padding: '10px', textAlign: 'left'}}>Estado</th>
                  <th style={{...celdaTitulo, padding: '10px', textAlign: 'left'}}>Fecha</th>
                </tr>
              </thead>
              <tbody>
                {leadsDelComercial.map((lead, idx) => (
                  <tr 
                    key={lead.id} 
                    style={{background: idx % 2 === 0 ? 'white' : '#f9f9f9', cursor: 'pointer'}}
                    onClick={() => handleToggleLead(lead.id)}
                  >
                    <td style={{padding: '8px', textAlign: 'center'}}>
                      <input type="checkbox" checked={selectedLeads.includes(lead.id)} onChange={() => {}} />
                    </td>
                    <td style={{padding: '8px'}}>{lead.nombre} {lead.apellidos}</td>
                    <td style={{padding: '8px'}}>{lead.origen}</td>
                    <td style={{padding: '8px'}}>{lead.marca} {lead.modelo}</td>
                    <td style={{padding: '8px'}}>
                      <span style={pildora(colorEstado(lead.estado))}>
                        {lead.estado}
                      </span>
                    </td>
                    <td style={{padding: '8px'}}>{formatDateTime(lead.fechaCreacion)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      
      <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #e7e9ec'}}>
        <button onClick={onClose} style={{padding: '10px 20px', borderRadius: 10, border: `1px solid ${C.line}`, background: 'white', cursor: 'pointer', fontSize: '14px'}}>Cerrar</button>
        <button 
          onClick={handleTraspasoMasivo} 
          disabled={!comercialDestino || selectedLeads.length === 0 || isLoading}
          style={{padding: '10px 24px', borderRadius: 10, border: 'none', background: (comercialDestino && selectedLeads.length > 0) ? '#005187' : '#ccc', color: 'white', cursor: (comercialDestino && selectedLeads.length > 0) ? 'pointer' : 'not-allowed', fontSize: '14px', fontWeight: 600}}
        >
          {isLoading ? 'Traspasando...' : `🔄 Traspasar ${selectedLeads.length} Lead${selectedLeads.length !== 1 ? 's' : ''}`}
        </button>
      </div>
    </Modal>
  );
};

// Detalle Llamadas Modal
const DetalleLlamadasModal = ({ leads, onClose }) => {
  const [localFilters, setLocalFilters] = useState({ search: '', usuario: 'Todos', origen: 'Todos', vendedor: 'Todos', estado: 'Todos' });
  const usuarios = ['Todos', ...new Set(leads.map(l => l.usuarioAlta).filter(Boolean))];
  const origenes = ['Todos', ...new Set(leads.map(l => l.origen).filter(Boolean))];
  const vendedores = ['Todos', ...new Set(leads.map(l => l.vendedor).filter(Boolean))];
  const estados = ['Todos', ...Object.keys(ESTADOS_LEAD)];
  const filtered = useMemo(() => leads.filter(l => {
    if (localFilters.search && !`${l.nombre} ${l.apellidos}`.toLowerCase().includes(localFilters.search.toLowerCase())) return false;
    if (localFilters.usuario !== 'Todos' && l.usuarioAlta !== localFilters.usuario) return false;
    if (localFilters.origen !== 'Todos' && l.origen !== localFilters.origen) return false;
    if (localFilters.vendedor !== 'Todos' && l.vendedor !== localFilters.vendedor) return false;
    if (localFilters.estado !== 'Todos' && l.estado !== localFilters.estado) return false;
    return true;
  }), [leads, localFilters]);
  const stats = useMemo(() => ({ llamadas: filtered.filter(l => l.primeraLlamada).length, citas: filtered.filter(l => l.citaAgendada).length, ventas: filtered.filter(l => l.estado === 'Vendido').length }), [filtered]);
  
  // Componente para mostrar el estado con su color
  const EstadoBadge = ({ estado }) => {
    const estadoData = ESTADOS_LEAD[estado] || { color: '#808080' };
    return (
      <span style={{
        display: 'inline-block',
        padding: '3px 8px',
        borderRadius: 12,
        fontSize: '14px',
        fontWeight: 600,
        background: `${estadoData.color}20`,
        color: estadoData.color,
        border: `1px solid ${estadoData.color}40`,
        whiteSpace: 'nowrap'
      }}>
        {estado}
      </span>
    );
  };
  
  return (
    <Modal onClose={onClose} title="Detalle de los Leads" width="95%">
      <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '15px', padding: '10px', background: '#f5f5f5', borderRadius: 10}}>
        <input type="text" placeholder="Buscar cliente..." value={localFilters.search} onChange={e => setLocalFilters({...localFilters, search: e.target.value})} style={{padding: '6px 10px', borderRadius: 10, border: `1px solid ${C.line}`, fontSize: '14px', width: '150px'}} />
        <select value={localFilters.usuario} onChange={e => setLocalFilters({...localFilters, usuario: e.target.value})} style={{padding: '6px', borderRadius: 10, border: `1px solid ${C.line}`, fontSize: '14px'}}>{usuarios.map(u => <option key={u} value={u}>{u === 'Todos' ? 'Usuarios (Todos)' : u}</option>)}</select>
        <select value={localFilters.vendedor} onChange={e => setLocalFilters({...localFilters, vendedor: e.target.value})} style={{padding: '6px', borderRadius: 10, border: `1px solid ${C.line}`, fontSize: '14px'}}>{vendedores.map(v => <option key={v} value={v}>{v === 'Todos' ? 'Vendedor (Todos)' : v}</option>)}</select>
        <select value={localFilters.origen} onChange={e => setLocalFilters({...localFilters, origen: e.target.value})} style={{padding: '6px', borderRadius: 10, border: `1px solid ${C.line}`, fontSize: '14px'}}>{origenes.map(o => <option key={o} value={o}>{o === 'Todos' ? 'Origen (Todos)' : o}</option>)}</select>
        <select value={localFilters.estado} onChange={e => setLocalFilters({...localFilters, estado: e.target.value})} style={{padding: '6px', borderRadius: 10, border: `1px solid ${C.line}`, fontSize: '14px'}}>{estados.map(e => <option key={e} value={e}>{e === 'Todos' ? 'Estado (Todos)' : e}</option>)}</select>
      </div>
      <div style={{display: 'flex', gap: '20px', marginBottom: '20px'}}>
        <div style={{background: '#e8f5e9', padding: '20px 30px', borderRadius: 10, textAlign: 'center', border: '1px solid #c8e6c9'}}><div style={{fontSize: '28px', fontWeight: 700, color: '#1f7a44'}}>{stats.llamadas}</div><div style={{fontSize: '14px', color: '#388e3c', marginTop: '4px'}}>Llamadas atendidas</div></div>
        <div style={{background: '#e3f2fd', padding: '20px 30px', borderRadius: 10, textAlign: 'center', border: '1px solid #bbdefb'}}><div style={{fontSize: '28px', fontWeight: 700, color: '#1565c0'}}>{stats.citas}</div><div style={{fontSize: '14px', color: '#1976d2', marginTop: '4px'}}>Citas concertadas</div></div>
        <div style={{background: '#fff3e0', padding: '20px 30px', borderRadius: 10, textAlign: 'center', border: '1px solid #ffe0b2'}}><div style={{fontSize: '28px', fontWeight: 700, color: '#e65100'}}>{stats.ventas}</div><div style={{fontSize: '14px', color: '#f57c00', marginTop: '4px'}}>Ventas realizadas</div></div>
      </div>
      <div style={{background: 'white', borderRadius: 10, overflow: 'hidden', border: `1px solid ${C.line}`}}>
        <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '13px'}}>
          <thead><tr><th style={{...celdaTitulo, padding: '10px 8px'}}>Fecha</th><th style={{...celdaTitulo, padding: '10px 8px'}}>Cliente</th><th style={{...celdaTitulo, padding: '10px 8px'}}>Vehículo</th><th style={{...celdaTitulo, padding: '10px 8px'}}>Estado</th><th style={{...celdaTitulo, padding: '10px 8px', textAlign: 'center'}}>Completada</th><th style={{...celdaTitulo, padding: '10px 8px'}}>Usuario</th><th style={{...celdaTitulo, padding: '10px 8px'}}>Origen</th></tr></thead>
          <tbody>
            {filtered.slice(0, 100).map((lead, idx) => (
              <tr key={lead.id} style={{background: idx % 2 === 0 ? 'white' : '#f9f9f9', borderBottom: SEPARADOR_FILA}}>
                <td style={{padding: '8px', color: '#2b333d'}}>{formatDateTime(lead.primeraLlamada)}</td>
                <td style={{padding: '8px', color: '#2b333d', fontWeight: 500}}>{lead.nombre} {lead.apellidos}</td>
                <td style={{padding: '8px', color: '#8a929c'}}>{lead.marca} {lead.modelo}</td>
                <td style={{padding: '8px'}}><EstadoBadge estado={lead.estado} /></td>
                <td style={{padding: '8px', textAlign: 'center'}}>{lead.primeraLlamada ? <span style={{color: '#228B22'}}>✓</span> : <span style={{color: '#ccc'}}>○</span>}</td>
                <td style={{padding: '8px', color: '#2b333d'}}>{lead.usuarioAlta}</td>
                <td style={{padding: '8px', color: '#0066CC'}}>{lead.origen}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
};

// ==================== CONFIGURACION MODAL ====================
// Función helper para procesar configData - inicializa con arrays vacíos, el useEffect cargará los datos de Supabase
const processConfigDataForModal = (configData) => {
  return {
    centros: [],
    marcas: [],
    origenes: [],
    campanas: []
  };
};

// ==================== PÁGINA DE AUDITORÍA ====================
const AuditoriaPage = () => {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usuarios, setUsuarios] = useState([]); // NUEVO: Lista de usuarios
  const [generatingPDF, setGeneratingPDF] = useState(false); // NUEVO: Estado para PDF
  const [filters, setFilters] = useState({
    user_email: '',
    action: '',
    table_name: '',
    fecha_desde: '',
    fecha_hasta: ''
  });
  const [page, setPage] = useState(1);
  const itemsPerPage = 50;

  // NUEVO: Cargar usuarios (administradores + comerciales)
  useEffect(() => {
    const loadUsuarios = async () => {
      try {
        // Cargar administradores
        const adminsResponse = await fetch(`${SUPABASE_URL}/rest/v1/administradores?select=email,nombre`, {
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`
          }
        });
        const admins = await adminsResponse.json();

        // Cargar comerciales
        const comercialesResponse = await fetch(`${SUPABASE_URL}/rest/v1/comerciales?select=email,nombre`, {
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${SUPABASE_ANON_KEY}`
          }
        });
        const comerciales = await comercialesResponse.json();

        // Combinar y crear mapa email -> nombre
        const todosUsuarios = [
          ...admins.map(u => ({ email: u.email, nombre: u.nombre, tipo: 'Admin' })),
          ...comerciales.map(u => ({ email: u.email, nombre: u.nombre, tipo: 'Comercial' }))
        ];
        
        setUsuarios(todosUsuarios);
      } catch (error) {
        console.error('Error cargando usuarios:', error);
      }
    };
    loadUsuarios();
  }, []);

  // Cargar logs de auditoría
  const loadAuditoriaLogs = async () => {
    setLoading(true);
    try {
      console.log('📊 Cargando logs de auditoría...');
      const response = await fetch(`${SUPABASE_URL}/rest/v1/auditoria_logs?select=*&order=timestamp.desc&limit=1000`, {
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`
        }
      });
      
      if (!response.ok) {
        console.error('❌ Error en response:', response.status);
      }
      
      const data = await response.json();
      console.log('✅ Logs cargados:', data.length, 'registros');
      console.log('📋 Primeros 3 logs:', data.slice(0, 3));
      
      setLogs(data || []);
      setFilteredLogs(data || []);
    } catch (error) {
      console.error('❌ Error cargando logs de auditoría:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAuditoriaLogs();
  }, []);

  // Aplicar filtros
  useEffect(() => {
    let filtered = [...logs];
    
    if (filters.user_email) {
      filtered = filtered.filter(log => 
        log.user_email?.toLowerCase().includes(filters.user_email.toLowerCase())
      );
    }
    
    if (filters.action) {
      filtered = filtered.filter(log => 
        log.action?.toLowerCase().includes(filters.action.toLowerCase())
      );
    }
    
    if (filters.table_name) {
      filtered = filtered.filter(log => 
        log.table_name?.toLowerCase().includes(filters.table_name.toLowerCase())
      );
    }
    
    if (filters.fecha_desde) {
      filtered = filtered.filter(log => 
        new Date(log.timestamp) >= new Date(filters.fecha_desde)
      );
    }
    
    if (filters.fecha_hasta) {
      const fechaHasta = new Date(filters.fecha_hasta);
      fechaHasta.setHours(23, 59, 59, 999);
      filtered = filtered.filter(log => 
        new Date(log.timestamp) <= fechaHasta
      );
    }
    
    setFilteredLogs(filtered);
    setPage(1);
  }, [filters, logs]);

  // NUEVO: Función para obtener nombre de usuario
  const getNombreUsuario = (email) => {
    if (!email) return '-';
    const usuario = usuarios.find(u => u.email === email);
    return usuario ? `${usuario.nombre} (${usuario.tipo})` : email;
  };

  // Descargar informe CSV
  const descargarInformeCSV = () => {
    const headers = ['Fecha/Hora', 'Usuario', 'Email', 'Acción', 'Tabla', 'Record ID', 'User Agent'];
    const csvData = [
      headers.join(','),
      ...filteredLogs.map(log => [
        new Date(log.timestamp).toLocaleString('es-ES'),
        getNombreUsuario(log.user_email),
        log.user_email || '',
        log.action || '',
        log.table_name || '',
        log.record_id || '',
        log.user_agent || ''
      ].map(val => `"${val}"`).join(','))
    ].join('\n');
    
    const blob = new Blob(['\ufeff' + csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `auditoria_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // NUEVO: Generar informe PDF con análisis de OpenAI
  const generarInformePDF = async () => {
    setGeneratingPDF(true);
    
    try {
      // Preparar resumen de datos para OpenAI
      const resumen = {
        total_registros: filteredLogs.length,
        rango_fechas: {
          desde: filteredLogs.length > 0 ? new Date(filteredLogs[filteredLogs.length - 1].timestamp).toLocaleDateString('es-ES') : '-',
          hasta: filteredLogs.length > 0 ? new Date(filteredLogs[0].timestamp).toLocaleDateString('es-ES') : '-'
        },
        acciones: filteredLogs.reduce((acc, log) => {
          acc[log.action] = (acc[log.action] || 0) + 1;
          return acc;
        }, {}),
        usuarios: filteredLogs.reduce((acc, log) => {
          acc[log.user_email] = (acc[log.user_email] || 0) + 1;
          return acc;
        }, {}),
        tablas: filteredLogs.reduce((acc, log) => {
          if (log.table_name) {
            acc[log.table_name] = (acc[log.table_name] || 0) + 1;
          }
          return acc;
        }, {})
      };

      // PASO 1: Cargar jsPDF dinámicamente si no está disponible
      if (!window.jspdf) {
        console.log('📦 Cargando jsPDF...');
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
          script.onload = () => {
            console.log('✅ jsPDF cargado correctamente');
            resolve();
          };
          script.onerror = () => {
            console.error('❌ Error cargando jsPDF');
            reject(new Error('No se pudo cargar jsPDF'));
          };
          document.head.appendChild(script);
        });
      }

      // PASO 2: Obtener API Key desde Supabase Secrets
      const token = localStorage.getItem('sb-access-token');
      if (!token) {
        throw new Error('No hay sesión activa');
      }

      // Obtener la API Key de OpenAI desde Supabase
      const secretResponse = await fetch(`${SUPABASE_URL}/rest/v1/rpc/get_openai_key`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'apikey': SUPABASE_ANON_KEY
        }
      });

      let openaiApiKey;
      if (secretResponse.ok) {
        const secretData = await secretResponse.json();
        openaiApiKey = secretData;
        console.log('✅ API Key obtenida de Supabase');
      } else {
        // Fallback: usar API Key de variable de entorno
        console.warn('⚠️ No se pudo obtener API Key de Supabase, usando fallback');
        openaiApiKey = process.env.REACT_APP_OPENAI_API_KEY;
      }

      // PASO 3: Llamar a la API de OpenAI (GPT-4)
      console.log('🤖 Generando análisis con OpenAI...');
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${openaiApiKey}`
        },
        body: JSON.stringify({
          model: 'gpt-4o', // Puedes usar 'gpt-4o-mini' para ser más económico
          max_tokens: 2000,
          temperature: 0.7,
          messages: [{
            role: 'system',
            content: 'Eres un analista de sistemas CRM experto que genera informes ejecutivos profesionales en español para la dirección de empresas.'
          }, {
            role: 'user',
            content: `Analiza este informe de auditoría del CRM y genera un resumen ejecutivo en español:

Datos del sistema:
- Total de registros: ${resumen.total_registros}
- Período: ${resumen.rango_fechas.desde} - ${resumen.rango_fechas.hasta}

Acciones realizadas:
${Object.entries(resumen.acciones).map(([action, count]) => `- ${action}: ${count}`).join('\n')}

Usuarios más activos:
${Object.entries(resumen.usuarios).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([email, count]) => `- ${email}: ${count} acciones`).join('\n')}

Tablas modificadas:
${Object.entries(resumen.tablas).map(([table, count]) => `- ${table}: ${count} modificaciones`).join('\n')}

Genera un informe ejecutivo de 300-400 palabras que incluya:
1. Resumen general de la actividad
2. Análisis de las acciones más frecuentes
3. Usuarios más activos y su comportamiento
4. Recomendaciones de seguridad si aplican
5. Tendencias observadas

Usa un tono profesional y enfocado en insights para la dirección.`
          }]
        })
      });

      const data = await response.json();
      console.log('📊 Respuesta de OpenAI:', data);
      
      if (!data.choices || !data.choices[0]) {
        throw new Error('Error en la respuesta de OpenAI: ' + JSON.stringify(data));
      }
      
      const analisisIA = data.choices[0].message.content;

      // Crear PDF con jsPDF
      const { jsPDF } = window.jspdf;
      const doc = new jsPDF();
      
      // Configurar fuente
      doc.setFont('helvetica');
      
      // Encabezado con logo/marca
      doc.setFontSize(20);
      doc.setTextColor(0, 81, 135); // #005187
      doc.text('Informe de Auditoría - CRM Dimoauto', 20, 20);
      
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Generado: ${new Date().toLocaleString('es-ES')}`, 20, 28);
      
      // Línea separadora
      doc.setDrawColor(0, 81, 135);
      doc.line(20, 32, 190, 32);
      
      // Estadísticas Generales
      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.text('Estadísticas Generales', 20, 42);
      
      doc.setFontSize(10);
      let y = 50;
      doc.text(`Total de registros: ${resumen.total_registros}`, 25, y);
      y += 6;
      doc.text(`Período: ${resumen.rango_fechas.desde} - ${resumen.rango_fechas.hasta}`, 25, y);
      y += 10;
      
      // Acciones más frecuentes
      doc.setFontSize(12);
      doc.setTextColor(0, 81, 135);
      doc.text('Acciones Realizadas', 25, y);
      y += 6;
      
      doc.setFontSize(9);
      doc.setTextColor(60, 60, 60);
      const topAcciones = Object.entries(resumen.acciones)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5);
      topAcciones.forEach(([action, count]) => {
        doc.text(`• ${action}: ${count} (${Math.round(count/resumen.total_registros*100)}%)`, 30, y);
        y += 5;
      });
      y += 5;
      
      // Usuarios más activos
      doc.setFontSize(12);
      doc.setTextColor(0, 81, 135);
      doc.text('Usuarios Más Activos', 25, y);
      y += 6;
      
      doc.setFontSize(9);
      doc.setTextColor(60, 60, 60);
      const topUsuarios = Object.entries(resumen.usuarios)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5);
      topUsuarios.forEach(([email, count]) => {
        const nombre = getNombreUsuario(email);
        doc.text(`• ${nombre}: ${count} acciones`, 30, y);
        y += 5;
      });
      y += 10;
      
      // Análisis IA
      doc.setFontSize(14);
      doc.setTextColor(0, 81, 135);
      doc.text('Análisis Inteligente con IA', 20, y);
      y += 8;
      
      doc.setFontSize(9);
      doc.setTextColor(50, 50, 50);
      const splitText = doc.splitTextToSize(analisisIA, 170);
      doc.text(splitText, 20, y);
      
      // Pie de página
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Página ${i} de ${totalPages} - Grupo Dimoauto`, 20, 285);
        doc.text('Generado con IA (OpenAI GPT-4)', 135, 285);
      }
      
      // Guardar PDF
      doc.save(`informe-auditoria-${new Date().toISOString().split('T')[0]}.pdf`);
      
    } catch (error) {
      console.error('Error generando PDF:', error);
      alert('Error al generar el informe PDF. Por favor, descarga el CSV como alternativa.');
    } finally {
      setGeneratingPDF(false);
    }
  };

  // Paginación
  const totalPages = Math.ceil(filteredLogs.length / itemsPerPage);
  const paginatedLogs = filteredLogs.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  // Obtener valores únicos para filtros
  const uniqueActions = [...new Set(logs.map(l => l.action).filter(Boolean))];
  const uniqueTables = [...new Set(logs.map(l => l.table_name).filter(Boolean))];

  return (
    <div style={{ padding: '32px', maxWidth: '1600px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ 
          margin: '0 0 8px 0', 
          fontSize: '24px', 
          fontWeight: 700, 
          color: '#1f2a37',
          fontFamily: 'inherit'
        }}>
          🔍 Auditoría del Sistema
        </h1>
        <p style={{ 
          margin: 0, 
          fontSize: '13px', 
          color: '#8a929c',
          fontFamily: 'inherit'
        }}>
          Registro de todas las acciones realizadas en el sistema
        </p>
      </div>

      {/* Filtros */}
      <div style={{ 
        background: 'white', 
        borderRadius: 12, 
        padding: '24px', 
        marginBottom: '24px',
        border: '1px solid #e7e9ec'
      }}>
        <h3 style={{ 
          margin: '0 0 16px 0', 
          fontSize: '14px', 
          fontWeight: 600, 
          color: '#1f2a37',
          fontFamily: 'inherit'
        }}>
          🔎 Filtros
        </h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          {/* NUEVO: Filtro Usuario con desplegable */}
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '4px', 
              fontSize: '14px', 
              color: '#8a929c',
              fontWeight: 500,
              fontFamily: 'inherit'
            }}>
              Usuario
            </label>
            <select
              value={filters.user_email}
              onChange={(e) => setFilters({...filters, user_email: e.target.value})}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #e7e9ec',
                borderRadius: 10,
                fontSize: '13px',
                fontFamily: 'inherit'
              }}
            >
              <option value="">Todos los usuarios</option>
              {usuarios.map(usuario => (
                <option key={usuario.email} value={usuario.email}>
                  {usuario.nombre} - {usuario.tipo}
                </option>
              ))}
            </select>
          </div>

          {/* Filtro Acción */}
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '4px', 
              fontSize: '14px', 
              color: '#8a929c',
              fontWeight: 500,
              fontFamily: 'inherit'
            }}>
              Acción
            </label>
            <select
              value={filters.action}
              onChange={(e) => setFilters({...filters, action: e.target.value})}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #e7e9ec',
                borderRadius: 10,
                fontSize: '13px',
                fontFamily: 'inherit'
              }}
            >
              <option value="">Todas las acciones</option>
              {uniqueActions.map(action => (
                <option key={action} value={action}>{action}</option>
              ))}
            </select>
          </div>

          {/* Filtro Tabla */}
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '4px', 
              fontSize: '14px', 
              color: '#8a929c',
              fontWeight: 500,
              fontFamily: 'inherit'
            }}>
              Tabla
            </label>
            <select
              value={filters.table_name}
              onChange={(e) => setFilters({...filters, table_name: e.target.value})}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #e7e9ec',
                borderRadius: 10,
                fontSize: '13px',
                fontFamily: 'inherit'
              }}
            >
              <option value="">Todas las tablas</option>
              {uniqueTables.map(table => (
                <option key={table} value={table}>{table}</option>
              ))}
            </select>
          </div>

          {/* Filtro Fecha Desde */}
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '4px', 
              fontSize: '14px', 
              color: '#8a929c',
              fontWeight: 500,
              fontFamily: 'inherit'
            }}>
              Fecha Desde
            </label>
            <input
              type="date"
              value={filters.fecha_desde}
              onChange={(e) => setFilters({...filters, fecha_desde: e.target.value})}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #e7e9ec',
                borderRadius: 10,
                fontSize: '13px',
                fontFamily: 'inherit'
              }}
            />
          </div>

          {/* Filtro Fecha Hasta */}
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '4px', 
              fontSize: '14px', 
              color: '#8a929c',
              fontWeight: 500,
              fontFamily: 'inherit'
            }}>
              Fecha Hasta
            </label>
            <input
              type="date"
              value={filters.fecha_hasta}
              onChange={(e) => setFilters({...filters, fecha_hasta: e.target.value})}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #e7e9ec',
                borderRadius: 10,
                fontSize: '13px',
                fontFamily: 'inherit'
              }}
            />
          </div>
        </div>

        {/* Botones de acción */}
        <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
          <button
            onClick={() => setFilters({ user_email: '', action: '', table_name: '', fecha_desde: '', fecha_hasta: '' })}
            style={{
              padding: '8px 16px',
              background: '#f4f5f7',
              border: '1px solid #e7e9ec',
              borderRadius: 10,
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              color: '#8a929c',
              fontFamily: 'inherit'
            }}
          >
            🔄 Limpiar Filtros
          </button>
          
          {/* NUEVO: Botón Actualizar Informe */}
          <button
            onClick={loadAuditoriaLogs}
            disabled={loading}
            style={{
              padding: '8px 16px',
              background: loading ? '#e7e9ec' : '#f4f5f7',
              border: '1px solid #e7e9ec',
              borderRadius: 10,
              cursor: loading ? 'wait' : 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              color: '#8a929c',
              fontFamily: 'inherit'
            }}
          >
            {loading ? '⏳ Actualizando...' : '🔄 Actualizar Informe'}
          </button>
          
          <button
            onClick={descargarInformeCSV}
            disabled={filteredLogs.length === 0}
            style={{
              padding: '8px 16px',
              background: filteredLogs.length === 0 ? '#e7e9ec' : '#005187',
              border: 'none',
              borderRadius: 10,
              cursor: filteredLogs.length === 0 ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              color: 'white',
              fontFamily: 'inherit'
            }}
          >
            📥 Descargar CSV
          </button>

          {/* NUEVO: Botón Descargar PDF con IA */}
          <button
            onClick={generarInformePDF}
            disabled={filteredLogs.length === 0 || generatingPDF}
            style={{
              padding: '8px 16px',
              background: (filteredLogs.length === 0 || generatingPDF) ? '#e7e9ec' : '#2e9e5b',
              border: 'none',
              borderRadius: 10,
              cursor: (filteredLogs.length === 0 || generatingPDF) ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              color: 'white',
              fontFamily: 'inherit'
            }}
          >
            {generatingPDF ? '⏳ Generando PDF...' : '📄 Descargar PDF con IA'}
          </button>
        </div>

        {/* Estadísticas */}
        <div style={{ 
          marginTop: '16px', 
          padding: '12px', 
          background: '#f4f5f7', 
          borderRadius: 10,
          fontSize: '14px',
          color: '#8a929c',
          fontFamily: 'inherit'
        }}>
          📊 Mostrando <strong>{filteredLogs.length}</strong> de <strong>{logs.length}</strong> registros
        </div>
      </div>

      {/* Tabla de logs */}
      <div style={{ 
        background: 'white', 
        borderRadius: 12, 
        overflow: 'hidden',
        border: '1px solid #e7e9ec'
      }}>
        {loading ? (
          <div style={{ padding: '16px' }}>
            {[0, 1, 2, 3, 4, 5].map(i => <div key={i} className="fg-shimmer" style={{ height: 38, borderRadius: 12, marginBottom: 8 }} />)}
          </div>
        ) : filteredLogs.length === 0 ? (
          <EmptyState icon="🔍" title="No se encontraron registros con los filtros aplicados" />
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f4f5f7', borderBottom: '1px solid #e7e9ec' }}>
                    <th style={{ ...celdaTitulo, padding: '10px 12px', fontFamily: 'inherit' }}>
                      Fecha/Hora
                    </th>
                    <th style={{ ...celdaTitulo, padding: '10px 12px', fontFamily: 'inherit' }}>
                      Usuario
                    </th>
                    <th style={{ ...celdaTitulo, padding: '10px 12px', fontFamily: 'inherit' }}>
                      Acción
                    </th>
                    <th style={{ ...celdaTitulo, padding: '10px 12px', fontFamily: 'inherit' }}>
                      Tabla
                    </th>
                    <th style={{ ...celdaTitulo, padding: '10px 12px', fontFamily: 'inherit' }}>
                      Record ID
                    </th>
                    <th style={{ ...celdaTitulo, padding: '10px 12px', fontFamily: 'inherit' }}>
                      User Agent
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedLogs.map((log, index) => (
                    <tr 
                      key={log.id} 
                      style={{ 
                        borderBottom: '1px solid #f4f5f7',
                        background: index % 2 === 0 ? 'white' : '#f4f5f7'
                      }}
                    >
                      <td style={{ 
                        padding: '12px', 
                        fontSize: '14px', 
                        color: '#1f2a37',
                        fontFamily: 'inherit'
                      }}>
                        {new Date(log.timestamp).toLocaleString('es-ES')}
                      </td>
                      <td style={{ 
                        padding: '12px', 
                        fontSize: '14px', 
                        color: '#1f2a37',
                        fontFamily: 'inherit'
                      }}>
                        {getNombreUsuario(log.user_email)}
                      </td>
                      <td style={{ 
                        padding: '12px', 
                        fontSize: '14px',
                        fontFamily: 'inherit'
                      }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: 10,
                          background: log.action === 'login' ? '#dceaf4' : log.action === 'create_lead' ? '#e6f4ec' : log.action === 'modify_lead' ? '#fbf1de' : '#f4f5f7',
                          color: log.action === 'login' ? '#1e40af' : log.action === 'create_lead' ? '#1f7a44' : log.action === 'modify_lead' ? '#a8660f' : '#8a929c',
                          fontSize: '13px',
                          fontWeight: 500
                        }}>
                          {log.action}
                        </span>
                      </td>
                      <td style={{ 
                        padding: '12px', 
                        fontSize: '14px', 
                        color: '#8a929c',
                        fontFamily: 'inherit'
                      }}>
                        {log.table_name || '-'}
                      </td>
                      <td style={{ 
                        padding: '12px', 
                        fontSize: '14px', 
                        color: '#8a929c',
                        fontFamily: 'monospace'
                      }}>
                        {log.record_id || '-'}
                      </td>
                      <td style={{ 
                        padding: '12px', 
                        fontSize: '13px', 
                        color: '#9aa4af',
                        maxWidth: '200px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontFamily: 'monospace'
                      }}
                      title={log.user_agent}>
                        {log.user_agent || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginación */}
            {totalPages > 1 && (
              <div style={{ 
                padding: '16px', 
                display: 'flex', 
                justifyContent: 'center', 
                gap: '8px', 
                borderTop: '1px solid #e7e9ec' 
              }}>
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  style={{
                    padding: '6px 12px',
                    background: page === 1 ? '#f4f5f7' : 'white',
                    border: '1px solid #e7e9ec',
                    borderRadius: 10,
                    cursor: page === 1 ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    color: page === 1 ? '#9aa4af' : '#1f2a37',
                    fontFamily: 'inherit'
                  }}
                >
                  ← Anterior
                </button>
                
                <span style={{ 
                  padding: '6px 12px', 
                  fontSize: '14px', 
                  color: '#8a929c',
                  fontFamily: 'inherit'
                }}>
                  Página {page} de {totalPages}
                </span>
                
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  style={{
                    padding: '6px 12px',
                    background: page === totalPages ? '#f4f5f7' : 'white',
                    border: '1px solid #e7e9ec',
                    borderRadius: 10,
                    cursor: page === totalPages ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    color: page === totalPages ? '#9aa4af' : '#1f2a37',
                    fontFamily: 'inherit'
                  }}
                >
                  Siguiente →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ==================== MODAL GESTIÓN DE LEADS ====================
const GestionLeadsModal = ({ onClose, onOpenNuevoLead, onOpenTraspasoMasivo, onOpenGestionDuplicados }) => {
  const [activeSection, setActiveSection] = useState('nuevo');

  const sections = [
    { id: 'nuevo', label: '➕ Nuevo Lead', icon: '➕' },
    { id: 'traspaso', label: '🔄 Traspaso Leads', icon: '🔄' },
    { id: 'duplicados', label: '🔍 Duplicados', icon: '🔍' }
  ];

  return (
    <Modal onClose={onClose} title="📝 Gestión de Leads" width="600px">
      <div style={{ display: 'flex', height: '400px' }}>
        {/* Sidebar de secciones */}
        <div style={{ 
          width: '200px', 
          borderRight: '1px solid #e7e9ec', 
          paddingRight: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px'
        }}>
          {sections.map(section => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className="fg-nav"
              style={{
                padding: '9px 13px',
                background: activeSection === section.id ? GRAD : 'transparent',
                border: 'none',
                borderRadius: 10,
                cursor: 'pointer',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                color: activeSection === section.id ? '#fff' : C.page,
                boxShadow: activeSection === section.id ? '0 6px 16px rgba(0,81,135,.28)' : 'none',
                fontWeight: 700,
                fontSize: '13px',
                fontFamily: 'inherit'
              }}
            >
              <span style={{ fontSize: '18px' }}>{section.icon}</span>
              <span>{section.label}</span>
            </button>
          ))}
        </div>

        {/* Contenido de la sección */}
        <div style={{ flex: 1, padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {activeSection === 'nuevo' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <h3 style={{ 
                  margin: '0 0 8px 0', 
                  fontSize: '16px', 
                  color: '#1f2a37',
                  fontWeight: 600,
                  fontFamily: 'inherit'
                }}>
                  ➕ Crear Nuevo Lead
                </h3>
                <p style={{ 
                  margin: 0, 
                  fontSize: '13px', 
                  color: '#8a929c',
                  fontFamily: 'inherit'
                }}>
                  Crea un nuevo lead manualmente desde el panel de administración.
                </p>
              </div>
              
              <button
                onClick={() => {
                  onClose();
                  onOpenNuevoLead();
                }}
                className="fg-nav"
                style={{...botonPrimario, padding: '11px 20px'}}
              >
                ➕ Abrir Formulario Nuevo Lead
              </button>
            </div>
          )}

          {activeSection === 'traspaso' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <h3 style={{ 
                  margin: '0 0 8px 0', 
                  fontSize: '16px', 
                  color: '#1f2a37',
                  fontWeight: 600,
                  fontFamily: 'inherit'
                }}>
                  🔄 Traspaso Masivo de Leads
                </h3>
                <p style={{ 
                  margin: 0, 
                  fontSize: '13px', 
                  color: '#8a929c',
                  fontFamily: 'inherit'
                }}>
                  Transfiere múltiples leads de un comercial a otro de forma masiva.
                </p>
              </div>
              
              <button
                onClick={() => {
                  onClose();
                  onOpenTraspasoMasivo();
                }}
                className="fg-nav"
                style={{...botonPrimario, padding: '11px 20px'}}
              >
                🔄 Abrir Traspaso Masivo
              </button>
            </div>
          )}

          {activeSection === 'duplicados' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <h3 style={{ 
                  margin: '0 0 8px 0', 
                  fontSize: '16px', 
                  color: '#1f2a37',
                  fontWeight: 600,
                  fontFamily: 'inherit'
                }}>
                  🔍 Gestión de Duplicados
                </h3>
                <p style={{ 
                  margin: 0, 
                  fontSize: '13px', 
                  color: '#8a929c',
                  fontFamily: 'inherit'
                }}>
                  Detecta y elimina leads duplicados basándose en móvil+marca o email+marca.
                </p>
              </div>
              
              <button
                onClick={() => {
                  onClose();
                  onOpenGestionDuplicados();
                }}
                className="fg-nav"
                style={{...botonPeligro, padding: '11px 20px'}}
              >
                🔍 Abrir Gestión de Duplicados
              </button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

// Modal de Gestión de Duplicados
const GestionDuplicadosModal = ({ onClose, onRefresh }) => {
  const [duplicates, setDuplicates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadDuplicates();
  }, []);

  const loadDuplicates = async () => {
    setLoading(true);
    const duplicados = await findExistingDuplicates();
    setDuplicates(duplicados);
    setLoading(false);
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Eliminar este lead duplicado? Esta acción NO se puede deshacer.')) return;

    setDeleting(true);
    try {
      const response = await fetch(SUPABASE_URL + '/rest/v1/leads?id=eq.' + leadId, {
        method: 'DELETE',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': 'Bearer ' + SUPABASE_ANON_KEY
        }
      });

      if (!response.ok) throw new Error('Error al eliminar');

      console.log('Lead eliminado:', leadId);
      await loadDuplicates();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error:', error);
      alert('Error al eliminar: ' + error.message);
    } finally {
      setDeleting(false);
    }
  };

  const getTotalDuplicates = () => {
    return duplicates.reduce((sum, group) => sum + (group.count - 1), 0);
  };

  return (
    <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: VELO_MODAL, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px'}}>
      <div style={{background: '#fff', borderRadius: 12, width: '100%', maxWidth: '1200px', maxHeight: '90vh', display: 'flex', flexDirection: 'column'}}>
        <div style={{padding: '24px 32px', borderBottom: '1px solid #e7e9ec', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <div>
            <h2 style={{margin: 0, fontSize: '24px', fontWeight: 700}}>Gestión de Duplicados</h2>
            <p style={{margin: '4px 0 0 0', fontSize: '14px', color: '#8a929c'}}>
              {loading ? 'Analizando...' : duplicates.length + ' grupos (' + getTotalDuplicates() + ' duplicados)'}
            </p>
          </div>
          <button onClick={onClose} style={{background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer'}}>×</button>
        </div>

        <div style={{flex: 1, overflowY: 'auto', padding: '24px 32px'}}>
          {loading && (
            <div style={{padding: '12px'}}>
              {[0, 1, 2, 3].map(i => <div key={i} className="fg-shimmer" style={{height: 60, borderRadius: 12, marginBottom: 10}} />)}
            </div>
          )}

          {!loading && duplicates.length === 0 && (
            <EmptyState icon="✅" title="No hay duplicados" />
          )}

          {!loading && duplicates.length > 0 && (
            <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
              {duplicates.map((group, idx) => (
                <div key={idx} style={{border: '1px solid #e7e9ec', borderRadius: 12, overflow: 'hidden'}}>
                  <div style={{background: '#f4f5f7', padding: '12px 16px', borderBottom: '1px solid #e7e9ec'}}>
                    <span style={{fontWeight: 600}}>{group.type}: {group.key}</span>
                    <span style={{marginLeft: '12px', color: '#8a929c'}}>- Marca: {group.marca} - {group.count} leads</span>
                  </div>
                  <div style={{padding: '12px'}}>
                    {group.leads.map((lead, leadIdx) => (
                      <div key={lead.id} style={{
                        background: leadIdx === 0 ? '#f3faf6' : '#fff',
                        border: '1px solid ' + (leadIdx === 0 ? '#a6d7b8' : '#e7e9ec'),
                        borderRadius: 10,
                        padding: '12px',
                        marginBottom: leadIdx < group.leads.length - 1 ? '8px' : 0,
                        display: 'flex',
                        justifyContent: 'space-between'
                      }}>
                        <div>
                          {leadIdx === 0 && <span style={{background: '#2e9e5b', color: '#fff', padding: '2px 8px', borderRadius: 10, fontSize: '13px', marginRight: '8px'}}>MÁS RECIENTE</span>}
                          <span style={{fontWeight: 600}}>ID: {lead.id}</span>
                          <span style={{marginLeft: '8px'}}>{lead.nombre} {lead.apellidos}</span>
                          <div style={{fontSize: '14px', color: '#8a929c', marginTop: '4px'}}>
                            {lead.movil && <span>📱 {lead.movil} </span>}
                            {lead.email && <span>📧 {lead.email} </span>}
                            <span>👤 {lead.vendedor_nombre || 'Sin asignar'} </span>
                            <span>📊 {lead.estado_cita || 'Pendiente'}</span>
                          </div>
                        </div>
                        {leadIdx > 0 && (
                          <button
                            onClick={() => handleDeleteLead(lead.id)}
                            disabled={deleting}
                            style={{
                              background: '#fcf1f0',
                              color: '#d23b34',
                              border: '1px solid #f2ccca',
                              borderRadius: 10,
                              padding: '8px 16px',
                              cursor: deleting ? 'not-allowed' : 'pointer',
                              opacity: deleting ? 0.5 : 1
                            }}
                          >
                            Eliminar
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{padding: '16px 32px', borderTop: '1px solid #e7e9ec', display: 'flex', justifyContent: 'space-between'}}>
          <button onClick={loadDuplicates} disabled={loading} style={{background: '#f4f5f7', padding: '10px 20px', borderRadius: 10, border: '1px solid #e7e9ec', cursor: loading ? 'not-allowed' : 'pointer'}}>
            Recargar
          </button>
          <button onClick={onClose} style={{background: '#1a9cd4', color: '#fff', padding: '10px 24px', borderRadius: 10, border: 'none', cursor: 'pointer'}}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== MODAL CONFIGURACIÓN DEL SISTEMA ====================
const ConfiguracionModal = ({ onClose, configData, onUpdateConfig }) => {
  const [activeSection, setActiveSection] = useState('comerciales');
  const [localConfig, setLocalConfig] = useState(() => processConfigDataForModal(configData));
  const [newItem, setNewItem] = useState('');
  const [newComercial, setNewComercial] = useState({ nombre: '', email: '', password: '', centro: '' });
  const [newUsuario, setNewUsuario] = useState({ nombre: '', email: '', password: '', centro: '' });
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [comercialesList, setComercialesList] = useState([]);
  const [administradoresList, setAdministradoresList] = useState([]);

  // Cargar comerciales, administradores y configuración al montar
  useEffect(() => {
    const loadData = async () => {
      const comerciales = await fetchComerciales();
      const admins = await fetchAllAdministradores();
      setComercialesList(comerciales);
      setAdministradoresList(admins);
      
      // Cargar configuración desde Supabase
      const configRows = await fetchConfiguracion();
      console.log('📋 ConfigRows desde Supabase:', configRows);
      
      // Procesar configuración para actualizar variables globales
      if (configRows && configRows.length > 0) {
        processConfiguracion(configRows);
      }
      
      // Usar las variables globales ya actualizadas
      console.log('📊 Variables globales:', { CENTROS, MARCAS, ORIGENES, CAMPANAS });
      
      setLocalConfig({
        centros: [...CENTROS],
        marcas: [...MARCAS],
        origenes: ORIGENES.map(o => typeof o === 'object' ? o.nombre : o),
        campanas: [...CAMPANAS]
      });
    };
    loadData();
  }, []);

  const sections = [
    { id: 'comerciales', label: '👥 Comerciales', icon: '👥' },
    { id: 'centros', label: '🏢 Centros', icon: '🏢' },
    { id: 'marcas', label: '🚗 Datos Marcas', icon: '🚗' },
    { id: 'origenes', label: '📍 Origen Leads', icon: '📍' },
    { id: 'campanas', label: '📢 Campañas', icon: '📢' },
    { id: 'usuarios', label: '🔐 Usuarios', icon: '🔐' }
  ];

  const handleAddItem = async (section) => {
    if (!newItem.trim()) return;
    
    // Mapear sección a tipo de Supabase
    const tipoMap = {
      'centros': 'centro',
      'marcas': 'marca',
      'origenes': 'origen',
      'campanas': 'campana'
    };
    
    const tipo = tipoMap[section];
    if (tipo) {
      setSaving(true);
      const { error } = await insertConfiguracion(tipo, newItem.trim());
      if (error) {
        alert('Error al guardar: ' + error.message);
        setSaving(false);
        return;
      }
      
      // Recargar configuración desde Supabase
      const configRows = await fetchConfiguracion();
      processConfiguracion(configRows);
      
      // Actualizar estado local con las variables globales actualizadas
      setLocalConfig({
        centros: [...CENTROS],
        marcas: [...MARCAS],
        origenes: ORIGENES.map(o => typeof o === 'object' ? o.nombre : o),
        campanas: [...CAMPANAS]
      });
      
      console.log(`✅ ${tipo} guardado en Supabase`);
      setSaving(false);
    } else {
      setLocalConfig(prev => ({
        ...prev,
        [section]: [...(prev[section] || []), newItem.trim()]
      }));
    }
    setNewItem('');
  };

  const handleRemoveItem = async (section, index) => {
    const items = localConfig[section] || [];
    const itemToRemove = items[index];
    const valor = typeof itemToRemove === 'object' ? itemToRemove.nombre : itemToRemove;
    
    // Mapear sección a tipo de Supabase
    const tipoMap = {
      'centros': 'centro',
      'marcas': 'marca',
      'origenes': 'origen',
      'campanas': 'campana'
    };
    
    const tipo = tipoMap[section];
    if (tipo) {
      if (!window.confirm(`¿Eliminar "${valor}"?`)) return;
      
      setSaving(true);
      const { error } = await deleteConfiguracion(tipo, valor);
      if (error) {
        alert('Error al eliminar: ' + error.message);
        setSaving(false);
        return;
      }
      
      // Recargar configuración desde Supabase
      const configRows = await fetchConfiguracion();
      processConfiguracion(configRows);
      
      // Actualizar estado local con las variables globales actualizadas
      setLocalConfig({
        centros: [...CENTROS],
        marcas: [...MARCAS],
        origenes: ORIGENES.map(o => typeof o === 'object' ? o.nombre : o),
        campanas: [...CAMPANAS]
      });
      
      console.log(`✅ ${tipo} eliminado de Supabase`);
      setSaving(false);
    } else {
      setLocalConfig(prev => ({
        ...prev,
        [section]: prev[section].filter((_, i) => i !== index)
      }));
    }
  };

  // AÑADIR COMERCIAL A SUPABASE (con Auth)
  const handleAddComercial = async () => {
    if (!newComercial.nombre.trim() || !newComercial.email.trim()) {
      alert('El nombre y email son obligatorios');
      return;
    }
    if (!newComercial.password || newComercial.password.length < 6) {
      alert('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    setSaving(true);
    
    // 1. Crear usuario en Supabase Auth
    const { data: authData, error: authError } = await supabaseAuth.signUp(
      newComercial.email.trim().toLowerCase(),
      newComercial.password
    );
    
    if (authError) {
      alert('Error al crear credenciales: ' + authError);
      setSaving(false);
      return;
    }
    
    // 2. Insertar en tabla comerciales con auth_user_id
    const { data, error } = await insertComercial({
      nombre: newComercial.nombre.trim(),
      email: newComercial.email.trim().toLowerCase(),
      centro: newComercial.centro,
      auth_user_id: authData.user?.id || authData.id
    });
    
    if (error) {
      alert('Error al guardar comercial: ' + error.message);
    } else {
      const comerciales = await fetchComerciales();
      setComercialesList(comerciales);
      setNewComercial({ nombre: '', email: '', password: '', centro: '' });
      console.log('✅ Comercial creado con Auth + tabla');
    }
    setSaving(false);
  };

  // ELIMINAR COMERCIAL DE SUPABASE
  const handleRemoveComercial = async (comercial) => {
    if (!window.confirm(`¿Eliminar a ${comercial.nombre}?`)) return;
    
    setSaving(true);
    const { error } = await deleteComercial(comercial.id);
    
    if (error) {
      alert('Error al eliminar comercial: ' + error.message);
    } else {
      const comerciales = await fetchComerciales();
      setComercialesList(comerciales);
      console.log('✅ Comercial eliminado de Supabase');
    }
    setSaving(false);
  };

  // AÑADIR ADMINISTRADOR A SUPABASE (con Auth)
  const handleAddUsuario = async () => {
    if (!newUsuario.nombre.trim() || !newUsuario.email.trim()) {
      alert('Nombre y email son obligatorios');
      return;
    }
    if (!newUsuario.password || newUsuario.password.length < 6) {
      alert('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    setSaving(true);
    
    // 1. Crear usuario en Supabase Auth
    const { data: authData, error: authError } = await supabaseAuth.signUp(
      newUsuario.email.trim().toLowerCase(),
      newUsuario.password
    );
    
    if (authError) {
      alert('Error al crear credenciales: ' + authError);
      setSaving(false);
      return;
    }
    
    // 2. Insertar en tabla administradores con auth_user_id
    const { data, error } = await insertAdministrador({
      nombre: newUsuario.nombre.trim(),
      email: newUsuario.email.trim().toLowerCase(),
      auth_user_id: authData.user?.id || authData.id
    });
    
    if (error) {
      alert('Error al guardar administrador: ' + error.message);
    } else {
      const admins = await fetchAllAdministradores();
      setAdministradoresList(admins);
      setNewUsuario({ nombre: '', email: '', password: '', centro: '' });
      console.log('✅ Administrador creado con Auth + tabla');
    }
    setSaving(false);
  };

  // ELIMINAR ADMINISTRADOR DE SUPABASE
  const handleRemoveUsuario = async (admin) => {
    if (!window.confirm(`¿Eliminar a ${admin.nombre}?`)) return;
    
    setSaving(true);
    const { error } = await deleteAdministrador(admin.id);
    
    if (error) {
      alert('Error al eliminar administrador: ' + error.message);
    } else {
      const admins = await fetchAllAdministradores();
      setAdministradoresList(admins);
      console.log('✅ Administrador eliminado de Supabase');
    }
    setSaving(false);
  };

  const handleSave = () => {
    if (onUpdateConfig) onUpdateConfig(localConfig);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const inputStyle = {...campo, width: '100%', padding: '10px 12px'};
  const labelStyle = {display: 'block', fontSize: '12.5px', fontWeight: 700, color: C.muted, marginBottom: '5px'};
  const btnStyle = {padding: '10px 20px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 800, fontFamily: 'inherit'};
  const listItemStyle = {display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#f8f9fa', borderRadius: 10, marginBottom: '8px', border: '1px solid #e9ecef'};

  const renderSimpleList = (section, items, placeholder) => {
    const sectionNames = {
      'centros': 'Centros',
      'marcas': 'Marcas',
      'origenes': 'Orígenes',
      'campanas': 'Campañas'
    };
    
    return (
    <div>
      <div style={{background: '#e7f3ff', padding: '12px 16px', borderRadius: 10, marginBottom: '20px', border: '1px solid #b3d7ff'}}>
        <span style={{fontSize: '14px', color: '#004085'}}>💾 Los cambios se guardan directamente en Supabase.</span>
      </div>
      <div style={{display: 'flex', gap: '10px', marginBottom: '20px'}}>
        <input 
          style={{...inputStyle, flex: 1}} 
          placeholder={placeholder}
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleAddItem(section)}
        />
        <button onClick={() => handleAddItem(section)} disabled={saving} style={{...btnStyle, background: saving ? '#ccc' : '#228B22', color: 'white'}}>
          {saving ? '⏳' : '➕'} Añadir
        </button>
      </div>
      <h4 style={{margin: '0 0 15px 0', fontSize: '14px', color: '#2b333d'}}>{sectionNames[section]} en Supabase ({items.length})</h4>
      <div style={{maxHeight: '400px', overflowY: 'auto'}}>
        {items.map((item, idx) => (
          <div key={idx} style={listItemStyle}>
            <span style={{color: '#2b333d', fontSize: '13px'}}>{typeof item === 'object' ? item.nombre : item}</span>
            <button onClick={() => handleRemoveItem(section, idx)} disabled={saving} style={{background: '#d23b34', color: 'white', border: 'none', borderRadius: 10, padding: '6px 12px', cursor: 'pointer', fontSize: '14px', opacity: saving ? 0.5 : 1}}>
              🗑️ Eliminar
            </button>
          </div>
        ))}
        {items.length === 0 && (
          <EmptyState icon="🗂️" title="No hay elementos configurados en Supabase" />
        )}
      </div>
    </div>
  );
  };

  const renderComercialesSection = () => (
    <div>
      <div style={{background: '#e7f3ff', padding: '12px 16px', borderRadius: 10, marginBottom: '20px', border: '1px solid #b3d7ff'}}>
        <span style={{fontSize: '14px', color: '#004085'}}>👥 Los comerciales configurados aquí tienen acceso a la App de Comerciales y se guardan en Supabase.</span>
      </div>
      <div style={{background: '#f8f9fa', padding: '20px', borderRadius: 10, marginBottom: '20px', border: '1px solid #e9ecef'}}>
        <h4 style={{margin: '0 0 15px 0', fontSize: '14px', color: '#2b333d'}}>Añadir nuevo comercial</h4>
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '15px'}}>
          <div>
            <label style={labelStyle}>Nombre del comercial *</label>
            <input style={inputStyle} placeholder="Ej: Juan García" value={newComercial.nombre} onChange={(e) => setNewComercial({...newComercial, nombre: e.target.value})} />
          </div>
          <div>
            <label style={labelStyle}>Email *</label>
            <input style={inputStyle} type="email" placeholder="email@dimoauto.com" value={newComercial.email} onChange={(e) => setNewComercial({...newComercial, email: e.target.value})} />
          </div>
          <div>
            <label style={labelStyle}>Contraseña *</label>
            <input style={inputStyle} type="password" placeholder="Mínimo 6 caracteres" value={newComercial.password} onChange={(e) => setNewComercial({...newComercial, password: e.target.value})} />
          </div>
          <div>
            <label style={labelStyle}>Centro asignado</label>
            <select style={inputStyle} value={newComercial.centro} onChange={(e) => setNewComercial({...newComercial, centro: e.target.value})}>
              <option value="">Seleccionar centro...</option>
              {(localConfig.centros || CENTROS).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <button onClick={handleAddComercial} disabled={saving} style={{...btnStyle, background: saving ? '#ccc' : '#228B22', color: 'white'}}>
          {saving ? '⏳ Guardando...' : '➕ Añadir Comercial'}
        </button>
      </div>
      <h4 style={{margin: '0 0 15px 0', fontSize: '14px', color: '#2b333d'}}>Comerciales en Supabase ({comercialesList.length})</h4>
      <div style={{maxHeight: '350px', overflowY: 'auto'}}>
        {comercialesList.map((comercial) => (
          <div key={comercial.id} style={listItemStyle}>
            <div>
              <div style={{color: '#2b333d', fontSize: '13px', fontWeight: 500}}>{comercial.nombre}</div>
              <div style={{color: '#8a929c', fontSize: '13px', marginTop: '2px'}}>
                {comercial.email} • {comercial.centro || 'Sin centro'}
                {!comercial.activo && <span style={{color: '#d23b34', marginLeft: '8px'}}>⚠️ Inactivo</span>}
              </div>
            </div>
            <button onClick={() => handleRemoveComercial(comercial)} disabled={saving} style={{background: '#d23b34', color: 'white', border: 'none', borderRadius: 10, padding: '6px 12px', cursor: 'pointer', fontSize: '14px', opacity: saving ? 0.5 : 1}}>
              🗑️ Eliminar
            </button>
          </div>
        ))}
        {comercialesList.length === 0 && (
          <EmptyState icon="👥" title="No hay comerciales en Supabase" />
        )}
      </div>
    </div>
  );

  const renderUsuariosSection = () => (
    <div>
      <div style={{background: '#d4edda', padding: '12px 16px', borderRadius: 10, marginBottom: '20px', border: '1px solid #c3e6cb'}}>
        <span style={{fontSize: '14px', color: '#155724'}}>🔐 Los usuarios configurados aquí tienen acceso al panel de Administración y se guardan en Supabase.</span>
      </div>
      <div style={{background: '#f8f9fa', padding: '20px', borderRadius: 10, marginBottom: '20px', border: '1px solid #e9ecef'}}>
        <h4 style={{margin: '0 0 15px 0', fontSize: '14px', color: '#2b333d'}}>Añadir nuevo usuario administrador</h4>
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '15px'}}>
          <div>
            <label style={labelStyle}>Nombre *</label>
            <input style={inputStyle} placeholder="Nombre completo" value={newUsuario.nombre} onChange={(e) => setNewUsuario({...newUsuario, nombre: e.target.value})} />
          </div>
          <div>
            <label style={labelStyle}>Email *</label>
            <input style={inputStyle} type="email" placeholder="email@dimoauto.com" value={newUsuario.email} onChange={(e) => setNewUsuario({...newUsuario, email: e.target.value})} />
          </div>
          <div>
            <label style={labelStyle}>Contraseña *</label>
            <input style={inputStyle} type="password" placeholder="Mínimo 6 caracteres" value={newUsuario.password} onChange={(e) => setNewUsuario({...newUsuario, password: e.target.value})} />
          </div>
          <div>
            <label style={labelStyle}>Centro</label>
            <select style={inputStyle} value={newUsuario.centro} onChange={(e) => setNewUsuario({...newUsuario, centro: e.target.value})}>
              <option value="">Seleccionar centro...</option>
              {(localConfig.centros || CENTROS).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <button onClick={handleAddUsuario} disabled={saving} style={{...btnStyle, background: saving ? '#ccc' : '#228B22', color: 'white'}}>
          {saving ? '⏳ Guardando...' : '➕ Añadir Usuario'}
        </button>
      </div>
      <h4 style={{margin: '0 0 15px 0', fontSize: '14px', color: '#2b333d'}}>Administradores en Supabase ({administradoresList.length})</h4>
      <div style={{maxHeight: '300px', overflowY: 'auto'}}>
        {administradoresList.map((admin) => (
          <div key={admin.id} style={listItemStyle}>
            <div>
              <div style={{color: '#2b333d', fontSize: '13px', fontWeight: 500}}>{admin.nombre}</div>
              <div style={{color: '#8a929c', fontSize: '13px', marginTop: '2px'}}>
                {admin.email}
                {!admin.activo && <span style={{color: '#d23b34', marginLeft: '8px'}}>⚠️ Inactivo</span>}
              </div>
            </div>
            <button onClick={() => handleRemoveUsuario(admin)} disabled={saving} style={{background: '#d23b34', color: 'white', border: 'none', borderRadius: 10, padding: '6px 12px', cursor: 'pointer', fontSize: '14px', opacity: saving ? 0.5 : 1}}>
              🗑️ Eliminar
            </button>
          </div>
        ))}
        {administradoresList.length === 0 && (
          <EmptyState icon="🛡️" title="No hay administradores en Supabase" />
        )}
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'comerciales':
        return renderComercialesSection();
      case 'centros':
        return renderSimpleList('centros', localConfig.centros || [], 'Nombre del centro o punto de venta...');
      case 'marcas':
        return renderSimpleList('marcas', localConfig.marcas || [], 'Nombre de la marca...');
      case 'origenes':
        return renderSimpleList('origenes', localConfig.origenes || [], 'Origen del lead (ej: Facebook, Web...)');
      case 'campanas':
        return renderSimpleList('campanas', localConfig.campanas || [], 'Nombre de la campaña...');
      case 'usuarios':
        return renderUsuariosSection();
      default:
        return null;
    }
  };

  return (
    <Modal onClose={onClose} title="⚙️ Configuración del Sistema" width="1000px">
      {saved && (
        <div style={{background: '#d4edda', color: '#155724', padding: '12px 16px', borderRadius: 10, marginBottom: '20px', border: '1px solid #c3e6cb', display: 'flex', alignItems: 'center', gap: '10px'}}>
          <span>✅</span> Configuración guardada correctamente
        </div>
      )}
      <div style={{display: 'flex', gap: '24px'}}>
        {/* Sidebar */}
        <div style={{width: '220px', flexShrink: 0}}>
          <div style={{background: '#f8f9fa', borderRadius: 10, overflow: 'hidden', border: '1px solid #e9ecef'}}>
            {sections.map(section => (
              <div 
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                style={{
                  padding: '14px 16px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: activeSection === section.id ? '#005187' : '#2b333d',
                  background: activeSection === section.id ? '#fff' : 'transparent',
                  borderLeft: activeSection === section.id ? '3px solid #005187' : '3px solid transparent',
                  fontWeight: activeSection === section.id ? 600 : 400,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  borderBottom: '1px solid #e9ecef'
                }}
                className="fg-nav"
              >
                <span>{section.icon}</span>
                {section.label.replace(section.icon + ' ', '')}
              </div>
            ))}
          </div>
        </div>
        
        {/* Content */}
        <div style={{flex: 1, minHeight: '500px'}}>
          <div style={{marginBottom: '20px', paddingBottom: '15px', borderBottom: `1px solid ${C.line}`}}>
            <h3 style={{margin: 0, fontSize: '18px', color: '#2b333d'}}>
              {sections.find(s => s.id === activeSection)?.label}
            </h3>
            <p style={{margin: '8px 0 0 0', fontSize: '14px', color: '#8a929c'}}>
              {activeSection === 'comerciales' && 'Gestiona los comerciales y su asignación a centros. Se guardan en Supabase.'}
              {activeSection === 'centros' && 'Configura los centros o puntos de venta disponibles.'}
              {activeSection === 'marcas' && 'Define las marcas de vehículos que gestiona el concesionario.'}
              {activeSection === 'origenes' && 'Establece los orígenes posibles de los leads.'}
              {activeSection === 'campanas' && 'Administra las campañas de marketing activas.'}
              {activeSection === 'usuarios' && 'Gestiona los usuarios con acceso al panel de Administración. Se guardan en Supabase.'}
            </p>
          </div>
          {renderContent()}
        </div>
      </div>
      
      {/* Footer */}
      <div style={{display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #e9ecef'}}>
        <button onClick={onClose} className="fg-nav" style={{...btnStyle, ...botonSecundario}}>
          Cerrar
        </button>
        <button onClick={handleSave} style={{...btnStyle, ...botonPrimario}}>
          💾 Guardar Otros Cambios
        </button>
      </div>
    </Modal>
  );
};

// Nuevo Lead Modal
const NuevoLeadModal = ({ onClose, onAddLead }) => {
  const [formData, setFormData] = useState({ tipo: 'Lead Válido', origen: '', nombre: '', apellidos: '', telefono: '', movil: '', email: '', marca: '', modelo: '', matricula: '', vendedor: '', centro: '', tipoCliente: 'Particular', tipoVenta: 'VN' });
  const [saved, setSaved] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleSubmit = async (e) => { 
    e.preventDefault(); 
    if (isSubmitting) return;
    
    // Validar campos obligatorios
    if (!formData.origen) {
      alert('⚠️ El campo Origen es obligatorio');
      return;
    }
    if (!formData.movil) {
      alert('⚠️ El campo Móvil es obligatorio');
      return;
    }
    if (!formData.marca) {
      alert('⚠️ El campo Marca es obligatorio');
      return;
    }
    if (!formData.centro) {
      alert('⚠️ El campo Centro es obligatorio');
      return;
    }
    
    setIsSubmitting(true);
    const result = await onAddLead(formData);
    setIsSubmitting(false);
    
    if (result !== false) {
      setSaved(true); 
      setTimeout(() => { setSaved(false); onClose(); }, 1500); 
    }
  };
  
  const inputStyle = {...campo, width: '100%', padding: '9px 11px'};
  const labelStyle = {display: 'block', fontSize: '12.5px', fontWeight: 700, color: C.muted, marginBottom: '5px'};
  const sectionStyle = {background: '#fbfcfd', borderRadius: 10, padding: '16px', marginBottom: '16px', border: '1px solid #e7e9ec'};
  return (
    <Modal onClose={onClose} title="Nuevo Lead" width="900px">
      {saved && <div style={{background: '#4CAF50', color: 'white', padding: '12px', borderRadius: 10, marginBottom: '15px', textAlign: 'center', fontWeight: 500}}>✓ Lead guardado correctamente</div>}
      <form onSubmit={handleSubmit}>
        <div style={sectionStyle}>
          <h4 style={{margin: '0 0 12px 0', fontSize: '13px', color: '#005187', borderBottom: `1px solid ${C.line}`, paddingBottom: '5px'}}>Información del Lead</h4>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px'}}>
            <div><label style={labelStyle}>Tipo de Lead</label><select style={inputStyle} value={formData.tipo} onChange={e => setFormData({...formData, tipo: e.target.value})}>{Object.keys(LEAD_TIPOS).map(t => <option key={t}>{t}</option>)}</select></div>
            <div><label style={labelStyle}>Origen *</label><select style={inputStyle} value={formData.origen} onChange={e => setFormData({...formData, origen: e.target.value})}><option value="">Seleccionar...</option>{ORIGENES.map(o => <option key={o.nombre}>{o.nombre}</option>)}</select></div>
            <div><label style={labelStyle}>Tipo Venta</label><select style={inputStyle} value={formData.tipoVenta} onChange={e => setFormData({...formData, tipoVenta: e.target.value})}>{TIPOS_VENTA.map(t => <option key={t}>{t}</option>)}</select></div>
            <div><label style={labelStyle}>Tipo Cliente</label><select style={inputStyle} value={formData.tipoCliente} onChange={e => setFormData({...formData, tipoCliente: e.target.value})}>{TIPOS_CLIENTE.map(t => <option key={t}>{t}</option>)}</select></div>
          </div>
        </div>
        <div style={sectionStyle}>
          <h4 style={{margin: '0 0 12px 0', fontSize: '13px', color: '#005187', borderBottom: `1px solid ${C.line}`, paddingBottom: '5px'}}>Datos del Cliente</h4>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px'}}>
            <div><label style={labelStyle}>Nombre</label><input style={inputStyle} value={formData.nombre} onChange={e => setFormData({...formData, nombre: e.target.value})} /></div>
            <div><label style={labelStyle}>Apellidos</label><input style={inputStyle} value={formData.apellidos} onChange={e => setFormData({...formData, apellidos: e.target.value})} /></div>
            <div><label style={labelStyle}>Teléfono Fijo</label><input style={inputStyle} value={formData.telefono} onChange={e => setFormData({...formData, telefono: e.target.value})} /></div>
            <div><label style={labelStyle}>Móvil *</label><input style={inputStyle} value={formData.movil} onChange={e => setFormData({...formData, movil: e.target.value})} /></div>
            <div style={{gridColumn: 'span 2'}}><label style={labelStyle}>Email</label><input style={inputStyle} type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} /></div>
          </div>
        </div>
        <div style={sectionStyle}>
          <h4 style={{margin: '0 0 12px 0', fontSize: '13px', color: '#005187', borderBottom: `1px solid ${C.line}`, paddingBottom: '5px'}}>Vehículo y Asignación</h4>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px'}}>
            <div><label style={labelStyle}>Marca *</label><select style={inputStyle} value={formData.marca} onChange={e => setFormData({...formData, marca: e.target.value})}><option value="">Seleccionar...</option>{MARCAS.map(m => <option key={m}>{m}</option>)}</select></div>
            <div><label style={labelStyle}>Modelo</label><input style={inputStyle} value={formData.modelo} onChange={e => setFormData({...formData, modelo: e.target.value})} /></div>
            <div><label style={labelStyle}>Matrícula</label><input style={inputStyle} value={formData.matricula} onChange={e => setFormData({...formData, matricula: e.target.value.toUpperCase()})} placeholder="Ej: 1234ABC" /></div>
            <div><label style={labelStyle}>Vendedor</label><select style={inputStyle} value={formData.vendedor} onChange={e => setFormData({...formData, vendedor: e.target.value})}><option value="">Seleccionar...</option>{VENDEDORES.map(v => <option key={v}>{v}</option>)}</select></div>
            <div><label style={labelStyle}>Centro *</label><select style={{...inputStyle, borderColor: !formData.centro ? '#d23b34' : '#ccc'}} value={formData.centro} onChange={e => setFormData({...formData, centro: e.target.value})}><option value="">Seleccionar...</option>{CENTROS.map(c => <option key={c}>{c}</option>)}</select></div>
          </div>
        </div>
        <div style={{display: 'flex', gap: '12px', justifyContent: 'space-between', alignItems: 'center'}}>
          <span style={{fontSize: '13px', color: '#8a929c'}}>* Campos obligatorios</span>
          <div style={{display: 'flex', gap: '12px'}}>
            <button type="button" onClick={onClose} style={{padding: '10px 24px', background: '#8a929c', border: 'none', borderRadius: 10, color: 'white', cursor: 'pointer', fontSize: '13px'}}>Cancelar</button>
            <button type="submit" disabled={isSubmitting} style={{padding: '10px 24px', background: isSubmitting ? '#9aa4af' : '#228B22', border: 'none', borderRadius: 10, color: 'white', cursor: isSubmitting ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600}}>{isSubmitting ? '⏳ Verificando...' : '💾 Guardar Lead'}</button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

// ==================== FLOATING BUTTONS PARA ADMIN ====================
// ==================== CHAT INTERNO ADMIN ↔ COMERCIAL ====================
const fmtChatHora = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const hoy = new Date();
  const ayer = new Date(); ayer.setDate(ayer.getDate() - 1);
  const sameDay = d.toDateString() === hoy.toDateString();
  const isYest = d.toDateString() === ayer.toDateString();
  if (sameDay) return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  if (isYest) return 'Ayer ' + d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

// Hook reusable de contador no leídos
const useChatUnread = (email) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!email) return undefined;
    let alive = true;
    const fetchUnread = async () => {
      try {
        const r = await fetch(
          `${SUPABASE_URL}/rest/v1/chat_mensajes?destinatario_email=eq.${encodeURIComponent(email)}&leido=eq.false&select=id`,
          { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` } }
        );
        if (r.ok && alive) {
          const data = await r.json();
          setCount(Array.isArray(data) ? data.length : 0);
        }
      } catch (_) { /* silencio */ }
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 12000);
    return () => { alive = false; clearInterval(id); };
  }, [email]);
  return count;
};

const ChatAdminPanel = ({ admin, comerciales }) => {
  const [seleccionado, setSeleccionado] = useState(null);
  const [todos, setTodos] = useState([]);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  // ---- Broadcast (mensaje a varios comerciales a la vez) ----
  const [modoBroadcast, setModoBroadcast] = useState(false);
  const [seleccionadosBroadcast, setSeleccionadosBroadcast] = useState(new Set());
  const [textoBroadcast, setTextoBroadcast] = useState('');
  const [enviandoBroadcast, setEnviandoBroadcast] = useState(false);
  const [progresoBroadcast, setProgresoBroadcast] = useState({ done: 0, total: 0 });
  const messagesEndRef = useRef(null);

  const adminEmail = admin?.email || '';
  const adminNombre = admin?.nombre || 'Administración';

  const cargar = async () => {
    if (!adminEmail) return;
    try {
      const url = `${SUPABASE_URL}/rest/v1/chat_mensajes?or=(emisor_email.eq.${encodeURIComponent(adminEmail)},destinatario_email.eq.${encodeURIComponent(adminEmail)})&order=fecha_envio.asc&limit=2000`;
      const r = await fetch(url, { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` } });
      if (r.ok) setTodos(await r.json());
    } catch (_) { /* silencio */ }
  };

  useEffect(() => {
    cargar();
    const id = setInterval(cargar, 8000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminEmail]);

  // Auto-marcar leídos al abrir conversación con un comercial
  useEffect(() => {
    if (!seleccionado || !adminEmail) return;
    const noLeidos = todos.filter(m =>
      m.emisor_email === seleccionado.email &&
      m.destinatario_email === adminEmail &&
      !m.leido
    );
    if (noLeidos.length === 0) return;
    Promise.all(noLeidos.map(m =>
      fetch(`${SUPABASE_URL}/rest/v1/chat_mensajes?id=eq.${m.id}`, {
        method: 'PATCH',
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ leido: true, fecha_leido: new Date().toISOString() })
      })
    )).then(() => cargar()).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seleccionado, todos.length]);

  // Mensajes de la conversación seleccionada
  const mensajesConv = useMemo(() => {
    if (!seleccionado) return [];
    return todos.filter(m =>
      (m.emisor_email === seleccionado.email && m.destinatario_email === adminEmail) ||
      (m.emisor_email === adminEmail && m.destinatario_email === seleccionado.email)
    );
  }, [todos, seleccionado, adminEmail]);

  useEffect(() => {
    if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [mensajesConv.length]);

  // Map de unread por email del comercial (mensajes recibidos por admin no leídos)
  const unreadByEmail = useMemo(() => {
    const m = {};
    todos.forEach(x => {
      if (x.destinatario_email === adminEmail && !x.leido) {
        m[x.emisor_email] = (m[x.emisor_email] || 0) + 1;
      }
    });
    return m;
  }, [todos, adminEmail]);

  // Map de último mensaje por email del comercial (para preview)
  const lastByEmail = useMemo(() => {
    const m = {};
    todos.forEach(x => {
      const otro = x.emisor_email === adminEmail ? x.destinatario_email : x.emisor_email;
      const prev = m[otro];
      if (!prev || new Date(x.fecha_envio) > new Date(prev.fecha_envio)) m[otro] = x;
    });
    return m;
  }, [todos, adminEmail]);

  const enviar = async () => {
    if (!texto.trim() || !seleccionado || enviando || !adminEmail) return;
    setEnviando(true);
    try {
      await fetch(`${SUPABASE_URL}/rest/v1/chat_mensajes`, {
        method: 'POST',
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          emisor_tipo: 'admin',
          emisor_email: adminEmail,
          emisor_nombre: adminNombre,
          destinatario_tipo: 'comercial',
          destinatario_email: seleccionado.email,
          destinatario_nombre: seleccionado.nombre,
          mensaje: texto.trim()
        })
      });
      setTexto('');
      await cargar();
    } catch (e) {
      secureLog.error('ChatAdminPanel.enviar', e);
    } finally {
      setEnviando(false);
    }
  };

  const enviarBroadcast = async (destinatarios) => {
    const texto = textoBroadcast.trim();
    if (!texto || !adminEmail || destinatarios.length === 0 || enviandoBroadcast) return;
    if (!window.confirm(`¿Enviar este mensaje a ${destinatarios.length} comercial${destinatarios.length === 1 ? '' : 'es'}?`)) return;
    setEnviandoBroadcast(true);
    setProgresoBroadcast({ done: 0, total: destinatarios.length });
    let ok = 0, ko = 0;
    for (let i = 0; i < destinatarios.length; i++) {
      const c = destinatarios[i];
      try {
        const r = await fetch(`${SUPABASE_URL}/rest/v1/chat_mensajes`, {
          method: 'POST',
          headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            emisor_tipo: 'admin',
            emisor_email: adminEmail,
            emisor_nombre: adminNombre,
            destinatario_tipo: 'comercial',
            destinatario_email: c.email,
            destinatario_nombre: c.nombre,
            mensaje: texto
          })
        });
        if (r.ok) ok++; else ko++;
      } catch (_) { ko++; }
      setProgresoBroadcast({ done: i + 1, total: destinatarios.length });
    }
    setEnviandoBroadcast(false);
    setTextoBroadcast('');
    setSeleccionadosBroadcast(new Set());
    setModoBroadcast(false);
    await cargar();
    window.alert(`Enviado a ${ok} comercial${ok === 1 ? '' : 'es'}.${ko > 0 ? ` ${ko} fallaron.` : ''}`);
  };

  const CHAT_EXCLUIDOS = new Set(['Álvaro Bragulat', 'Edgar Sanchez']);
  const comercialesActivos = (comerciales || [])
    .filter(c => c.activo !== false && c.email && !CHAT_EXCLUIDOS.has(c.nombre))
    .sort((a, b) => {
      const ua = unreadByEmail[a.email] || 0;
      const ub = unreadByEmail[b.email] || 0;
      if (ua !== ub) return ub - ua;
      const la = lastByEmail[a.email]?.fecha_envio;
      const lb = lastByEmail[b.email]?.fecha_envio;
      if (la && lb) return new Date(lb) - new Date(la);
      if (la) return -1;
      if (lb) return 1;
      return String(a.nombre || '').localeCompare(String(b.nombre || ''));
    });

  return (
    <div style={{display: 'flex', height: '100%', background: '#fff', fontFamily: 'inherit'}}>
      <div style={{width: '260px', borderRight: '1px solid #e7e9ec', overflowY: 'auto', background: '#f4f5f7'}}>
        <div style={{padding: '10px 12px', fontSize: '11px', fontWeight: 700, color: '#8a929c', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #e7e9ec', background: '#fff', position: 'sticky', top: 0, zIndex: 1}}>
          Comerciales ({comercialesActivos.length})
        </div>
        {/* Botón broadcast / cancelar */}
        <div style={{padding: '8px 10px', borderBottom: '1px solid #e7e9ec', background: '#fff'}}>
          {!modoBroadcast ? (
            <button
              onClick={() => { setModoBroadcast(true); setSeleccionado(null); setSeleccionadosBroadcast(new Set()); }}
              style={{
                width: '100%',
                padding: '8px 10px',
                background: '#e08c1e',
                color: '#fff',
                border: 'none',
                borderRadius: 10,
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}
            >
              📢 Mensaje a varios / todos
            </button>
          ) : (
            <div style={{display: 'flex', gap: '6px'}}>
              <button
                onClick={() => {
                  if (seleccionadosBroadcast.size === comercialesActivos.length) {
                    setSeleccionadosBroadcast(new Set());
                  } else {
                    setSeleccionadosBroadcast(new Set(comercialesActivos.map(c => c.email)));
                  }
                }}
                style={{flex: 1, padding: '7px 8px', background: '#005187', color: '#fff', border: 'none', borderRadius: 10, fontSize: '11px', fontWeight: 600, cursor: 'pointer'}}
              >
                {seleccionadosBroadcast.size === comercialesActivos.length ? 'Quitar todos' : 'Todos'}
              </button>
              <button
                onClick={() => { setModoBroadcast(false); setSeleccionadosBroadcast(new Set()); setTextoBroadcast(''); }}
                style={{flex: 1, padding: '7px 8px', background: '#fff', color: '#2b333d', border: '1px solid #e7e9ec', borderRadius: 10, fontSize: '11px', fontWeight: 600, cursor: 'pointer'}}
              >
                Cancelar
              </button>
            </div>
          )}
        </div>
        {comercialesActivos.length === 0 && (
          <div style={{padding: '16px', fontSize: '13px', color: '#9aa4af', textAlign: 'center'}}>Sin comerciales activos</div>
        )}
        {comercialesActivos.map(c => {
          const unread = unreadByEmail[c.email] || 0;
          const isSel = seleccionado?.email === c.email;
          const isBroadcastSel = seleccionadosBroadcast.has(c.email);
          const last = lastByEmail[c.email];
          const onItemClick = () => {
            if (modoBroadcast) {
              const next = new Set(seleccionadosBroadcast);
              if (next.has(c.email)) next.delete(c.email); else next.add(c.email);
              setSeleccionadosBroadcast(next);
            } else {
              setSeleccionado(c);
            }
          };
          return (
            <div
              key={c.id}
              onClick={onItemClick}
              style={{
                padding: '10px 12px',
                borderBottom: '1px solid #f4f5f7',
                cursor: 'pointer',
                background: modoBroadcast
                  ? (isBroadcastSel ? '#fbf1de' : 'transparent')
                  : (isSel ? '#dceaf4' : (unread > 0 ? '#fdf8ef' : 'transparent')),
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {modoBroadcast && (
                <input
                  type="checkbox"
                  checked={isBroadcastSel}
                  onChange={() => {}}
                  style={{cursor: 'pointer', flexShrink: 0}}
                />
              )}
              <div style={{flex: 1, minWidth: 0}}>
                <div style={{fontSize: '13px', fontWeight: isSel || unread || isBroadcastSel ? 700 : 500, color: '#2b333d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                  {c.nombre}
                </div>
                <div style={{fontSize: '11px', color: '#8a929c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                  {last ? (last.emisor_email === adminEmail ? 'Tú: ' : '') + (last.mensaje || '') : (c.centro || c.email)}
                </div>
              </div>
              {!modoBroadcast && unread > 0 && (
                <span style={{background: '#d23b34', color: 'white', fontSize: '11px', fontWeight: 700, padding: '2px 7px', borderRadius: 10, minWidth: '20px', textAlign: 'center'}}>
                  {unread}
                </span>
              )}
            </div>
          );
        })}
      </div>
      <div style={{flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0}}>
        {modoBroadcast ? (
          <>
            <div style={{padding: '12px 16px', borderBottom: '1px solid #e7e9ec', background: '#fff7ed', color: '#a8660f', fontSize: '14px', fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
              <div>📢 Envío masivo</div>
              <div style={{fontSize: '12px', color: '#a8660f', fontWeight: 500}}>
                {seleccionadosBroadcast.size} comercial{seleccionadosBroadcast.size === 1 ? '' : 'es'} seleccionado{seleccionadosBroadcast.size === 1 ? '' : 's'}
              </div>
            </div>
            <div style={{flex: 1, overflowY: 'auto', padding: '20px', background: '#f4f5f7'}}>
              {seleccionadosBroadcast.size === 0 ? (
                <div style={{textAlign: 'center', color: '#9aa4af', fontSize: '13px', padding: '40px 20px'}}>
                  <div style={{fontSize: '40px', marginBottom: '10px'}}>👈</div>
                  <div>Selecciona uno o más comerciales en la lista de la izquierda.</div>
                  <div style={{fontSize: '12px', marginTop: '6px'}}>Pulsa "Todos" para enviar a los {comercialesActivos.length} a la vez.</div>
                </div>
              ) : (
                <div>
                  <div style={{fontSize: '12px', color: '#2b333d', marginBottom: '8px', fontWeight: 600}}>
                    Destinatarios ({seleccionadosBroadcast.size}):
                  </div>
                  <div style={{display: 'flex', flexWrap: 'wrap', gap: '6px', maxHeight: '160px', overflowY: 'auto', padding: '8px', background: '#fff', border: '1px solid #e7e9ec', borderRadius: 10}}>
                    {comercialesActivos.filter(c => seleccionadosBroadcast.has(c.email)).map(c => (
                      <span key={c.id} style={{padding: '4px 10px', background: '#fbf1de', color: '#a8660f', border: '1px solid #f5dfae', borderRadius: 12, fontSize: '12px', fontWeight: 500}}>
                        {c.nombre}
                      </span>
                    ))}
                  </div>
                  {enviandoBroadcast && (
                    <div style={{marginTop: '14px', padding: '10px', background: '#fff', border: '1px solid #e7e9ec', borderRadius: 10}}>
                      <div style={{fontSize: '12px', color: '#2b333d', marginBottom: '6px'}}>
                        Enviando... {progresoBroadcast.done}/{progresoBroadcast.total}
                      </div>
                      <div style={{height: '6px', background: '#e7e9ec', borderRadius: '3px', overflow: 'hidden'}}>
                        <div style={{height: '100%', width: `${progresoBroadcast.total > 0 ? (progresoBroadcast.done / progresoBroadcast.total) * 100 : 0}%`, background: '#005187', transition: 'width 0.2s'}} />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div style={{padding: '10px 12px', borderTop: '1px solid #e7e9ec', display: 'flex', gap: '8px', background: '#fff'}}>
              <textarea
                value={textoBroadcast}
                onChange={e => setTextoBroadcast(e.target.value)}
                placeholder={seleccionadosBroadcast.size === 0 ? 'Primero selecciona destinatarios...' : `Mensaje a ${seleccionadosBroadcast.size} comercial${seleccionadosBroadcast.size === 1 ? '' : 'es'}...`}
                rows={1}
                disabled={enviandoBroadcast}
                style={{flex: 1, border: '1px solid #e7e9ec', borderRadius: 10, padding: '10px 12px', fontSize: '13px', resize: 'none', fontFamily: 'inherit', minHeight: '40px', maxHeight: '120px'}}
              />
              <button
                onClick={() => enviarBroadcast(comercialesActivos.filter(c => seleccionadosBroadcast.has(c.email)))}
                disabled={enviandoBroadcast || !textoBroadcast.trim() || seleccionadosBroadcast.size === 0}
                style={{
                  padding: '8px 18px',
                  background: enviandoBroadcast || !textoBroadcast.trim() || seleccionadosBroadcast.size === 0 ? '#9aa4af' : '#e08c1e',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 10,
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: enviandoBroadcast || !textoBroadcast.trim() || seleccionadosBroadcast.size === 0 ? 'not-allowed' : 'pointer'
                }}
              >
                {enviandoBroadcast ? '...' : `Enviar a ${seleccionadosBroadcast.size}`}
              </button>
            </div>
          </>
        ) : !seleccionado ? (
          <div style={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9aa4af', fontSize: '14px', flexDirection: 'column', gap: '8px', textAlign: 'center', padding: '20px'}}>
            <div style={{fontSize: '40px'}}>💬</div>
            <div>Selecciona un comercial de la izquierda para empezar a chatear</div>
          </div>
        ) : (
          <>
            <div style={{padding: '12px 16px', borderBottom: '1px solid #e7e9ec', background: '#fff', fontSize: '14px', fontWeight: 600, color: '#2b333d', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
              <div>💬 {seleccionado.nombre}</div>
              <div style={{fontSize: '11px', color: '#8a929c', fontWeight: 400}}>{seleccionado.email}</div>
            </div>
            <div style={{flex: 1, overflowY: 'auto', padding: '12px', background: '#f4f5f7'}}>
              {mensajesConv.length === 0 ? (
                <div style={{textAlign: 'center', color: '#9aa4af', fontSize: '13px', padding: '40px 20px'}}>Sin mensajes aún. Empieza la conversación.</div>
              ) : mensajesConv.map(m => {
                const isMine = m.emisor_email === adminEmail;
                return (
                  <div key={m.id} style={{display: 'flex', justifyContent: isMine ? 'flex-end' : 'flex-start', marginBottom: '8px'}}>
                    <div style={{
                      maxWidth: '75%',
                      background: isMine ? '#005187' : '#fff',
                      color: isMine ? '#fff' : '#2b333d',
                      padding: '8px 12px',
                      borderRadius: 12,
                      fontSize: '13px',
                      lineHeight: '1.4',
                      boxShadow: 'none',
                      border: isMine ? 'none' : '1px solid #e7e9ec',
                      wordBreak: 'break-word'
                    }}>
                      <div style={{whiteSpace: 'pre-wrap'}}>{m.mensaje}</div>
                      <div style={{fontSize: '10px', opacity: 0.7, marginTop: '4px', textAlign: 'right'}}>
                        {fmtChatHora(m.fecha_envio)} {isMine && (m.leido ? '✓✓' : '✓')}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
            <div style={{padding: '10px 12px', borderTop: '1px solid #e7e9ec', display: 'flex', gap: '8px', background: '#fff'}}>
              <textarea
                value={texto}
                onChange={e => setTexto(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); } }}
                placeholder="Mensaje (Enter envía, Shift+Enter salto de línea)"
                rows={1}
                style={{flex: 1, border: '1px solid #e7e9ec', borderRadius: 10, padding: '10px 12px', fontSize: '13px', resize: 'none', fontFamily: 'inherit', minHeight: '40px', maxHeight: '120px'}}
              />
              <button
                onClick={enviar}
                disabled={enviando || !texto.trim()}
                style={{
                  padding: '8px 18px',
                  background: enviando || !texto.trim() ? '#9aa4af' : '#005187',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 10,
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: enviando || !texto.trim() ? 'not-allowed' : 'pointer'
                }}
              >
                {enviando ? '...' : 'Enviar'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const FloatingButtonsAdmin = ({ onOpenAsistente, onOpenTelegram, onOpenChat, chatUnread = 0 }) => {
  const [showTooltip, setShowTooltip] = useState(null);
  
  const buttonStyle = {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: SOMBRA_MODAL,
    transition: 'all 0.2s ease',
    fontSize: '24px'
  };

  return (
    <div style={{
      position: 'fixed',
      bottom: '24px',
      right: '24px',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      zIndex: 1000
    }}>
      {/* Botón Chat (Mensajes) — encima de Telegram */}
      <div style={{position: 'relative'}}>
        {showTooltip === 'chat' && (
          <div style={{
            position: 'absolute',
            right: '68px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: '#1f2a37',
            color: 'white',
            padding: '8px 14px',
            borderRadius: 10,
            fontSize: '13px',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
          }}>
            Mensajes a comerciales{chatUnread > 0 ? ` · ${chatUnread} sin leer` : ''}
          </div>
        )}
        <button
          onClick={onOpenChat}
          onMouseEnter={() => setShowTooltip('chat')}
          onMouseLeave={() => setShowTooltip(null)}
          style={{
            ...buttonStyle,
            background: 'linear-gradient(135deg, #e08c1e 0%, #e08c1e 100%)',
            color: 'white',
            position: 'relative'
          }}
          onMouseOver={e => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(245,158,11,0.4)'; }}
          onMouseOut={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.25)'; }}
          aria-label="Mensajes a comerciales"
        >
          💬
          {chatUnread > 0 && (
            <div style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              minWidth: '20px',
              height: '20px',
              padding: '0 5px',
              background: '#d23b34',
              borderRadius: 10,
              border: '2px solid white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              color: 'white',
              fontWeight: 'bold'
            }}>{chatUnread > 99 ? '99+' : chatUnread}</div>
          )}
        </button>
      </div>

      {/* Botón Telegram */}
      <div style={{position: 'relative'}}>
        {showTooltip === 'telegram' && (
          <div style={{
            position: 'absolute',
            right: '68px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: '#1f2a37',
            color: 'white',
            padding: '8px 14px',
            borderRadius: 10,
            fontSize: '13px',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
          }}>
            Configurar Telegram
          </div>
        )}
        <button
          onClick={onOpenTelegram}
          onMouseEnter={() => setShowTooltip('telegram')}
          onMouseLeave={() => setShowTooltip(null)}
          style={{
            ...buttonStyle,
            background: 'linear-gradient(135deg, #0088cc 0%, #0077b5 100%)',
            color: 'white'
          }}
          onMouseOver={e => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,136,204,0.4)'; }}
          onMouseOut={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.25)'; }}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/>
          </svg>
        </button>
      </div>

      {/* Botón DimoGPT */}
      <div style={{position: 'relative'}}>
        {showTooltip === 'asistente' && (
          <div style={{
            position: 'absolute',
            right: '68px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: '#1f2a37',
            color: 'white',
            padding: '8px 14px',
            borderRadius: 10,
            fontSize: '13px',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
          }}>
            DimoGPT - Asistente IA
          </div>
        )}
        <button
          onClick={onOpenAsistente}
          onMouseEnter={() => setShowTooltip('asistente')}
          onMouseLeave={() => setShowTooltip(null)}
          style={{
            ...buttonStyle,
            background: 'linear-gradient(135deg, #005187 0%, #0066a5 100%)',
            color: 'white'
          }}
          onMouseOver={e => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,81,135,0.4)'; }}
          onMouseOut={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.25)'; }}
        >
          🤖
        </button>
      </div>
    </div>
  );
};

// ==================== FLOATING MODAL ====================
const FloatingModal = ({ isOpen, onClose, title, children }) => {
  const [isMinimized, setIsMinimized] = useState(false);
  if (!isOpen) return null;
  
  return (
    <>
      {isMinimized && (
        <div
          onClick={() => setIsMinimized(false)}
          style={{
            position: 'fixed',
            bottom: '24px',
            right: '24px',
            background: '#005187',
            borderRadius: 12,
            boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
            zIndex: 1002,
            cursor: 'pointer',
            padding: '12px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}
          className="fg-card"
        >
          <span style={{fontSize: '20px'}}>💬</span>
          <div>
            <div style={{color: 'white', fontSize: '14px', fontWeight: 600}}>{title}</div>
            <div style={{color: '#a5d4ff', fontSize: '11px'}}>Click para expandir</div>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            style={{
              marginLeft: '8px',
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              color: 'white',
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >×</button>
        </div>
      )}
      <div style={{
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        width: '700px',
        height: 'calc(100vh - 48px)',
        maxHeight: '1050px',
        maxWidth: 'calc(100vw - 48px)',
        background: '#ffffff',
        borderRadius: 12,
        boxShadow: SOMBRA_MODAL,
        zIndex: 1001,
        display: isMinimized ? 'none' : 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        animation: 'slideUp 0.3s ease-out'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          background: '#005187',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span style={{fontSize: '15px', fontWeight: 600}}>{title}</span>
          <div style={{display: 'flex', gap: '6px'}}>
            <button
              onClick={() => setIsMinimized(true)}
              title="Minimizar"
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                color: 'white',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                cursor: 'pointer',
                fontSize: '18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >−</button>
            <button
              onClick={onClose}
              title="Cerrar"
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                color: 'white',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                cursor: 'pointer',
                fontSize: '18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >×</button>
          </div>
        </div>
        {/* Content */}
        <div style={{flex: 1, overflow: 'hidden'}}>
          {children}
        </div>
        <style>{`
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
    </>
  );
};

// ==================== TELEGRAM CONFIG PAGE PARA ADMIN ====================
const TelegramConfigPageAdmin = ({ admin, onUpdateAdmin }) => {
  const [telegramConfig, setTelegramConfig] = useState({
    activo: admin?.telegram_activo || false,
    horaResumen: admin?.telegram_hora_resumen || '09:00',
    chatId: admin?.telegram_chat_id || null,
    codigoVinculacion: admin?.telegram_codigo_vinculacion || null
  });
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [generatingCode, setGeneratingCode] = useState(false);

  const TELEGRAM_BOT_URL = 'https://t.me/Dimoautoasistentecrmbot';

  const generarCodigoVinculacion = async () => {
    setGeneratingCode(true);
    const codigo = 'DIMO-ADMIN-' + Math.random().toString(36).substring(2, 8).toUpperCase();
    
    try {
      const response = await fetch(SUPABASE_URL + '/rest/v1/administradores?id=eq.' + admin.id, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': 'Bearer ' + SUPABASE_ANON_KEY,
          'Prefer': 'return=minimal'
        },
        body: JSON.stringify({ telegram_codigo_vinculacion: codigo })
      });
      
      if (response.ok || response.status === 204) {
        setTelegramConfig(prev => ({ ...prev, codigoVinculacion: codigo }));
        console.log('✅ Código de vinculación generado:', codigo);
      }
    } catch (error) {
      console.error('Error generando código:', error);
      alert('Error al generar código. Inténtalo de nuevo.');
    } finally {
      setGeneratingCode(false);
    }
  };

  const guardarConfiguracion = async () => {
    setSaving(true);
    try {
      const response = await fetch(SUPABASE_URL + '/rest/v1/administradores?id=eq.' + admin.id, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': 'Bearer ' + SUPABASE_ANON_KEY,
          'Prefer': 'return=minimal'
        },
        body: JSON.stringify({ 
          telegram_activo: telegramConfig.activo,
          telegram_hora_resumen: telegramConfig.horaResumen
        })
      });
      
      if (response.ok || response.status === 204) {
        if (onUpdateAdmin) {
          onUpdateAdmin({ 
            ...admin, 
            telegram_activo: telegramConfig.activo,
            telegram_hora_resumen: telegramConfig.horaResumen
          });
        }
        alert('✅ Configuración guardada');
      }
    } catch (error) {
      console.error('Error guardando:', error);
      alert('Error al guardar. Inténtalo de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  const copiarCodigo = () => {
    navigator.clipboard.writeText(telegramConfig.codigoVinculacion);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const vinculado = !!telegramConfig.chatId;

  return (
    <div style={{height: '100%', overflowY: 'auto', background: '#ffffff', padding: '20px'}}>
      <div style={{maxWidth: '100%', margin: '0 auto'}}>
        {/* Descripción */}
        <div style={{background: '#f0f6fa', borderRadius: 10, padding: '14px 16px', marginBottom: '16px', border: '1px solid #cfe4f1'}}>
          <div style={{fontSize: '13px', color: '#1e40af', lineHeight: '1.5'}}>
            📲 <strong>Vincula tu Telegram</strong> para recibir notificaciones del CRM: alertas de nuevos leads, resúmenes globales y estadísticas sin abrir la aplicación.
          </div>
        </div>

        {/* Estado de vinculación */}
        <div style={{background: vinculado ? '#e6f4ec' : '#fbf1de', borderRadius: 10, padding: '14px 16px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px'}}>
          <span style={{fontSize: '24px'}}>{vinculado ? '✅' : '⏳'}</span>
          <div>
            <div style={{fontSize: '14px', fontWeight: 600, color: vinculado ? '#1f7a44' : '#a8660f'}}>
              {vinculado ? 'Telegram vinculado' : 'No vinculado'}
            </div>
            <div style={{fontSize: '14px', color: vinculado ? '#1f7a44' : '#a16207'}}>
              {vinculado ? 'Listo para recibir notificaciones' : 'Sigue los pasos para vincular'}
            </div>
          </div>
        </div>

        {/* Pasos para vincular - solo si no vinculado */}
        {!vinculado && (
          <div style={{background: '#f4f5f7', borderRadius: 10, padding: '16px', marginBottom: '16px', border: '1px solid #e7e9ec'}}>
            <div style={{fontSize: '13px', fontWeight: 600, color: '#2b333d', marginBottom: '14px'}}>Pasos para vincular:</div>
            
            {/* Paso 1 */}
            <div style={{display: 'flex', gap: '10px', marginBottom: '14px'}}>
              <div style={{width: '24px', height: '24px', borderRadius: '50%', background: '#005187', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 600, flexShrink: 0}}>1</div>
              <div>
                <div style={{fontSize: '14px', fontWeight: 500, color: '#1f2a37', marginBottom: '6px'}}>Abre el bot en Telegram</div>
                <a 
                  href={TELEGRAM_BOT_URL} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: '#0088cc', color: 'white', borderRadius: 10, textDecoration: 'none', fontSize: '14px', fontWeight: 500}}
                >
                  📱 Abrir Bot
                </a>
              </div>
            </div>

            {/* Paso 2 */}
            <div style={{display: 'flex', gap: '10px', marginBottom: '14px'}}>
              <div style={{width: '24px', height: '24px', borderRadius: '50%', background: '#005187', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 600, flexShrink: 0}}>2</div>
              <div style={{fontSize: '14px', color: '#2b333d'}}>
                Pulsa <strong>"Iniciar"</strong> en el bot
              </div>
            </div>

            {/* Paso 3 */}
            <div style={{display: 'flex', gap: '10px'}}>
              <div style={{width: '24px', height: '24px', borderRadius: '50%', background: '#005187', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 600, flexShrink: 0}}>3</div>
              <div style={{flex: 1}}>
                <div style={{fontSize: '14px', fontWeight: 500, color: '#1f2a37', marginBottom: '8px'}}>Envía este código:</div>
                {telegramConfig.codigoVinculacion ? (
                  <div style={{display: 'flex', gap: '6px', alignItems: 'center'}}>
                    <code style={{flex: 1, padding: '10px 12px', background: '#1f2a37', color: '#2e9e5b', borderRadius: 10, fontSize: '13px', fontWeight: 600, letterSpacing: '0.5px'}}>
                      {telegramConfig.codigoVinculacion}
                    </code>
                    <button 
                      onClick={copiarCodigo}
                      style={{padding: '10px 12px', background: copied ? '#2e9e5b' : '#005187', color: 'white', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: '13px', fontWeight: 500}}
                    >
                      {copied ? '✓' : '📋'}
                    </button>
                  </div>
                ) : (
                  <button 
                    onClick={generarCodigoVinculacion}
                    disabled={generatingCode}
                    style={{width: '100%', padding: '12px 16px', background: '#005187', color: 'white', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: '13px', fontWeight: 500, opacity: generatingCode ? 0.7 : 1}}
                  >
                    {generatingCode ? '⏳ Generando...' : '🔑 Generar código'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Sección de Notificaciones - SIEMPRE VISIBLE pero desactivada si no vinculado */}
        <div style={{background: '#f4f5f7', borderRadius: 10, padding: '16px', marginBottom: '16px', border: '1px solid #e7e9ec', opacity: vinculado ? 1 : 0.5, pointerEvents: vinculado ? 'auto' : 'none'}}>
          <div style={{fontSize: '14px', fontWeight: 600, color: '#2b333d', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px'}}>
            🔔 Notificaciones
            {!vinculado && <span style={{fontSize: '13px', fontWeight: 400, color: '#9aa4af'}}>(vincula Telegram para activar)</span>}
          </div>
          
          {/* Toggle Resumen diario */}
          <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px'}}>
            <div>
              <div style={{fontSize: '13px', fontWeight: 500, color: '#1f2a37'}}>Resumen diario</div>
              <div style={{fontSize: '13px', color: '#8a929c'}}>Estadísticas globales y leads pendientes</div>
            </div>
            <button
              onClick={() => setTelegramConfig(prev => ({ ...prev, activo: !prev.activo }))}
              disabled={!vinculado}
              style={{
                width: '48px',
                height: '26px',
                borderRadius: 999,
                border: 'none',
                cursor: vinculado ? 'pointer' : 'not-allowed',
                background: telegramConfig.activo ? '#2e9e5b' : '#e7e9ec',
                position: 'relative',
                transition: 'background 0.2s ease'
              }}
            >
              <div style={{
                width: '22px',
                height: '22px',
                borderRadius: '50%',
                background: 'white',
                position: 'absolute',
                top: '2px',
                left: telegramConfig.activo ? '24px' : '2px',
                transition: 'left 0.2s ease',
                boxShadow: 'none'
              }}></div>
            </button>
          </div>

          {/* Selector de hora */}
          <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
            <div style={{fontSize: '13px', fontWeight: 500, color: '#1f2a37'}}>Hora del resumen</div>
            <input
              type="time"
              value={telegramConfig.horaResumen}
              onChange={(e) => setTelegramConfig(prev => ({ ...prev, horaResumen: e.target.value }))}
              disabled={!vinculado}
              style={{
                padding: '8px 12px',
                borderRadius: 10,
                border: '1px solid #e7e9ec',
                fontSize: '13px',
                color: '#2b333d',
                background: vinculado ? 'white' : '#f4f5f7',
                cursor: vinculado ? 'text' : 'not-allowed'
              }}
            />
          </div>
        </div>

        {/* Botón Guardar - SIEMPRE VISIBLE pero desactivado si no vinculado */}
        <button
          onClick={guardarConfiguracion}
          disabled={saving || !vinculado}
          style={{
            width: '100%',
            padding: '14px',
            background: vinculado ? '#005187' : '#9aa4af',
            color: 'white',
            border: 'none',
            borderRadius: 10,
            fontSize: '14px',
            fontWeight: 600,
            cursor: vinculado ? 'pointer' : 'not-allowed',
            opacity: saving ? 0.7 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            marginBottom: '16px'
          }}
        >
          {saving ? '⏳ Guardando...' : '💾 Guardar'}
        </button>

        {/* Funciones disponibles - SIEMPRE VISIBLE */}
        <div style={{background: '#f3faf6', borderRadius: 10, padding: '16px', marginBottom: '16px', border: '1px solid #d3ecdd'}}>
          <div style={{fontSize: '13px', fontWeight: 600, color: '#1f7a44', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px'}}>
            💡 Funciones disponibles en Telegram
          </div>
          <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#1f7a44'}}>
              <span style={{color: '#2e9e5b'}}>✓</span> Resumen diario global de todos los leads
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#1f7a44'}}>
              <span style={{color: '#2e9e5b'}}>✓</span> Consultas con IA: "¿Cuántos leads hay?" "¿Rendimiento de vendedores?"
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#1f7a44'}}>
              <span style={{color: '#2e9e5b'}}>✓</span> Gestión de leads: cambiar estado, reasignar vendedor
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#1f7a44'}}>
              <span style={{color: '#2e9e5b'}}>✓</span> Buscar leads por nombre, teléfono o vendedor
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#1f7a44'}}>
              <span style={{color: '#2e9e5b'}}>✓</span> Estadísticas de rendimiento por centro y marca
            </div>
          </div>
        </div>

        {/* Botón Descargar Guía PDF */}
        <a
          href="/Guia_Uso_Asistente_Telegram_Admin.pdf"
          download="Guia_Uso_Asistente_Telegram_Admin.pdf"
          style={{
            width: '100%',
            padding: '14px',
            background: '#2e9e5b',
            color: 'white',
            border: 'none',
            borderRadius: 10,
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            textDecoration: 'none',
            boxSizing: 'border-box'
          }}
        >
          📄 Descargar Guía de Uso (PDF)
        </a>
      </div>
    </div>
  );
};

// DimoGPT Page - Asistente IA con OpenAI para administradores
const DimoGPTPage = ({ leads: leadsInput, comerciales: comercialesInput, isFloating = false }) => {
  const leads = leadsInput || [];
  const comerciales = comercialesInput || [];
  
  console.log('📊 DimoGPT - Leads recibidos:', leads.length);
  console.log('👥 DimoGPT - Comerciales recibidos:', comerciales.length);
  
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = React.useRef(null);
  const [initialized, setInitialized] = useState(false);

  const EDGE_FUNCTION_URL = `${SUPABASE_URL}/functions/v1/dimogpt`;

  // Inicializar mensaje de bienvenida cuando lleguen los datos
  useEffect(() => {
    if (!initialized && leads.length > 0) {
      setMessages([{
        role: 'assistant',
        content: `¡Hola! Soy DimoGPT, tu asistente de inteligencia artificial para el CRM de Grupo Dimoauto. 🚗\n\n📊 Tengo acceso a **${leads.length} leads** y **${comerciales.length} comerciales**.\n\nPuedo ayudarte a:\n• Consultar datos de leads y ventas\n• Generar informes y estadísticas\n• Analizar rendimiento de vendedores\n• **Informes de tiempos de respuesta por vendedor**\n• Gestionar leads (cambiar estado, agendar citas, etc.)\n\n¿En qué puedo ayudarte hoy?`
      }]);
      setInitialized(true);
    } else if (!initialized && leads.length === 0) {
      setMessages([{
        role: 'assistant',
        content: `¡Hola! Soy DimoGPT, tu asistente de inteligencia artificial para el CRM de Grupo Dimoauto. 🚗\n\n⏳ Cargando datos del CRM...\n\nEn cuanto se carguen los leads podré ayudarte con consultas y gestión.`
      }]);
    }
  }, [leads.length, comerciales.length, initialized]);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Buscar lead por nombre o teléfono
  const findLead = (query) => {
    const q = query.toLowerCase();
    return leads.find(l => {
      const nombre = `${l.nombre || ''} ${l.apellidos || ''}`.toLowerCase();
      const razon = (l.razonSocial || '').toLowerCase();
      const tel = l.telefono || l.movil || '';
      return nombre.includes(q) || razon.includes(q) || tel.includes(q);
    });
  };

  // Ejecutar acción en Supabase (usando ANON key + RLS)
  const executeAction = async (action) => {
    try {
      let response;
      const now = new Date().toISOString();
      
      console.log('🔧 Ejecutando acción:', action);
      
      switch (action.funcion) {
        case 'CAMBIAR_ESTADO': {
          const lead = findLead(action.params.nombre_lead);
          if (!lead) return { success: false, msg: `❌ No encontré ningún lead con "${action.params.nombre_lead}"` };
          
          const leadId = lead.id || lead.lead_id;
          response = await fetch(`${SUPABASE_URL}/rest/v1/leads?id=eq.${leadId}`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'apikey': SUPABASE_ANON_KEY,
              'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
              'Prefer': 'return=minimal'
            },
            body: JSON.stringify({
              estado_cita: action.params.nuevo_estado,
              fecha_modificacion: now
            })
          });
          
          if (response.ok || response.status === 204) {
            const nombreLead = lead.nombre ? `${lead.nombre} ${lead.apellidos || ''}`.trim() : lead.razonSocial || 'Lead';
            return { success: true, msg: `✅ Estado actualizado\n\n👤 ${nombreLead}\n📊 Nuevo estado: ${action.params.nuevo_estado}` };
          }
          return { success: false, msg: `❌ Error al actualizar: ${response.status}` };
        }
        
        case 'AGENDAR_CITA': {
          const lead = findLead(action.params.nombre_lead);
          if (!lead) return { success: false, msg: `❌ No encontré ningún lead con "${action.params.nombre_lead}"` };
          
          const leadId = lead.id || lead.lead_id;
          let fechaCita = action.params.fecha;
          if (action.params.hora) {
            fechaCita = `${action.params.fecha}T${action.params.hora}:00`;
          }
          
          response = await fetch(`${SUPABASE_URL}/rest/v1/leads?id=eq.${leadId}`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'apikey': SUPABASE_ANON_KEY,
              'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
              'Prefer': 'return=minimal'
            },
            body: JSON.stringify({
              estado_cita: 'Citado',
              fecha_agendada: fechaCita,
              fecha_modificacion: now
            })
          });
          
          if (response.ok || response.status === 204) {
            const fechaFormateada = new Date(fechaCita).toLocaleString('es-ES');
            const nombreLead = lead.nombre ? `${lead.nombre} ${lead.apellidos || ''}`.trim() : lead.razonSocial || 'Lead';
            return { success: true, msg: `✅ Cita agendada\n\n👤 ${nombreLead}\n📅 ${fechaFormateada}\n📊 Estado: Citado` };
          }
          return { success: false, msg: `❌ Error al agendar: ${response.status}` };
        }
        
        case 'REASIGNAR_LEAD': {
          const lead = findLead(action.params.nombre_lead);
          if (!lead) return { success: false, msg: `❌ No encontré ningún lead con "${action.params.nombre_lead}"` };
          
          const leadId = lead.id || lead.lead_id;
          response = await fetch(`${SUPABASE_URL}/rest/v1/leads?id=eq.${leadId}`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'apikey': SUPABASE_ANON_KEY,
              'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
              'Prefer': 'return=minimal'
            },
            body: JSON.stringify({
              vendedor_nombre: action.params.nuevo_vendedor,
              fecha_modificacion: now
            })
          });
          
          if (response.ok || response.status === 204) {
            const nombreLead = lead.nombre ? `${lead.nombre} ${lead.apellidos || ''}`.trim() : lead.razonSocial || 'Lead';
            return { success: true, msg: `✅ Lead reasignado\n\n👤 ${nombreLead}\n➡️ Nuevo vendedor: ${action.params.nuevo_vendedor}` };
          }
          return { success: false, msg: `❌ Error al reasignar: ${response.status}` };
        }
        
        case 'BUSCAR_LEAD': {
          const q = (action.params.query || '').toLowerCase();
          const encontrados = leads.filter(l => {
            const nombre = `${l.nombre || ''} ${l.apellidos || ''}`.toLowerCase();
            const razon = (l.razonSocial || '').toLowerCase();
            const tel = l.telefono || l.movil || '';
            const marca = (l.marca || '').toLowerCase();
            const modelo = (l.modelo || '').toLowerCase();
            const vendedor = (l.vendedor || '').toLowerCase();
            return nombre.includes(q) || razon.includes(q) || tel.includes(q) || marca.includes(q) || modelo.includes(q) || vendedor.includes(q);
          }).slice(0, 10);
          
          if (encontrados.length === 0) {
            return { success: true, msg: `🔍 No encontré leads con "${action.params.query}"` };
          }
          
          let msg = `🔍 Encontré ${encontrados.length} lead${encontrados.length > 1 ? 's' : ''}:\n\n`;
          encontrados.forEach((l, i) => {
            const nombre = `${l.nombre || ''} ${l.apellidos || ''}`.trim() || l.razonSocial || 'Sin nombre';
            msg += `${i + 1}. ${nombre}\n`;
            msg += `   🚗 ${l.marca || ''} ${l.modelo || ''}\n`;
            msg += `   👤 ${l.vendedor || 'Sin asignar'}\n`;
            msg += `   📊 ${l.estado || 'Sin estado'}\n\n`;
          });
          return { success: true, msg };
        }
        
        default:
          return { success: false, msg: `❌ Función "${action.funcion}" no reconocida` };
      }
    } catch (error) {
      console.error('❌ Error ejecutando acción:', error);
      return { success: false, msg: `❌ Error: ${error.message}` };
    }
  };

  // Construir contexto de datos para el asistente
  const buildDataContext = () => {
    try {
      const totalLeads = leads.length;
      const pendientes = leads.filter(l => (l.estado || l.estado_cita) === 'Pendiente').length;
      const atendidos = leads.filter(l => (l.estado || l.estado_cita) === 'Atendido').length;
      const citados = leads.filter(l => (l.estado || l.estado_cita) === 'Citado').length;
      const ofertados = leads.filter(l => (l.estado || l.estado_cita) === 'Ofertado').length;
      const cancelados = leads.filter(l => (l.estado || l.estado_cita) === 'Cancelado').length;
      const vendidos = leads.filter(l => (l.estado || l.estado_cita) === 'Vendido').length;
      
      // Debug: Ver qué datos de fechas hay disponibles
      const leadsConFechaCreacion = leads.filter(l => l.fechaCreacion).length;
      const leadsConFechaModificacion = leads.filter(l => l.fechaModificacion).length;
      const leadsConPrimeraLlamada = leads.filter(l => l.primeraLlamada).length;
      const leadsConTiempoRespuesta = leads.filter(l => l.tiempoHastaPrimeraLlamada && l.tiempoHastaPrimeraLlamada > 0).length;
      console.log('📊 DimoGPT Debug - Datos de fechas disponibles:', {
        totalLeads,
        leadsConFechaCreacion,
        leadsConFechaModificacion,
        leadsConPrimeraLlamada,
        leadsConTiempoRespuesta,
        ejemploLead: leads[0] ? {
          fechaCreacion: leads[0].fechaCreacion,
          fechaModificacion: leads[0].fechaModificacion,
          primeraLlamada: leads[0].primeraLlamada,
          tiempoHastaPrimeraLlamada: leads[0].tiempoHastaPrimeraLlamada,
          estado: leads[0].estado
        } : 'No hay leads'
      });
      
      // Stats por vendedor
      const vendedores = {};
      leads.forEach(l => {
        const v = l.vendedor || l.vendedor_nombre || 'Sin asignar';
        if (!vendedores[v]) vendedores[v] = { total: 0, vendidos: 0, pendientes: 0, atendidos: 0, tiemposRespuesta: [] };
        vendedores[v].total++;
        const estado = l.estado || l.estado_cita;
        if (estado === 'Vendido') vendedores[v].vendidos++;
        if (estado === 'Pendiente') vendedores[v].pendientes++;
        if (estado === 'Atendido') vendedores[v].atendidos++;
        
        // Recopilar tiempos de respuesta
        // Prioridad 1: tiempoHastaPrimeraLlamada (si existe primera_llamada)
        // Prioridad 2: tiempo entre fecha_creacion y fecha_modificacion (para leads ya atendidos)
        let tiempoRespuesta = null;
        
        if (l.tiempoHastaPrimeraLlamada && l.tiempoHastaPrimeraLlamada > 0) {
          tiempoRespuesta = l.tiempoHastaPrimeraLlamada;
        } else if (estado !== 'Pendiente') {
          // FIX 30-08: primera acción real del historial, nunca fecha_modificacion
          const diffMs = primeraRespuestaMs(l);
          if (diffMs != null && diffMs > 0 && diffMs < 30 * 24 * 60 * 60 * 1000) {
            tiempoRespuesta = diffMs;
          }
        }
        
        if (tiempoRespuesta && tiempoRespuesta > 0) {
          vendedores[v].tiemposRespuesta.push(tiempoRespuesta);
        }
      });
      
      // Función auxiliar para formatear tiempo
      const formatearTiempo = (ms) => {
        const minutos = Math.floor(ms / 60000);
        const horas = Math.floor(minutos / 60);
        const dias = Math.floor(horas / 24);
        const mins = minutos % 60;
        const hrs = horas % 24;
        
        if (dias > 0) return `${dias}d ${hrs}h`;
        if (horas > 0) return `${horas}h ${mins}min`;
        return `${minutos}min`;
      };
      
      // Calcular tiempo medio de respuesta por vendedor
      Object.keys(vendedores).forEach(v => {
        const tiempos = vendedores[v].tiemposRespuesta;
        if (tiempos.length > 0) {
          const suma = tiempos.reduce((a, b) => a + b, 0);
          const mediaMs = suma / tiempos.length;
          vendedores[v].tiempoMedioRespuesta = formatearTiempo(mediaMs);
          vendedores[v].tiempoMedioMs = mediaMs;
          vendedores[v].leadsConTiempo = tiempos.length;
        } else {
          vendedores[v].tiempoMedioRespuesta = 'Sin datos suficientes';
          vendedores[v].tiempoMedioMs = null;
          vendedores[v].leadsConTiempo = 0;
        }
      });
      
      // Stats por marca
      const marcas = {};
      leads.forEach(l => {
        const m = l.marca || 'Sin marca';
        if (!marcas[m]) marcas[m] = 0;
        marcas[m]++;
      });
      
      // Stats por origen
      const origenes = {};
      leads.forEach(l => {
        const o = l.origen || 'Desconocido';
        if (!origenes[o]) origenes[o] = 0;
        origenes[o]++;
      });

      // Leads por mes (este mes)
      const hoy = new Date();
      const inicioMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
      const leadsEsteMes = leads.filter(l => {
        const fecha = l.fechaCreacion || l.fecha_creacion;
        if (!fecha) return false;
        try {
          return new Date(fecha) >= inicioMes;
        } catch (e) {
          return false;
        }
      }).length;

      const manana = new Date(hoy);
      manana.setHours(0, 0, 0, 0);
      manana.setDate(manana.getDate() + 1);
      hoy.setHours(0, 0, 0, 0);
      
      const citasHoy = leads.filter(l => {
        const fecha = l.citaAgendada || l.fecha_agendada;
        if (!fecha) return false;
        try {
          const cita = new Date(fecha);
          return cita >= hoy && cita < manana;
        } catch (e) {
          return false;
        }
      });

      // Lista de los últimos 15 leads para contexto detallado
      const leadsDetalle = leads.slice(0, 15).map(l => ({
        nombre: `${l.nombre || ''} ${l.apellidos || ''}`.trim() || l.razonSocial || 'Sin nombre',
        telefono: l.telefono || l.movil || 'Sin tel',
        marca: l.marca || '',
        modelo: l.modelo || '',
        estado: l.estado || l.estado_cita || 'Sin estado',
        vendedor: l.vendedor || l.vendedor_nombre || 'Sin asignar',
        fecha: l.fechaCreacion || l.fecha_creacion || 'Sin fecha'
      }));

      return `
DATOS DEL CRM - GRUPO DIMOAUTO
Fecha actual: ${new Date().toLocaleDateString('es-ES')}

RESUMEN GENERAL:
- Total leads en sistema: ${totalLeads}
- Leads este mes: ${leadsEsteMes}
- Pendientes: ${pendientes}
- Atendidos: ${atendidos}
- Citados: ${citados}
- Ofertados: ${ofertados}
- Cancelados: ${cancelados}
- Vendidos: ${vendidos}
- Tasa conversión: ${totalLeads > 0 ? ((vendidos/totalLeads)*100).toFixed(1) : 0}%

CITAS HOY: ${citasHoy.length}
${citasHoy.slice(0, 5).map(c => `- ${c.nombre || c.razonSocial || 'Sin nombre'} (${c.marca || ''} ${c.modelo || ''}) - ${c.vendedor || c.vendedor_nombre || 'Sin vendedor'}`).join('\n') || 'Ninguna'}

ESTADÍSTICAS POR VENDEDOR (incluye tiempo medio de respuesta):
${Object.entries(vendedores).slice(0, 10).map(([v, s]) => `- ${v}: ${s.total} leads, ${s.vendidos} vendidos, ${s.pendientes} pendientes, Tiempo respuesta: ${s.tiempoMedioRespuesta}${s.leadsConTiempo > 0 ? ` (${s.leadsConTiempo} mediciones)` : ''}`).join('\n')}

LEADS POR MARCA:
${Object.entries(marcas).sort((a,b) => b[1] - a[1]).slice(0, 8).map(([m, c]) => `- ${m}: ${c}`).join('\n')}

LEADS POR ORIGEN:
${Object.entries(origenes).sort((a,b) => b[1] - a[1]).slice(0, 8).map(([o, c]) => `- ${o}: ${c}`).join('\n')}

TIEMPOS DE RESPUESTA POR VENDEDOR (tiempo medio hasta primera atención):
${(() => {
  const vendedoresConTiempo = Object.entries(vendedores)
    .filter(([v, s]) => s.tiempoMedioMs !== null && v !== 'Sin asignar')
    .sort((a, b) => a[1].tiempoMedioMs - b[1].tiempoMedioMs);
  
  const vendedoresSinTiempo = Object.entries(vendedores)
    .filter(([v, s]) => s.tiempoMedioMs === null && v !== 'Sin asignar');
  
  let resultado = '';
  
  if (vendedoresConTiempo.length > 0) {
    resultado += 'Ranking (de más rápido a más lento):\n';
    resultado += vendedoresConTiempo.map(([v, s], i) => 
      `${i + 1}. ${v}: ${s.tiempoMedioRespuesta} (${s.leadsConTiempo} leads medidos, ${s.total} totales)`
    ).join('\n');
  }
  
  if (vendedoresSinTiempo.length > 0) {
    if (resultado) resultado += '\n\n';
    resultado += 'Vendedores sin datos de tiempo de respuesta:\n';
    resultado += vendedoresSinTiempo.map(([v, s]) => 
      `- ${v}: ${s.total} leads (${s.atendidos || 0} atendidos, ${s.pendientes} pendientes) - No hay fechas registradas para calcular tiempo`
    ).join('\n');
  }
  
  return resultado || 'No hay vendedores registrados';
})()}

COMERCIALES ACTIVOS: ${comerciales.length}
${comerciales.slice(0, 10).map(c => `- ${c.nombre || 'Sin nombre'} (${c.centro || 'Sin centro'})`).join('\n')}

DETALLE ÚLTIMOS 15 LEADS:
${JSON.stringify(leadsDetalle, null, 1)}
`;
    } catch (error) {
      console.error('Error buildDataContext:', error);
      return 'No hay datos disponibles en este momento.';
    }
  };
  
  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;
    
    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);
    
    try {
      const systemPrompt = `Eres DimoGPT, el asistente de IA del CRM de Grupo Dimoauto. Tienes acceso a TODOS los datos del sistema y puedes gestionar leads.

FUNCIONES DISPONIBLES (responde SOLO con JSON cuando el usuario pida una accion):

1. AGENDAR_CITA - Programar cita (también cambia estado a Citado)
   JSON: {"funcion": "AGENDAR_CITA", "params": {"nombre_lead": "...", "fecha": "YYYY-MM-DD", "hora": "HH:MM"}}

2. CAMBIAR_ESTADO - Cambiar estado de un lead
   Estados: Pendiente, Atendido, Citado, Ofertado, Cancelado, Vendido
   JSON: {"funcion": "CAMBIAR_ESTADO", "params": {"nombre_lead": "...", "nuevo_estado": "..."}}

3. REASIGNAR_LEAD - Reasignar lead a otro vendedor
   JSON: {"funcion": "REASIGNAR_LEAD", "params": {"nombre_lead": "...", "nuevo_vendedor": "..."}}

4. BUSCAR_LEAD - Buscar leads
   JSON: {"funcion": "BUSCAR_LEAD", "params": {"query": "..."}}

INFORMES DISPONIBLES (responde en texto con los datos):
- Tiempos de respuesta por vendedor: Tienes acceso al tiempo medio que tarda cada vendedor en hacer la primera llamada a un lead. Usa la sección "RANKING TIEMPOS DE RESPUESTA" y "ESTADÍSTICAS POR VENDEDOR" de los datos.
- Rendimiento de vendedores: leads totales, vendidos, pendientes
- Estadísticas por marca, origen, centro
- Citas del día

REGLAS:
- Si mencionan FECHA/HORA -> usa AGENDAR_CITA
- Para CONSULTAS/INFORMES -> responde en texto con datos del contexto
- Para ACCIONES -> responde SOLO JSON
- Sé conciso y usa emojis
- Fecha hoy: ${new Date().toLocaleDateString('es-ES')}
- Año: ${new Date().getFullYear()}

${buildDataContext()}`;

      // Llamar a la Edge Function
      const response = await fetch(EDGE_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          action: 'chat',
          payload: {
            systemPrompt,
            messages: [
              ...messages.slice(-8).map(m => ({
                role: m.role,
                content: m.content
              })),
              { role: 'user', content: userMessage }
            ]
          }
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Error en DimoGPT');
      }
      
      const data = await response.json();
      let respuesta = data.content || 'No pude procesar la solicitud.';
      
      console.log('🤖 Respuesta GPT:', respuesta);
      
      // Intentar parsear como JSON (es una acción)
      try {
        let jsonStr = respuesta.trim();
        if (jsonStr.startsWith('```json')) jsonStr = jsonStr.replace(/```json\n?/, '').replace(/```$/, '');
        if (jsonStr.startsWith('```')) jsonStr = jsonStr.replace(/```\n?/, '').replace(/```$/, '');
        
        const action = JSON.parse(jsonStr);
        
        if (action.funcion) {
          setMessages(prev => [...prev, { role: 'assistant', content: '⏳ Ejecutando...' }]);
          const resultado = await executeAction(action);
          setMessages(prev => [...prev.slice(0, -1), { role: 'assistant', content: resultado.msg }]);
          return;
        }
      } catch (e) {
        // No es JSON, respuesta normal
      }
      
      setMessages(prev => [...prev, { role: 'assistant', content: respuesta }]);
    } catch (error) {
      console.error('Error DimoGPT:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `⚠️ Error: ${error.message}\n\nAsegúrate de que la Edge Function esté desplegada.`
      }]);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };
  
  const quickQuestions = [
    '¿Cuántos leads hay este mes?',
    '¿Quién es el mejor vendedor?',
    'Resumen de ventas por marca',
    'Busca leads de Peugeot'
  ];

  return (
    <div style={{height: 'calc(100vh - 180px)', display: 'flex', flexDirection: 'column'}}>
      {/* Header */}
      <div style={{background: 'white', borderRadius: '8px 8px 0 0', padding: '16px 20px', borderBottom: '3px solid #005187', display: 'flex', alignItems: 'center', gap: '12px'}}>
        <div style={{width: '44px', height: '44px', borderRadius: '50%', background: '#005187', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px'}}>🤖</div>
        <div>
          <div style={{color: '#2b333d', fontWeight: 700, fontSize: '18px'}}>DimoGPT</div>
          <div style={{color: '#8a929c', fontSize: '14px'}}>Asistente IA del CRM • Conectado a OpenAI</div>
        </div>
      </div>
      
      {/* Messages Area */}
      <div style={{flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px', background: 'white'}}>
        {messages.map((msg, idx) => (
          <div key={idx} style={{display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start'}}>
            <div style={{
              maxWidth: '75%',
              padding: '12px 16px',
              borderRadius: msg.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
              background: msg.role === 'user' ? '#005187' : '#f4f5f7',
              color: msg.role === 'user' ? 'white' : '#2b333d',
              fontSize: '13px',
              lineHeight: '1.6',
              whiteSpace: 'pre-wrap',
              boxShadow: 'none'
            }}>
              {msg.content}
            </div>
          </div>
        ))}
        {isLoading && (
          <div style={{display: 'flex', justifyContent: 'flex-start'}}>
            <div style={{padding: '12px 16px', borderRadius: '18px 18px 18px 4px', background: '#f4f5f7', color: '#8a929c', fontSize: '13px'}}>
              <span style={{display: 'inline-flex', gap: '4px'}}>
                <span style={{animation: 'pulse 1s infinite'}}>●</span>
                <span style={{animation: 'pulse 1s infinite 0.2s'}}>●</span>
                <span style={{animation: 'pulse 1s infinite 0.4s'}}>●</span>
              </span> Pensando...
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Quick Questions */}
      <div style={{padding: '12px 20px', background: '#f4f5f7', borderTop: '1px solid #e7e9ec', display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
        {quickQuestions.map((q, idx) => (
          <button 
            key={idx}
            onClick={() => { setInput(q); }}
            style={{
              padding: '6px 12px',
              borderRadius: 12,
              border: '1px solid #e7e9ec',
              background: 'white',
              color: '#8a929c',
              fontSize: '13px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            className="fg-nav"
          >
            {q}
          </button>
        ))}
      </div>
      
      {/* Input Area */}
      <div style={{padding: '16px 20px', background: 'white', borderTop: '1px solid #e7e9ec', borderRadius: '0 0 8px 8px', display: 'flex', gap: '12px', alignItems: 'flex-end'}}>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Escribe tu pregunta sobre los datos del CRM..."
          style={{
            flex: 1,
            padding: '12px 16px',
            borderRadius: 12,
            border: '1px solid #e7e9ec',
            background: '#f4f5f7',
            color: '#2b333d',
            fontSize: '13px',
            resize: 'none',
            outline: 'none',
            minHeight: '44px',
            maxHeight: '120px',
            fontFamily: 'inherit'
          }}
          rows={1}
          onFocus={(e) => e.target.style.borderColor = '#005187'}
          onBlur={(e) => e.target.style.borderColor = '#e7e9ec'}
        />
        <button
          onClick={sendMessage}
          disabled={!input.trim() || isLoading}
          style={{
            padding: '12px 20px',
            borderRadius: 12,
            border: 'none',
            background: input.trim() && !isLoading ? '#005187' : '#e7e9ec',
            color: 'white',
            fontSize: '14px',
            cursor: input.trim() && !isLoading ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontWeight: 600,
            transition: 'all 0.2s'
          }}
        >
          Enviar ➤
        </button>
      </div>
    </div>
  );
};

// Nuevo Lead Page
const NuevoLeadPage = ({ onAddLead, prefillData }) => {
  const emptyForm = {
    tipo: 'Lead Válido', origen: '', detalleOrigen: 'Orgánico', campana: '',
    nombre: '', apellidos: '', razonSocial: '', tipoCliente: 'Particular', telefono: '', email: '', cp: '', provincia: '',
    marca: '', modelo: '', motor: '', combustible: '', kilometros: '', matricula: '', tipoVenta: 'VN',
    vendedor: '', centro: '', observaciones: ''
  };
  
  const [formData, setFormData] = useState(prefillData ? {...emptyForm, ...prefillData} : emptyForm);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (prefillData) {
      setFormData(prev => ({...prev, ...prefillData}));
    }
  }, [prefillData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (onAddLead) onAddLead(formData);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };
  
  const handleClear = () => setFormData(emptyForm);

  const inputStyle = {...campo, width: '100%', padding: '9px 11px'};
  const labelStyle = {display: 'block', fontSize: '12.5px', fontWeight: 700, color: C.muted, marginBottom: '5px'};
  const sectionStyle = {background: '#FFFAF0', borderRadius: 10, padding: '16px', marginBottom: '16px', boxShadow: 'none'};
  const sectionTitleStyle = {fontSize: '13px', fontWeight: 600, color: '#005187', marginBottom: '12px', paddingBottom: '6px', borderBottom: `1px solid ${C.line}`};

  return (
    <div style={{maxWidth: '1000px', margin: '0 auto'}}>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px'}}>
        <h2 style={{color: 'white', fontSize: '18px', fontWeight: 600}}>➕ Nuevo Lead</h2>
        {saved && <div style={{background: '#228B22', color: 'white', padding: '8px 16px', borderRadius: 10, fontSize: '14px'}}>✓ Lead guardado correctamente</div>}
      </div>
      
      <div style={{background: '#2d2d2d', borderRadius: 10, padding: '20px'}}>
        <div style={{background: 'rgba(255,193,7,0.1)', border: '1px solid #FFC107', borderRadius: 10, padding: '12px', marginBottom: '20px'}}>
          <div style={{fontSize: '14px', color: '#FFC107', fontWeight: 600}}>⚠️ Nota: Los leads añadidos aquí se guardan localmente.</div>
          <div style={{fontSize: '13px', color: '#9aa4af', marginTop: '4px'}}>Los datos se guardan directamente en Supabase.</div>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>Información del Lead</div>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px'}}>
              <div><label style={labelStyle}>Tipo de Lead</label><select style={inputStyle} value={formData.tipo} onChange={e => setFormData({...formData, tipo: e.target.value})}>{Object.keys(LEAD_TIPOS).map(t => <option key={t}>{t}</option>)}</select></div>
              <div><label style={labelStyle}>Origen</label><select style={inputStyle} value={formData.origen} onChange={e => setFormData({...formData, origen: e.target.value})}><option value="">Seleccionar...</option>{ORIGENES.map(o => <option key={o.nombre}>{o.nombre}</option>)}</select></div>
              <div><label style={labelStyle}>Campaña</label><select style={inputStyle} value={formData.campana} onChange={e => setFormData({...formData, campana: e.target.value})}><option value="">Seleccionar...</option>{CAMPANAS.map(c => <option key={c}>{c}</option>)}</select></div>
              <div><label style={labelStyle}>Detalle Origen</label><input style={inputStyle} value={formData.detalleOrigen} onChange={e => setFormData({...formData, detalleOrigen: e.target.value})} /></div>
            </div>
          </div>

          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>Datos del Cliente</div>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px'}}>
              <div><label style={labelStyle}>Nombre</label><input style={inputStyle} value={formData.nombre} onChange={e => setFormData({...formData, nombre: e.target.value})} /></div>
              <div><label style={labelStyle}>Apellidos</label><input style={inputStyle} value={formData.apellidos} onChange={e => setFormData({...formData, apellidos: e.target.value})} /></div>
              <div><label style={labelStyle}>Tipo Cliente</label><select style={inputStyle} value={formData.tipoCliente} onChange={e => setFormData({...formData, tipoCliente: e.target.value})}>{TIPOS_CLIENTE.map(t => <option key={t}>{t}</option>)}</select></div>
              <div><label style={labelStyle}>Razón Social</label><input style={inputStyle} value={formData.razonSocial} onChange={e => setFormData({...formData, razonSocial: e.target.value})} placeholder="Solo empresas" /></div>
              <div><label style={labelStyle}>Teléfono</label><input style={inputStyle} value={formData.telefono} onChange={e => setFormData({...formData, telefono: e.target.value})} /></div>
              <div><label style={labelStyle}>Email</label><input style={inputStyle} type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} /></div>
              <div><label style={labelStyle}>Código Postal</label><input style={inputStyle} value={formData.cp} onChange={e => setFormData({...formData, cp: e.target.value})} /></div>
              <div><label style={labelStyle}>Provincia</label><input style={inputStyle} value={formData.provincia} onChange={e => setFormData({...formData, provincia: e.target.value})} /></div>
            </div>
          </div>

          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>Datos del Vehículo</div>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px'}}>
              <div><label style={labelStyle}>Marca</label><select style={inputStyle} value={formData.marca} onChange={e => setFormData({...formData, marca: e.target.value})}><option value="">Seleccionar...</option>{MARCAS.map(m => <option key={m}>{m}</option>)}</select></div>
              <div><label style={labelStyle}>Modelo</label><input style={inputStyle} value={formData.modelo} onChange={e => setFormData({...formData, modelo: e.target.value})} /></div>
              <div><label style={labelStyle}>Motor</label><input style={inputStyle} value={formData.motor} onChange={e => setFormData({...formData, motor: e.target.value})} /></div>
              <div><label style={labelStyle}>Combustible</label><select style={inputStyle} value={formData.combustible} onChange={e => setFormData({...formData, combustible: e.target.value})}><option value="">Seleccionar...</option>{COMBUSTIBLES.map(c => <option key={c}>{c}</option>)}</select></div>
              <div><label style={labelStyle}>Kilómetros</label><input style={inputStyle} type="number" value={formData.kilometros} onChange={e => setFormData({...formData, kilometros: e.target.value})} /></div>
              <div><label style={labelStyle}>Matrícula</label><input style={inputStyle} value={formData.matricula} onChange={e => setFormData({...formData, matricula: e.target.value})} /></div>
              <div><label style={labelStyle}>Tipo Venta</label><select style={inputStyle} value={formData.tipoVenta} onChange={e => setFormData({...formData, tipoVenta: e.target.value})}>{TIPOS_VENTA.map(t => <option key={t}>{t}</option>)}</select></div>
            </div>
          </div>

          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>Asignación</div>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px'}}>
              <div><label style={labelStyle}>Vendedor</label><select style={inputStyle} value={formData.vendedor} onChange={e => setFormData({...formData, vendedor: e.target.value})}><option value="">Seleccionar...</option>{VENDEDORES.map(v => <option key={v}>{v}</option>)}</select></div>
              <div><label style={labelStyle}>Centro</label><select style={inputStyle} value={formData.centro} onChange={e => setFormData({...formData, centro: e.target.value})}><option value="">Seleccionar...</option>{CENTROS.map(c => <option key={c}>{c}</option>)}</select></div>
            </div>
          </div>

          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>Observaciones</div>
            <div>
              <label style={labelStyle}>Comentarios u observaciones (sin caracteres especiales)</label>
              <textarea 
                style={{...inputStyle, minHeight: '80px', resize: 'vertical'}} 
                value={formData.observaciones} 
                onChange={e => {
                  // Sanitizar: solo permitir caracteres seguros para JSON
                  const sanitized = e.target.value
                    .replace(/[^\w\sáéíóúüñÁÉÍÓÚÜÑ.,;:!?¿¡@#€$%&()+-]/g, '')
                    .replace(/[\t\r]/g, ' ');
                  setFormData({...formData, observaciones: sanitized});
                }}
                placeholder="Escribe aquí las observaciones del lead..."
                maxLength={500}
              />
              <div style={{fontSize: '14px', color: '#888', marginTop: '4px', textAlign: 'right'}}>
                {formData.observaciones?.length || 0}/500 caracteres
              </div>
            </div>
          </div>

          <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end'}}>
            <button type="button" onClick={handleClear} style={{padding: '10px 24px', background: '#8a929c', border: 'none', borderRadius: 10, color: 'white', cursor: 'pointer', fontSize: '13px'}}>Limpiar</button>
            <button type="submit" style={{padding: '10px 24px', background: '#228B22', border: 'none', borderRadius: 10, color: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: 600}}>💾 Guardar Lead</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Analysis Page
const getHeatColor = (value, max, baseColor) => {
  if (!value || max === 0) return 'transparent';
  const intensity = value / max;
  return `${baseColor}${Math.floor(intensity * 60 + 20).toString(16).padStart(2, '0')}`;
};

// Componente de desglose de modelos por marca (para Datos marcas)
const ModelosByMarcaCard = ({ leads }) => {
  // Agrupar modelos por marca usando normalización
  const modelosPorMarca = useMemo(() => {
    const marcasMap = {};
    
    leads.forEach(lead => {
      if (!lead.marca || !lead.modelo) return;
      
      const marca = lead.marca;
      const modeloNorm = normalizeModelo(lead.modelo);
      
      if (!marcasMap[marca]) {
        marcasMap[marca] = {
          marca,
          modelos: {},
          total: 0
        };
      }
      
      if (!marcasMap[marca].modelos[modeloNorm]) {
        marcasMap[marca].modelos[modeloNorm] = {
          nombre: modeloNorm,
          count: 0,
          vendidos: 0,
          variantes: new Set()
        };
      }
      
      marcasMap[marca].modelos[modeloNorm].count++;
      marcasMap[marca].modelos[modeloNorm].variantes.add(lead.modelo);
      if (lead.estado === 'Vendido') {
        marcasMap[marca].modelos[modeloNorm].vendidos++;
      }
      marcasMap[marca].total++;
    });
    
    // Convertir a array y ordenar
    return Object.values(marcasMap)
      .map(m => ({
        ...m,
        modelos: Object.values(m.modelos)
          .map(mod => ({
            ...mod,
            variantes: Array.from(mod.variantes)
          }))
          .sort((a, b) => b.count - a.count)
      }))
      .sort((a, b) => b.total - a.total);
  }, [leads]);

  const [expandedMarca, setExpandedMarca] = useState(null);

  if (modelosPorMarca.length === 0) return null;

  return (
    <div style={{
      background: 'white',
      borderRadius: 10,
      boxShadow: 'none',
      overflow: 'hidden',
      marginTop: '16px'
    }}>
      <div style={{
        background: GRAD,
        color: 'white',
        padding: '12px 16px',
        fontWeight: 800,
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <span>🚗</span>
        Desglose de Modelos por Marca
      </div>
      
      <div style={{ padding: '16px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px' }}>
          {modelosPorMarca.map(marca => (
            <div 
              key={marca.marca}
              style={{
                border: '1px solid #e7e9ec',
                borderRadius: 10,
                overflow: 'hidden',
                background: expandedMarca === marca.marca ? '#f4f5f7' : 'white'
              }}
            >
              {/* Header de marca */}
              <div 
                onClick={() => setExpandedMarca(expandedMarca === marca.marca ? null : marca.marca)}
                style={{
                  padding: '12px 16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  background: '#f4f5f7',
                  borderBottom: expandedMarca === marca.marca ? '1px solid #e7e9ec' : 'none'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontWeight: 600, color: '#005187' }}>{marca.marca}</span>
                  <span style={{
                    background: '#005187',
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: 12,
                    fontSize: '13px',
                    fontWeight: 600
                  }}>
                    {marca.total} leads
                  </span>
                </div>
                <span style={{ color: '#8a929c', fontSize: '14px' }}>
                  {expandedMarca === marca.marca ? '▲' : '▼'} {marca.modelos.length} modelos
                </span>
              </div>
              
              {/* Listado de modelos (expandible) */}
              {expandedMarca === marca.marca && (
                <div style={{ padding: '8px' }}>
                  {marca.modelos.map((modelo, idx) => (
                    <div 
                      key={modelo.nombre}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 12px',
                        background: idx % 2 === 0 ? 'white' : '#f4f5f7',
                        borderRadius: 10,
                        marginBottom: '4px'
                      }}
                    >
                      <div>
                        <span style={{ fontWeight: 500, color: '#2b333d' }}>{modelo.nombre}</span>
                        {modelo.variantes.length > 1 && (
                          <span 
                            style={{ 
                              fontSize: '14px', 
                              color: '#8a929c', 
                              marginLeft: '6px',
                              cursor: 'help'
                            }}
                            title={modelo.variantes.join(', ')}
                          >
                            ({modelo.variantes.length} variantes)
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ 
                          fontSize: '14px', 
                          color: '#8a929c',
                          minWidth: '50px',
                          textAlign: 'right'
                        }}>
                          {modelo.count} leads
                        </span>
                        {modelo.vendidos > 0 && (
                          <span style={{
                            background: '#2e9e5b',
                            color: 'white',
                            padding: '2px 6px',
                            borderRadius: 10,
                            fontSize: '14px',
                            fontWeight: 600
                          }}>
                            {modelo.vendidos} vendidos
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ==================== MODAL DESGLOSE DE LEADS ====================
const LeadsDesgloseModal = ({ isOpen, onClose, leads, titulo }) => {
  if (!isOpen) return null;
  
  const formatFechaHora = (fecha) => {
    if (!fecha) return { dia: '-', hora: '-' };
    const d = new Date(fecha);
    return {
      dia: d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }),
      hora: d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
    };
  };

  return (
    <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: VELO_MODAL, display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000}} onClick={onClose}>
      <div style={{background: 'white', borderRadius: 12, width: '90%', maxWidth: '900px', maxHeight: '80vh', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'}} onClick={e => e.stopPropagation()}>
        <div style={{background: GRAD, color: 'white', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <h3 style={{margin: 0, fontSize: '16px', fontWeight: 600}}>{titulo} ({leads.length} leads)</h3>
          <button onClick={onClose} style={{background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', width: '32px', height: '32px', borderRadius: 10, cursor: 'pointer', fontSize: '18px'}}>×</button>
        </div>
        <div style={{overflowY: 'auto', maxHeight: 'calc(80vh - 60px)'}}>
          <table style={{width: '100%', borderCollapse: 'collapse'}}>
            <thead style={{position: 'sticky', top: 0, background: '#f4f5f7'}}>
              <tr>
                <th style={{...celdaTitulo, padding: '10px 8px', textAlign: 'center', width: '60px'}}>Nº</th>
                <th style={{...celdaTitulo, padding: '10px 8px', textAlign: 'left'}}>NOMBRE</th>
                <th style={{...celdaTitulo, padding: '10px 8px', textAlign: 'left'}}>ORIGEN</th>
                <th style={{...celdaTitulo, padding: '10px 8px', textAlign: 'center'}}>ESTADO</th>
                <th style={{...celdaTitulo, padding: '10px 8px', textAlign: 'center'}}>DÍA ENTRADA</th>
                <th style={{...celdaTitulo, padding: '10px 8px', textAlign: 'center'}}>HORA</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead, idx) => {
                const { dia, hora } = formatFechaHora(lead.fechaCreacion);
                const estadoInfo = { color: colorEstado(lead.estado) };
                const nombreCompleto = [lead.nombre, lead.apellidos].filter(Boolean).join(' ') || '-';
                return (
                  <tr key={idx} style={{background: idx % 2 === 0 ? 'white' : '#f4f5f7'}}>
                    <td style={{padding: '10px 8px', fontSize: '12.5px', color: C.brand, fontWeight: 800, textAlign: 'center', borderBottom: SEPARADOR_FILA}}>{lead.num || String(idx + 1).padStart(3, '0')}</td>
                    <td style={{padding: '10px 8px', fontSize: '12.5px', color: C.page, fontWeight: 700, borderBottom: SEPARADOR_FILA}}>{nombreCompleto}</td>
                    <td style={{padding: '10px 8px', fontSize: '12.5px', color: C.page, borderBottom: SEPARADOR_FILA}}>{lead.origen || '-'}</td>
                    <td style={{padding: '10px 8px', textAlign: 'center', borderBottom: SEPARADOR_FILA}}>
                      <span style={pildora(estadoInfo.color)}>{lead.estado || '-'}</span>
                    </td>
                    <td style={{padding: '10px 8px', fontSize: '12.5px', color: C.page, textAlign: 'center', borderBottom: SEPARADOR_FILA}}>{dia}</td>
                    <td style={{padding: '10px 8px', fontSize: '14px', color: '#8a929c', textAlign: 'center', borderBottom: '1px solid #e7e9ec'}}>{hora}</td>
                  </tr>
                );
              })}
              {leads.length === 0 && (
                <tr><td colSpan={6}><EmptyState icon="📋" title="No hay leads para mostrar" /></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ==================== INFORMES PAGE (Configurador + PDF profesional 1 página) ====================
const INFORME_PERIODOS = [
  { id: 'diario', label: 'Diario (hoy)' },
  { id: 'semanal', label: 'Semanal (últimos 7 días)' },
  { id: 'mensual', label: 'Mensual (este mes)' },
  { id: 'personalizado', label: 'Personalizado' }
];

// Listas excluidas del configurador de informes (datos sucios o duplicados)
const VENDEDORES_EXCLUIDOS_INFORME = new Set(['Nuria Carrion', 'Núria Carrión', 'Álvaro Bragulat', 'Edgar Sanchez']);
const MARCAS_EXCLUIDAS_INFORME = new Set(['PEUGEOT']); // mayúsculas duplicadas; usar 'Peugeot'
const ORIGENES_EXCLUIDOS_INFORME = new Set(['Web Leapmotor']);
// Nombre con el que Recepción figura como vendedor_nombre (panel-recepcion)
const RECEPCION_NOMBRE_INFORME = 'Adriana';

// Origen "desglosado" para los informes (petición Adriana 31/08):
//  · Coches.net  → «Coches.net VN» / «Coches.net VO» (según tipo de venta)
//  · Web Dimoauto → «Web Dimoauto VN» / «Web Dimoauto VO»
//  · Carnovo     → «Carnovo Peugeot», «Carnovo Jeep»… (por marca)
// El resto de orígenes se quedan tal cual.
const origenInforme = (l) => {
  const o = String(l?.origen || '').trim();
  if (!o) return '-';
  const tv = String(l?.tipoVenta || '').toUpperCase();
  const sufijoVenta = (base) => {
    const m = o.match(/\b(VN|VO)\b/i); // si el origen ya viene con VN/VO, se respeta
    const t = m ? m[1].toUpperCase() : (tv === 'VO' ? 'VO' : tv === 'B2B' ? 'B2B' : 'VN');
    return `${base} ${t}`;
  };
  if (/^coches\.?net/i.test(o)) return sufijoVenta('Coches.net');
  if (/^web\s*dimoauto/i.test(o)) return sufijoVenta('Web Dimoauto');
  if (/^carnovo/i.test(o)) {
    const marca = String(l?.marca || '').trim();
    return `Carnovo ${marca && marca !== 'Desconocido' ? marca : 'sin marca'}`;
  }
  return o;
};

const InformesPage = ({ leads, todosLeads, comerciales = [], adminNombre }) => {
  // Selectores - todos por defecto
  const today = new Date();
  const fmtDate = (d) => d.toISOString().split('T')[0];

  const computeRango = (periodo, customDesde, customHasta) => {
    const hasta = new Date();
    let desde = new Date();
    if (periodo === 'diario') {
      desde = new Date(); desde.setHours(0, 0, 0, 0);
    } else if (periodo === 'semanal') {
      desde = new Date(); desde.setDate(desde.getDate() - 7); desde.setHours(0, 0, 0, 0);
    } else if (periodo === 'mensual') {
      desde = new Date(); desde.setDate(1); desde.setHours(0, 0, 0, 0);
    } else if (periodo === 'personalizado') {
      desde = customDesde ? new Date(customDesde + 'T00:00:00') : desde;
      const h = customHasta ? new Date(customHasta + 'T23:59:59') : hasta;
      return { desde, hasta: h };
    }
    return { desde, hasta };
  };

  const [periodo, setPeriodo] = useState('mensual');
  const [customDesde, setCustomDesde] = useState(fmtDate(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [customHasta, setCustomHasta] = useState(fmtDate(today));

  const vendedoresDisponibles = useMemo(
    () => [...new Set([...(comerciales || []).filter(c => c.activo !== false).map(c => c.nombre), ...leads.map(l => l.vendedor).filter(Boolean)])]
            .filter(v => !VENDEDORES_EXCLUIDOS_INFORME.has(v))
            .sort(),
    [comerciales, leads]
  );
  // Orígenes ya desglosados (Coches.net VN/VO, Web Dimoauto VN/VO, Carnovo por marca)
  const origenesDisponibles = useMemo(
    () => [...new Set(leads.map(origenInforme).filter(o => o && o !== '-'))]
            .filter(o => !ORIGENES_EXCLUIDOS_INFORME.has(o))
            .sort(),
    [leads]
  );
  const marcasDisponibles = useMemo(
    () => [...new Set(leads.map(l => l.marca).filter(Boolean))]
            .filter(m => !MARCAS_EXCLUIDAS_INFORME.has(m))
            .sort(),
    [leads]
  );
  const centrosDisponibles = useMemo(
    () => [...new Set(leads.map(l => l.centro).filter(Boolean))].sort(),
    [leads]
  );

  const [selVendedores, setSelVendedores] = useState([]);
  const [selOrigenes, setSelOrigenes] = useState([]);
  const [selMarcas, setSelMarcas] = useState([]);
  const [selCentros, setSelCentros] = useState([]);

  const [incTratamiento, setIncTratamiento] = useState(true);
  const [incMovimientos, setIncMovimientos] = useState(true);
  const [incActividad, setIncActividad] = useState(true);
  const [incTiempoResp, setIncTiempoResp] = useState(true);
  const [incIA, setIncIA] = useState(true);

  const [generando, setGenerando] = useState(false);
  const [progresoMsg, setProgresoMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Toggle helpers
  const toggleSel = (current, setter, value) => {
    if (current.includes(value)) setter(current.filter(v => v !== value));
    else setter([...current, value]);
  };
  const toggleAll = (current, setter, all) => {
    if (current.length === all.length) setter([]); else setter([...all]);
  };

  // Filtros del configurador que no dependen de la fecha
  const pasaSeleccion = useCallback((l) => {
    if (selVendedores.length > 0 && !selVendedores.includes(l.vendedor)) return false;
    if (selOrigenes.length > 0 && !selOrigenes.includes(origenInforme(l))) return false;
    if (selMarcas.length > 0 && !selMarcas.includes(l.marca)) return false;
    if (selCentros.length > 0 && !selCentros.includes(l.centro)) return false;
    return true;
  }, [selVendedores, selOrigenes, selMarcas, selCentros]);

  // Aplicar filtros del configurador a los leads (ENTRADOS en el período)
  const leadsInforme = useMemo(() => {
    const { desde, hasta } = computeRango(periodo, customDesde, customHasta);
    return leads.filter(l => {
      const fc = l.fechaCreacion ? new Date(l.fechaCreacion) : null;
      if (fc && (fc < desde || fc > hasta)) return false;
      return pasaSeleccion(l);
    });
  }, [leads, periodo, customDesde, customHasta, pasaSeleccion]);

  // PEDIDOS del período por FECHA DE VENTA (como en el informe del panel de
  // Recepción): incluye leads que entraron antes y se vendieron ahora. Se
  // calcula sobre TODOS los leads (sin el filtro de fechas del listado).
  const ventasInforme = useMemo(() => {
    const { desde, hasta } = computeRango(periodo, customDesde, customHasta);
    const base = Array.isArray(todosLeads) && todosLeads.length ? todosLeads : leads;
    return base.filter(l => {
      if (l.estado !== 'Vendido') return false;
      const fv = l.fechaVenta ? new Date(l.fechaVenta) : null;
      if (!fv || isNaN(fv.getTime())) return false;
      if (fv < desde || fv > hasta) return false;
      return pasaSeleccion(l);
    });
  }, [todosLeads, leads, periodo, customDesde, customHasta, pasaSeleccion]);

  // Cargar jsPDF
  const ensureJsPDF = async () => {
    if (window.jspdf) return;
    await new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
      script.onload = resolve;
      script.onerror = () => reject(new Error('No se pudo cargar jsPDF'));
      document.head.appendChild(script);
    });
  };

  // Resumen narrativo (IA opcional, fallback plantilla)
  const generarNarrativa = async (resumen) => {
    if (!incIA) return generarNarrativaPlantilla(resumen);
    try {
      const token = localStorage.getItem('sb-access-token');
      let openaiApiKey;
      if (token) {
        const sr = await fetch(`${SUPABASE_URL}/rest/v1/rpc/get_openai_key`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'apikey': SUPABASE_ANON_KEY }
        });
        if (sr.ok) openaiApiKey = await sr.json();
      }
      if (!openaiApiKey) openaiApiKey = process.env.REACT_APP_OPENAI_API_KEY;
      if (!openaiApiKey) return generarNarrativaPlantilla(resumen);

      const r = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${openaiApiKey}` },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          max_tokens: 1100,
          temperature: 0.55,
          messages: [
            { role: 'system', content: 'Eres analista comercial senior de un grupo de concesionarios (Grupo Dimoauto). Escribes resúmenes ejecutivos en español, claros y profesionales para el jefe de ventas. Lenguaje natural, sin tecnicismos, con observaciones útiles para tomar decisiones. Prosa fluida, sin listas ni bullets.' },
            { role: 'user', content: `Genera un resumen ejecutivo extenso (entre 280 y 380 palabras, 3-4 párrafos bien diferenciados) para el jefe de ventas a partir de estos datos:

Período: ${resumen.periodoLabel}
Total leads: ${resumen.total}
Vendidos: ${resumen.vendidos} (tasa de conversión ${resumen.tasaConversion}%)
Pedidos del período por fecha de venta: ${resumen.pedidos ?? 0} (${resumen.pedidosDelPeriodo ?? 0} de leads del período + ${resumen.pedidosAnteriores ?? 0} de leads anteriores)
Tasa de atención global: ${resumen.tasaAtencion}%
${resumen.detalleOrigenes ? 'Detalle por origen (Coches.net y Web por VN/VO, Carnovo por marca): ' + resumen.detalleOrigenes : ''}
Estados: Pendientes ${resumen.pendientes} · Atendidos ${resumen.atendidos} · Citados ${resumen.citados} · Ofertados ${resumen.ofertados} · Cancelados ${resumen.cancelados} · Vendidos ${resumen.vendidos}
Top origen: ${resumen.topOrigen} | Top marca: ${resumen.topMarca} | Top vendedor: ${resumen.topVendedor} | Top centro: ${resumen.topCentro}
Tiempo medio de primera respuesta: ${resumen.tiempoRespuestaTxt}
${resumen.movimientosTotal ? 'Movimientos registrados en CRM (cambios + auditoría) en el período: ' + resumen.movimientosTotal : ''}
${resumen.detalleVendedores ? 'Detalle por vendedor (nombre/total/vendidos/%cierre): ' + resumen.detalleVendedores : ''}

Estructura sugerida:
1) Visión general del período: volumen de leads, comparativa implícita y tono general.
2) Análisis del rendimiento comercial: tratamiento de leads (cuántos se atienden, cuántos se cierran), señalando puntos fuertes y cuellos de botella.
3) Comportamiento por canal/marca/centro/vendedor según los topes y la distribución descrita.
4) Recomendaciones concretas y priorizadas (2-3) que el jefe de ventas pueda accionar mañana.

NO uses listas, bullets, ni encabezados; solo prosa fluida y profesional. Evita repetir cifras ya presentes en el resto del informe; interprétalas.` }
          ]
        })
      });
      const data = await r.json();
      if (data?.choices?.[0]?.message?.content) return data.choices[0].message.content.trim();
      return generarNarrativaPlantilla(resumen);
    } catch (e) {
      return generarNarrativaPlantilla(resumen);
    }
  };

  const generarNarrativaPlantilla = (r) => {
    const p1 = [];
    p1.push(`Durante el período ${r.periodoLabel} se han registrado ${r.total} leads en el CRM, de los cuales ${r.vendidos} se han traducido en ventas confirmadas.`);
    if (r.total > 0) p1.push(`Esto sitúa la tasa global de conversión en el ${r.tasaConversion}% y la tasa de atención (leads que han salido del estado Pendiente) en el ${r.tasaAtencion}%.`);

    const p2 = [];
    if (r.atendidos + r.citados + r.ofertados > 0) p2.push(`En cuanto al tratamiento, ${r.atendidos} leads se han atendido, ${r.citados} han llegado a cita y ${r.ofertados} han recibido oferta, lo que muestra el avance natural del embudo.`);
    if (r.pendientes > 0) p2.push(`Quedan ${r.pendientes} leads pendientes de gestionar; conviene priorizar su revisión para no perder oportunidades.`);
    if (r.cancelados > 0) p2.push(`${r.cancelados} leads han sido cancelados, un volumen que conviene auditar para entender los motivos principales.`);
    if (r.tiempoRespuestaTxt && r.tiempoRespuestaTxt !== '-') p2.push(`El tiempo medio de primera respuesta se sitúa en ${r.tiempoRespuestaTxt}, métrica clave en la fricción inicial con el cliente.`);

    const p3 = [];
    if (r.topOrigen && r.topOrigen !== '-') p3.push(`El canal más activo ha sido ${r.topOrigen} y la marca con mayor demanda ${r.topMarca}.`);
    if (r.topVendedor && r.topVendedor !== '-') p3.push(`El vendedor con más leads asignados es ${r.topVendedor}, mientras que el centro con mayor volumen es ${r.topCentro}.`);
    if (r.movimientosTotal) p3.push(`Se han registrado ${r.movimientosTotal} movimientos (cambios + auditoría) en el CRM durante el período, indicador del nivel de actividad del equipo.`);

    const p4 = [];
    p4.push(`Como recomendación, se sugiere reforzar la atención sobre los leads pendientes y reducir el tiempo de primera respuesta, especialmente en los canales con más volumen.`);
    if (r.cancelados > r.vendidos) p4.push(`También conviene revisar las causas de cancelación para reorientar el discurso comercial.`);
    if (r.topVendedor && r.topVendedor !== '-') p4.push(`Apoyarse en las buenas prácticas de ${r.topVendedor} puede acelerar el cierre del resto del equipo.`);

    return [p1.join(' '), p2.join(' '), p3.join(' '), p4.join(' ')].filter(Boolean).join('\n\n');
  };

  // Helpers de agrupación
  const groupCount = (arr, field) => {
    const m = {};
    arr.forEach(l => { const k = l[field] || '-'; m[k] = (m[k] || 0) + 1; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  };

  const formatMs = (ms) => {
    if (!ms || ms < 0) return '-';
    const min = Math.round(ms / 60000);
    if (min < 60) return `${min} min`;
    const h = Math.floor(min / 60);
    const m = min % 60;
    return `${h}h ${m}m`;
  };

  // Generador principal del PDF
  const generarPDF = async () => {
    setErrorMsg('');
    setGenerando(true);
    setProgresoMsg('Cargando librería PDF...');
    try {
      await ensureJsPDF();
      const { desde, hasta } = computeRango(periodo, customDesde, customHasta);

      // Auditoría + historial leads (combinado)
      let movimientosTotal = 0;
      let topAcciones = [];
      let topUsuariosMov = [];
      let actividadReciente = [];
      // Tratamiento por comercial: agregamos por usuario y por estado resultante
      let tratamientoPorUsuario = [];
      if (incMovimientos || incActividad) {
        setProgresoMsg('Cargando movimientos del período...');
        // Cambios y movimientos = SOLO el historial[] de los leads filtrados.
        // Se ignora la tabla auditoria_logs por petición del usuario.
        const nombresSeleccionados = selVendedores.length > 0 ? new Set(selVendedores) : null;
        const historialEvts = [];
        const tratPorUsr = {};
        leadsInforme.forEach(l => {
          (l.historial || []).forEach(h => {
            const f = h.fecha ? new Date(h.fecha) : null;
            if (!f || f < desde || f > hasta) return;
            if (nombresSeleccionados && !nombresSeleccionados.has(h.usuario)) return;
            const usr = h.usuario || '-';
            const estadoRaw = String(h.estado || '').toLowerCase();
            historialEvts.push({
              timestamp: f.toISOString(),
              user_email: usr,
              action: h.accion || h.estado || 'Cambio',
              estadoNuevo: h.estado || '-',
              table_name: 'leads',
              record_id: l.id,
              lead: `${l.nombre || ''} ${l.apellidos || ''}`.trim() || l.email || '-'
            });
            if (!tratPorUsr[usr]) {
              tratPorUsr[usr] = { total: 0, atendidos: 0, citados: 0, ofertados: 0, vendidos: 0, cancelados: 0 };
            }
            tratPorUsr[usr].total++;
            if (estadoRaw === 'atendido') tratPorUsr[usr].atendidos++;
            else if (estadoRaw === 'citado') tratPorUsr[usr].citados++;
            else if (estadoRaw === 'ofertado') tratPorUsr[usr].ofertados++;
            else if (estadoRaw === 'vendido') tratPorUsr[usr].vendidos++;
            else if (estadoRaw === 'cancelado') tratPorUsr[usr].cancelados++;
          });
        });
        tratamientoPorUsuario = Object.entries(tratPorUsr)
          .map(([nombre, s]) => ({ nombre, ...s }))
          .sort((a, b) => b.total - a.total)
          .slice(0, 7);
        const todos = historialEvts.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        movimientosTotal = todos.length;
        const accCnt = {};
        todos.forEach(e => { const k = e.action || '-'; accCnt[k] = (accCnt[k] || 0) + 1; });
        topAcciones = Object.entries(accCnt).sort((a, b) => b[1] - a[1]).slice(0, 5);
        const userCnt = {};
        todos.forEach(e => { const k = e.user_email || '-'; userCnt[k] = (userCnt[k] || 0) + 1; });
        topUsuariosMov = Object.entries(userCnt).sort((a, b) => b[1] - a[1]).slice(0, 5);
        actividadReciente = todos.slice(0, 7);
      }

      // KPIs y totales
      const total = leadsInforme.length;
      const cnt = (estado) => leadsInforme.filter(l => l.estado === estado).length;
      const vendidos = cnt('Vendido');
      const atendidos = cnt('Atendido');
      const citados = cnt('Citado');
      const ofertados = cnt('Ofertado');
      const pendientes = cnt('Pendiente');
      const cancelados = cnt('Cancelado');
      const tasaConversion = total > 0 ? ((vendidos / total) * 100).toFixed(1) : '0.0';
      const tasaAtencion = total > 0 ? (((total - pendientes) / total) * 100).toFixed(1) : '0.0';

      // Distribuciones — si hay selección, todas las seleccionadas; si no, top 6
      // Origen: DESGLOSADO (Coches.net VN/VO, Web Dimoauto VN/VO, Carnovo por
      // marca) y con todas las filas — la tarjeta se adapta en altura.
      const porOrigen = (() => {
        const m = {};
        leadsInforme.forEach(l => { const k = origenInforme(l); m[k] = (m[k] || 0) + 1; });
        return Object.entries(m).sort((a, b) => b[1] - a[1]).slice(0, 14);
      })();

      // ── Información como en el informe del panel de Recepción ──
      // (petición Adriana 31/08: "el informe visual del admin pero con la
      //  información como en el mío")
      const esCitaReal = (l) => !!l.citaAgendada && l.tipoAgenda !== 'seguimiento' && l.tipoAgenda !== 'llamada';
      const pasadoAComercial = (l) => { const v = String(l.vendedor || '').trim(); return !!v && v !== RECEPCION_NOMBRE_INFORME; };
      const pedidos = ventasInforme.length;
      const pedidosDelPeriodo = ventasInforme.filter(l => l.fechaCreacion && new Date(l.fechaCreacion) >= desde).length;
      const pedidosAnteriores = pedidos - pedidosDelPeriodo;
      const pedidosPorVendedor = {};
      ventasInforme.forEach(l => { const k = l.vendedor || '— Sin asignar —'; pedidosPorVendedor[k] = (pedidosPorVendedor[k] || 0) + 1; });
      // Por origen / portal (desglosado)
      const filasOrigenRec = (() => {
        const m = {};
        const fila = (k) => (m[k] = m[k] || { nombre: k, entrados: 0, sinTratar: 0, gestionados: 0, pasados: 0, citas: 0, descartados: 0, vendidos: 0 });
        leadsInforme.forEach(l => {
          const f = fila(origenInforme(l));
          f.entrados++;
          if (l.estado === 'Pendiente') f.sinTratar++; else f.gestionados++;
          if (pasadoAComercial(l)) f.pasados++;
          if (esCitaReal(l)) f.citas++;
          if (l.estado === 'Cancelado') f.descartados++;
        });
        ventasInforme.forEach(l => { fila(origenInforme(l)).vendidos++; });
        return Object.values(m).sort((a, b) => b.entrados - a.entrados);
      })();
      // Por marca
      const filasMarcaRec = (() => {
        const m = {};
        const fila = (k) => (m[k] = m[k] || { nombre: k, entrados: 0, pasados: 0, citas: 0, descartados: 0, vendidos: 0 });
        const marcaDe = (l) => (l.marca && l.marca !== 'Desconocido' ? l.marca : '— Sin marca —');
        leadsInforme.forEach(l => {
          const f = fila(marcaDe(l));
          f.entrados++;
          if (pasadoAComercial(l)) f.pasados++;
          if (esCitaReal(l)) f.citas++;
          if (l.estado === 'Cancelado') f.descartados++;
        });
        ventasInforme.forEach(l => { fila(marcaDe(l)).vendidos++; });
        return Object.values(m).sort((a, b) => b.entrados - a.entrados);
      })();
      const sumaFilas = (filas, campos) => campos.reduce((t, c) => ({ ...t, [c]: filas.reduce((s, f) => s + (f[c] || 0), 0) }), {});
      const totOrigenRec = sumaFilas(filasOrigenRec, ['entrados', 'sinTratar', 'gestionados', 'pasados', 'citas', 'descartados', 'vendidos']);
      const totMarcaRec = sumaFilas(filasMarcaRec, ['entrados', 'pasados', 'citas', 'descartados', 'vendidos']);
      // Motivos de descarte
      const motivosDescarte = (() => {
        const m = {};
        leadsInforme.filter(l => l.estado === 'Cancelado').forEach(l => {
          const k = String(l.motivoNoCompra || '').trim() || 'Sin motivo indicado';
          m[k] = (m[k] || 0) + 1;
        });
        return Object.entries(m).sort((a, b) => b[1] - a[1]);
      })();
      const porMarca = groupCount(leadsInforme, 'marca').slice(0, selMarcas.length > 0 ? Math.max(selMarcas.length, 1) : 6);
      const porVendedor = groupCount(leadsInforme, 'vendedor').slice(0, selVendedores.length > 0 ? selVendedores.length : 6);
      const porCentro = groupCount(leadsInforme, 'centro').slice(0, selCentros.length > 0 ? Math.max(selCentros.length, 1) : 6);

      // Tabla detallada por vendedor: nombres a mostrar
      const vendedoresTabla = selVendedores.length > 0
        ? selVendedores
        : [...new Set(leadsInforme.map(l => l.vendedor).filter(Boolean))]
            .map(v => [v, leadsInforme.filter(l => l.vendedor === v).length])
            .sort((a, b) => b[1] - a[1])
            .slice(0, 8)
            .map(x => x[0]);
      const vendedorRowsAll = vendedoresTabla.map(v => {
        const sub = leadsInforme.filter(l => l.vendedor === v);
        const t = sub.length;
        const cE = (e) => sub.filter(l => l.estado === e).length;
        const ven = cE('Vendido');
        // Citas REALES del período: leads con día y hora agendados que no son
        // un seguimiento ni una llamada programada. A diferencia del estado
        // "Citado", esto no se pierde cuando el lead avanza a Ofertado/Vendido.
        const conCita = sub.filter(l => l.citaAgendada && l.tipoAgenda !== 'seguimiento' && l.tipoAgenda !== 'llamada');
        return {
          nombre: v,
          total: t,
          pendiente: cE('Pendiente'),
          atendido: cE('Atendido'),
          citado: cE('Citado'),
          citas: conCita.length,
          vino: conCita.filter(l => l.asistenciaCita === 'Ha venido').length,
          noVino: conCita.filter(l => l.asistenciaCita === 'No ha venido').length,
          ofertado: cE('Ofertado'),
          cancelado: cE('Cancelado'),
          vendido: ven,
          // Pedidos por FECHA DE VENTA (como en el panel de Recepción): puede
          // incluir leads que entraron antes del período
          pedidos: pedidosPorVendedor[v] || 0,
          cierre: t > 0 ? ((ven / t) * 100).toFixed(2) + '%' : '0.00%'
        };
      });
      // Filtrar fuera de la tabla los vendedores sin leads en el período;
      // se mencionan al pie en una sola línea para que el jefe sepa que no
      // se han ocultado por error.
      const vendedorRows = vendedorRowsAll.filter(r => r.total > 0 || r.pedidos > 0);
      const vendedoresSinActividad = vendedorRowsAll.filter(r => r.total === 0 && r.pedidos === 0).map(r => r.nombre);
      const porEstado = Object.keys(ESTADOS_LEAD).map(e => [e, cnt(e)]);

      const topOrigen = porOrigen[0]?.[0] || '-';
      const topMarca = porMarca[0]?.[0] || '-';
      const topVendedor = porVendedor[0]?.[0] || '-';
      const topCentro = porCentro[0]?.[0] || '-';

      // Tiempo respuesta medio — FIX 30-08: primera acción REAL del lead
      // (primer apunte del historial o primera llamada), nunca fechaModificacion.
      const leadsConRespuesta = leadsInforme.filter(l => l.fechaCreacion && l.estado !== 'Pendiente');
      const tiemposResp = leadsConRespuesta
        .map(l => ({ l, ms: primeraRespuestaMs(l) }))
        .filter(x => x.ms != null);
      const tiempoRespuestaMs = tiemposResp.length > 0 ? tiemposResp.reduce((a, x) => a + x.ms, 0) / tiemposResp.length : 0;
      const tiempoRespuestaTxt = tiemposResp.length > 0 ? formatMs(tiempoRespuestaMs) : '-';

      // Tiempo medio de respuesta POR COMERCIAL (misma métrica corregida)
      const tiempoRespPorComercial = (() => {
        const m = {};
        tiemposResp.forEach(({ l, ms }) => {
          const v = l.vendedor || '— Sin asignar —';
          if (!m[v]) m[v] = [];
          m[v].push(ms);
        });
        const DAY = 24 * 3600 * 1000;
        return Object.entries(m)
          .map(([nombre, arr]) => {
            const sorted = [...arr].sort((a, b) => a - b);
            const media = arr.reduce((a, b) => a + b, 0) / arr.length;
            const mediana = sorted.length % 2 === 1
              ? sorted[(sorted.length - 1) / 2]
              : (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2;
            const under24 = arr.filter(x => x <= DAY).length;
            const under72 = arr.filter(x => x <= 3 * DAY).length;
            return {
              nombre,
              leads: arr.length,
              mediaMs: media,
              medianaMs: mediana,
              pctUnder24: Math.round((under24 / arr.length) * 100),
              pctUnder72: Math.round((under72 / arr.length) * 100)
            };
          })
          .sort((a, b) => a.medianaMs - b.medianaMs);
      })();

      const periodoLabel = `${desde.toLocaleDateString('es-ES')} – ${hasta.toLocaleDateString('es-ES')}`;
      const detalleVendedores = vendedorRows.length > 0
        ? vendedorRows.map(r => `${r.nombre}: ${r.total} leads, ${r.vendido} vendidos, cierre ${r.cierre}`).join(' | ')
        : '';
      const resumen = {
        periodoLabel, total, vendidos, atendidos, citados, ofertados, pendientes, cancelados,
        tasaConversion, tasaAtencion,
        topOrigen, topMarca, topVendedor, topCentro,
        tiempoRespuestaTxt, movimientosTotal, detalleVendedores,
        pedidos, pedidosDelPeriodo, pedidosAnteriores,
        detalleOrigenes: filasOrigenRec.slice(0, 12).map(f => `${f.nombre}: ${f.entrados} entrados, ${f.vendidos} vendidos`).join(' | '),
      };

      setProgresoMsg('Generando resumen narrativo...');
      const narrativa = await generarNarrativa(resumen);

      setProgresoMsg('Componiendo PDF...');
      const { jsPDF } = window.jspdf;
      const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
      const PW = 210, PH = 297, MARGIN = 10;
      const FOOTER_RESERVED = 10;
      const TOP_AFTER_BREAK = 14;
      const addNewPage = () => {
        doc.addPage();
        doc.setFont('helvetica');
      };
      const ensureSpace = (h) => {
        if (y + h > PH - FOOTER_RESERVED) {
          addNewPage();
          y = TOP_AFTER_BREAK;
        }
      };
      const COL_BLUE = [0, 81, 135];      // #005187
      const COL_LIGHT = [232, 240, 250];
      const COL_GREEN = [50, 175, 90];
      const COL_GRAY = [110, 110, 110];

      doc.setFont('helvetica');

      // Header
      doc.setFillColor(...COL_BLUE);
      doc.rect(0, 0, PW, 16, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Grupo Dimoauto · Informe Comercial', MARGIN, 10);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(`Generado ${new Date().toLocaleString('es-ES')}`, PW - MARGIN, 10, { align: 'right' });

      // Subheader: filtros aplicados
      let y = 21;
      doc.setTextColor(...COL_GRAY);
      doc.setFontSize(8);
      const filtroTxt = [
        `Período: ${periodoLabel}`,
        selVendedores.length ? `Vendedores: ${selVendedores.length}` : `Vendedores: Todos`,
        selOrigenes.length ? `Orígenes: ${selOrigenes.length}` : `Orígenes: Todos`,
        selMarcas.length ? `Marcas: ${selMarcas.length}` : `Marcas: Todas`,
        selCentros.length ? `Centros: ${selCentros.length}` : `Centros: Todos`
      ].join('  ·  ');
      doc.text(filtroTxt, MARGIN, y);
      y += 4;

      // KPIs (6 tarjetas) — «Pedidos» cuenta por fecha de venta, como en el
      // informe del panel de Recepción (leads del período + leads anteriores)
      const kpis = [
        { label: 'Leads', value: total, color: COL_BLUE },
        { label: 'Vendidos (entrados)', value: vendidos, color: COL_GREEN },
        { label: 'Pedidos (f. venta)', value: pedidos, color: COL_GREEN, sub: pedidosAnteriores > 0 ? `${pedidosDelPeriodo} del período + ${pedidosAnteriores} anteriores` : 'todos de leads del período' },
        { label: 'Conversión', value: tasaConversion + '%', color: COL_BLUE },
        { label: 'Atención', value: tasaAtencion + '%', color: COL_BLUE },
        { label: 'T. respuesta', value: tiempoRespuestaTxt, color: COL_BLUE }
      ];
      const kpiW = (PW - MARGIN * 2 - 5 * 2) / 6;
      const kpiH = 17;
      const kpiY = y;
      kpis.forEach((k, i) => {
        const x = MARGIN + i * (kpiW + 2);
        doc.setFillColor(...COL_LIGHT);
        doc.roundedRect(x, kpiY, kpiW, kpiH, 1.2, 1.2, 'F');
        doc.setTextColor(...COL_GRAY);
        doc.setFontSize(6.5);
        doc.setFont('helvetica', 'normal');
        doc.text(k.label.toUpperCase(), x + kpiW / 2, kpiY + 4.5, { align: 'center' });
        doc.setTextColor(...k.color);
        doc.setFontSize(k.sub ? 12 : 13);
        doc.setFont('helvetica', 'bold');
        doc.text(String(k.value), x + kpiW / 2, kpiY + (k.sub ? 10.5 : 12), { align: 'center' });
        if (k.sub) {
          doc.setTextColor(...COL_GRAY);
          doc.setFontSize(5.2);
          doc.setFont('helvetica', 'normal');
          doc.text(k.sub, x + kpiW / 2, kpiY + 14.8, { align: 'center' });
        }
      });
      y = kpiY + kpiH + 4;

      // Helper para tarjeta de distribución (label adaptado al ancho real)
      const drawDistribution = (x, yPos, w, h, title, rows, totalRef) => {
        doc.setDrawColor(220, 220, 220);
        doc.setLineWidth(0.2);
        doc.roundedRect(x, yPos, w, h, 1.2, 1.2, 'S');
        doc.setFillColor(...COL_BLUE);
        doc.rect(x, yPos, w, 4.5, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.text(title, x + 2, yPos + 3.2);
        doc.setTextColor(40, 40, 40);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        let ry = yPos + 8;
        const maxVal = Math.max(...rows.map(r => r[1]), 1);
        const NUM_W = 22;                                      // espacio derecha para "104 (52%)"
        const BAR_W = Math.min(Math.max(w * 0.32, 28), 70);    // ancho máximo barra (entre 28 y 70mm)
        const labelMaxW = w - 4 - BAR_W - NUM_W;               // resto para texto
        const labelMaxChars = Math.max(8, Math.floor(labelMaxW / 1.45));
        // Nº de filas que caben en la altura dada (mín. 6 — el tamaño de siempre)
        const maxFilas = Math.max(6, Math.floor((h - 8 - 1) / 4) + 1);
        rows.slice(0, maxFilas).forEach(([k, v]) => {
          const raw = String(k);
          const label = raw.length > labelMaxChars ? raw.slice(0, labelMaxChars - 1) + '…' : raw;
          doc.setTextColor(40, 40, 40);
          doc.text(label, x + 2, ry);
          const barX = x + w - NUM_W - BAR_W;
          const barW = (v / maxVal) * BAR_W;
          doc.setFillColor(...COL_BLUE);
          doc.rect(barX, ry - 2.4, barW, 2.4, 'F');
          doc.setTextColor(...COL_GRAY);
          const pct = totalRef > 0 ? Math.round((v / totalRef) * 100) : 0;
          doc.text(`${v} (${pct}%)`, x + w - 2, ry, { align: 'right' });
          ry += 4;
        });
      };

      // Fila 1: Distribución por Origen DESGLOSADO a ancho completo (Coches.net
      // VN/VO, Web Dimoauto VN/VO, Carnovo por marca…) — altura según nº filas
      const colW = (PW - MARGIN * 2 - 3) / 2;
      const origenVisible = Math.min(porOrigen.length, 14);
      const origenH = Math.max(22, 8 + origenVisible * 4 + 2);
      drawDistribution(MARGIN, y, PW - MARGIN * 2, origenH, 'Distribución por Origen (Coches.net y Web por VN/VO · Carnovo por marca)', porOrigen, total);
      y += origenH + 3;
      // Fila 2: Marca + Centro en 2 columnas (altura adaptable a nº de filas)
      const marcaVisible = Math.min(porMarca.length, 8);
      const centroVisible = Math.min(porCentro.length, 8);
      const fila2H = Math.max(22, 8 + Math.max(marcaVisible, centroVisible) * 4 + 2);
      drawDistribution(MARGIN, y, colW, fila2H, 'Distribución por Marca (seleccionadas)', porMarca, total);
      drawDistribution(MARGIN + colW + 3, y, colW, fila2H, 'Distribución por Centro (seleccionados)', porCentro, total);
      y += fila2H + 3;

      // Tabla detallada por Vendedor (estados de leads) — multi-página si no cabe
      const tblHeaderH = 5;
      const tblRowH = 3.8;
      // 10 columnas numéricas repartidas de MARGIN+48 al borde derecho.
      // «Citas» = citas de verdad agendadas (no el estado Citado) y «Vino /
      // No vino» = asistencia confirmada por el comercial (petición de Adriana).
      // «Pedidos» = ventas marcadas en el período por FECHA DE VENTA (aunque el
      // lead entrara antes), igual que en el informe del panel de Recepción.
      const NUM_COLS = 11;
      const colX0 = MARGIN + 44;
      const colXN = PW - MARGIN - 2;
      const colsX = [MARGIN + 2, ...Array.from({ length: NUM_COLS }, (_, i) => colX0 + ((colXN - colX0) * i) / (NUM_COLS - 1))];
      const colsLabel = ['Vendedor', 'Total', 'Pend.', 'Atend.', 'Citas', 'Vino', 'No vino', 'Ofert.', 'Cancel.', 'Vendido', 'Pedidos', '% Cierre'];
      const colsAlign = ['left', 'right', 'right', 'right', 'right', 'right', 'right', 'right', 'right', 'right', 'right', 'right'];

      const drawTablaHeader = (title) => {
        const headerH = 4.5;
        doc.setDrawColor(220, 220, 220);
        doc.setLineWidth(0.2);
        doc.setFillColor(...COL_BLUE);
        doc.rect(MARGIN, y, PW - MARGIN * 2, headerH, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.text(title, MARGIN + 2, y + 3.2);
        y += headerH;
        // Cabecera de columnas
        doc.setFontSize(6.5);
        doc.setTextColor(...COL_GRAY);
        doc.setFont('helvetica', 'bold');
        const headRowY = y + 3;
        colsLabel.forEach((lab, i) => {
          doc.text(lab, colsX[i], headRowY, { align: colsAlign[i] });
        });
        doc.setDrawColor(...COL_BLUE);
        doc.setLineWidth(0.15);
        doc.line(MARGIN + 1, headRowY + 1.2, PW - MARGIN - 1, headRowY + 1.2);
        y = headRowY + 4.6;
      };

      // Verificar que al menos cabe el header + 1 fila; si no, salto de página
      ensureSpace(tblHeaderH + 4 + tblRowH);
      const titleSufijo = selVendedores.length === 0
        ? ' top'
        : (vendedoresSinActividad.length > 0
            ? ` con actividad · ${vendedoresSinActividad.length} sin leads en el período`
            : ' seleccionados');
      drawTablaHeader(`Detalle por Vendedor (${vendedorRows.length}${titleSufijo})`);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);
      vendedorRows.forEach((r, idx) => {
        // Si no cabe la siguiente fila, salto a nueva página y redibujo la cabecera
        if (y + tblRowH > PH - FOOTER_RESERVED) {
          addNewPage();
          y = TOP_AFTER_BREAK;
          drawTablaHeader(`Detalle por Vendedor (continuación)`);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(6.5);
        }
        if (idx % 2 === 0) {
          doc.setFillColor(247, 250, 254);
          doc.rect(MARGIN + 1, y - 2.6, PW - MARGIN * 2 - 2, tblRowH, 'F');
        }
        const nameTrim = String(r.nombre || '-').length > 22 ? String(r.nombre).slice(0, 21) + '…' : String(r.nombre || '-');
        doc.setTextColor(35, 35, 35);
        doc.text(nameTrim, colsX[0], y, { align: 'left' });
        const vals = [r.total, r.pendiente, r.atendido, r.citas, r.vino, r.noVino, r.ofertado, r.cancelado, r.vendido, r.pedidos, r.cierre];
        vals.forEach((v, i) => {
          if ((i === 8 && r.vendido > 0) || (i === 9 && r.pedidos > 0)) doc.setTextColor(...COL_GREEN);
          else doc.setTextColor(35, 35, 35);
          doc.text(String(v), colsX[i + 1], y, { align: 'right' });
        });
        y += tblRowH;
      });
      // Fila TOTAL del detalle por vendedor
      if (vendedorRows.length > 0) {
        ensureSpace(tblRowH + 1);
        const tot = (k) => vendedorRows.reduce((s, r) => s + (Number(r[k]) || 0), 0);
        doc.setFillColor(...COL_LIGHT);
        doc.rect(MARGIN + 1, y - 2.6, PW - MARGIN * 2 - 2, tblRowH, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(35, 35, 35);
        doc.text('TOTAL', colsX[0], y, { align: 'left' });
        const tTotal = tot('total'); const tVend = tot('vendido');
        const totVals = [tTotal, tot('pendiente'), tot('atendido'), tot('citas'), tot('vino'), tot('noVino'), tot('ofertado'), tot('cancelado'), tVend, tot('pedidos'), tTotal > 0 ? ((tVend / tTotal) * 100).toFixed(2) + '%' : '0.00%'];
        totVals.forEach((v, i) => doc.text(String(v), colsX[i + 1], y, { align: 'right' }));
        doc.setFont('helvetica', 'normal');
        y += tblRowH;
      }
      // Nota al pie con vendedores sin actividad
      if (vendedoresSinActividad.length > 0) {
        ensureSpace(4);
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(6.5);
        doc.setTextColor(...COL_GRAY);
        const lista = vendedoresSinActividad.join(', ');
        const sinActividadLine = `Sin actividad en el período: ${lista}`;
        const splitNote = doc.splitTextToSize(sinActividadLine, PW - MARGIN * 2 - 4);
        splitNote.forEach(line => {
          ensureSpace(3);
          doc.text(line, MARGIN + 2, y);
          y += 3;
        });
        doc.setFont('helvetica', 'normal');
      }
      y += 5;

      // ── Tablas «como en el informe de Recepción» ─────────────────────
      // Tabla genérica con salto de página: cabecera azul + columnas + filas + TOTAL
      const drawTablaGenerica = (title, cols, rows, totalRow, nota) => {
        // cols: [{ lbl, key, align, w? }] — la primera columna es texto (izq.)
        const rowH = 3.8;
        const primeraW = 52;
        const nNum = cols.length - 1;
        const x0 = MARGIN + 2 + primeraW;
        const xN = PW - MARGIN - 2;
        const xs = [MARGIN + 2, ...Array.from({ length: nNum }, (_, i) => x0 + ((xN - x0) * i) / Math.max(1, nNum - 1))];
        const header = (t) => {
          doc.setFillColor(...COL_BLUE);
          doc.rect(MARGIN, y, PW - MARGIN * 2, 4.5, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(8);
          doc.text(t, MARGIN + 2, y + 3.2);
          y += 4.5;
          doc.setFontSize(6.5);
          doc.setTextColor(...COL_GRAY);
          const hy = y + 3;
          cols.forEach((c, i) => doc.text(c.lbl, xs[i], hy, { align: i === 0 ? 'left' : 'right' }));
          doc.setDrawColor(...COL_BLUE);
          doc.setLineWidth(0.15);
          doc.line(MARGIN + 1, hy + 1.2, PW - MARGIN - 1, hy + 1.2);
          y = hy + 4.6;
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(6.5);
        };
        ensureSpace(4.5 + 4 + rowH * 2);
        header(title);
        rows.forEach((r, idx) => {
          if (y + rowH > PH - FOOTER_RESERVED) {
            addNewPage();
            y = TOP_AFTER_BREAK;
            header(`${title} (continuación)`);
          }
          if (idx % 2 === 0) {
            doc.setFillColor(247, 250, 254);
            doc.rect(MARGIN + 1, y - 2.6, PW - MARGIN * 2 - 2, rowH, 'F');
          }
          cols.forEach((c, i) => {
            const v = r[c.key];
            if (i === 0) {
              const s = String(v ?? '-');
              doc.setTextColor(35, 35, 35);
              doc.text(s.length > 34 ? s.slice(0, 33) + '…' : s, xs[0], y, { align: 'left' });
            } else {
              if (c.verde && Number(v) > 0) doc.setTextColor(...COL_GREEN); else doc.setTextColor(35, 35, 35);
              doc.text(String(v ?? 0), xs[i], y, { align: 'right' });
            }
          });
          y += rowH;
        });
        if (totalRow) {
          ensureSpace(rowH + 1);
          doc.setFillColor(...COL_LIGHT);
          doc.rect(MARGIN + 1, y - 2.6, PW - MARGIN * 2 - 2, rowH, 'F');
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(35, 35, 35);
          cols.forEach((c, i) => {
            const v = i === 0 ? 'TOTAL' : (totalRow[c.key] ?? '');
            doc.text(String(v), xs[i], y, { align: i === 0 ? 'left' : 'right' });
          });
          doc.setFont('helvetica', 'normal');
          y += rowH;
        }
        if (nota) {
          doc.setFont('helvetica', 'italic');
          doc.setFontSize(6);
          doc.setTextColor(...COL_GRAY);
          doc.splitTextToSize(nota, PW - MARGIN * 2 - 4).forEach(line => {
            ensureSpace(3);
            doc.text(line, MARGIN + 2, y);
            y += 2.8;
          });
          doc.setFont('helvetica', 'normal');
        }
        y += 4;
      };

      // Detalle por Origen / portal (Coches.net y Web Dimoauto por VN/VO, Carnovo por marca)
      if (filasOrigenRec.length > 0) {
        drawTablaGenerica(
          `Detalle por Origen / portal (${filasOrigenRec.length}) — Coches.net y Web Dimoauto por VN/VO · Carnovo por marca`,
          [
            { lbl: 'Origen / portal', key: 'nombre' },
            { lbl: 'Entrados', key: 'entrados' },
            { lbl: 'Sin tratar', key: 'sinTratar' },
            { lbl: 'Gestionados', key: 'gestionados' },
            { lbl: 'Pasados a com.', key: 'pasados' },
            { lbl: 'Citas', key: 'citas' },
            { lbl: 'Descartados', key: 'descartados' },
            { lbl: 'Vendidos', key: 'vendidos', verde: true },
          ],
          filasOrigenRec,
          totOrigenRec,
          'Sin tratar + Gestionados = Entrados. «Pasados a comerciales», «Citas», «Descartados» y «Vendidos» no se suman entre sí (un mismo lead puede contar en varias). «Vendidos» cuenta por fecha de venta, aunque el lead entrara en un período anterior.'
        );
      }

      // Detalle por Marca
      if (filasMarcaRec.length > 0) {
        drawTablaGenerica(
          `Detalle por Marca (${filasMarcaRec.length})`,
          [
            { lbl: 'Marca', key: 'nombre' },
            { lbl: 'Leads entrados', key: 'entrados' },
            { lbl: 'Pasados a comerciales', key: 'pasados' },
            { lbl: 'Citas', key: 'citas' },
            { lbl: 'Descartados', key: 'descartados' },
            { lbl: 'Vendidos', key: 'vendidos', verde: true },
          ],
          filasMarcaRec,
          totMarcaRec,
          null
        );
      }

      // Motivos de descarte (barras)
      if (motivosDescarte.length > 0) {
        const mVisible = Math.min(motivosDescarte.length, 10);
        const mH = Math.max(22, 8 + mVisible * 4 + 2);
        ensureSpace(mH + 2);
        drawDistribution(MARGIN, y, PW - MARGIN * 2, mH, `Motivos de descarte (${cancelados} leads descartados en el período)`, motivosDescarte.slice(0, 10), cancelados);
        y += mH + 3;
      }

      // Tratamiento de leads (estados)
      if (incTratamiento) {
        const trH = 22;
        ensureSpace(trH + 2);
        doc.setDrawColor(220, 220, 220);
        doc.roundedRect(MARGIN, y, PW - MARGIN * 2, trH, 1.2, 1.2, 'S');
        doc.setFillColor(...COL_BLUE);
        doc.rect(MARGIN, y, PW - MARGIN * 2, 4.5, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.text('Tratamiento de Leads (estado actual)', MARGIN + 2, y + 3.2);
        // Píldoras por estado
        const estW = (PW - MARGIN * 2 - 4) / porEstado.length;
        porEstado.forEach(([est, c], i) => {
          const px = MARGIN + 2 + i * estW;
          const py = y + 7;
          const colorHex = (ESTADOS_LEAD[est] && ESTADOS_LEAD[est].color) || '#cccccc';
          const rgb = colorHex.match(/[A-Fa-f0-9]{2}/g)?.map(h => parseInt(h, 16)) || [200, 200, 200];
          doc.setFillColor(...rgb);
          doc.roundedRect(px, py, estW - 2, 11, 1, 1, 'F');
          doc.setTextColor(20, 20, 20);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(11);
          doc.text(String(c), px + (estW - 2) / 2, py + 5.5, { align: 'center' });
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(6.5);
          const pct = total > 0 ? Math.round((c / total) * 100) : 0;
          doc.text(`${est} · ${pct}%`, px + (estW - 2) / 2, py + 9.5, { align: 'center' });
        });
        y += trH + 3;
      }

      // Tiempo medio de respuesta (global + por comercial)
      if (incTiempoResp && tiempoRespPorComercial.length > 0) {
        const headerH = 4.5;
        const padTop = 4;
        const rowH = 2.8;
        const trH = headerH + padTop + rowH * (tiempoRespPorComercial.length + 1) + 3;
        ensureSpace(trH + 2);
        doc.setDrawColor(220, 220, 220);
        doc.roundedRect(MARGIN, y, PW - MARGIN * 2, trH, 1.2, 1.2, 'S');
        doc.setFillColor(...COL_BLUE);
        doc.rect(MARGIN, y, PW - MARGIN * 2, headerH, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.text(`Tiempo medio de respuesta — global ${tiempoRespuestaTxt} · proxy fecha_modificacion − fecha_creacion (leads que ya no están en Pendiente)`, MARGIN + 2, y + 3.2);

        const x = MARGIN;
        const cw = PW - MARGIN * 2;
        const padX = 2;
        const cols = [
          { lbl: 'Comercial', x: x + padX, align: 'left' },
          { lbl: 'Leads', x: x + cw - padX - 80, align: 'right' },
          { lbl: 'Media', x: x + cw - padX - 60, align: 'right' },
          { lbl: 'Mediana', x: x + cw - padX - 36, align: 'right' },
          { lbl: '< 24 h', x: x + cw - padX - 18, align: 'right' },
          { lbl: '< 72 h', x: x + cw - padX, align: 'right' }
        ];
        let headerY = y + headerH + padTop;
        doc.setTextColor(...COL_GRAY);
        doc.setFontSize(6);
        doc.setFont('helvetica', 'bold');
        cols.forEach(c => doc.text(c.lbl, c.x, headerY, { align: c.align }));
        doc.setDrawColor(...COL_BLUE);
        doc.setLineWidth(0.15);
        doc.line(x + 1, headerY + 1.2, x + cw - 1, headerY + 1.2);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6);
        doc.setTextColor(40, 40, 40);
        let ry = headerY + 4;
        tiempoRespPorComercial.forEach(r => {
          const labelMaxChars = 30;
          const nombre = String(r.nombre).length > labelMaxChars
            ? String(r.nombre).slice(0, labelMaxChars - 1) + '…'
            : r.nombre;
          doc.text(nombre, cols[0].x, ry, { align: 'left' });
          doc.text(String(r.leads), cols[1].x, ry, { align: 'right' });
          doc.text(formatMs(r.mediaMs), cols[2].x, ry, { align: 'right' });
          doc.text(formatMs(r.medianaMs), cols[3].x, ry, { align: 'right' });
          doc.text(`${r.pctUnder24}%`, cols[4].x, ry, { align: 'right' });
          doc.text(`${r.pctUnder72}%`, cols[5].x, ry, { align: 'right' });
          ry += rowH;
        });
        y += trH + 3;
      }

      // Movimientos + Actividad reciente (2 columnas si ambos)
      if (incMovimientos || incActividad) {
        const movH = 32;
        ensureSpace(movH + 2);
        const showBoth = incMovimientos && incActividad;
        const cw = showBoth ? (PW - MARGIN * 2 - 3) / 2 : PW - MARGIN * 2;

        if (incMovimientos) {
          const x = MARGIN;
          doc.setDrawColor(220, 220, 220);
          doc.roundedRect(x, y, cw, movH, 1.2, 1.2, 'S');
          doc.setFillColor(...COL_BLUE);
          doc.rect(x, y, cw, 4.5, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(8);
          doc.text(`Cambios y Movimientos (${movimientosTotal}) — tratamiento por comercial`, x + 2, y + 3.2);
          // Cabecera de columnas
          doc.setTextColor(...COL_GRAY);
          doc.setFontSize(6);
          doc.setFont('helvetica', 'bold');
          // Calculamos posiciones X para cw variable
          const padX = 2;
          const cols = [
            { lbl: 'Comercial', x: x + padX, align: 'left' },
            { lbl: 'Cambios', x: x + cw - padX - 70, align: 'right' },
            { lbl: 'Atend.', x: x + cw - padX - 56, align: 'right' },
            { lbl: 'Cit.', x: x + cw - padX - 42, align: 'right' },
            { lbl: 'Ofer.', x: x + cw - padX - 28, align: 'right' },
            { lbl: 'Vend.', x: x + cw - padX - 14, align: 'right' },
            { lbl: 'Canc.', x: x + cw - padX, align: 'right' }
          ];
          let headerY = y + 8;
          cols.forEach(c => doc.text(c.lbl, c.x, headerY, { align: c.align }));
          // Línea bajo cabecera
          doc.setDrawColor(...COL_BLUE);
          doc.setLineWidth(0.15);
          doc.line(x + 1, headerY + 1.2, x + cw - 1, headerY + 1.2);
          // Filas
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(6);
          doc.setTextColor(40, 40, 40);
          let ry = headerY + 4;
          tratamientoPorUsuario.forEach(t => {
            const labelMaxChars = 22;
            const nombre = String(t.nombre).length > labelMaxChars
              ? String(t.nombre).slice(0, labelMaxChars - 1) + '…'
              : t.nombre;
            doc.setTextColor(40, 40, 40);
            doc.text(nombre, cols[0].x, ry, { align: 'left' });
            doc.text(String(t.total), cols[1].x, ry, { align: 'right' });
            doc.text(String(t.atendidos), cols[2].x, ry, { align: 'right' });
            doc.text(String(t.citados), cols[3].x, ry, { align: 'right' });
            doc.text(String(t.ofertados), cols[4].x, ry, { align: 'right' });
            if (t.vendidos > 0) doc.setTextColor(...COL_GREEN);
            doc.text(String(t.vendidos), cols[5].x, ry, { align: 'right' });
            doc.setTextColor(40, 40, 40);
            doc.text(String(t.cancelados), cols[6].x, ry, { align: 'right' });
            ry += 2.8;
          });
        }

        if (incActividad) {
          const x = showBoth ? MARGIN + cw + 3 : MARGIN;
          doc.setDrawColor(220, 220, 220);
          doc.roundedRect(x, y, cw, movH, 1.2, 1.2, 'S');
          doc.setFillColor(...COL_BLUE);
          doc.rect(x, y, cw, 4.5, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(8);
          doc.text('Actividad reciente', x + 2, y + 3.2);
          doc.setTextColor(40, 40, 40);
          doc.setFontSize(6.5);
          doc.setFont('helvetica', 'normal');
          let ry = y + 8;
          // Cabecera columnas
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(6);
          doc.setTextColor(...COL_GRAY);
          doc.text('Fecha', x + 2, ry);
          doc.text('Comercial', x + 18, ry);
          doc.text('Cliente / acción', x + 44, ry);
          doc.text('Estado', x + cw - 2, ry, { align: 'right' });
          doc.setDrawColor(...COL_BLUE);
          doc.setLineWidth(0.15);
          doc.line(x + 1, ry + 1.2, x + cw - 1, ry + 1.2);
          ry += 4;
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(6);
          actividadReciente.slice(0, 7).forEach(e => {
            const fecha = e.timestamp ? new Date(e.timestamp).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-';
            const usuario = String(e.user_email || '-');
            const usrLabel = usuario.length > 14 ? usuario.slice(0, 13) + '…' : usuario;
            const cliente = String(e.lead || '-');
            const cliLabel = cliente.length > 22 ? cliente.slice(0, 21) + '…' : cliente;
            const estadoNuevo = String(e.estadoNuevo || e.action || '-');
            const estLabel = estadoNuevo.length > 12 ? estadoNuevo.slice(0, 11) + '…' : estadoNuevo;
            doc.setTextColor(...COL_GRAY);
            doc.text(fecha, x + 2, ry);
            doc.setTextColor(40, 40, 40);
            doc.text(usrLabel, x + 18, ry);
            doc.text(cliLabel, x + 44, ry);
            doc.setTextColor(...COL_BLUE);
            doc.text(estLabel, x + cw - 2, ry, { align: 'right' });
            doc.setTextColor(40, 40, 40);
            ry += 2.8;
          });
        }
        y += movH + 3;
      }

      // Resumen narrativo — multi-página si la narrativa es larga
      const lineH = 4.0;
      const headerNarrH = 4.5;
      ensureSpace(headerNarrH + lineH * 4);

      // Pintar cabecera del resumen
      const drawResumenHeader = (label) => {
        doc.setFillColor(...COL_BLUE);
        doc.rect(MARGIN, y, PW - MARGIN * 2, headerNarrH, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.text(label, MARGIN + 2, y + 3.2);
        y += headerNarrH + 3;
        doc.setTextColor(35, 35, 35);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.2);
      };
      drawResumenHeader('Resumen ejecutivo' + (incIA ? ' (IA)' : ''));

      // splitTextToSize DEBE llamarse DESPUÉS de fijar la fuente, si no usa la
      // medición anterior (size=6.5 de la última tabla) y las líneas salen más
      // anchas que la página, cortándose por la derecha.
      // Padding lateral aumentado a 8mm (4mm a cada lado) para asegurar margen.
      const narrWidth = PW - MARGIN * 2 - 8;
      const splitAll = doc.splitTextToSize(narrativa || '', narrWidth);

      for (let i = 0; i < splitAll.length; i++) {
        if (y + lineH > PH - FOOTER_RESERVED) {
          addNewPage();
          y = TOP_AFTER_BREAK;
          drawResumenHeader('Resumen ejecutivo (continuación)');
          // Re-medir tras el header (asegura que la fuente sigue siendo 8.2)
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8.2);
          doc.setTextColor(35, 35, 35);
        }
        doc.text(splitAll[i], MARGIN + 4, y);
        y += lineH;
      }

      // Footer en TODAS las páginas con numeración correcta
      const footerY = PH - 10;
      const totalPages = doc.internal.getNumberOfPages();
      for (let p = 1; p <= totalPages; p++) {
        doc.setPage(p);
        doc.setDrawColor(...COL_BLUE);
        doc.setLineWidth(0.3);
        doc.line(MARGIN, footerY - 1, PW - MARGIN, footerY - 1);
        doc.setTextColor(...COL_GRAY);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.text(`Grupo Dimoauto · Informe generado por ${adminNombre || 'Administración'}`, MARGIN, footerY + 3);
        doc.text(`Página ${p} de ${totalPages}`, PW - MARGIN, footerY + 3, { align: 'right' });
      }

      const fname = `informe-comercial-${desde.toISOString().split('T')[0]}_${hasta.toISOString().split('T')[0]}.pdf`;
      doc.save(fname);
      setProgresoMsg('');
    } catch (e) {
      secureLog.error('InformesPage.generarPDF', e);
      setErrorMsg('No se pudo generar el informe. Revisa la conexión y vuelve a intentarlo.');
    } finally {
      setGenerando(false);
      setTimeout(() => setProgresoMsg(''), 1500);
    }
  };

  // Render configurador
  const SectionTitle = ({ children }) => (
    <h3 style={{margin: '0 0 10px 0', fontSize: '14px', color: '#005187', fontWeight: 600, borderBottom: '2px solid #e8f0fa', paddingBottom: '6px'}}>{children}</h3>
  );

  const Chip = ({ active, onClick, children }) => (
    <button onClick={onClick} type="button" style={{
      padding: '5px 10px', borderRadius: 14, border: active ? '1px solid #005187' : '1px solid #e7e9ec',
      background: active ? '#005187' : '#fff', color: active ? '#fff' : '#2b333d',
      fontSize: '12px', cursor: 'pointer', fontWeight: 500, transition: 'all 0.15s'
    }}>{children}</button>
  );

  const MultiSelectGroup = ({ title, all, selected, setSelected }) => (
    <div style={{background: '#f4f5f7', padding: '12px', borderRadius: 10, border: '1px solid #e7e9ec'}}>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px'}}>
        <strong style={{fontSize: '13px', color: '#2b333d'}}>{title} ({selected.length === 0 ? 'Todos' : selected.length})</strong>
        <button type="button" onClick={() => toggleAll(selected, setSelected, all)} style={{fontSize: '11px', background: 'none', border: '1px solid #e7e9ec', padding: '3px 8px', borderRadius: 10, cursor: 'pointer', color: '#2b333d'}}>
          {selected.length === all.length ? 'Limpiar' : 'Todos'}
        </button>
      </div>
      <div style={{display: 'flex', flexWrap: 'wrap', gap: '6px'}}>
        {all.length === 0 && <span style={{color: '#9aa4af', fontSize: '12px'}}>Sin opciones disponibles</span>}
        {all.map(v => (
          <Chip key={v} active={selected.includes(v)} onClick={() => toggleSel(selected, setSelected, v)}>{v}</Chip>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{padding: '24px', maxWidth: '1400px', margin: '0 auto'}}>
      <div style={{marginBottom: '20px'}}>
        <h2 style={{margin: 0, fontSize: '22px', color: '#2b333d', fontWeight: 600}}>📑 Generador de Informes</h2>
        <p style={{margin: '4px 0 0 0', color: '#8a929c', fontSize: '13px'}}>
          Configura los parámetros y genera un informe profesional en PDF (1 página) listo para presentar a dirección.
        </p>
      </div>

      <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px'}}>
        {/* Período */}
        <div style={{background: '#fff', padding: '16px', borderRadius: 10, border: '1px solid #e7e9ec'}}>
          <SectionTitle>1. Período del informe</SectionTitle>
          <div style={{display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px'}}>
            {INFORME_PERIODOS.map(p => (
              <Chip key={p.id} active={periodo === p.id} onClick={() => setPeriodo(p.id)}>{p.label}</Chip>
            ))}
          </div>
          {periodo === 'personalizado' && (
            <div style={{display: 'flex', gap: '10px', alignItems: 'center', marginTop: '6px'}}>
              <label style={{fontSize: '12px', color: '#2b333d'}}>
                Desde:&nbsp;
                <input type="date" value={customDesde} onChange={e => setCustomDesde(e.target.value)} style={{padding: '5px 8px', border: '1px solid #e7e9ec', borderRadius: 10, fontSize: '12px'}} />
              </label>
              <label style={{fontSize: '12px', color: '#2b333d'}}>
                Hasta:&nbsp;
                <input type="date" value={customHasta} onChange={e => setCustomHasta(e.target.value)} style={{padding: '5px 8px', border: '1px solid #e7e9ec', borderRadius: 10, fontSize: '12px'}} />
              </label>
            </div>
          )}
          <div style={{marginTop: '10px', fontSize: '12px', color: '#8a929c'}}>
            Leads en el período seleccionado: <strong style={{color: '#005187'}}>{leadsInforme.length}</strong>
          </div>
        </div>

        {/* Secciones */}
        <div style={{background: '#fff', padding: '16px', borderRadius: 10, border: '1px solid #e7e9ec'}}>
          <SectionTitle>2. Secciones a incluir</SectionTitle>
          <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
            <label style={{display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer'}}>
              <input type="checkbox" checked={incTratamiento} onChange={e => setIncTratamiento(e.target.checked)} />
              Tratamiento de leads (estados, conversión)
            </label>
            <label style={{display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer'}}>
              <input type="checkbox" checked={incMovimientos} onChange={e => setIncMovimientos(e.target.checked)} />
              Cambios y movimientos (auditoría + historial)
            </label>
            <label style={{display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer'}}>
              <input type="checkbox" checked={incActividad} onChange={e => setIncActividad(e.target.checked)} />
              Actividad reciente (últimos eventos)
            </label>
            <label style={{display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer'}}>
              <input type="checkbox" checked={incTiempoResp} onChange={e => setIncTiempoResp(e.target.checked)} />
              Tiempo medio de respuesta (global y por comercial)
            </label>
            <label style={{display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer'}}>
              <input type="checkbox" checked={incIA} onChange={e => setIncIA(e.target.checked)} />
              Resumen ejecutivo con IA (lenguaje natural)
            </label>
          </div>
        </div>
      </div>

      <div style={{background: '#fff', padding: '16px', borderRadius: 10, border: '1px solid #e7e9ec', marginBottom: '16px'}}>
        <SectionTitle>3. Filtros (vacío = Todos)</SectionTitle>
        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px'}}>
          <MultiSelectGroup title="Vendedores" all={vendedoresDisponibles} selected={selVendedores} setSelected={setSelVendedores} />
          <MultiSelectGroup title="Orígenes" all={origenesDisponibles} selected={selOrigenes} setSelected={setSelOrigenes} />
          <MultiSelectGroup title="Marcas" all={marcasDisponibles} selected={selMarcas} setSelected={setSelMarcas} />
          <MultiSelectGroup title="Centros" all={centrosDisponibles} selected={selCentros} setSelected={setSelCentros} />
        </div>
      </div>

      <div style={{display: 'flex', alignItems: 'center', gap: '14px', justifyContent: 'flex-end', flexWrap: 'wrap'}}>
        {progresoMsg && <span style={{color: '#005187', fontSize: '13px'}}>⏳ {progresoMsg}</span>}
        {errorMsg && <span style={{color: '#a32b25', fontSize: '13px'}}>⚠️ {errorMsg}</span>}
        <button
          type="button"
          onClick={generarPDF}
          disabled={generando || leadsInforme.length === 0}
          style={{
            padding: '12px 22px', borderRadius: 10, border: 'none',
            background: generando || leadsInforme.length === 0 ? '#9aa4af' : '#005187',
            color: '#fff', fontSize: '14px', fontWeight: 600,
            cursor: generando || leadsInforme.length === 0 ? 'not-allowed' : 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: '8px'
          }}
        >
          {generando ? '⏳ Generando...' : '📄 Generar Informe PDF'}
        </button>
      </div>

      {leadsInforme.length === 0 && (
        <div style={{marginTop: '14px', padding: '12px', background: '#fbf1de', borderRadius: 10, color: '#a8660f', fontSize: '13px', border: '1px solid #f5dfae'}}>
          No hay leads que cumplan los criterios seleccionados en este período. Ajusta los filtros o el rango de fechas.
        </div>
      )}
    </div>
  );
};

const AnalysisPage = ({ leads, field, dimension, showColorIndicator = false }) => {
  const [modalData, setModalData] = useState({ isOpen: false, leads: [], titulo: '' });
  
  const data = useMemo(() => aggregateByField(leads, field), [leads, field]);
  const totals = useMemo(() => {
    const t = { total: 0, porcentajeCierre: '0.00' };
    Object.keys(ESTADOS_LEAD).forEach(e => t[e] = 0);
    data.forEach(item => {
      t.total += item.total;
      Object.keys(ESTADOS_LEAD).forEach(e => t[e] += item[e] || 0);
    });
    t.porcentajeCierre = t.total > 0 ? ((t['Vendido'] || 0) / t.total * 100).toFixed(2) : '0.00';
    return t;
  }, [data]);
  
  const maxValues = useMemo(() => {
    const max = {};
    Object.keys(ESTADOS_LEAD).forEach(e => max[e] = Math.max(...data.map(item => item[e] || 0)));
    return max;
  }, [data]);

  // Función para abrir modal con leads filtrados
  const handleCellClick = (itemNombre, estado = null, count) => {
    if (count === 0) return;
    
    let filteredLeads = leads.filter(lead => lead[field] === itemNombre);
    let titulo = `${itemNombre}`;
    
    if (estado) {
      filteredLeads = filteredLeads.filter(lead => lead.estado === estado);
      titulo = `${itemNombre} - ${estado}`;
    } else {
      titulo = `${itemNombre} - Total`;
    }
    
    setModalData({ isOpen: true, leads: filteredLeads, titulo });
  };

  // Función para click en totales
  const handleTotalClick = (estado = null, count) => {
    if (count === 0) return;
    
    let filteredLeads = leads;
    let titulo = 'TOTALES';
    
    if (estado) {
      filteredLeads = leads.filter(lead => lead.estado === estado);
      titulo = `TOTALES - ${estado}`;
    }
    
    setModalData({ isOpen: true, leads: filteredLeads, titulo });
  };

  // Misma tabla que el resto de la suite: cabecera gris clara en versalitas y
  // celdas de 12,5 px separadas por una línea fina.
  const thStyle = {...celdaTitulo, padding: '8px 6px', textAlign: 'center', whiteSpace: 'nowrap'};
  const tdStyle = {padding: '7px 6px', borderBottom: SEPARADOR_FILA, color: C.page, fontSize: '12.5px', textAlign: 'center'};

  return (
    <div>
      <ExportButtons leads={leads} />
      <div className="fg-card" style={{...tarjeta, overflow: 'hidden', padding: 0}}>
        <div style={{overflowX: 'auto'}}>
          <table style={{width: '100%', borderCollapse: 'collapse'}}>
            <thead>
              <tr>
                <th style={{...thStyle, textAlign: 'left'}}>{dimension}</th>
                <th style={thStyle}>Total</th>
                {Object.keys(ESTADOS_LEAD).map(estado => <th key={estado} style={thStyle}>{estado}</th>)}
                <th style={thStyle}>% Cierre</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, idx) => (
                <tr key={idx} style={{background: idx % 2 === 0 ? '#fff' : '#fbfcfd'}}>
                  <td style={{...tdStyle, textAlign: 'left', fontWeight: 700}}>
                    {showColorIndicator && <span style={{display: 'inline-block', width: 10, height: 10, borderRadius: 999, marginRight: 7, verticalAlign: 'middle', background: ORIGENES.find(o => o.nombre === item.nombre)?.color || C.gris}} />}
                    {item.nombre}
                  </td>
                  <td 
                    style={{...tdStyle, fontWeight: 600, cursor: item.total > 0 ? 'pointer' : 'default'}} 
                    onClick={() => handleCellClick(item.nombre, null, item.total)}
                    title={item.total > 0 ? 'Click para ver desglose' : ''}
                  >
                    {item.total}
                  </td>
                  {Object.keys(ESTADOS_LEAD).map(estado => (
                    <td 
                      key={estado} 
                      style={{...tdStyle, background: getHeatColor(item[estado] || 0, maxValues[estado], colorEstado(estado)), cursor: (item[estado] || 0) > 0 ? 'pointer' : 'default'}}
                      onClick={() => handleCellClick(item.nombre, estado, item[estado] || 0)}
                      title={(item[estado] || 0) > 0 ? 'Click para ver desglose' : ''}
                    >
                      {item[estado] || 0}
                    </td>
                  ))}
                  <td style={{...tdStyle, fontWeight: 600}}>{item.porcentajeCierre} %</td>
                </tr>
              ))}
              <tr style={{background: C.brand}}>
                <td style={{...tdStyle, color: 'white', fontWeight: 800, textAlign: 'left'}}>TOTALES</td>
                <td 
                  style={{...tdStyle, color: 'white', fontWeight: 800, cursor: totals.total > 0 ? 'pointer' : 'default'}}
                  onClick={() => handleTotalClick(null, totals.total)}
                  title={totals.total > 0 ? 'Click para ver desglose' : ''}
                >
                  {totals.total}
                </td>
                {Object.keys(ESTADOS_LEAD).map(estado => (
                  <td 
                    key={estado} 
                    style={{...tdStyle, color: 'white', fontWeight: 800, cursor: totals[estado] > 0 ? 'pointer' : 'default'}}
                    onClick={() => handleTotalClick(estado, totals[estado])}
                    title={totals[estado] > 0 ? 'Click para ver desglose' : ''}
                  >
                    {totals[estado]}
                  </td>
                ))}
                <td style={{...tdStyle, color: 'white', fontWeight: 800}}>{totals.porcentajeCierre} % *</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div style={{padding: '10px 16px', background: '#f7f8fa', borderTop: `1px solid ${C.line}`, fontSize: '12.5px', color: C.muted}}>
          * Los datos están desglosados por contactos del lead y por {dimension.toLowerCase()} • Click en cualquier número para ver el desglose
        </div>
      </div>
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '16px', marginTop: '16px'}}>
        <DonutChart data={data} title={`Segmentación por ${dimension}`} />
      </div>
      {/* Mostrar desglose de modelos solo en Datos marcas */}
      {field === 'marca' && <ModelosByMarcaCard leads={leads} />}
      
      {/* Modal de desglose */}
      <LeadsDesgloseModal 
        isOpen={modalData.isOpen} 
        onClose={() => setModalData({ ...modalData, isOpen: false })} 
        leads={modalData.leads} 
        titulo={modalData.titulo} 
      />
    </div>
  );
};

// Main App
// Helper para obtener fechas dinámicas
const getDefaultDates = () => {
  const today = new Date();
  const hasta = today.toISOString().split('T')[0];
  const desde = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  return { desde, hasta };
};


// ==================== HOOK DETECCIÓN MÓVIL ====================
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
};

// ==================== COMPONENTES MÓVILES FIELES AL DISEÑO ====================

// Header con logo y usuario para cerrar sesión
const MobileAdminHeader = ({ onLogout, onRefresh, isLoading }) => {
  const [showMenu, setShowMenu] = useState(false);
  return (
    <>
      <header style={{ background: '#ffffff', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e7e9ec' }}>
        <div>
          <div style={{ fontSize: '17px', fontWeight: 800, color: C.ink, letterSpacing: '-0.2px' }}>Lead Center</div>
          <div style={{ fontSize: '11.5px', color: C.muted, marginTop: 2 }}>Panel de administración</div>
        </div>
        <button onClick={() => setShowMenu(true)} style={{ background: '#f4f5f7', border: '1px solid #e7e9ec', borderRadius: '50%', width: '40px', height: '40px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontSize: '20px' }}>👤</span>
        </button>
      </header>
      {showMenu && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: VELO_MODAL, zIndex: 200 }} onClick={() => setShowMenu(false)}>
          <div style={{ position: 'absolute', top: '60px', right: '16px', background: 'white', borderRadius: 12, padding: '8px', minWidth: '200px', boxShadow: SOMBRA_MODAL }} onClick={e => e.stopPropagation()}>
            <button onClick={() => { onRefresh(); setShowMenu(false); }} style={{ width: '100%', padding: '12px 16px', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '14px', borderBottom: '1px solid #e7e9ec' }}>🔄 Actualizar datos</button>
            <button onClick={onLogout} style={{ width: '100%', padding: '12px 16px', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', color: '#d23b34', fontWeight: 500, fontSize: '14px' }}>🚪 Cerrar Sesión</button>
          </div>
        </div>
      )}
    </>
  );
};

// Barra azul con selector de vista (sin iconos hamburguesa ni ajustes)
const MobileNavBar = ({ activeView, setActiveView, viewOptions }) => (
  <div style={{ background: C.card, padding: '10px 16px', borderBottom: `1px solid ${C.line}` }}>
    <select value={activeView} onChange={(e) => setActiveView(e.target.value)} style={{ ...campo, width: '100%', padding: '11px 14px', fontWeight: 700, cursor: 'pointer', textAlign: 'center' }}>
      {viewOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
    </select>
  </div>
);

// Vista INICIO - Estado Lead Center (Imagen 1)
const MobileInicioView = ({ leads, comerciales }) => {
  const stats = useMemo(() => {
    const c = {}; leads.forEach(l => { c[l.estado] = (c[l.estado] || 0) + 1; }); return c;
  }, [leads]);

  const vendidos = stats['Vendido'] || 0;
  const pendientes = stats['Pendiente'] || 0;
  const atendido = stats['Atendido'] || 0;
  const citado = stats['Citado'] || 0;
  const ofertado = stats['Ofertado'] || 0;
  const cancelados = stats['Cancelado'] || 0;

  const particulares = leads.filter(l => l.tipoCliente === 'Particular').length;
  const empresas = leads.filter(l => l.tipoCliente === 'Empresa').length;
  const vnCount = leads.filter(l => l.tipoVenta === 'VN').length;
  const voCount = leads.filter(l => l.tipoVenta === 'VO').length;
  const b2bCount = leads.filter(l => l.tipoVenta === 'B2B').length;
  const leadsAsignados = leads.filter(l => l.vendedor).length;
  const leadsSinAsignar = leads.length - leadsAsignados;
  const comercialesActivos = comerciales?.filter(c => c.activo)?.length || 0;

  const segmentacion = [
    { nombre: 'Pendiente', key: 'Pendiente', color: '#C0C0C0' },
    { nombre: 'Contactado', key: 'Contactado', color: '#7DD3FC' },
    { nombre: 'Atendido', key: 'Atendido', color: '#FFD700' },
    { nombre: 'Citado', key: 'Citado', color: '#FFB366' },
    { nombre: 'Ofertado', key: 'Ofertado', color: '#FFA500' },
    { nombre: 'Cancelado', key: 'Cancelado', color: '#808080' },
    { nombre: 'Vendido', key: 'Vendido', color: '#32CD32' }
  ];

  const pct = (n) => leads.length > 0 ? Math.round((n / leads.length) * 100) : 0;

  return (
    <div style={{ padding: '12px', paddingBottom: '100px', background: '#f4f5f7' }}>
      {/* Estado Lead Center */}
      <div className="fg-card" style={{ ...tarjeta, padding: '16px', marginBottom: '12px', boxShadow: 'none' }}>
        <div style={{ fontSize: '18px', fontWeight: 700, color: '#2b333d', marginBottom: '16px' }}>Estado Lead Center</div>
        
        {/* 3 métricas principales */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '20px' }}>
          <div style={{ textAlign: 'center', padding: '12px 8px', background: '#f4f5f7', borderRadius: 10, border: '1px solid #e7e9ec' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
              <span style={{ fontSize: '18px', color: '#1a9cd4' }}>🔍</span>
              <span style={{ fontSize: '26px', fontWeight: 700, color: '#005187' }}>{leads.length}</span>
            </div>
            <div style={{ fontSize: '13px', color: '#8a929c', marginTop: '4px' }}>Leads Totales</div>
          </div>
          <div style={{ textAlign: 'center', padding: '12px 8px', background: '#f4f5f7', borderRadius: 10, border: '1px solid #e7e9ec' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
              <span style={{ fontSize: '18px', color: '#2e9e5b' }}>👤</span>
              <span style={{ fontSize: '26px', fontWeight: 700, color: '#005187' }}>{Math.floor(leads.length * 0.9)}</span>
              <span style={{ fontSize: '13px', color: '#2e9e5b', fontWeight: 600 }}>18%</span>
            </div>
            <div style={{ fontSize: '13px', color: '#8a929c', marginTop: '4px' }}>Leads Únicos</div>
          </div>
          <div style={{ textAlign: 'center', padding: '12px 8px', background: '#f4f5f7', borderRadius: 10, border: '1px solid #e7e9ec' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
              <span style={{ fontSize: '18px', color: '#8a929c' }}>📞</span>
              <span style={{ fontSize: '26px', fontWeight: 700, color: '#005187' }}>0</span>
            </div>
            <div style={{ fontSize: '14px', color: '#8a929c', marginTop: '4px' }}>En validación</div>
          </div>
        </div>

        {/* Segmentación de Leads */}
        <div style={{ borderTop: '1px solid #e7e9ec', paddingTop: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
            <span style={{ fontSize: '14px', fontWeight: 600, color: '#2b333d' }}>Segmentación de Leads</span>
            <span style={{ fontSize: '14px', color: '#8a929c' }}>100%</span>
          </div>
          {segmentacion.map(item => {
            const count = stats[item.key] || 0;
            return (
            <div key={item.nombre} style={{ display: 'grid', gridTemplateColumns: '10px 110px 40px 1fr 40px', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: item.color }}></div>
              <span style={{ fontSize: '13px', color: '#2b333d' }}>{item.nombre}</span>
              <span style={{ fontSize: '13px', color: '#8a929c', textAlign: 'right' }}>{pct(count)}%</span>
              <div style={{ height: '20px', background: '#e7e9ec', borderRadius: 10, overflow: 'hidden', position: 'relative' }}>
                <div style={{ width: `${Math.max(pct(count) * 2.5, count > 0 ? 15 : 0)}%`, height: '100%', background: item.color, borderRadius: 10, display: 'flex', alignItems: 'center', paddingLeft: '6px' }}>
                  {count > 0 && <span style={{ color: 'white', fontSize: '14px', fontWeight: 600, textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>{count}</span>}
                </div>
              </div>
              <span style={{ fontSize: '13px', color: '#8a929c', textAlign: 'right' }}>{pct(count)}%</span>
            </div>
            );
          })}
        </div>
      </div>

      {/* Tipo cliente y Tipo vehículo */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
        <div className="fg-card" style={{ ...tarjeta, padding: '14px', boxShadow: 'none' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #e7e9ec' }}>Tipo de cliente</div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span>👤</span><span style={{ fontSize: '14px' }}>Particulares</span></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><span style={{ fontSize: '14px', color: '#8a929c' }}>{pct(particulares)}%</span><span style={{ fontWeight: 700, color: '#005187' }}>{particulares}</span></div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span>🏢</span><span style={{ fontSize: '14px' }}>Empresas</span></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><span style={{ fontSize: '14px', color: '#8a929c' }}>{pct(empresas)}%</span><span style={{ fontWeight: 700, color: '#005187' }}>{empresas}</span></div>
          </div>
        </div>
        <div className="fg-card" style={{ ...tarjeta, padding: '14px', boxShadow: 'none' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #e7e9ec' }}>Tipo de vehículo</div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span>🚗</span><span style={{ fontSize: '14px' }}>VN Nuevos</span></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><span style={{ fontSize: '14px', color: '#8a929c' }}>{pct(vnCount)}%</span><span style={{ fontWeight: 700, color: '#005187' }}>{vnCount}</span></div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span>🚙</span><span style={{ fontSize: '14px' }}>VO Ocasión</span></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><span style={{ fontSize: '14px', color: '#8a929c' }}>{pct(voCount)}%</span><span style={{ fontWeight: 700, color: '#005187' }}>{voCount}</span></div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span>🏢</span><span style={{ fontSize: '14px' }}>Empresa (B2B)</span></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><span style={{ fontSize: '14px', color: '#8a929c' }}>{pct(b2bCount)}%</span><span style={{ fontWeight: 700, color: '#2e9e5b' }}>{b2bCount}</span></div>
          </div>
        </div>
      </div>

      {/* Asignaciones y Ventas */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
        <div className="fg-card" style={{ ...tarjeta, padding: '14px', boxShadow: 'none' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #e7e9ec' }}>Asignaciones</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '14px' }}><span>👥 Comerciales Asignados</span><span style={{ fontWeight: 700 }}>{comercialesActivos}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '14px' }}><span>📋 Leads Asignados</span><span style={{ fontWeight: 700 }}>{leadsAsignados}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}><span>⚠️ Leads sin asignar</span><span style={{ fontWeight: 700 }}>{leadsSinAsignar}</span></div>
        </div>
        <div className="fg-card" style={{ ...tarjeta, padding: '14px', boxShadow: 'none' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #e7e9ec' }}>Ventas</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '14px' }}><span>💰 Ventas</span><span style={{ fontWeight: 700, color: '#2e9e5b' }}>{vendidos}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}><span>📅 Periodo</span><span style={{ fontWeight: 700 }}>{vendidos}</span></div>
        </div>
      </div>

      {/* 3 Tarjetas: Ventas, VN, VO */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
        <div style={{ borderRadius: 12, overflow: 'hidden', boxShadow: 'none' }}>
          <div style={{ background: '#f7f8fa', padding: '9px', textAlign: 'center', borderBottom: `1px solid ${C.line}` }}><span style={{ color: C.muted, fontWeight: 800, fontSize: '10.5px', letterSpacing: '0.4px', textTransform: 'uppercase' }}>Ventas</span></div>
          <div style={{ background: 'white', padding: '12px', textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end', gap: '6px', marginBottom: '8px' }}>
              <div style={{ width: '18px', height: '28px', background: '#e08c1e', borderRadius: '2px' }}></div>
              <div style={{ width: '18px', height: '18px', background: '#e7e9ec', borderRadius: '2px' }}></div>
            </div>
            <div style={{ fontSize: '22px', fontWeight: 700 }}>{vendidos}</div>
            <div style={{ fontSize: '13px', color: '#8a929c' }}>Ventas Periodo</div>
          </div>
        </div>
        <div style={{ borderRadius: 12, overflow: 'hidden', boxShadow: 'none' }}>
          <div style={{ background: '#1a9cd4', padding: '10px', textAlign: 'center' }}><span style={{ color: 'white', fontWeight: 600, fontSize: '13px' }}>VN</span></div>
          <div style={{ background: 'white', padding: '12px', textAlign: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}><span>🚗</span><span style={{ fontSize: '18px', fontWeight: 700 }}>{vnCount}</span><span style={{ fontSize: '13px', color: '#8a929c' }}>/{leads.length}</span></div>
            <div style={{ fontSize: '13px', color: '#8a929c' }}>VN Peugeot</div>
            <div style={{ fontSize: '8px', color: '#9aa4af' }}>🏢 CUSTOMER</div>
          </div>
        </div>
        <div style={{ borderRadius: 12, overflow: 'hidden', boxShadow: 'none' }}>
          <div style={{ background: '#7cc4e8', padding: '10px', textAlign: 'center' }}><span style={{ color: '#005187', fontWeight: 600, fontSize: '13px' }}>VO</span></div>
          <div style={{ background: 'white', padding: '12px', textAlign: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}><span>🚙</span><span style={{ fontSize: '18px', fontWeight: 700 }}>{voCount}</span><span style={{ fontSize: '13px', color: '#8a929c' }}>/{leads.length}</span></div>
            <div style={{ fontSize: '13px', color: '#8a929c' }}>VO Peugeot</div>
            <div style={{ fontSize: '8px', color: '#9aa4af' }}>🏢 COCHES.NET</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Vista LEADS - Vendedores con tabla (Imagen 2)
const MobileVendedoresView = ({ leads }) => {
  const estados = ['Pendiente', 'Contactado', 'Atendido', 'Citado', 'Ofertado', 'Cancelado', 'Vendido'];
  const estadoColors = Object.fromEntries(estados.map(e => [e, colorEstado(e)]));

  const porVendedor = useMemo(() => {
    const d = {};
    leads.forEach(l => {
      const v = l.vendedor || 'Sin asignar';
      if (!d[v]) d[v] = { nombre: v, total: 0, estados: {} };
      d[v].total++;
      d[v].estados[l.estado] = (d[v].estados[l.estado] || 0) + 1;
    });
    return Object.values(d).sort((a, b) => b.total - a.total);
  }, [leads]);

  const totals = useMemo(() => {
    const t = {};
    leads.forEach(l => { t[l.estado] = (t[l.estado] || 0) + 1; });
    return t;
  }, [leads]);

  const colors = ['#E53935', '#1E88E5', '#43A047', '#FB8C00', '#8E24AA', '#00ACC1', '#5E35B1', '#D81B60', '#6D4C41', '#546E7A'];

  return (
    <div style={{ paddingBottom: '100px', background: '#f4f5f7' }}>
      {/* Filtros de fecha */}
      <div style={{ padding: '12px 16px', background: 'white', display: 'flex', gap: '12px', borderBottom: '1px solid #e7e9ec' }}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', background: '#f4f5f7', borderRadius: 10, border: '1px solid #e7e9ec' }}>
          <span>📅</span><span style={{ fontSize: '13px' }}>1 - 31 Dic 2025</span>
        </div>
        <div style={{ padding: '10px 16px', background: '#f7f8fa', color: C.page, border: `1px solid ${C.line}`, borderRadius: 10, display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', fontWeight: 700 }}>
          <span>▼</span> Todos los Leads
        </div>
      </div>

      {/* Tabla */}
      <div style={{ overflowX: 'auto', padding: '12px' }}>
        <table style={{ width: '100%', minWidth: '600px', borderCollapse: 'collapse', background: 'white', borderRadius: 10, overflow: 'hidden', boxShadow: 'none' }}>
          <thead>
            <tr>
              <th style={{ ...celdaTitulo, padding: '10px 8px', textAlign: 'left' }}>Num</th>
              <th style={{ ...celdaTitulo, padding: '10px 8px', textAlign: 'left' }}>Lead Tipo</th>
              <th style={{ ...celdaTitulo, padding: '10px 8px', textAlign: 'center' }}>Pendiente</th>
              <th style={{ ...celdaTitulo, padding: '10px 8px', textAlign: 'center' }}>Atendido</th>
              <th style={{ ...celdaTitulo, padding: '10px 8px', textAlign: 'center' }}>Citado</th>
              <th style={{ ...celdaTitulo, padding: '10px 8px', textAlign: 'center' }}>Ofertado</th>
              <th style={{ ...celdaTitulo, padding: '10px 8px', textAlign: 'center' }}>Cancelado</th>
              <th style={{ ...celdaTitulo, padding: '10px 8px', textAlign: 'center' }}>Vendido</th>
            </tr>
          </thead>
          <tbody>
            {porVendedor.slice(0, 7).map((v, i) => (
              <tr key={v.nombre} style={{ borderBottom: '1px solid #e7e9ec' }}>
                <td style={{ padding: '10px 8px', fontSize: '14px', fontWeight: 600, color: '#005187' }}>{String(i + 1).padStart(3, '0')}</td>
                <td style={{ padding: '10px 8px', fontSize: '14px' }}>{v.nombre.split(' ').slice(0, 2).join(' ')}</td>
                <td style={{ padding: '10px 8px', fontSize: '14px', textAlign: 'center' }}>{v.estados['Pendiente'] || 0}</td>
                <td style={{ padding: '10px 8px', fontSize: '14px', textAlign: 'center' }}>{v.estados['Atendido'] || 0}</td>
                <td style={{ padding: '10px 8px', fontSize: '14px', textAlign: 'center' }}>{v.estados['Citado'] || 0}</td>
                <td style={{ padding: '10px 8px', fontSize: '14px', textAlign: 'center' }}>{v.estados['Ofertado'] || 0}</td>
                <td style={{ padding: '10px 8px', fontSize: '14px', textAlign: 'center' }}>{v.estados['Cancelado'] || 0}</td>
                <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                  <span style={{ background: '#2e9e5b', color: 'white', padding: '3px 8px', borderRadius: 10, fontSize: '13px' }}>{v.estados['Vendido'] || 0}</span>
                </td>
              </tr>
            ))}
            <tr style={{ background: '#f4f5f7', fontWeight: 700 }}>
              <td colSpan="2" style={{ padding: '10px 8px', fontSize: '14px' }}>TOTALES</td>
              <td style={{ padding: '10px 8px', fontSize: '14px', textAlign: 'center', color: '#C0C0C0' }}>{totals['Pendiente'] || 0}</td>
              <td style={{ padding: '10px 8px', fontSize: '14px', textAlign: 'center' }}>{totals['Atendido'] || 0}</td>
              <td style={{ padding: '10px 8px', fontSize: '14px', textAlign: 'center' }}>{totals['Citado'] || 0}</td>
              <td style={{ padding: '10px 8px', fontSize: '14px', textAlign: 'center' }}>{totals['Ofertado'] || 0}</td>
              <td style={{ padding: '10px 8px', fontSize: '14px', textAlign: 'center' }}>{totals['Cancelado'] || 0}</td>
              <td style={{ padding: '10px 8px', fontSize: '14px', textAlign: 'center', color: '#2e9e5b' }}>{totals['Vendido'] || 0}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Gráfico de dona */}
      <div style={{ padding: '12px' }}>
        <div className="fg-card" style={{ ...tarjeta, padding: '20px', boxShadow: 'none' }}>
          <div style={{ fontSize: '16px', fontWeight: 600, textAlign: 'center', marginBottom: '20px' }}>Segmentación por Vendedor</div>
          <div style={{ position: 'relative', width: '200px', height: '200px', margin: '0 auto' }}>
            <svg viewBox="0 0 100 100" style={{ transform: 'rotate(-90deg)' }}>
              {porVendedor.slice(0, 10).reduce((acc, v, i) => {
                const pct = leads.length > 0 ? (v.total / leads.length) * 100 : 0;
                const startAngle = acc.offset;
                const endAngle = startAngle + (pct * 3.6);
                const x1 = 50 + 40 * Math.cos(startAngle * Math.PI / 180);
                const y1 = 50 + 40 * Math.sin(startAngle * Math.PI / 180);
                const x2 = 50 + 40 * Math.cos(endAngle * Math.PI / 180);
                const y2 = 50 + 40 * Math.sin(endAngle * Math.PI / 180);
                const largeArc = pct > 50 ? 1 : 0;
                acc.paths.push(
                  <path key={i} d={`M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`} fill={colors[i % colors.length]} />
                );
                acc.offset = endAngle;
                return acc;
              }, { paths: [], offset: 0 }).paths}
              <circle cx="50" cy="50" r="25" fill="white" />
            </svg>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
            {porVendedor.slice(0, 10).map((v, i) => (
              <div key={v.nombre} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px' }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '2px', background: colors[i % colors.length] }}></div>
                <span>{v.nombre.split(' ')[0]} ({Math.round((v.total / leads.length) * 100)}%)</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Vista LEADS - Listado Detalle (Imagen 3)
const MobileListadoDetalleView = ({ leads }) => {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    if (!search) return leads.slice(0, 50);
    const s = search.toLowerCase();
    return leads.filter(l => l.nombre?.toLowerCase().includes(s) || l.origen?.toLowerCase().includes(s)).slice(0, 50);
  }, [leads, search]);

  const getEstadoColor = (estado) => colorEstado(estado);

  return (
    <div style={{ paddingBottom: '100px', background: '#f4f5f7' }}>
      {/* Barra de búsqueda */}
      <div style={{ padding: '12px 16px', background: 'white', display: 'flex', gap: '12px', borderBottom: '1px solid #e7e9ec' }}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', background: '#f4f5f7', borderRadius: 10, border: '1px solid #e7e9ec' }}>
          <span>🔍</span>
          <input type="text" placeholder="Buscar" value={search} onChange={(e) => setSearch(e.target.value)} style={{ border: 'none', background: 'none', outline: 'none', fontSize: '13px', width: '100%' }} />
        </div>
        <div style={{ padding: '10px 16px', background: '#f7f8fa', color: C.page, border: `1px solid ${C.line}`, borderRadius: 10, display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', fontWeight: 700 }}>
          <span>▼</span> Todos los Leads
        </div>
      </div>

      {/* Tabla */}
      <div style={{ overflowX: 'auto', padding: '12px' }}>
        <table style={{ width: '100%', minWidth: '700px', borderCollapse: 'collapse', background: 'white', borderRadius: 10, overflow: 'hidden', boxShadow: 'none' }}>
          <thead>
            <tr>
              <th style={{ ...celdaTitulo, padding: '10px 8px', textAlign: 'left' }}>Num</th>
              <th style={{ ...celdaTitulo, padding: '10px 8px', textAlign: 'left' }}>Lead Tipo</th>
              <th style={{ ...celdaTitulo, padding: '10px 8px', textAlign: 'left' }}>Origen</th>
              <th style={{ ...celdaTitulo, padding: '10px 8px', textAlign: 'left' }}>Detalle Cliente</th>
              <th style={{ ...celdaTitulo, padding: '10px 8px', textAlign: 'left' }}>Estado</th>
              <th style={{ ...celdaTitulo, padding: '10px 8px', textAlign: 'left' }}>Detalle Cita</th>
              <th style={{ ...celdaTitulo, padding: '10px 8px', textAlign: 'left' }}>Publicac</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((lead, i) => (
              <tr key={lead.id} style={{ borderBottom: '1px solid #e7e9ec', background: i % 2 === 0 ? 'white' : '#f4f5f7' }}>
                <td style={{ padding: '10px 8px', fontSize: '14px', fontWeight: 600, color: '#005187' }}>{String(i + 1).padStart(3, '0')}</td>
                <td style={{ padding: '10px 8px' }}>
                  <span style={pildora(C.brand)}>{lead.tipoVenta || 'VN'}</span>
                </td>
                <td style={{ padding: '10px 8px', fontSize: '13px', color: '#005187' }}>{lead.origen}</td>
                <td style={{ padding: '10px 8px', fontSize: '13px' }}>{lead.tipoCliente || 'Origen'}</td>
                <td style={{ padding: '10px 8px' }}>
                  <span style={pildora(getEstadoColor(lead.estado))}>{lead.estado}</span>
                </td>
                <td style={{ padding: '10px 8px', fontSize: '13px' }}>{lead.detalleOrigen || 'Origen'}</td>
                <td style={{ padding: '10px 8px', fontSize: '13px' }}>{lead.fechaCreacion ? new Date(lead.fechaCreacion).toLocaleDateString('es-ES') : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Vista VENTAS
const MobileVentasView = ({ leads }) => {
  const vendidos = leads.filter(l => l.estado === 'Vendido');
  const porVendedor = useMemo(() => {
    const d = {};
    vendidos.forEach(l => { const v = l.vendedor || 'Sin asignar'; d[v] = (d[v] || 0) + 1; });
    return Object.entries(d).sort((a, b) => b[1] - a[1]);
  }, [vendidos]);

  return (
    <div style={{ padding: '12px', paddingBottom: '100px', background: '#f4f5f7' }}>
      <div style={{ background: 'linear-gradient(135deg, #005187, #1a9cd4)', borderRadius: 12, padding: '24px', marginBottom: '16px', textAlign: 'center', color: 'white' }}>
        <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>Total Ventas</div>
        <div style={{ fontSize: '52px', fontWeight: 700 }}>{vendidos.length}</div>
      </div>
      <div className="fg-card" style={{ ...tarjeta, padding: '16px', boxShadow: 'none' }}>
        <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>Ranking de Vendedores</div>
        {porVendedor.map(([vendedor, count], i) => (
          <div key={vendedor} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 0', borderBottom: i < porVendedor.length - 1 ? '1px solid #f4f5f7' : 'none' }}>
            <span style={{ width: '32px', height: '32px', borderRadius: '50%', background: i === 0 ? '#e08c1e' : i === 1 ? '#9aa4af' : i === 2 ? '#CD7F32' : '#e7e9ec', color: i < 3 ? 'white' : '#2b333d', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 600 }}>{i + 1}</span>
            <div style={{ flex: 1, fontWeight: 500 }}>{vendedor}</div>
            <span style={{ fontSize: '20px', fontWeight: 700, color: '#2e9e5b' }}>{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Vista USUARIOS
const MobileUsuariosView = ({ leads }) => {
  const porUsuario = useMemo(() => {
    const d = {};
    leads.forEach(l => {
      const u = l.vendedor || 'Sin asignar';
      if (!d[u]) d[u] = { total: 0, vendidos: 0, pendientes: 0 };
      d[u].total++;
      if (l.estado === 'Vendido') d[u].vendidos++;
      if (l.estado === 'Pendiente') d[u].pendientes++;
    });
    return Object.entries(d).map(([n, s]) => ({ nombre: n, ...s })).sort((a, b) => b.total - a.total);
  }, [leads]);

  return (
    <div style={{ padding: '12px', paddingBottom: '100px', background: '#f4f5f7' }}>
      {porUsuario.map((u, i) => (
        <div key={u.nombre} style={{ background: 'white', borderRadius: 12, padding: '16px', marginBottom: '12px', boxShadow: 'none' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: '#005187', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: '18px' }}>{u.nombre.charAt(0)}</div>
              <span style={{ fontWeight: 600, fontSize: '15px' }}>{u.nombre}</span>
            </div>
            <span style={{ fontSize: '24px', fontWeight: 700, color: '#005187' }}>{u.total}</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
            <div style={{ textAlign: 'center', padding: '10px', background: '#f3faf6', borderRadius: 10 }}><div style={{ fontSize: '18px', fontWeight: 600, color: '#2e9e5b' }}>{u.vendidos}</div><div style={{ fontSize: '14px', color: '#8a929c' }}>Vendidos</div></div>
            <div style={{ textAlign: 'center', padding: '10px', background: '#fbf1de', borderRadius: 10 }}><div style={{ fontSize: '18px', fontWeight: 600, color: '#e08c1e' }}>{u.pendientes}</div><div style={{ fontSize: '14px', color: '#8a929c' }}>Pendientes</div></div>
            <div style={{ textAlign: 'center', padding: '10px', background: '#f4f5f7', borderRadius: 10 }}><div style={{ fontSize: '18px', fontWeight: 600 }}>{u.total - u.vendidos - u.pendientes}</div><div style={{ fontSize: '14px', color: '#8a929c' }}>Otros</div></div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Vista MÁS (Config)
const MobileMasView = ({ admin, onLogout }) => (
  <div style={{ padding: '16px', paddingBottom: '100px', background: '#f4f5f7' }}>
    <div className="fg-card" style={{ ...tarjeta, padding: '24px', marginBottom: '16px', textAlign: 'center', boxShadow: 'none' }}>
      <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'linear-gradient(135deg, #005187, #1a9cd4)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: '32px', color: 'white', fontWeight: 700 }}>{admin?.nombre?.charAt(0) || 'A'}</div>
      <div style={{ fontSize: '20px', fontWeight: 600 }}>{admin?.nombre}</div>
      <div style={{ fontSize: '14px', color: '#8a929c' }}>Administrador</div>
    </div>
    <button onClick={onLogout} style={{ ...botonPeligro, width: '100%', padding: '14px' }}>Cerrar Sesión</button>
  </div>
);

// Bottom Navigation estilo botones (solo texto, sin iconos)
const MobileAdminBottomNav = ({ activeTab, setActiveTab, pendientesCount }) => {
  const tabs = [
    { id: 'inicio', label: 'Inicio' },
    { id: 'leads', label: 'Leads', count: pendientesCount },
    { id: 'ventas', label: 'Ventas' }
  ];
  return (
    <nav style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: C.card, borderTop: `1px solid ${C.line}`, display: 'flex', justifyContent: 'space-around', padding: '10px 16px 26px', gap: '10px', zIndex: 100 }}>
      {tabs.map(t => (
        <button key={t.id} onClick={() => setActiveTab(t.id)} className="fg-nav" style={{
          flex: 1,
          background: activeTab === t.id ? GRAD : 'transparent',
          boxShadow: activeTab === t.id ? '0 6px 16px rgba(0,81,135,.28)' : 'none',
          border: 'none',
          borderRadius: 10,
          padding: '12px 8px',
          cursor: 'pointer',
          position: 'relative'
        }}>
          <span style={{ fontSize: '13px', fontWeight: 700, color: activeTab === t.id ? '#fff' : C.page }}>{t.label}</span>
          {t.count > 0 && <span style={{ position: 'absolute', top: '6px', right: '12px', background: C.rojo, color: 'white', fontSize: '10.5px', fontWeight: 800, padding: '2px 7px', borderRadius: 999, minWidth: '16px', textAlign: 'center' }}>{t.count}</span>}
        </button>
      ))}
    </nav>
  );
};

// Login Móvil
const MobileAdminLoginScreen = ({ onLogin, administradores, isLoading, error }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginError('');
    setIsSubmitting(true);

    try {
      // 1. Intentar login con Supabase Auth
      const { data: authData, error: authError } = await supabaseAuth.signIn(email, password);
      
      if (authData && !authError) {
        const authUser = await supabaseAuth.getCurrentUser();
        if (authUser) {
          const admin = await findAdminByAuthId(authUser.id);
          if (admin) {
            onLogin(admin);
            return;
          }
        }
      }
      
      // 2. Buscar en tabla local (usuario no migrado)
      const adminLocal = administradores.find(
        a => a.email.toLowerCase() === email.toLowerCase() && a.password === password
      );
      
      if (adminLocal) {
        // Migrar a Supabase Auth
        await migrarUsuarioAAuth(email, password, adminLocal.id);
        await supabaseAuth.signIn(email, password);
        onLogin(adminLocal);
        return;
      }
      
      setLoginError('Email o contraseña incorrectos');
    } catch (err) {
      setLoginError('Error de conexión');
    }
    
    setIsSubmitting(false);
  };

  return (
    <div style={{ minHeight: '100vh', position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px', overflow: 'hidden' }}>
      <AnimatedLoginBackground cardRect={null} />
      <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: '360px', background: '#ffffff', borderRadius: 12, padding: '32px 24px', boxShadow: SOMBRA_MODAL }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', marginBottom: '28px' }}>
          <h1 style={{ fontSize: '19px', fontWeight: 800, color: C.ink, margin: '0 0 4px 0', letterSpacing: '-0.2px' }}>Panel de Administración</h1>
          <p style={{ fontSize: '12.5px', color: C.muted, margin: 0 }}>Grupo Dimoauto</p>
        </div>

        {(error || loginError) && (
          <div style={{ background: '#f9e4e3', border: '1px solid #f2ccca', borderRadius: 10, padding: '12px', marginBottom: '20px', textAlign: 'center' }}>
            <span style={{ color: '#d23b34', fontSize: '13px' }}>⚠️ {error || loginError}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            required
            style={{ width: '100%', padding: '14px 16px', marginBottom: '12px', borderRadius: 10, border: '1px solid #e7e9ec', fontSize: '15px', boxSizing: 'border-box' }}
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Contraseña"
            required
            style={{ width: '100%', padding: '14px 16px', marginBottom: '20px', borderRadius: 10, border: '1px solid #e7e9ec', fontSize: '15px', boxSizing: 'border-box' }}
          />
          <button
            type="submit"
            disabled={isSubmitting || isLoading}
            style={{ width: '100%', padding: '14px', background: isSubmitting ? '#9aa4af' : '#005187', border: 'none', borderRadius: 12, color: 'white', fontSize: '15px', fontWeight: 600, cursor: isSubmitting ? 'wait' : 'pointer' }}
          >
            {isSubmitting ? 'Verificando...' : '🔐 Iniciar Sesión'}
          </button>
        </form>

        <div style={{ marginTop: '16px', padding: '10px', background: '#f3faf6', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
          <span style={{ fontSize: '14px' }}>🔒</span>
          <span style={{ fontSize: '13px', color: '#1f7a44' }}>Conexión segura</span>
        </div>
      </div>
    </div>
  );
};

// APP MÓVIL PRINCIPAL
const MobileAdminApp = ({ admin, administradores, leads, comerciales, isLoading, onLogin, onLogout, onRefresh, authLoading, authError }) => {
  const [activeTab, setActiveTab] = useState('inicio');
  const [leadsView, setLeadsView] = useState('vendedores');
  const pendientesCount = useMemo(() => leads.filter(l => l.estado === 'Pendiente').length, [leads]);

  if (authLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f4f5f7' }}>
        <div style={{ fontSize: '19px', fontWeight: 800, color: C.ink, marginBottom: '6px' }}>Panel de Administración</div>
        <div style={{ fontSize: '12.5px', color: C.muted, marginBottom: '20px' }}>Cargando…</div>
        <div className="fg-shimmer" style={{ width: '220px', height: 10, borderRadius: 12 }} />
      </div>
    );
  }
  if (!admin) return <MobileAdminLoginScreen onLogin={onLogin} administradores={administradores} isLoading={authLoading} error={authError} />;

  const renderContent = () => {
    switch (activeTab) {
      case 'inicio': return <MobileInicioView leads={leads} comerciales={comerciales} />;
      case 'leads': return leadsView === 'vendedores' ? <MobileVendedoresView leads={leads} /> : <MobileListadoDetalleView leads={leads} />;
      case 'ventas': return <MobileVentasView leads={leads} />;
      default: return <MobileInicioView leads={leads} comerciales={comerciales} />;
    }
  };

  // Solo mostrar NavBar en Inicio y Leads
  const showNavBar = activeTab === 'inicio' || activeTab === 'leads';
  
  const viewOptions = activeTab === 'inicio' 
    ? [{ value: 'todos', label: 'Todos los Leads' }]
    : [{ value: 'vendedores', label: 'Vendedores' }, { value: 'listado', label: 'Listado Detalle' }];

  return (
    <div style={{ minHeight: '100vh', background: '#f4f5f7', fontFamily: 'inherit' }}>
      <MobileAdminHeader onLogout={onLogout} onRefresh={onRefresh} isLoading={isLoading} />
      {showNavBar && <MobileNavBar activeView={activeTab === 'leads' ? leadsView : 'todos'} setActiveView={activeTab === 'leads' ? setLeadsView : () => {}} viewOptions={viewOptions} />}
      {renderContent()}
      <MobileAdminBottomNav activeTab={activeTab} setActiveTab={setActiveTab} pendientesCount={pendientesCount} />
    </div>
  );
};

// ==================== APP PRINCIPAL ====================
export default function App() {
  const isMobile = useIsMobile();
  const [admin, setAdmin] = useState(null);

  // ── Sesión compartida de la suite (cookie de *.dimoauto.com) ──────────────
  // Si ya se entró en el Centro de Aplicaciones, aquí no se vuelve a pedir la
  // contraseña. Si no hay sesión compartida, o el correo no es administrador
  // activo, se enseña el login de siempre: esto solo añade un atajo.
  const [sesionProbada, setSesionProbada] = useState(false);
  useEffect(() => {
    let vivo = true;
    entrarConSesionCompartida(SUPABASE_URL, SUPABASE_ANON_KEY)
      .then((a) => { if (vivo && a) setAdmin(a); })
      .finally(() => { if (vivo) setSesionProbada(true); });
    return () => { vivo = false; };
  }, []);
  const [administradores, setAdministradores] = useState([]);
  const [leads, setLeads] = useState([]);
  const [comercialesList, setComercialesList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [activeTab, setActiveTab] = useState('Resumen');
  const [filters, setFilters] = useState(() => {
    // Por defecto, mostrar los leads de los últimos 30 días.
    // El usuario puede cambiar 'desde'/'hasta' o pulsar "Limpiar filtros".
    const today = new Date();
    const hasta = today.toISOString().split('T')[0];
    const desde = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    return { search: '', desde, hasta, tipoLead: 'all', marca: '', modelo: '', comercial: '' };
  });
  const [lastUpdate, setLastUpdate] = useState(null);
  const [configData, setConfigData] = useState(null);
  const [showDetalleLlamadas, setShowDetalleLlamadas] = useState(false);
  const [showNuevoLeadModal, setShowNuevoLeadModal] = useState(false);
  const [showConfiguracionModal, setShowConfiguracionModal] = useState(false);
  const [showGestionLeadsModal, setShowGestionLeadsModal] = useState(false); // NUEVO
  const [showGestionDuplicados, setShowGestionDuplicados] = useState(false); // MODAL DUPLICADOS
  const [showAuditoria, setShowAuditoria] = useState(false); // NUEVO
  const [showAsistente, setShowAsistente] = useState(false);
  const [showTelegram, setShowTelegram] = useState(false);
  const [showTraspasoMasivo, setShowTraspasoMasivo] = useState(false);
  const [showAgenda, setShowAgenda] = useState(false);
  const [showClientes, setShowClientes] = useState(false);
  const [showAutoLeadsAudit, setShowAutoLeadsAudit] = useState(false); // P7 - Dashboard equidad
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const chatUnreadCount = useChatUnread(admin?.email || '');

  useEffect(() => {
    const loadInitialData = async () => {
      setAuthLoading(true);
      try {
        // ❌ AUTO-LOGIN DESACTIVADO POR SEGURIDAD
        // Ya NO verificamos sesión automáticamente
        // El usuario DEBE hacer clic en "Iniciar sesión" cada vez
        
        // Solo cargar datos de configuración (sin auto-login)
        // 2. Cargar datos de configuración
        const configRows = await fetchConfiguracion();
        if (configRows?.length > 0) { processConfiguracion(configRows); setConfigData(configRows); }
        await loadVendedoresFromComerciales();
        const comercialesData = await fetchComerciales();
        if (comercialesData) setComercialesList(comercialesData);
        
        // 3. Cargar administradores
        const admins = await fetchAdministradores();
        if (admins?.length > 0) { setAdministradores(admins); } 
        else { setAdministradores([]); if (!admin) setAuthError('No hay administradores configurados en la base de datos'); }
      } catch (err) {
        console.error('Error en loadInitialData:', err);
        setAdministradores([]);
        setAuthError('Error de conexión con la base de datos');
      } finally { setAuthLoading(false); }
    };
    loadInitialData();
  }, []);

  const handleLogin = (adminData) => { setAdmin(adminData); setAuthError(null); };
  
  const handleLogout = async () => { 
    borrarSesion();
    await supabaseAuth.signOut(); // Cerrar sesión de Supabase Auth
    setAdmin(null); 
    setActiveTab('Resumen'); 
  };

  const loadData = async () => {
    setIsLoading(true); setError(null);
    try {
      const rows = await fetchLeads();
      if (rows === null) throw new Error('Error de conexión');
      setLeads(transformSupabaseData(rows));
      setLastUpdate(new Date().toLocaleTimeString('es-ES'));
    } catch (err) { setError(err.message); }
    finally { setIsLoading(false); }
  };

  // Real-time subscription
  useEffect(() => {
    if (!admin) return;
    
    // Cargar datos iniciales
    loadData();
    
    // Crear conexión WebSocket para Real-time
    let ws = null;
    let reconnectTimeout = null;
    
    const connectRealtime = () => {
      const wsUrl = SUPABASE_URL.replace('https://', 'wss://') + '/realtime/v1/websocket?apikey=' + SUPABASE_ANON_KEY + '&vsn=1.0.0';
      
      ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log('🔴 Real-time conectado');
        
        // Suscribirse a cambios en la tabla leads
        const subscribeMsg = {
          topic: 'realtime:public:leads',
          event: 'phx_join',
          payload: {
            config: {
              broadcast: { self: false },
              presence: { key: '' },
              postgres_changes: [
                { event: '*', schema: 'public', table: 'leads' }
              ]
            }
          },
          ref: '1'
        };
        ws.send(JSON.stringify(subscribeMsg));
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Ignorar heartbeats y confirmaciones
          if (data.event === 'phx_reply' || data.event === 'presence_state') return;
          
          // Detectar cambios en leads
          if (data.event === 'postgres_changes' || 
              data.payload?.type === 'INSERT' || 
              data.payload?.type === 'UPDATE' || 
              data.payload?.type === 'DELETE') {
            console.log('📡 Cambio detectado en leads:', data.payload?.type || 'UPDATE');
            // Recargar datos
            loadData();
          }
        } catch (e) {
          // Ignorar errores de parsing
        }
      };
      
      ws.onerror = (error) => {
        console.warn('⚠️ Error en Real-time:', error);
      };
      
      ws.onclose = () => {
        console.log('🔴 Real-time desconectado');
        // Reconectar después de 5 segundos
        reconnectTimeout = setTimeout(() => {
          if (admin) connectRealtime();
        }, 5000);
      };
      
      // Enviar heartbeat cada 30 segundos para mantener conexión
      const heartbeatInterval = setInterval(() => {
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ topic: 'phoenix', event: 'heartbeat', payload: {}, ref: Date.now().toString() }));
        }
      }, 30000);
      
      return () => clearInterval(heartbeatInterval);
    };
    
    connectRealtime();
    
    // Cleanup
    return () => {
      if (ws) {
        ws.close();
      }
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
    };
  }, [admin]);

  // YYYY-MM-DD en hora local (no UTC, para no saltar de día) de un Date o ISO
  const ymdLocal = (raw) => {
    if (raw instanceof Date && !isNaN(raw.getTime())) {
      return `${raw.getFullYear()}-${String(raw.getMonth() + 1).padStart(2, '0')}-${String(raw.getDate()).padStart(2, '0')}`;
    }
    if (typeof raw === 'string' && raw.length >= 10) return raw.slice(0, 10);
    return null;
  };

  // Filtros que NO dependen de la fecha (búsqueda, tipo, marca, modelo,
  // comercial). Se comparten entre el listado del período y el cálculo de
  // "Ventas anteriores" (leads de antes del período vendidos dentro de él).
  const pasaFiltrosSinFecha = useCallback((lead) => {
      // Búsqueda por nombre completo (case-insensitive), email, teléfono (móvil o fijo, normalizado) y origen
      if (filters.search) {
        const searchLower = filters.search.toLowerCase().trim();
        const searchPhone = normalizePhone(filters.search);
        const nombreCompleto = `${lead.nombre || ''} ${lead.apellidos || ''}`.toLowerCase().trim();

        const matchesNombre = nombreCompleto.includes(searchLower);
        const matchesEmail = lead.email?.toLowerCase().includes(searchLower);
        const matchesMovil = searchPhone.length >= 3 && (
          normalizePhone(lead.movil).includes(searchPhone) ||
          normalizePhone(lead.telefono).includes(searchPhone)
        );
        const matchesOrigen = lead.origen?.toLowerCase().includes(searchLower);

        if (!matchesNombre && !matchesEmail && !matchesMovil && !matchesOrigen) return false;
      }
      if (filters.tipoLead && filters.tipoLead !== 'all' && lead.tipo !== filters.tipoLead) return false;
      if (filters.marca && lead.marca !== filters.marca) return false;
      // Filtro de modelo usando normalización (agrupa variantes)
      if (filters.modelo && !modeloMatchesNormalized(lead.modelo, filters.modelo)) return false;
      if (filters.comercial && lead.vendedor !== filters.comercial) return false;
      return true;
  }, [filters]);

  const filteredLeads = useMemo(() => {
    return leads.filter(lead => {
      if (!pasaFiltrosSinFecha(lead)) return false;
      // Filtro por rango de fechas (fecha_creacion). 'desde' inclusivo,
      // 'hasta' inclusivo (todo el día). En este componente los leads ya
      // pasan por transformSupabaseData, que convierte la fecha string a
      // un objeto Date y lo guarda como `fechaCreacion`. También cubrimos
      // el caso `fecha_creacion` (string ISO) por si llega sin transformar.
      if (filters.desde || filters.hasta) {
        const fechaIso = ymdLocal(lead.fechaCreacion || lead.fecha_creacion);
        if (fechaIso) {
          if (filters.desde && fechaIso < filters.desde) return false;
          if (filters.hasta && fechaIso > filters.hasta) return false;
        }
      }
      return true;
    });
  }, [leads, filters, pasaFiltrosSinFecha]);

  // "Vendido (Leads anteriores)": leads que ENTRARON antes del período pero
  // cuya VENTA se marcó dentro de él. Antes era un número inventado
  // (vendidos * 0.6) — de ahí que el Resumen no cuadrara con los informes
  // del panel de Recepción, que cuentan los pedidos por fecha de venta.
  const ventasAnteriores = useMemo(() => {
    if (!filters.desde) return 0;
    return leads.filter(lead => {
      if (lead.estado !== 'Vendido') return false;
      if (!pasaFiltrosSinFecha(lead)) return false;
      const creado = ymdLocal(lead.fechaCreacion || lead.fecha_creacion);
      if (!creado || creado >= filters.desde) return false; // entró dentro del período → ya cuenta en "Leads periodo"
      const venta = ymdLocal(lead.fechaVenta || lead.fecha_venta || lead.fechaModificacion || lead.fecha_modificacion);
      if (!venta) return false;
      if (venta < filters.desde) return false;
      if (filters.hasta && venta > filters.hasta) return false;
      return true;
    }).length;
  }, [leads, filters, pasaFiltrosSinFecha]);

  if (!admin && !sesionProbada) return <Entrando />;
  // MÓVIL
  if (isMobile) {
    return <MobileAdminApp admin={admin} administradores={administradores} leads={filteredLeads} comerciales={comercialesList} isLoading={isLoading} onLogin={handleLogin} onLogout={handleLogout} onRefresh={loadData} authLoading={authLoading} authError={authError} />;
  }

  // DESKTOP
  if (!admin) return <LoginScreen onLogin={handleLogin} administradores={administradores} isLoading={authLoading} error={authError} />;
  if (isLoading && leads.length === 0) return <LoadingScreen />;
  if (error && leads.length === 0) return <ErrorScreen error={error} onRetry={loadData} />;

  const handleAddLead = async (newLeadData) => {
    // Verificar si ya existe un lead con el mismo móvil y marca
    if (newLeadData.movil && newLeadData.marca) {
      const { exists, lead: existingLead } = await checkDuplicateLead(newLeadData.movil, newLeadData.marca);
      if (exists) {
        const fechaCreacion = existingLead.fecha_creacion 
          ? new Date(existingLead.fecha_creacion).toLocaleDateString('es-ES') 
          : 'fecha desconocida';
        alert(
          `⚠️ LEAD DUPLICADO (MÓVIL + MARCA)\n\n` +
          `Ya existe un lead con este móvil (${newLeadData.movil}) para la marca ${newLeadData.marca}.\n\n` +
          `Lead existente:\n` +
          `• Cliente: ${existingLead.nombre || ''} ${existingLead.apellidos || ''}\n` +
          `• Vendedor: ${existingLead.vendedor_nombre || 'Sin asignar'}\n` +
          `• Estado: ${existingLead.estado_cita || 'Pendiente'}\n` +
          `• Fecha: ${fechaCreacion}\n\n` +
          `No se ha creado el nuevo lead.`
        );
        return false;
      }
    }
    
    // Verificar si ya existe un lead con el mismo email y marca
    if (newLeadData.email && newLeadData.marca) {
      const { exists, lead: existingLead } = await checkDuplicateByEmail(newLeadData.email, newLeadData.marca);
      if (exists) {
        const fechaCreacion = existingLead.fecha_creacion 
          ? new Date(existingLead.fecha_creacion).toLocaleDateString('es-ES') 
          : 'fecha desconocida';
        alert(
          `⚠️ LEAD DUPLICADO (EMAIL + MARCA)\n\n` +
          `Ya existe un lead con este email (${newLeadData.email}) para la marca ${newLeadData.marca}.\n\n` +
          `Lead existente:\n` +
          `• Cliente: ${existingLead.nombre || ''} ${existingLead.apellidos || ''}\n` +
          `• Email: ${existingLead.email || ''}\n` +
          `• Vendedor: ${existingLead.vendedor_nombre || 'Sin asignar'}\n` +
          `• Estado: ${existingLead.estado_cita || 'Pendiente'}\n` +
          `• Fecha: ${fechaCreacion}\n\n` +
          `No se ha creado el nuevo lead.`
        );
        return false;
      }
    }
    
    const supabaseLead = { lead_id: `lead-${Date.now()}`, lead_tipo: newLeadData.tipo || 'Lead', origen: newLeadData.origen, estado_cita: 'Pendiente', usuario_alta: admin?.nombre || 'Sistema', fecha_creacion: new Date().toISOString(), vendedor_nombre: newLeadData.vendedor, centro: newLeadData.centro, nombre: newLeadData.nombre, apellidos: newLeadData.apellidos, tipo_cliente: newLeadData.tipoCliente || 'Particular', telefono: newLeadData.telefono, movil: newLeadData.movil, email: newLeadData.email, marca: newLeadData.marca, modelo: newLeadData.modelo, matricula: newLeadData.matricula || null };
    
    const { error } = await insertLead(supabaseLead);
    if (error) {
      // Capturar error de duplicado de la base de datos
      if (error.message?.includes('duplicate') || error.message?.includes('unique')) {
        alert('⚠️ Este lead ya existe en el sistema (mismo móvil y marca).');
        return false;
      }
      alert('Error al crear el lead: ' + error.message);
      return false;
    }
    
    // Enviar email de notificación al comercial asignado
    if (newLeadData.vendedor) {
      await notifyComercialLeadUpdate(
        comercialesList,
        newLeadData.vendedor,
        { ...newLeadData, estado: 'Pendiente' },
        'new',
        admin?.nombre || 'Admin'
      );
    }
    
    await loadData();
    return true;
  };

  const handleTraspasar = async (leadId, nuevoComercial) => {
    // Obtener el lead antes de traspasarlo
    const leadToTransfer = leads.find(l => l.id === leadId);
    
    const { error } = await updateLead(leadId, { 
      vendedor_nombre: nuevoComercial,
      fecha_modificacion: new Date().toISOString()
    });
    
    if (error) throw error;
    
    // Enviar email de notificación al nuevo comercial asignado
    if (nuevoComercial && leadToTransfer) {
      await notifyComercialLeadUpdate(
        comercialesList,
        nuevoComercial,
        { ...leadToTransfer, vendedor: nuevoComercial },
        'update',
        admin?.nombre || 'Admin'
      );
    }
    
    await loadData();
  };

  // ==================== EDITAR LEAD ====================
  // Recibe (leadId, formData camelCase, diff {campo: {antes, despues}}, leadOriginal)
  // - Mapea sólo los campos cambiados a columnas snake_case de Supabase
  // - Llama a updateLead -> que ya registra 'modificar_lead' en auditoria_logs
  // - Registra adicionalmente 'editar_lead' con el diff antes/después completo
  //   para que en la sección Auditoría se vea exactamente qué cambió.
  const handleEditarLead = async (leadId, formData, diff, leadOriginal) => {
    const fieldMap = {
      tipo: 'lead_tipo',
      origen: 'origen',
      detalleOrigen: 'detalle_origen',
      campana: 'campana',
      estado: 'estado_cita',
      motivoNoCompra: 'motivo_no_compra',
      nombre: 'nombre',
      apellidos: 'apellidos',
      razonSocial: 'razon_social',
      tipoCliente: 'tipo_cliente',
      telefono: 'telefono',
      movil: 'movil',
      email: 'email',
      cp: 'cp',
      provincia: 'provincia',
      marca: 'marca',
      modelo: 'modelo',
      motor: 'motor',
      combustible: 'combustible',
      kilometros: 'kilometros',
      matricula: 'matricula',
      tipoVenta: 'tipo_venta',
      vendedor: 'vendedor_nombre',
      centro: 'centro',
      observaciones: 'observaciones'
    };

    const updates = {};
    Object.keys(diff).forEach(k => {
      const dbCol = fieldMap[k];
      if (!dbCol) return;
      let v = formData[k];
      if (k === 'motivoNoCompra' && v === '') v = null;
      if (k === 'razonSocial' && v === '') v = null;
      if (k === 'matricula' && v === '') v = null;
      updates[dbCol] = v;
    });

    if (Object.keys(updates).length === 0) {
      window.alert('ℹ️ No hay cambios reales para guardar.');
      return false;
    }

    updates.fecha_modificacion = new Date().toISOString();

    const { error } = await updateLead(leadId, updates);
    if (error) throw error;

    const clienteLabel = (leadOriginal?.razonSocial || `${leadOriginal?.nombre || ''} ${leadOriginal?.apellidos || ''}`.trim()) || 'Sin nombre';
    await registrarAuditoria('editar_lead', 'leads', leadId, {
      lead_id: leadOriginal?.leadId || null,
      cliente: clienteLabel,
      origen: leadOriginal?.origen || null,
      marca: leadOriginal?.marca || null,
      campos_modificados: Object.keys(diff),
      cambios: diff
    });

    if (diff.vendedor && formData.vendedor && leadOriginal) {
      try {
        await notifyComercialLeadUpdate(
          comercialesList,
          formData.vendedor,
          { ...leadOriginal, ...formData, vendedor: formData.vendedor },
          'update',
          admin?.nombre || 'Admin'
        );
      } catch (e) {
        console.warn('No se pudo notificar al nuevo comercial:', e);
      }
    }

    await loadData();
    return true;
  };

  const renderContent = () => {
    if (showAutoLeadsAudit) {
      return <AutoLeadsAuditPage />;
    }
    if (showAuditoria) {
      return <AuditoriaPage />;
    }
    if (showAgenda) {
      return <AgendaAdminPage leads={filteredLeads} comerciales={comercialesList} onRefresh={loadData} adminNombre={admin?.nombre} />;
    }
    if (showClientes) {
      return <ClientesAdminPage leads={filteredLeads} comerciales={comercialesList} />;
    }
    switch (activeTab) {
      case 'Resumen': return <ResumenPage leads={filteredLeads} filters={filters} ventasAnteriores={ventasAnteriores} />;
      case 'Listado de detalle': return <ListadoDetallePage leads={filteredLeads} comerciales={comercialesList} onTraspasar={handleTraspasar} onEditar={handleEditarLead} />;
      case 'Vendedores': return <AnalysisPage leads={filteredLeads} field="vendedor" dimension="Vendedor" />;
      case 'Centros': return <AnalysisPage leads={filteredLeads} field="centro" dimension="Centro" />;
      case 'Datos marcas': return <AnalysisPage leads={filteredLeads} field="marca" dimension="Marcas" />;
      case 'Leads': return <AnalysisPage leads={filteredLeads} field="origen" dimension="Origen" showColorIndicator={true} />;
      case 'Campañas': return <AnalysisPage leads={filteredLeads} field="campana" dimension="Campaña" />;
      case 'Usuarios': return <AnalysisPage leads={filteredLeads} field="usuarioAlta" dimension="Usuario" />;
      case 'Informes': return <InformesPage leads={filteredLeads} todosLeads={leads} comerciales={comercialesList} adminNombre={admin?.nombre} />;
      default: return <ResumenPage leads={filteredLeads} filters={filters} ventasAnteriores={ventasAnteriores} />;
    }
  };

  return (
    <div style={{height: '100vh', display: 'flex', flexDirection: 'column', background: '#f4f5f7', fontFamily: 'inherit', overflow: 'hidden'}}>
      <Header admin={admin} onLogout={handleLogout} onOpenChangePassword={() => setShowChangePassword(true)}
        filters={filters} setFilters={setFilters} />
      <FilterBar filters={filters} setFilters={setFilters} onRefresh={loadData} leads={leads} />
      <div style={{display: 'flex', flex: 1, overflow: 'hidden'}}>
        <SidebarNavigation
          activeTab={activeTab}
          setActiveTab={(tab) => {
            setActiveTab(tab);
            setShowAgenda(false);
            setShowAuditoria(false);
            setShowClientes(false);
            setShowAutoLeadsAudit(false);
          }}
          onOpenDetalleLlamadas={() => setShowDetalleLlamadas(true)}
          onOpenNuevoLead={() => setShowNuevoLeadModal(true)}
          onOpenConfiguracion={() => setShowConfiguracionModal(true)}
          onOpenTraspasoMasivo={() => setShowTraspasoMasivo(true)}
          onOpenAgenda={() => {
            setShowAgenda(true);
            setShowAuditoria(false);
            setShowClientes(false);
            setShowAutoLeadsAudit(false);
            setActiveTab('');
          }}
          onOpenGestionLeads={() => setShowGestionLeadsModal(true)}
          onOpenAuditoria={() => {
            setShowAuditoria(true);
            setShowAgenda(false);
            setShowClientes(false);
            setShowAutoLeadsAudit(false);
            setActiveTab('');
          }}
          onOpenClientes={() => {
            setShowClientes(true);
            setShowAgenda(false);
            setShowAuditoria(false);
            setShowAutoLeadsAudit(false);
            setActiveTab('');
          }}
          onOpenAutoLeadsAudit={() => {
            setShowAutoLeadsAudit(true);
            setShowAgenda(false);
            setShowAuditoria(false);
            setShowClientes(false);
            setActiveTab('');
          }}
          isAgendaActive={showAgenda}
          isAuditoriaActive={showAuditoria}
          isClientesActive={showClientes}
          isAutoLeadsAuditActive={showAutoLeadsAudit}
        />
        <main style={{flex: 1, padding: '24px', overflowY: 'auto', background: '#f4f5f7'}}>{renderContent()}</main>
      </div>
      {showDetalleLlamadas && <DetalleLlamadasModal leads={filteredLeads} onClose={() => setShowDetalleLlamadas(false)} />}
      {showNuevoLeadModal && <NuevoLeadModal onClose={() => setShowNuevoLeadModal(false)} onAddLead={handleAddLead} comerciales={comercialesList} />}
      {showGestionLeadsModal && <GestionLeadsModal onClose={() => setShowGestionLeadsModal(false)} onOpenNuevoLead={() => setShowNuevoLeadModal(true)} onOpenTraspasoMasivo={() => setShowTraspasoMasivo(true)} onOpenGestionDuplicados={() => setShowGestionDuplicados(true)} />}
      {showGestionDuplicados && <GestionDuplicadosModal onClose={() => setShowGestionDuplicados(false)} onRefresh={loadData} />}
      {showConfiguracionModal && <ConfiguracionModal onClose={() => setShowConfiguracionModal(false)} configData={configData} onUpdateConfig={setConfigData} />}
      {showTraspasoMasivo && <TraspasoMasivoModal leads={leads} comerciales={comercialesList} onClose={() => setShowTraspasoMasivo(false)} onTraspasar={handleTraspasar} onRefresh={loadData} />}
      {showChangePassword && <ChangePasswordModal onClose={() => setShowChangePassword(false)} admin={admin} />}
      <FloatingButtonsAdmin onOpenAsistente={() => setShowAsistente(true)} onOpenTelegram={() => setShowTelegram(true)} onOpenChat={() => setShowChat(true)} chatUnread={chatUnreadCount} />
      <FloatingModal isOpen={showChat} onClose={() => setShowChat(false)} title="💬 Mensajes a Comerciales"><ChatAdminPanel admin={admin} comerciales={comercialesList} /></FloatingModal>
      <FloatingModal isOpen={showAsistente} onClose={() => setShowAsistente(false)} title="🤖 DimoGPT"><DimoGPTPage leads={leads} comerciales={comercialesList} isFloating={true} /></FloatingModal>
      <FloatingModal isOpen={showTelegram} onClose={() => setShowTelegram(false)} title="📱 Telegram"><TelegramConfigPageAdmin admin={admin} /></FloatingModal>
    </div>
  );
}
